import React, { useEffect, useMemo, useState, useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import { api } from '../api';
import ArchiveManagement from './ArchiveManagement';
import { AuthContext } from '../AuthContext';
import { generateBlankProxyForm } from './BlankProxyForm';
import CombinedBallotView from './CombinedBallotView';
import DeviceTestTab from './DeviceTestTab';
import OTPGate from './OTPGate';
import { formatLocal, toLocal } from '../utils/timezones';

// Helper function to format dates in HOA timezone
const formatDateInHOATimezone = (isoString, hoaTimezone, format = 'MMM d, yyyy h:mm a z') => {
  if (!isoString) return 'Unknown';
  if (!hoaTimezone) return new Date(isoString).toLocaleString(); // Fallback
  return formatLocal(isoString, hoaTimezone, format);
};

// Print Nomination Form for mailing to off-platform members
const printNominationForm = (election, questions, hoaTimezone = 'America/Chicago', hoaSettings = {}) => {
  const printWindow = window.open('', '_blank');
  const currentDate = formatLocal(new Date().toISOString(), hoaTimezone, 'MMM d, yyyy');
  
  // Group questions by section
  const sections = {};
  questions.forEach(q => {
    const section = q.section || 'general';
    if (!sections[section]) sections[section] = [];
    sections[section].push(q);
  });
  
  // Generate questions HTML
  let questionsHTML = '';
  
  // Standard fields first
  questionsHTML += `
    <div class="section">
      <h3>Nominee Information</h3>
      <div class="field">
        <label>Full Name: <span class="required">*</span></label>
        <div class="input-line"></div>
      </div>
      <div class="field">
        <label>Email Address: <span class="required">*</span></label>
        <div class="input-line"></div>
      </div>
      <div class="field">
        <label>Phone Number:</label>
        <div class="input-line"></div>
      </div>
      <div class="field">
        <label>Property Address / Unit #: <span class="required">*</span></label>
        <div class="input-line"></div>
      </div>
      <div class="field">
        <label>Position Running For:</label>
        <div class="input-line"></div>
      </div>
    </div>
  `;
  
  // Custom questions by section
  const sectionLabels = {
    personal: '👤 Personal Information',
    qualifications: '🎓 Qualifications & Experience',
    platform: '📋 Platform & Goals',
    general: '📝 Additional Questions'
  };
  
  Object.keys(sections).forEach(sectionKey => {
    const sectionQuestions = sections[sectionKey];
    const sectionLabel = sectionLabels[sectionKey] || sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1);
    
    questionsHTML += `<div class="section"><h3>${sectionLabel}</h3>`;
    
    sectionQuestions.forEach((q, idx) => {
      const requiredMark = q.required ? '<span class="required">*</span>' : '';
      
      if (q.type === 'textarea') {
        questionsHTML += `
          <div class="field">
            <label>${idx + 1}. ${q.question} ${requiredMark}</label>
            <div class="textarea-box"></div>
          </div>
        `;
      } else if (q.type === 'radio' && q.options && q.options.length > 0) {
        questionsHTML += `
          <div class="field">
            <label>${idx + 1}. ${q.question} ${requiredMark}</label>
            <div class="options">
              ${q.options.map(opt => `<div class="option">☐ ${opt}</div>`).join('')}
            </div>
          </div>
        `;
      } else if (q.type === 'checkbox') {
        questionsHTML += `
          <div class="field">
            <label>${idx + 1}. ${q.question} ${requiredMark}</label>
            <div class="options">
              <div class="option">☐ Yes</div>
              <div class="option">☐ No</div>
            </div>
          </div>
        `;
      } else {
        questionsHTML += `
          <div class="field">
            <label>${idx + 1}. ${q.question} ${requiredMark}</label>
            <div class="input-line"></div>
          </div>
        `;
      }
    });
    
    questionsHTML += `</div>`;
  });
  
  const nominationHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Nomination Form - ${election?.name || 'Election'}</title>
      <style>
        body { 
          font-family: Arial, sans-serif; 
          max-width: 8.5in; 
          margin: 0 auto; 
          padding: 0.75in;
          background: white;
          font-size: 11pt;
        }
        .header { 
          text-align: center; 
          border-bottom: 2px solid #000; 
          padding-bottom: 15px; 
          margin-bottom: 20px; 
        }
        .header h1 { margin: 0 0 5px 0; font-size: 18pt; }
        .header h2 { margin: 0 0 10px 0; font-size: 14pt; font-weight: normal; color: #333; }
        .header p { margin: 5px 0; color: #666; font-size: 10pt; }
        .instructions {
          background: #f5f5f5;
          padding: 15px;
          border-radius: 5px;
          margin-bottom: 20px;
          font-size: 10pt;
        }
        .instructions h4 { margin: 0 0 10px 0; }
        .instructions ul { margin: 0; padding-left: 20px; }
        .instructions li { margin-bottom: 5px; }
        .section {
          margin-bottom: 25px;
          page-break-inside: avoid;
        }
        .section h3 {
          background: #e0e0e0;
          padding: 8px 12px;
          margin: 0 0 15px 0;
          font-size: 12pt;
          border-radius: 3px;
        }
        .field {
          margin-bottom: 15px;
        }
        .field label {
          display: block;
          margin-bottom: 5px;
          font-weight: 500;
        }
        .required { color: red; }
        .input-line {
          border-bottom: 1px solid #999;
          height: 25px;
          margin-top: 5px;
        }
        .textarea-box {
          border: 1px solid #999;
          min-height: 80px;
          margin-top: 5px;
          border-radius: 3px;
        }
        .options {
          margin-top: 5px;
        }
        .option {
          display: inline-block;
          margin-right: 20px;
          padding: 3px 0;
        }
        .signature-section {
          margin-top: 30px;
          padding-top: 20px;
          border-top: 2px solid #000;
        }
        .signature-line {
          display: flex;
          gap: 30px;
          margin-top: 30px;
        }
        .signature-field {
          flex: 1;
        }
        .signature-field .line {
          border-bottom: 1px solid #000;
          height: 30px;
        }
        .signature-field label {
          font-size: 9pt;
          color: #666;
        }
        .footer {
          margin-top: 30px;
          text-align: center;
          font-size: 9pt;
          color: #666;
          border-top: 1px solid #ddd;
          padding-top: 15px;
        }
        @media print {
          body { padding: 0.5in; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>📋 Official Nomination Form</h1>
        <h2>${election?.name || 'Board Election'}</h2>
        <p>Please complete this form and return it by the nomination deadline</p>
        ${election?.nominations_close ? `<p><strong>Deadline:</strong> ${formatLocal(election.nominations_close, hoaTimezone, 'MMMM d, yyyy')}</p>` : ''}
      </div>
      
      <div class="instructions">
        <h4>📌 Instructions:</h4>
        <ul>
          <li>Complete all required fields marked with <span class="required">*</span></li>
          <li>Print clearly in blue or black ink</li>
          <li>Sign and date the form at the bottom</li>
          <li>Return the completed form via:
            <ul style="margin-top: 5px;">
              ${hoaSettings?.hoa_email ? `<li><strong>Email:</strong> ${hoaSettings.hoa_email}</li>` : ''}
              ${hoaSettings?.mailing_address ? `<li><strong>Mail:</strong> ${hoaSettings.mailing_address}</li>` : ''}
              ${!hoaSettings?.hoa_email && !hoaSettings?.mailing_address ? '<li>Contact your HOA office for return instructions</li>' : ''}
            </ul>
          </li>
          <li>Keep a copy for your records</li>
        </ul>
      </div>
      
      ${questionsHTML}
      
      <div class="signature-section">
        <h3>Certification & Signature</h3>
        <p style="font-size: 10pt; margin-bottom: 20px;">
          I hereby certify that I am a member in good standing of this association, I meet all eligibility 
          requirements for the position(s) indicated, and all information provided in this nomination form 
          is true and accurate to the best of my knowledge.
        </p>
        <div class="signature-line">
          <div class="signature-field">
            <div class="line"></div>
            <label>Nominee Signature</label>
          </div>
          <div class="signature-field" style="max-width: 150px;">
            <div class="line"></div>
            <label>Date</label>
          </div>
        </div>
      </div>
      
      <div class="footer">
        <p>Form generated on ${currentDate}</p>
        <p>For questions, contact your HOA management office</p>
      </div>
    </body>
    </html>
  `;
  
  printWindow.document.write(nominationHTML);
  printWindow.document.close();
  printWindow.focus();
  
  // Auto-trigger print dialog
  setTimeout(() => {
    printWindow.print();
  }, 500);
};

// Print all submitted nominations for the board's records
const printSubmittedNominations = (election, nominations, hoaTimezone = 'America/Chicago', hoaSettings = {}) => {
  const printWindow = window.open('', '_blank');
  const currentDate = formatLocal(new Date().toISOString(), hoaTimezone, 'MMM d, yyyy h:mm a');
  
  // Group nominations by position
  const byPosition = {};
  nominations.forEach(nom => {
    const position = nom.position || nom.role || 'Board Position';
    if (!byPosition[position]) byPosition[position] = [];
    byPosition[position].push(nom);
  });
  
  // Generate nominations HTML
  let nominationsHTML = '';
  
  Object.keys(byPosition).forEach(position => {
    const positionNominations = byPosition[position];
    nominationsHTML += `
      <div class="position-section">
        <h3>📌 ${position} (${positionNominations.length} nominee${positionNominations.length > 1 ? 's' : ''})</h3>
        ${positionNominations.map((nom, idx) => `
          <div class="nominee-card">
            <div class="nominee-header">
              <strong>${idx + 1}. ${nom.name || 'Unknown'}</strong>
              <span class="status status-${(nom.status || 'pending').toLowerCase().replace('_', '-')}">${nom.status || 'Pending'}</span>
            </div>
            <div class="nominee-details">
              <div class="detail-row">
                <span class="label">Property:</span>
                <span>${nom.property_identifier || 'Not provided'}</span>
              </div>
              <div class="detail-row">
                <span class="label">Nominated By:</span>
                <span>${nom.nominated_by_type === 'Self' ? 'Self-nomination' : (nom.nominated_by_name || nom.nominated_by_email || 'Another member')}</span>
              </div>
              <div class="detail-row">
                <span class="label">Submitted:</span>
                <span>${nom.created_at ? formatLocal(nom.created_at, hoaTimezone, 'MMM d, yyyy h:mm a') : 'Unknown'}</span>
              </div>
              ${nom.bio ? `
                <div class="bio-section">
                  <span class="label">Bio/Statement:</span>
                  <p>${nom.bio}</p>
                </div>
              ` : ''}
              ${nom.platform ? `
                <div class="bio-section">
                  <span class="label">Platform:</span>
                  <p>${nom.platform}</p>
                </div>
              ` : ''}
              ${nom.experience ? `
                <div class="bio-section">
                  <span class="label">Experience:</span>
                  <p>${nom.experience}</p>
                </div>
              ` : ''}
              ${nom.skills ? `
                <div class="bio-section">
                  <span class="label">Skills:</span>
                  <p>${nom.skills}</p>
                </div>
              ` : ''}
              ${nom.priorities ? `
                <div class="bio-section">
                  <span class="label">Priorities:</span>
                  <p>${nom.priorities}</p>
                </div>
              ` : ''}
              ${nom.previous_service ? `
                <div class="bio-section">
                  <span class="label">Previous Service:</span>
                  <p>${nom.previous_service}</p>
                </div>
              ` : ''}
              ${nom.accomplishments ? `
                <div class="bio-section">
                  <span class="label">Accomplishments:</span>
                  <p>${nom.accomplishments}</p>
                </div>
              ` : ''}
              ${nom.time_commitment ? `
                <div class="bio-section">
                  <span class="label">Time Commitment:</span>
                  <p>${nom.time_commitment}</p>
                </div>
              ` : ''}
              ${nom.nomination_responses && Object.keys(nom.nomination_responses).length > 0 ? `
                <div class="answers-section">
                  <span class="label">Additional Responses:</span>
                  ${Object.entries(nom.nomination_responses).map(([q, a]) => `
                    <div class="answer-item">
                      <em>${q}:</em> ${a || 'No answer'}
                    </div>
                  `).join('')}
                </div>
              ` : ''}
            </div>
          </div>
        `).join('')}
      </div>
    `;
  });
  
  const nominationsReport = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Submitted Nominations - ${election?.name || 'Election'}</title>
      <style>
        body { 
          font-family: Arial, sans-serif; 
          max-width: 8.5in; 
          margin: 0 auto; 
          padding: 0.5in;
          background: white;
          font-size: 10pt;
        }
        .header { 
          text-align: center; 
          border-bottom: 2px solid #000; 
          padding-bottom: 15px; 
          margin-bottom: 20px; 
        }
        .header h1 { margin: 0 0 5px 0; font-size: 18pt; }
        .header h2 { margin: 0 0 10px 0; font-size: 14pt; font-weight: normal; color: #333; }
        .header p { margin: 5px 0; color: #666; font-size: 9pt; }
        .summary {
          background: #f0f0f0;
          padding: 10px 15px;
          border-radius: 5px;
          margin-bottom: 20px;
          font-size: 10pt;
        }
        .summary-item { display: inline-block; margin-right: 30px; }
        .position-section {
          margin-bottom: 25px;
          page-break-inside: avoid;
        }
        .position-section h3 {
          background: #333;
          color: white;
          padding: 8px 12px;
          margin: 0 0 15px 0;
          font-size: 12pt;
          border-radius: 3px;
        }
        .nominee-card {
          border: 1px solid #ddd;
          border-radius: 5px;
          margin-bottom: 15px;
          overflow: hidden;
        }
        .nominee-header {
          background: #f5f5f5;
          padding: 10px 15px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #ddd;
        }
        .status {
          padding: 3px 8px;
          border-radius: 3px;
          font-size: 9pt;
          text-transform: uppercase;
        }
        .status-confirmed, .status-approved, .status-accepted { background: #d4edda; color: #155724; }
        .status-pending { background: #fff3cd; color: #856404; }
        .status-rejected, .status-declined { background: #f8d7da; color: #721c24; }
        .nominee-details {
          padding: 15px;
        }
        .detail-row {
          display: flex;
          margin-bottom: 5px;
        }
        .detail-row .label {
          width: 120px;
          color: #666;
          font-weight: 500;
        }
        .bio-section, .answers-section {
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid #eee;
        }
        .bio-section p, .answers-section p {
          margin: 5px 0;
          font-style: italic;
          color: #444;
        }
        .answer-item {
          margin: 5px 0 5px 15px;
          font-size: 9pt;
        }
        .footer {
          margin-top: 30px;
          text-align: center;
          font-size: 9pt;
          color: #666;
          border-top: 1px solid #ddd;
          padding-top: 15px;
        }
        @media print {
          body { padding: 0.3in; }
          .position-section { page-break-inside: avoid; }
          .nominee-card { page-break-inside: avoid; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>📋 Submitted Nominations Report</h1>
        <h2>${election?.name || 'Board Election'}</h2>
        <p>Generated: ${currentDate}</p>
        ${hoaSettings?.hoa_name ? `<p>${hoaSettings.hoa_name}</p>` : ''}
      </div>
      
      <div class="summary">
        <div class="summary-item"><strong>Total Nominees:</strong> ${nominations.length}</div>
        <div class="summary-item"><strong>Positions:</strong> ${Object.keys(byPosition).length}</div>
        <div class="summary-item"><strong>Confirmed:</strong> ${nominations.filter(n => ['confirmed', 'approved', 'accepted'].includes(n.status?.toLowerCase())).length}</div>
        <div class="summary-item"><strong>Pending:</strong> ${nominations.filter(n => n.status?.toLowerCase() === 'pending' || !n.status).length}</div>
      </div>
      
      ${nominationsHTML}
      
      <div class="footer">
        <p>This is an official record of nominations submitted for ${election?.name || 'this election'}</p>
        ${hoaSettings?.hoa_email ? `<p>Contact: ${hoaSettings.hoa_email}</p>` : ''}
      </div>
    </body>
    </html>
  `;
  
  printWindow.document.write(nominationsReport);
  printWindow.document.close();
  printWindow.focus();
  
  setTimeout(() => {
    printWindow.print();
  }, 500);
};

// Helper function to check if voting add-on is enabled
const hasVotingAddon = (hoaSettings) => {
  if (!hoaSettings?.subscription) return false;
  
  // Check add_ons array (hoa_subscriptions collection format - CURRENT)
  if (Array.isArray(hoaSettings.subscription.add_ons)) {
    const hasAddon = hoaSettings.subscription.add_ons.some(addon => 
      addon.id === 'voting_platform' || addon.addon_id === 'voting_platform'
    );
    if (hasAddon) return true;
  }
  
  // Check active_addons array (legacy)
  if (Array.isArray(hoaSettings.subscription.active_addons) && 
      hoaSettings.subscription.active_addons.includes('voting_platform')) {
    return true;
  }
  
  // Check if addons array exists and includes voting_platform (legacy)
  if (Array.isArray(hoaSettings.subscription.addons) && 
      hoaSettings.subscription.addons.includes('voting_platform')) {
    return true;
  }
  
  // Check if plan.add_ons exists (Stripe format - legacy)
  if (hoaSettings.subscription.plan?.add_ons && Array.isArray(hoaSettings.subscription.plan.add_ons)) {
    const hasAddon = hoaSettings.subscription.plan.add_ons.some(addon => 
      addon.addon_id === 'voting_platform' || addon.id === 'voting_platform'
    );
    if (hasAddon) return true;
  }
  
  return false;
};

// Printable Ballot Function
const printBallot = (election, candidates, hoaName = 'HOA', hoaTimezone = 'America/Chicago') => {
  const printWindow = window.open('', '_blank');
  const currentDate = formatLocal(new Date().toISOString(), hoaTimezone, 'MMM d, yyyy');
  
  const hasCandidates = candidates && candidates.length > 0;
  const hasTopics = election.special_topics && election.special_topics.length > 0;
  
  const ballotHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Official Ballot - ${election.name}</title>
      <style>
        body { 
          font-family: Arial, sans-serif; 
          max-width: 8.5in; 
          margin: 0 auto; 
          padding: 1in;
          background: white;
        }
        .header { 
          text-align: center; 
          border-bottom: 2px solid #000; 
          padding-bottom: 20px; 
          margin-bottom: 30px; 
        }
        .ballot-title { 
          font-size: 24px; 
          font-weight: bold; 
          margin-bottom: 10px; 
        }
        .election-info { 
          font-size: 14px; 
          color: #666; 
          margin-bottom: 20px; 
        }
        .instructions { 
          background: #f5f5f5; 
          padding: 15px; 
          margin-bottom: 30px; 
          border-left: 4px solid #333; 
        }
        .section { 
          margin-bottom: 30px; 
        }
        .section-title {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 15px;
          padding-bottom: 10px;
          border-bottom: 1px solid #ddd;
        }
        .candidate { 
          display: flex; 
          align-items: center; 
          padding: 15px; 
          border: 1px solid #ddd; 
          margin-bottom: 10px; 
        }
        .motion-item {
          border: 1px solid #ddd;
          padding: 15px;
          margin-bottom: 15px;
        }
        .motion-title {
          font-size: 16px;
          font-weight: bold;
          margin-bottom: 10px;
        }
        .motion-options {
          margin-top: 10px;
          padding-left: 20px;
        }
        .motion-option {
          display: flex;
          align-items: center;
          margin-bottom: 8px;
        }
        .checkbox { 
          width: 20px; 
          height: 20px; 
          border: 2px solid #000; 
          margin-right: 15px; 
          flex-shrink: 0; 
        }
        .candidate-info { 
          flex-grow: 1; 
        }
        .candidate-name { 
          font-size: 18px; 
          font-weight: bold; 
          margin-bottom: 5px; 
        }
        .candidate-bio { 
          font-size: 14px; 
          color: #666; 
        }
        .footer { 
          margin-top: 50px; 
          border-top: 1px solid #ddd; 
          padding-top: 20px; 
          font-size: 12px; 
          color: #666; 
        }
        .signature-line { 
          margin-top: 30px; 
          border-bottom: 1px solid #000; 
          width: 300px; 
          height: 20px; 
        }
        @media print {
          body { margin: 0; padding: 0.5in; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="ballot-title">OFFICIAL BALLOT</div>
        <div class="election-info">
          <strong>${election.name}</strong><br>
          Voting Period: ${formatLocal(election.voting_open, hoaTimezone, 'MMM d, yyyy')} - ${formatLocal(election.meeting_start_time || election.voting_close, hoaTimezone, 'MMM d, yyyy')}<br>
          Ballot Generated: ${currentDate}
        </div>
      </div>

      <div class="instructions">
        <strong>VOTING INSTRUCTIONS:</strong><br>
        • Mark your choice clearly with a pen or pencil<br>
        ${hasCandidates ? '• Vote for only ONE candidate per position<br>' : ''}
        ${hasTopics ? '• Mark ONE answer for each motion<br>' : ''}
        • Return completed ballot to the designated election official<br>
        • Ballots must be received by the voting deadline
      </div>

      ${hasCandidates ? `
        <div class="section">
          <h3 class="section-title">Board Member Positions</h3>
          ${candidates.map(candidate => `
            <div class="candidate">
              <div class="checkbox"></div>
              <div class="candidate-info">
                <div class="candidate-name">${candidate.name}</div>
                <div class="candidate-bio">${candidate.bio || 'No biography provided'}</div>
                ${candidate.position ? `<div><strong>Position:</strong> ${candidate.position}</div>` : ''}
              </div>
            </div>
          `).join('')}
        </div>
      ` : ''}

      ${hasTopics ? `
        <div class="section">
          <h3 class="section-title">Motions & Special Topics</h3>
          ${election.special_topics.map((topic, index) => `
            <div class="motion-item">
              <div class="motion-title">${index + 1}. ${topic.name}</div>
              ${topic.description ? `<p style="font-size: 14px; color: #666; margin-bottom: 10px;">${topic.description}</p>` : ''}
              <div class="motion-options">
                ${topic.answers && topic.answers.map(answer => `
                  <div class="motion-option">
                    <div class="checkbox"></div>
                    <span style="font-size: 16px;">${answer}</span>
                  </div>
                `).join('')}
              </div>
            </div>
          `).join('')}
        </div>
      ` : ''}

      <div class="footer">
        <div style="margin-top: 15px; font-size: 10px;">
          This ballot is for the ${election.name}. Only registered homeowners may vote.
          Questions? Contact your HOA board.
        </div>
        <div style="margin-top: 15px; font-size: 10px; font-style: italic;">
          <strong>Secret Ballot:</strong> Your vote is anonymous. Do not include identifying information.
        </div>
      </div>

      <div class="no-print" style="text-align: center; margin-top: 30px;">
        <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px;">Print Ballot</button>
        <button onclick="window.close()" style="padding: 10px 20px; font-size: 16px; margin-left: 10px;">Close</button>
      </div>
    </body>
    </html>
  `;
  
  printWindow.document.write(ballotHTML);
  printWindow.document.close();
};

export default function VotingTab() {
  const { user } = useContext(AuthContext);
  const [election, setElection] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [hoaData, setHoaData] = useState(null);
  
  // Helper function to get HOA name dynamically
  const getHoaName = () => {
    return (hoaData?.name) || (user?.hoa_name) || 'HOA';
  };

  // Load HOA data
  useEffect(() => {
    const loadHoaData = async () => {
      try {
        if (user?.hoa_id) {
          const response = await api.get(`/api/hoa/profile`, {
            headers: { 'X-HOA-ID': user.hoa_id }
          });
          setHoaData(response.data);
        }
      } catch (error) {
        console.error('Error loading HOA data:', error);
      }
    };
    loadHoaData();
  }, [user?.hoa_id]);
  const [stats, setStats] = useState([]);
  const [consent, setConsent] = useState(false);
  const [proxy, setProxy] = useState({ type: 'default' });
  const [loading, setLoading] = useState(true);
  // Check if user is a team member (to hide e-consent)
  const teamRoles = ['team_member', 'maintenance', 'security', 'staff', 'attorney', 'accountant'];
  const isTeamMember = teamRoles.includes(user?.role?.toLowerCase());
  
  const [activeTab, setActiveTab] = useState('voting');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [consentStats, setConsentStats] = useState(null);
  const [elections, setElections] = useState([]);
  const [selectedElectionResults, setSelectedElectionResults] = useState(null);
  const [showCombinedBallot, setShowCombinedBallot] = useState(false);
  const [polls, setPolls] = useState([]);
  const [pendingPolls, setPendingPolls] = useState([]);
  const [archivedPolls, setArchivedPolls] = useState([]);
  const [pollSettings, setPollSettings] = useState({
    poll_creation_permissions: 'board_only',
    one_vote_per_property: false,
    require_poll_approval: false
  });
  const [isBoard, setIsBoard] = useState(false);
  const [showCreateElection, setShowCreateElection] = useState(false);
  const [showCreatePoll, setShowCreatePoll] = useState(false);
  const [showPollSettings, setShowPollSettings] = useState(false);
  const [showSaveLoadModal, setShowSaveLoadModal] = useState(false);
  const [boardMembers, setBoardMembers] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showMinutesPreview, setShowMinutesPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [meetingNotes, setMeetingNotes] = useState('');
  const [actionItems, setActionItems] = useState([]);
  const [liveVotingAccess, setLiveVotingAccess] = useState({
    canViewBod: false,
    canInteractBod: false,
    canViewCommittee: false,
    canInteractCommittee: false,
    canCreatePolls: false,
    settings: {
      live_voting_bod: {
        view_permissions: 'board_only',
        interaction_permissions: 'board_only',
        specific_users: []
      },
      live_voting_committee: {
        view_permissions: 'board_and_committee', 
        interaction_permissions: 'board_and_committee',
        specific_users: []
      },
      poll_creation_permissions: 'all_homeowners'
    }
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Committee and meeting types state
  const [committees, setCommittees] = useState([]);
  const [loadingCommittees, setLoadingCommittees] = useState(false);
  
  // HOA settings for meeting minutes
  const [hoaSettings, setHoaSettings] = useState({
    hoa_name: '',
    mailing_address: '',
    hoa_email: '',
    phone: '',
    website: ''
  });
  
  // HOA timezone for correct date display
  const [hoaTimezone, setHoaTimezone] = useState('America/Chicago'); // Default fallback
  
  // Helper to format date in HOA timezone
  const formatHOADate = (isoString, format) => {
    return formatDateInHOATimezone(isoString, hoaTimezone, format);
  };
  
  // Meeting rules from settings (SINGLE SOURCE OF TRUTH)
  const [meetingRules, setMeetingRules] = useState([]);
  const [speakingTimeMinutes, setSpeakingTimeMinutes] = useState(3);
  const [rollCallDisplayMode, setRollCallDisplayMode] = useState('name_only'); // NEW: name_only, lot_only, or address
  
  // Load committees from backend
  const loadCommittees = async () => {
    setLoadingCommittees(true);
    try {
      const response = await api.get(`/api/hoa/${user?.hoa_id}/committees`);
      setCommittees(response.data?.committees || []);
    } catch (error) {
      console.error('Error loading committees:', error);
      setCommittees([]);
    }
    setLoadingCommittees(false);
  };

  // Load HOA settings for meeting minutes
  const loadHoaSettings = async () => {
    try {
      const response = await api.get(`/api/hoa/${user?.hoa_id}/settings`);
      if (response.data?.success && response.data?.settings) {
        setHoaSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error loading HOA settings for meeting minutes:', error);
    }
  };
  
  // Load HOA timezone from main HOA document
  const loadHoaTimezone = async () => {
    try {
      const response = await api.get(`/api/hoa/${user?.hoa_id}`);
      if (response.data?.success && response.data?.hoa?.timezone) {
        setHoaTimezone(response.data.hoa.timezone);
        console.log('✅ Loaded HOA timezone:', response.data.hoa.timezone);
      }
    } catch (error) {
      console.error('Error loading HOA timezone:', error);
    }
  };
  
  // Load meeting rules from settings (SINGLE SOURCE OF TRUTH)
  const loadMeetingRules = async () => {
    try {
      const response = await api.get(`/api/hoa/${user?.hoa_id}/voting-settings`);
      if (response.data?.success && response.data?.settings) {
        const settings = response.data.settings;
        const rules = settings.meeting_rules || [];
        const speakingTime = settings.speaking_time_limit_minutes || 3;
        const rollCallMode = settings.roll_call_display_mode || 'name_only';
        
        // Store roll call display mode for use in attendance formatting
        setRollCallDisplayMode(rollCallMode);
        
        // Replace {speaking_time} placeholder with actual value
        const processedRules = rules.map(rule => 
          rule.replace('{speaking_time}', speakingTime.toString())
        );
        
        setMeetingRules(processedRules);
        setSpeakingTimeMinutes(speakingTime);
        
        console.log('📋 Loaded meeting rules from settings:', processedRules);
      }
    } catch (error) {
      console.error('Error loading meeting rules:', error);
      // Fallback to default rules
      setMeetingRules([
        "Members of the HOA will be allotted 3 minutes to speak, address issues, concerns or ask questions that are listed on the agenda, during 'Open to the Floor'.",
        "Board members have no time restriction for responses.",
        "Speakers will be reminded to state their name and property identifier, speak calmly, and clearly for all to hear.",
        "Board members and homeowners are reminded that all ideas are welcome and this is a safe space for everyone to voice their concerns even in difference of opinions.",
        "To transact such other business as may properly come before the Board or any Adjournment or Postponement thereof."
      ]);
    }
  };
  
  // Helper function to format member display based on roll call setting
  const formatMemberDisplay = (member) => {
    const memberName = member.name || member.user_id || 'Unknown';
    
    if (rollCallDisplayMode === 'name_only') {
      return memberName;
    } else if (rollCallDisplayMode === 'lot_only') {
      const lot = member.property_identifier || member.unit || member.lot || member.property_number || '';
      return lot ? `${memberName} (Lot ${lot})` : memberName;
    } else if (rollCallDisplayMode === 'address') {
      const address = member.property_address || member.address || '';
      return address ? `${memberName} (${address})` : memberName;
    }
    return memberName;
  };

  // Check Live Voting access permissions based on new settings
  const checkLiveVotingAccess = async () => {
    if (!user || !user.hoa_id) return;
    
    // Define role variables outside try block so they're available in catch
    const userRole = user?.role?.toLowerCase() || '';
    const isBoardMember = ['board_member', 'president', 'vice_president', 'treasurer', 'secretary'].includes(userRole);
    let isCommittee = userRole === 'committee_member' || (user?.committees && user.committees.length > 0);
    const userId = user?.id;
    
    // Set isBoard state
    setIsBoard(isBoardMember);
    
    // CRITICAL: Check committee membership FIRST, before any API calls that might fail
    // This ensures isCommittee is always set correctly even if other API calls fail
    try {
      const committeesResp = await api.get(`/api/hoa/${user?.hoa_id}/committees`);
      const committeesList = committeesResp.data?.committees || [];
      const isMemberByServer = committeesList.some(c => 
        Array.isArray(c.members) && c.members.some(m => 
          (m.user_id === userId) || 
          (m.id === userId) || 
          (m.email && user?.email && m.email.toLowerCase() === user.email.toLowerCase())
        )
      );
      // CRITICAL: Use server-side check as source of truth
      isCommittee = isMemberByServer;
      
      console.log('🔍 Committee membership server check:', {
        userId,
        userEmail: user?.email,
        isMemberByServer,
        committeesFound: committeesList.length
      });
    } catch (e) {
      console.warn('Committee membership check failed; proceeding with local role hints', e);
    }
    
    console.log('[VOTING ACCESS DEBUG]', {
      email: user?.email,
      role: userRole,
      isBoardMember,
      isCommittee,
      committees: user?.committees
    });
    
    try {
      const response = await api.get(`/api/hoa/${user?.hoa_id}/voting/settings`);
      const settings = response.data?.settings || {};
      
      console.log('🔍 Live Voting Access Check:');
      console.log('User Info:', { 
        name: `${user?.first_name} ${user?.last_name}`,
        email: user?.email,
        userRole, 
        isBoard: isBoardMember, 
        isCommittee, 
        userId,
        committees: user?.committees 
      });
      console.log('Settings received:', settings);
      
      // BOD Live Voting Access
      const bodSettings = settings.live_voting_bod || {};
      let canViewBod = false;
      let canInteractBod = false;
      
      // BOD Live Voting Access - FIX: Allow homeowners to VIEW but not INTERACT with live voting
      // This addresses user request: "I would like to see as a homeowner the live voting taking place"
      if (bodSettings.view_permissions === 'board_only' && isBoardMember) {
        canViewBod = true;
      } else if (bodSettings.view_permissions === 'all_members') {
        canViewBod = true;
      } else if (bodSettings.specific_users && bodSettings.specific_users.includes(userId)) {
        canViewBod = true;
      }
      
      // FIX: Enable BOD viewing for homeowners - they can see motions live but cannot vote
      // TEAM MEMBERS: Only allow if they have polls_access permission
      if (!canViewBod) {
        const teamRoles = ['team_member', 'maintenance', 'security', 'staff', 'attorney', 'accountant'];
        const isTeamMember = teamRoles.includes(userRole);
        
        if (isTeamMember) {
          // Team members need explicit polls_access permission to view Live Voting
          const teamMember = hoaData?.team_members?.find(tm => tm.email === user?.email);
          canViewBod = teamMember?.permissions?.includes('polls_access') || false;
          console.log('Team member BOD access check:', { hasPermission: canViewBod });
        } else {
          // Homeowners get access by default
          canViewBod = true;
          console.log('FIX: Enabling BOD view for homeowners - they can see motions but not interact');
        }
      }
      
      if (bodSettings.interaction_permissions === 'board_only' && isBoardMember) {
        canInteractBod = true;
      } else if (bodSettings.specific_users && bodSettings.specific_users.includes(userId)) {
        canInteractBod = true;
      }
      
      // Committee Live Voting Access
      const committeeSettings = settings.live_voting_committee || {};
      let canViewCommittee = false;
      let canInteractCommittee = false;
      
      // CRITICAL: Board members ALWAYS get access to committee tab
      if (isBoardMember) {
        canViewCommittee = true;
      } else if (!committeeSettings.view_permissions || committeeSettings.view_permissions === 'board_and_committee') {
        // Default: If no settings configured OR board_and_committee, allow committee members
        canViewCommittee = isCommittee;
      } else if (committeeSettings.view_permissions === 'all_members') {
        // Team members need explicit polls_access permission
        const teamRoles = ['team_member', 'maintenance', 'security', 'staff', 'attorney', 'accountant'];
        const isTeamMember = teamRoles.includes(userRole);
        if (isTeamMember) {
          const teamMember = hoaData?.team_members?.find(tm => tm.email === user?.email);
          canViewCommittee = teamMember?.permissions?.includes('polls_access') || false;
        } else {
          canViewCommittee = true;
        }
      } else if (committeeSettings.view_permissions === 'committee_only' && isCommittee) {
        // If explicitly set to committee_only, allow committee members
        canViewCommittee = true;
      } else if (committeeSettings.specific_users && committeeSettings.specific_users.includes(userId)) {
        canViewCommittee = true;
      }
      
      // CRITICAL: Board members ALWAYS can interact with committee tab
      if (isBoardMember) {
        canInteractCommittee = true;
      } else if (!committeeSettings.interaction_permissions || committeeSettings.interaction_permissions === 'board_and_committee') {
        // Default: If no settings configured OR board_and_committee, allow committee members
        canInteractCommittee = isCommittee;
      } else if (committeeSettings.interaction_permissions === 'committee_only' && isCommittee) {
        // If explicitly set to committee_only, allow committee members
        canInteractCommittee = true;
      } else if (committeeSettings.specific_users && committeeSettings.specific_users.includes(userId)) {
        canInteractCommittee = true;
      }
      
      // Poll Creation Permissions
      let canCreatePolls = false;
      const pollPermissions = settings.poll_creation_permissions || 'all_homeowners';
      
      if (pollPermissions === 'all_homeowners') {
        canCreatePolls = true;
      } else if (pollPermissions === 'board_and_committee' && (isBoardMember || isCommittee)) {
        canCreatePolls = true;
      } else if (pollPermissions === 'specific_users') {
        const specificUsers = settings.poll_creation_specific_users || [];
        // CRITICAL FIX: Compare as strings to handle type mismatches
        canCreatePolls = specificUsers.map(id => String(id)).includes(String(userId));
        console.log('🔍 Poll specific users check:', { specificUsers, userId, canCreatePolls });
      } else if (pollPermissions === 'board_only' && isBoardMember) {
        canCreatePolls = true;
      }
      
      console.log('🎯 Final access permissions:', { 
        canViewBod, 
        canInteractBod, 
        canViewCommittee, 
        canInteractCommittee, 
        canCreatePolls 
      });
      
      // Determine canCreatePolls based on settings
      let finalCanCreatePolls = false;
      if (settings?.poll_creation_permissions === 'all_homeowners') {
        finalCanCreatePolls = true;
      } else if (settings?.poll_creation_permissions === 'board_only') {
        finalCanCreatePolls = isBoardMember;
      } else if (settings?.poll_creation_permissions === 'specific_committees') {
        const specificCommittees = settings?.poll_creation_specific_committees || [];
        const userRole = user?.role?.toLowerCase() || '';
        finalCanCreatePolls = specificCommittees.some(committee => userRole.includes(committee.toLowerCase()));
      } else if (settings?.poll_creation_permissions === 'specific_users') {
        const specificUsers = settings?.poll_creation_specific_users || [];
        // Normalize both user IDs to strings for comparison
        const normalizedUserId = String(userId);
        const normalizedSpecificUsers = specificUsers.map(id => String(id));
        finalCanCreatePolls = normalizedSpecificUsers.includes(normalizedUserId);
        
        console.log('🔍 Specific Users Poll Permission Check:');
        console.log('  Current User ID:', userId, '(type:', typeof userId, ')');
        console.log('  Normalized User ID:', normalizedUserId);
        console.log('  Specific Users:', specificUsers);
        console.log('  Normalized Specific Users:', normalizedSpecificUsers);
        console.log('  Can Create Polls:', finalCanCreatePolls);
      }
      
      console.log('🔍 Setting Live Voting Access:');
      console.log('  canViewCommittee:', canViewCommittee);
      console.log('  isCommittee:', isCommittee);
      console.log('  isBoardMember:', isBoardMember);
      console.log('  committeeSettings:', committeeSettings);
      
      setLiveVotingAccess({
        canViewBod,
        canInteractBod,
        canViewCommittee, // Use calculated value from settings check above
        canInteractCommittee,
        canCreatePolls: finalCanCreatePolls,
        settings
      });
    } catch (error) {
      console.error('Error checking live voting access:', error);
      console.log('🔍 FALLBACK - Live Voting Access (error occurred):');
      console.log('  isBoardMember:', isBoardMember);
      console.log('  isCommittee:', isCommittee);
      console.log('  userId:', userId);
      console.log('  user?.email:', user?.email);
      console.log('  user?.role:', user?.role);
      
      // Fallback: sensible defaults from your request
      const fallbackCanViewCommittee = isBoardMember || isCommittee;
      
      console.log('  fallbackCanViewCommittee:', fallbackCanViewCommittee);
      
      setLiveVotingAccess({
        canViewBod: true,              // everyone can view BOD
        canInteractBod: isBoardMember,       // only board can process BOD
        canViewCommittee: fallbackCanViewCommittee, // Board or committee members
        canInteractCommittee: isBoardMember || isCommittee, // board or committee can process
        canCreatePolls: isBoardMember,       // default: board only
        settings: {}
      });
    }
  };

  // Meeting Minutes Preview Functions
  const showMeetingMinutesPreview = (attendance, boardMembers, motions) => {
    const now = new Date().toISOString();
    const preview = {
      attendance,
      boardMembers,
      motions,
      date: formatLocal(now, hoaTimezone, 'MMM d, yyyy'),
      time: formatLocal(now, hoaTimezone, 'h:mm a')
    };
    setPreviewData(preview);
    setShowMinutesPreview(true);
  };

  const exportFromPreview = (format, passedLiveData = null, passedLiveNotes = null, passedLiveActions = null, passedMeetingTypes = null, passedMeetingType = null, passedSectionVisibility = null) => {
    console.log('exportFromPreview called with format:', format);
    console.log('previewData:', previewData);
    console.log('passedLiveData:', passedLiveData);
    console.log('passedSectionVisibility:', passedSectionVisibility);
    
    // Use passed live data if available (from Live Voting), otherwise use previewData
    const dataToUse = passedLiveData || previewData;
    const notesToUse = passedLiveData ? passedLiveNotes : meetingNotes;
    const actionsToUse = passedLiveData ? passedLiveActions : actionItems;
    const visibility = passedSectionVisibility || { meetingInfo: true, attendance: true, priorBusiness: true, motions: true, discussions: true, notes: true, actionItems: true };
    
    if (!dataToUse) {
      alert('No preview data available');
      return;
    }
    
    if (format === 'text') {
      if (passedLiveData) {
        // Use the enhanced text export with Community Services integration
        const currentDate = formatLocal(new Date().toISOString(), hoaTimezone, 'MMM d, yyyy');
        const meetingTypeLabel = passedMeetingTypes?.find(t => t.value === passedMeetingType)?.label || 'Board Meeting';
        
        let minutesText = `${meetingTypeLabel.toUpperCase()}\n`;
        minutesText += `${hoaData?.hoa_name || hoaData?.name || 'HOMEOWNERS ASSOCIATION'}\n`;
        minutesText += `${'='.repeat(60)}\n\n`;
        minutesText += `MEETING TYPE: ${meetingTypeLabel.toUpperCase()}\n\n`;
        minutesText += `1. MEETING CALLED TO ORDER\n`;
        minutesText += `${'-'.repeat(40)}\n`;
        minutesText += `Date: ${currentDate}\n`;
        minutesText += `Called to Order: ${passedLiveData.startTime || 'Not recorded'}\n`;
        if (passedLiveData.meeting_location || passedLiveData.meetingLocation) {
          minutesText += `Location: ${passedLiveData.meeting_location || passedLiveData.meetingLocation}\n`;
        }
        minutesText += `\n`;
        
        // 2. Roll Call — only present members
        minutesText += `2. ROLL CALL\n`;
        minutesText += `${'-'.repeat(40)}\n`;
        minutesText += `Members Present:\n`;
        let memberCount = 1;
        
        // Board Members Present
        const presentBoard = (passedLiveData.boardMembers || []).filter(m => (passedLiveData.attendance?.[m.id || m.user_id]) === true);
        if (presentBoard.length > 0) {
          presentBoard.forEach(m => {
            const role = m.role?.replace('_', ' ').toUpperCase() || 'BOARD MEMBER';
            minutesText += `${memberCount}. ${m.name} - ${role}\n`;
            memberCount++;
          });
        }
        
        // Homeowners Present (excluding external)
        if (passedLiveData.homeowners && passedLiveData.homeowners.length > 0) {
          const presentHOs = passedLiveData.homeowners.filter(h => (h.present || h.attendance_status) === true && h.type !== 'external');
          if (presentHOs.length > 0) {
            presentHOs.forEach(h => {
              // Default to showing property identifier (lot, unit, etc.)
              const propertyDisplay = h.property_identifier || h.unit || h.lot || h.property_number || h.property_address || h.address || '';
              const displayProperty = propertyDisplay ? ` ${propertyDisplay}` : '';
              minutesText += `${memberCount}. ${h.name} - Homeowner${displayProperty}\n`;
              memberCount++;
            });
          }
          
          // External Attendees Present
          const presentExternal = passedLiveData.homeowners.filter(h => (h.present || h.attendance_status) === true && h.type === 'external');
          if (presentExternal.length > 0) {
            presentExternal.forEach(h => {
              const roleLabels = {
                attorney: 'Attorney',
                accountant: 'Accountant',
                staff: 'Staff',
                maintenance: 'Maintenance',
                security: 'Security',
                other: 'Other'
              };
              const roleLabel = roleLabels[h.externalRole] || 'External';
              minutesText += `${memberCount}. ${h.name} - ${roleLabel}\n`;
              memberCount++;
            });
          }

          // Team Members Present
          const presentTeamMembers = passedLiveData.homeowners.filter(h => (h.present || h.attendance_status) === true && h.type === 'team_member');
          if (presentTeamMembers.length > 0) {
            presentTeamMembers.forEach(h => {
              const roleLabels = {
                attorney: 'Attorney',
                accountant: 'Accountant',
                staff: 'Staff',
                other: 'Team Member'
              };
              const roleLabel = roleLabels[h.role] || 'Team Member';
              minutesText += `${memberCount}. ${h.name} - ${roleLabel}\n`;
              memberCount++;
            });
          }
        }
        
        if (memberCount === 1) {
          minutesText += `No members present\n`;
        }
        
        const presentCount = Object.values(passedLiveData.attendance || {}).filter(s => s === true).length;
        const totalCount = (passedLiveData.boardMembers?.length || 0) + (passedLiveData.homeowners?.length || 0);
        // Removed attendance summary per user request
        minutesText += `Board Quorum: ${presentCount >= Math.ceil((passedLiveData.boardMembers?.length || 0) / 2) ? 'ACHIEVED' : 'NOT ACHIEVED'}\n\n`;
        
        // 3. RULES OF THE MEETING — use rules from settings (SINGLE SOURCE OF TRUTH)
        minutesText += `3. RULES OF THE MEETING\n`;
        minutesText += `${'-'.repeat(40)}\n`;
        
        // Use meeting rules from settings if available, otherwise use defaults
        const rulesToUse = (typeof meetingRules !== 'undefined' && meetingRules && meetingRules.length > 0) ? meetingRules : [
          "Members of the HOA will be allotted 3 minutes to speak, address issues, concerns or ask questions that are listed on the agenda, during 'Open to the Floor'.",
          "Board members have no time restriction for responses.",
          "Speakers will be reminded to state their name and address or identifier, speak calmly, no yelling, no profanity or smearing of other individuals.",
          "Anyone who has an outburst, speaks out of turn and refuses to adhere to the rules will be politely asked to leave to include the BOD or ARC. If individuals refuse to leave, then other legal means will be taken to have those individuals escorted off the property or call.",
          "To transact such other business as may properly come before the Board or any Adjournment or Postponement thereof."
        ];
        
        rulesToUse.forEach((rule, index) => {
          minutesText += `${String.fromCharCode(97 + index)}.) ${rule}\n`;
        });
        minutesText += `\n`;
        
        // 4. CURRENT BUSINESS (Motions)
        minutesText += `4. CURRENT BUSINESS\n`;
        minutesText += `${'-'.repeat(40)}\n`;
        if (passedLiveData.motions && passedLiveData.motions.length > 0) {
          passedLiveData.motions.forEach((motion, index) => {
            minutesText += `Motion ${index + 1}: ${motion.title}\n`;
            minutesText += `Description: ${motion.description || 'No description provided'}\n`;
            minutesText += `Status: ${motion.status || 'Pending'}\n`;
            if (motion.vote_counts || motion.total_counts) {
              const answerOpts = motion.answer_options || motion.answers || ['Yes', 'No'];
              const counts = motion.total_counts || motion.vote_counts || {};
              minutesText += `Vote Results: ${answerOpts.map(opt => `${opt}=${counts[opt] || 0}`).join(', ')}\n`;
            }
            minutesText += `\n`;
          });
        } else {
          minutesText += `No motions presented during this meeting.\n\n`;
        }
        
        // 5. HOA OFFICIAL MEANS OF COMMUNICATIONS — use Community Services settings
        minutesText += `5. HOA OFFICIAL MEANS OF COMMUNICATIONS\n`;
        minutesText += `${'-'.repeat(40)}\n`;
        minutesText += `${getHoaName()} Account: Available to all residents\n`;
        minutesText += `HOA Mailing Address: ${hoaData?.mailing_address || '[To be configured in Community Settings]'}\n`;
        minutesText += `HOA Official Email: ${hoaData?.official_email || hoaData?.hoa_email || '[To be configured in Community Settings]'}\n\n`;
        
        // 6. OPEN DISCUSSION (honor visibility)
        if (!visibility || visibility.discussions !== false) {
          minutesText += `6. OPEN DISCUSSION\n`;
          minutesText += `${'-'.repeat(40)}\n`;
          if (passedLiveData.discussions && passedLiveData.discussions.length > 0) {
            passedLiveData.discussions.forEach((discussion, index) => {
              minutesText += `Discussion ${index + 1}: ${discussion.topic || 'General Discussion'}\n`;
              minutesText += `Submitted by: ${discussion.member_name || 'Unknown'}\n`;
              if (discussion.property_address) { minutesText += `Property: ${discussion.property_address}\n`; }
              if (discussion.ccr_rule || discussion.bylaw_rule) {
                const citations = [discussion.ccr_rule, discussion.bylaw_rule].filter(Boolean).join(' • ');
                minutesText += `Rule cited: ${citations}\n`;
              }
              if (discussion.notes) { minutesText += `Notes: ${discussion.notes}\n`; }
              minutesText += `Status: ${discussion.status || 'Pending'}\n\n`;
            });
          } else {
            minutesText += `No additional discussions recorded.\n\n`;
          }
        }
        
        // 7. NOTES FROM SECRETARY (honor visibility)
        if (!visibility || visibility.notes !== false) {
          minutesText += `7. NOTES FROM SECRETARY\n`;
          minutesText += `${'-'.repeat(40)}\n`;
          if (passedLiveNotes && String(passedLiveNotes).trim()) {
            minutesText += `${passedLiveNotes}\n\n`;
          } else {
            minutesText += `No additional notes recorded.\n\n`;
          }
        }
        
        // Action Items (honor visibility)
        if (!visibility || visibility.actionItems !== false) {
          if (passedLiveActions && passedLiveActions.length > 0) {
            minutesText += `ACTION ITEMS:\n`;
            minutesText += `${'-'.repeat(20)}\n`;
            passedLiveActions.forEach((item, index) => { minutesText += `${index + 1}. ${item}\n`; });
            minutesText += `\n`;
          }
        }
        
        // 8. MEETING ADJOURNED — always include
        minutesText += `8. MEETING ADJOURNED\n`;
        minutesText += `${'-'.repeat(40)}\n`;
        minutesText += `Meeting adjourned: ${passedLiveData.endTime || 'Not recorded'}\n\n`;
        minutesText += `Minutes prepared by: ${getHoaName()}\n`;
        minutesText += `Date of preparation: ${currentDate}\n`;
        
        // Download as text
        const blob = new Blob([minutesText], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${passedMeetingType}-meeting-minutes-${currentDate.replace(/\//g, '-')}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        alert('Meeting minutes downloaded as text file!');
      } else {
        exportMeetingMinutesAsText(dataToUse, notesToUse, actionsToUse);
      }
    } else if (format === 'pdf') {
      if (passedLiveData) {
        // Generate professional PDF export honoring preview visibility
        const meetingTypeLabel = passedMeetingTypes?.find(t => t.value === passedMeetingType)?.label || 'Board Meeting';
        const currentDate = formatLocal(new Date().toISOString(), hoaTimezone, 'MMMM d, yyyy');
        const htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <title>${meetingTypeLabel} Minutes - ${currentDate}</title>
    <style>
        body { font-family: 'Times New Roman', serif; max-width: 8.5in; margin: 0 auto; padding: 40px 20px; line-height: 1.6; }
        .header { text-align: center; border-bottom: 3px double #333; padding-bottom: 20px; margin-bottom: 30px; }
        .header h1 { font-size: 24px; margin: 0; font-weight: bold; }
        .header h2 { font-size: 18px; margin: 5px 0; font-weight: normal; }
        .section { margin-bottom: 25px; page-break-inside: avoid; }
        .section-title { font-weight: bold; font-size: 16px; color: #000; margin-bottom: 10px; border-bottom: 1px solid #333; padding-bottom: 5px; }
        .subsection { margin-left: 20px; margin-bottom: 15px; }
        .attendance-grid { display: grid; grid-template-columns: 1fr 150px; gap: 10px; margin-bottom: 5px; align-items: center; }
        .attendance-header { font-weight: bold; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
        .motion-item { border: 1px solid #ddd; padding: 15px; margin-bottom: 15px; border-radius: 5px; background: #f9f9f9; }
        .rules-list { margin-left: 20px; }
        .rules-list li { margin-bottom: 10px; }
        .signature-section { margin-top: 40px; display: grid; grid-template-columns: 1fr 1fr; gap: 40px; }
        .signature-line { border-bottom: 1px solid #000; padding-bottom: 2px; margin-bottom: 5px; height: 30px; }
        @media print { 
            body { margin: 0; padding: 20px; font-size: 12px; }
            * { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            .section { page-break-inside: avoid; }
            img { max-width: 100%; height: auto; page-break-inside: avoid; }
            iframe { display: none; } /* Hide iframes in print as they won't render */
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>${meetingTypeLabel.toUpperCase()}</h1>
        <h2>${(hoaSettings?.hoa_name || hoaSettings?.name || 'HOMEOWNERS ASSOCIATION')}</h2>
        ${(hoaSettings?.mailing_address || hoaSettings?.hoa_email || hoaSettings?.phone) ? `<p style="font-size: 12px; margin: 5px 0;">
          ${hoaSettings?.mailing_address || ''}${hoaSettings?.hoa_email ? (hoaSettings?.mailing_address ? ' | ' : '') + hoaSettings.hoa_email : ''}${hoaSettings?.phone ? (hoaSettings?.mailing_address || hoaSettings?.hoa_email ? ' | ' : '') + hoaSettings.phone : ''}
        </p>` : ''}
        <p style="font-size: 14px; margin: 5px 0;"><strong>Date:</strong> ${currentDate}${startTime ? ` | <strong>Time:</strong> ${startTime}` : ''}</p>
    </div>
    
    <div class="section">
        <div class="section-title">1. MEETING CALLED TO ORDER</div>
        <p><strong>Called to Order:</strong> ${passedLiveData.startTime || 'Not recorded'}</p>
    </div>
    
    ${passedLiveData.meeting_location || passedLiveData.meetingLocation ? `
    <div class="section">
        <div class="section-title">1a. MEETING LOCATION</div>
        <p>${passedLiveData.meeting_location || passedLiveData.meetingLocation}</p>
    </div>
    ` : ''}
    
    ${(passedLiveData.proofOfNotice || passedLiveData.proof_of_notice) && (!visibility || visibility.proofOfNotice !== false) ? `
    <div class="section">
        <div class="section-title">${passedLiveData.meeting_location || passedLiveData.meetingLocation ? '1b' : '1a'}. PROOF OF NOTICE</div>
        <div class="subsection">
            ${(() => {
              const proof = passedLiveData.proofOfNotice || passedLiveData.proof_of_notice;
              let html = '';
              if (proof.postedOnEvents && proof.emailSent) {
                html += '<p>✓ Notice posted on events calendar and emailed to all homeowners</p>';
              }
              if (proof.mailPacket) {
                html += `<p>✓ Notice sent via mail packet${proof.mailPacketDate ? ` on ${formatLocal(proof.mailPacketDate, hoaTimezone, 'MMM d, yyyy')}` : ''}</p>`;
              }
              if (proof.other && proof.otherNotes) {
                html += `<p>✓ Other: ${proof.otherNotes}</p>`;
              }
              return html;
            })()}
        </div>
    </div>
    ` : ''}
    
    ${(!visibility || visibility.attendance !== false) ? `
    <div class="section">
        <div class="section-title">2. ROLL CALL</div>
        <div class="subsection">
            <p><strong>Board Members Present:</strong></p>
            <div class="attendance-grid">
                <div class="attendance-header">Name & Position</div>
                <div class="attendance-header">Time Present</div>
                ${(dataToUse.boardMembers || []).filter(m => (dataToUse.attendance?.[m.id || m.user_id]) === true).map(m => {
                  const role = m.role?.replace('_', ' ').toUpperCase() || 'BOARD MEMBER';
                  return `
                    <div>${m.name} - ${role}</div>
                    <div>${passedLiveData.startTime || '-'}</div>
                  `;
                }).join('')}
                ${(dataToUse.boardMembers || []).filter(m => (dataToUse.attendance?.[m.id || m.user_id]) === true).length === 0 ? '<div colspan="2">No board members present</div>' : ''}
            </div>
        </div>
        ${dataToUse.homeowners && dataToUse.homeowners.filter(h => (h.present || h.attendance_status) === true && h.type !== 'external').length > 0 ? `
        <div class="subsection">
            <p><strong>Homeowners Present:</strong></p>
            <div class="attendance-grid">
                <div class="attendance-header">Name & Property</div>
                <div class="attendance-header">Time Present</div>
                ${dataToUse.homeowners.filter(h => (h.present || h.attendance_status) === true && h.type !== 'external').map(h => {
                  const property = h.property_address || 'Property not provided';
                  return `
                    <div>${h.name} - ${property}</div>
                    <div>${passedLiveData.startTime || '-'}</div>
                  `;
                }).join('')}
            </div>
        </div>
        ` : ''}
        ${dataToUse.homeowners && dataToUse.homeowners.filter(h => (h.present || h.attendance_status) === true && h.type === 'external').length > 0 ? `
        <div class="subsection">
            <p><strong>External Attendees Present:</strong></p>
            <div class="attendance-grid">
                <div class="attendance-header">Name & Role</div>
                <div class="attendance-header">Time Present</div>
                ${dataToUse.homeowners.filter(h => (h.present || h.attendance_status) === true && h.type === 'external').map(h => {
                  const roleLabels = {
                    attorney: 'Attorney',
                    accountant: 'Accountant',
                    staff: 'Staff',
                    maintenance: 'Maintenance',
                    security: 'Security',
                    other: 'Other'
                  };
                  const roleLabel = roleLabels[h.externalRole] || 'External';
                  return `
                    <div>${h.name} - ${roleLabel}</div>
                    <div>${passedLiveData.startTime || '-'}</div>
                  `;
                }).join('')}
            </div>
        </div>
        ` : ''}
        ${dataToUse.homeowners && dataToUse.homeowners.filter(h => (h.present || h.attendance_status) === true && h.type === 'team_member').length > 0 ? `
        <div class="subsection">
            <p><strong>Team Members Present:</strong></p>
            <div class="attendance-grid">
                <div class="attendance-header">Name & Role</div>
                <div class="attendance-header">Time Present</div>
                ${dataToUse.homeowners.filter(h => (h.present || h.attendance_status) === true && h.type === 'team_member').map(h => {
                  const roleLabels = {
                    attorney: 'Attorney',
                    accountant: 'Accountant',
                    staff: 'Staff',
                    other: 'Team Member'
                  };
                  const roleLabel = roleLabels[h.role] || 'Team Member';
                  return `
                    <div>${h.name} - ${roleLabel}</div>
                    <div>${passedLiveData.startTime || '-'}</div>
                  `;
                }).join('')}
            </div>
        </div>
        ` : ''}
        <div class="subsection">
            <!-- Removed attendance summary per user request -->
            <p><strong>Board Quorum:</strong> ${Object.values(dataToUse.attendance || {}).filter(s => s === true).length >= Math.ceil((dataToUse.boardMembers?.length || 0) / 2) ? 'ACHIEVED' : 'NOT ACHIEVED'}</p>
        </div>
    </div>
    ` : ''}
    
    ${sectionVisibility.rules !== false ? `
    <div class="section">
        <div class="section-title">3. RULES OF THE MEETING</div>
        <ol class="rules-list">
            ${(() => {
              const rulesToUse = (passedLiveData.rulesOfConduct && passedLiveData.rulesOfConduct.length > 0) 
                ? passedLiveData.rulesOfConduct 
                : (passedLiveData.rules_of_conduct && passedLiveData.rules_of_conduct.length > 0)
                ? passedLiveData.rules_of_conduct
                : [
                  "Members of the HOA will be allotted 3 minutes to speak, address issues, concerns or ask questions that are listed on the agenda, during 'Open to the Floor'.",
                  "Board members have no time restriction for responses.",
                  "Speakers will be reminded to state their name and address or identifier, speak calmly, no yelling, no profanity or smearing of other individuals.",
                  "Anyone who has an outburst, speaks out of turn and refuses to adhere to the rules will be politely asked to leave to include the BOD or ARC. If individuals refuse to leave, then other legal means will be taken to have those individuals escorted off the property or call.",
                  "To transact such other business as may properly come before the Board or any Adjournment or Postponement thereof."
                ];
              return rulesToUse.map(rule => `<li>${rule}</li>`).join('');
            })()}
        </ol>
    </div>
    ` : ''}
    
    ${(!visibility || visibility.priorBusiness !== false) && passedLiveData.prior_minutes_approval ? `
    <div class="section">
        <div class="section-title">4. PRIOR BUSINESS - APPROVAL OF PRIOR MEETING MINUTES</div>
        <div class="subsection">
            <p><strong>Meeting:</strong> ${passedLiveData.prior_minutes_approval.title || 'Prior Meeting'}</p>
            <p><strong>Date:</strong> ${passedLiveData.prior_minutes_approval.date ? formatDateInHOATimezone(passedLiveData.prior_minutes_approval.date, hoaTimezone, 'MMM d, yyyy') : 'Unknown'}</p>
            <p><strong>Motion:</strong> To approve the prior meeting minutes as presented.</p>
            <p><strong>Status:</strong> ${passedLiveData.prior_minutes_approval.status || 'Pending approval'}</p>
            ${(!visibility || visibility.manualPriorMinutes !== false) && (passedLiveData.manualPriorMinutes || passedLiveData.manual_prior_minutes) ? `
              <div style="margin-top: 15px; padding: 10px; background: #f5f5f5; border-left: 3px solid #666;">
                <p><strong>Additional Prior Meeting Minutes Notes:</strong></p>
                <p style="white-space: pre-wrap;">${passedLiveData.manualPriorMinutes || passedLiveData.manual_prior_minutes}</p>
              </div>
            ` : ''}
        </div>
    </div>
    ` : ''}
    
    ${(!visibility || visibility.motions !== false) ? `
    <div class="section">
        <div class="section-title">${passedLiveData.prior_minutes_approval ? '5' : '4'}. CURRENT BUSINESS</div>
        ${dataToUse.motions && dataToUse.motions.length > 0 ? 
          dataToUse.motions.map((motion, index) => `
            <div class="motion-item">
                <p><strong>Motion ${index + 1}:</strong> ${motion.title}</p>
                <p><strong>Description:</strong> ${motion.description || 'No description provided'}</p>
                ${motion.discussion ? `<p><strong>Discussion:</strong> ${motion.discussion}</p>` : ''}
                ${motion.voting_rule === 'propose_second' && (motion.proposer_name || motion.seconder_name) ? `
                  <p><strong>Proposed by:</strong> ${motion.proposer_name || 'Not set'}</p>
                  <p><strong>Seconded by:</strong> ${motion.seconder_name || 'Not set'}</p>
                ` : ''}
                <p><strong>Status:</strong> ${motion.status || 'Pending'}</p>
                ${(motion.vote_counts || motion.votes || motion.total_counts) ? (() => {
                  const answerOpts = motion.answer_options || motion.answers || ['Yes', 'No'];
                  const counts = motion.total_counts || motion.vote_counts || motion.votes || {};
                  return `<p><strong>Vote Results:</strong> ${answerOpts.map(opt => `${opt}: ${counts[opt] || 0}`).join(', ')}</p>`;
                })() : ''}
                ${motion.individual_votes ? (() => {
                  const answerOpts = motion.answer_options || motion.answers || ['Yes', 'No'];
                  const indVotes = motion.individual_votes || {};
                  return `
                  <div style="margin-left: 20px; margin-top: 10px;">
                    ${answerOpts.map(opt => {
                      const voters = indVotes[opt] || [];
                      return voters.length > 0 ? `<p><strong>${opt}:</strong> ${voters.map(v => v.name).join(', ')}</p>` : '';
                    }).join('')}
                  </div>
                `;})() : ''}
            </div>
          `).join('') : 
          '<p>No motions presented during this meeting.</p>'
        }
    </div>
    ` : ''}
    
    ${(!visibility || visibility.discussions !== false) ? `
    <div class="section">
        <div class="section-title">5. OPEN DISCUSSION</div>
        ${dataToUse.discussions && dataToUse.discussions.length > 0 ? 
          dataToUse.discussions.map((discussion, index) => `
            <div class="subsection">
                <p><strong>Discussion ${index + 1}:</strong> ${discussion.topic || 'General Discussion'}</p>
                <p><strong>Submitted by:</strong> ${discussion.member_name || 'Unknown'}</p>
                ${discussion.property_address ? `<p><strong>Property:</strong> ${discussion.property_address}</p>` : ''}
                ${discussion.ccr_rule || discussion.bylaw_rule ? `<p><strong>Rule cited:</strong> ${[discussion.ccr_rule, discussion.bylaw_rule].filter(Boolean).join(' • ')}</p>` : ''}
                ${discussion.notes ? `<p><strong>Notes:</strong> ${discussion.notes}</p>` : ''}
                <p><strong>Status:</strong> ${discussion.status || 'Pending'}</p>
            </div>
          `).join('') : 
          '<p>No additional discussions recorded.</p>'
        }
    </div>
    ` : ''}
    
    ${(!visibility || visibility.notes !== false || visibility.actionItems !== false) ? `
    <div class="section">
        <div class="section-title">6. NOTES FROM SECRETARY</div>
        ${(!visibility || visibility.notes !== false) ? (notesToUse ? `<p>${notesToUse.replace(/\n/g, '<br>')}</p>` : '<p>No additional notes recorded.</p>') : ''}
        ${(!visibility || visibility.actionItems !== false) ? (actionsToUse && actionsToUse.length > 0 ? `
        <div class="subsection">
            <p><strong>Action Items:</strong></p>
            <ol>
                ${actionsToUse.map(item => `<li>${item}</li>`).join('')}
            </ol>
        </div>
        ` : '') : ''}
    </div>
    ` : ''}
    
    <div class="section">
        <div class="section-title">7. MEETING ADJOURNED</div>
        <p><strong>Meeting adjourned:</strong> ${passedLiveData.endTime || 'Not recorded'}</p>
    </div>
    
    <div class="signature-section">
        <div>
            <div class="signature-line"></div>
            <p style="text-align: center; margin: 0;"><strong>Secretary Signature</strong></p>
            <p style="text-align: center; margin: 0; font-size: 12px;">Date: ___________</p>
        </div>
        <div>
            <div class="signature-line"></div>
            <p style="text-align: center; margin: 0;"><strong>President Signature</strong></p>
            <p style="text-align: center; margin: 0; font-size: 12px;">Date: ___________</p>
        </div>
    </div>
    
    <div style="margin-top: 30px; font-size: 12px; color: #666; text-align: center;">
        <p>Minutes prepared by: ${hoaSettings?.hoa_name || hoaSettings?.name || getHoaName()} | Date of preparation: ${currentDate}</p>
    </div>
</body>
</html>`;
        
        try {
          const printWindow = window.open('', '_blank');
          if (printWindow && printWindow.document) {
            printWindow.document.write(htmlContent);
            printWindow.document.close();
            setTimeout(() => { try { printWindow.print(); } catch (e) {} }, 800);
          } else {
            // Fallback: download as HTML
            const blob = new Blob([htmlContent], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${passedMeetingType}-meeting-minutes-${currentDate.replace(/\//g, '-')}.html`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            alert('Downloaded as HTML file. Open it in your browser to print as PDF.');
          }
        } catch (error) {
          console.error('Error opening PDF window:', error);
          alert('Error generating PDF. Please try text export instead.');
        }
      } else {
        exportMeetingMinutesAsPDF(dataToUse, notesToUse, actionsToUse);
      }
    }
  };

  const exportMeetingMinutesAsText = (data, notes, actions) => {
    // Get HOA name from hoaData, user context, or fallback
    const hoaName = (hoaData?.name) || (user?.hoa_name) || 'HOA';
    
    let minutes = `MEETING MINUTES\n`;
    minutes += `Date: ${data.date}\n`;
    minutes += `Time: ${data.time}\n`;
    minutes += `HOA: ${hoaName}\n\n`;
    
    minutes += `ATTENDANCE:\n`;
    minutes += `${'-'.repeat(50)}\n`;
    data.boardMembers.forEach(member => {
      const status = data.attendance[member.id] === true ? 'PRESENT' : 
                     data.attendance[member.id] === false ? 'ABSENT' : 'UNKNOWN';
      minutes += `${member.name.padEnd(25)} ${status}\n`;
    });
    
    if (notes) {
      minutes += `\nMEETING NOTES:\n`;
      minutes += `${'-'.repeat(50)}\n`;
      minutes += `${notes}\n\n`;
    }
    
    if (actions && actions.length > 0) {
      minutes += `ACTION ITEMS:\n`;
      minutes += `${'-'.repeat(50)}\n`;
      actions.forEach((item, index) => {
        minutes += `${index + 1}. ${item}\n`;
      });
    }
    
    // Download as text file
    const blob = new Blob([minutes], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `meeting-minutes-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportMeetingMinutesAsPDF = (data, notes, actions) => {
    // Create PDF content using browser's print functionality
    const printWindow = window.open('', '_blank');
    const now = new Date().toISOString();
    const currentDate = formatLocal(now, hoaTimezone, 'MMMM d, yyyy');
    const currentTime = formatLocal(now, hoaTimezone, 'h:mm a z');
    
    const pdfHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Meeting Minutes - ${data.date}</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            max-width: 8.5in; 
            margin: 0 auto; 
            padding: 1in;
            background: white;
            line-height: 1.6;
          }
          .header { 
            text-align: center; 
            border-bottom: 2px solid #000; 
            padding-bottom: 20px; 
            margin-bottom: 30px; 
          }
          .title { 
            font-size: 24px; 
            font-weight: bold; 
            margin-bottom: 10px; 
          }
          .subtitle { 
            font-size: 16px; 
            color: #666; 
            margin-bottom: 20px; 
          }
          .section { 
            margin-bottom: 30px; 
          }
          .section-title { 
            font-size: 18px; 
            font-weight: bold; 
            color: #333; 
            border-bottom: 1px solid #ccc; 
            padding-bottom: 5px; 
            margin-bottom: 15px; 
          }
          .attendance-item { 
            display: flex; 
            justify-content: space-between; 
            padding: 8px 0; 
            border-bottom: 1px dotted #ddd; 
          }
          .motion-item { 
            margin-bottom: 20px; 
            padding: 15px; 
            border: 1px solid #ddd; 
            border-radius: 5px; 
          }
          .vote-results { 
            display: flex; 
            justify-content: space-around; 
            margin-top: 10px; 
            padding: 10px; 
            background: #f5f5f5; 
          }
          .action-item { 
            margin-bottom: 8px; 
            padding-left: 20px; 
          }
          .footer { 
            margin-top: 50px; 
            border-top: 1px solid #ddd; 
            padding-top: 20px; 
            font-size: 12px; 
            color: #666; 
          }
          @media print {
            body { margin: 0; padding: 0.5in; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="title">MEETING MINUTES</div>
          <div class="subtitle">
            <strong>${hoaData?.name || 'HOA'}</strong><br>
            Date: ${data.date}<br>
            Time: ${data.time}<br>
            Generated: ${currentDate} at ${currentTime}
          </div>
        </div>

        <div class="section">
          <div class="section-title">ATTENDANCE</div>
          ${data.boardMembers.map(member => {
            const memberId = member.id || member.user_id;
            const status = data.attendance[memberId] === true ? 'PRESENT' : 
                          data.attendance[memberId] === false ? 'ABSENT' : 'UNKNOWN';
            return `<div class="attendance-item">
              <span><strong>${member.name}</strong> (${member.role?.replace('_', ' ').toUpperCase() || 'BOARD'})</span>
              <span>${status}</span>
            </div>`;
          }).join('')}
        </div>

        ${data.motions && data.motions.length > 0 ? `
        <div class="section">
          <div class="section-title">MOTIONS & VOTING RESULTS</div>
          ${data.motions.map((motion, index) => `
            <div class="motion-item">
              <h4>Motion #${index + 1}: ${motion.title}</h4>
              ${motion.description ? `<p>${motion.description}</p>` : ''}
              ${motion.discussion ? `<p><strong>Discussion:</strong> ${motion.discussion}</p>` : ''}
              ${motion.voting_rule === 'propose_second' && (motion.proposer_name || motion.seconder_name) ? `
                <p><strong>Proposed by:</strong> ${motion.proposer_name || 'Not set'}</p>
                <p><strong>Seconded by:</strong> ${motion.seconder_name || 'Not set'}</p>
              ` : ''}
              ${motion.votes || motion.total_counts ? (() => {
                const answerOpts = motion.answer_options || motion.answers || ['Yes', 'No'];
                const counts = motion.total_counts || motion.votes || {};
                return `
                <div class="vote-results">
                  ${answerOpts.map(opt => `<div><strong>${opt}:</strong> ${counts[opt] || 0}</div>`).join('')}
                </div>
              `;})() : ''}
              ${motion.individual_votes ? (() => {
                const answerOpts = motion.answer_options || motion.answers || ['Yes', 'No'];
                const indVotes = motion.individual_votes || {};
                return `
                <div style="margin-left: 20px; margin-top: 10px; font-size: 0.9em;">
                  ${answerOpts.map(opt => {
                    const voters = indVotes[opt] || [];
                    return voters.length > 0 ? `<p><strong>${opt}:</strong> ${voters.map(v => v.name).join(', ')}</p>` : '';
                  }).join('')}
                </div>
              `;})() : ''}
            </div>
          `).join('')}
        </div>
        ` : ''}

        ${notes ? `
        <div class="section">
          <div class="section-title">MEETING NOTES</div>
          <div>${notes.replace(/\n/g, '<br>')}</div>
        </div>
        ` : ''}

        ${actions && actions.length > 0 ? `
        <div class="section">
          <div class="section-title">ACTION ITEMS</div>
          ${actions.map((item, index) => `
            <div class="action-item">${index + 1}. ${item}</div>
          `).join('')}
        </div>
        ` : ''}

        <div class="footer">
          <div><strong>Meeting Minutes prepared by:</strong> {getHoaName()}</div>
          <div><strong>Document Status:</strong> Draft - For Review Only</div>
          <div style="margin-top: 15px;">
            <em>Note: This is a draft document generated for review purposes. 
            Official minutes require board approval and electronic signatures.</em>
          </div>
        </div>

        <div class="no-print" style="text-align: center; margin-top: 30px;">
          <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; margin-right: 10px;">Print PDF</button>
          <button onclick="window.close()" style="padding: 10px 20px; font-size: 16px;">Close</button>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(pdfHTML);
    printWindow.document.close();
  };

  // Export poll results function
  const exportPollResults = async (pollId) => {
    try {
      const response = await api.get(`/api/vote/polls/${pollId}/export`);
      
      // Create CSV content
      const poll = response.data.poll;
      const votes = response.data.detailed_votes;
      
      let csv = 'Poll Results Export\n\n';
      csv += `Poll: ${poll.title}\n`;
      csv += `Description: ${poll.description || 'N/A'}\n`;
      csv += `Opens: ${formatLocal(poll.opens_at, hoaTimezone, 'MMM d, yyyy h:mm a z')}\n`;
      csv += `Closes: ${formatLocal(poll.closes_at, hoaTimezone, 'MMM d, yyyy h:mm a z')}\n`;
      csv += `Total Votes: ${response.data.total_votes}\n\n`;
      
      csv += 'Vote Totals:\n';
      Object.entries(response.data.vote_counts).forEach(([option, count]) => {
        csv += `${option}: ${count}\n`;
      });
      
      csv += `\nWinner(s): ${response.data.winners.join(', ')}\n`;
      if (response.data.is_tie) {
        csv += 'Status: TIE\n';
      }
      
      csv += '\n\nDetailed Voter Information:\n';
      csv += 'Name,Email,Property,Option,Voted At\n';
      votes.forEach(vote => {
        csv += `"${vote.voter_name}","${vote.voter_email}","${vote.property_address}","${vote.option}","${formatLocal(vote.voted_at, hoaTimezone, 'MMM d, yyyy h:mm a z')}"\n`;
      });
      
      // Download CSV
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `poll_${pollId}_results.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting poll results:', error);
      alert('Failed to export poll results. Please try again.');
    }
  };

  const loadVotingData = async () => {
    console.log('🔍 loadVotingData CALLED');
    setLoading(true);
    try {
      // Load current election data
      const electionResponse = await api.get('/api/vote/election/current');
      const electionData = electionResponse.data;
      
      setElection(electionData?.election || null);
      setCandidates(electionData?.candidates || []);
      setConsent(electionData?.consent || false);
      setProxy(electionData?.proxy || { type: 'default' });

      // Load voting stats if election exists
      if (electionData?.election) {
        try {
          const statsResponse = await api.get('/api/vote/election/current/stats');
          setStats(statsResponse.data?.results || []);
        } catch (error) {
          console.error('Error loading voting stats:', error);
          setStats([]);
        }
      }

      // Load consent statistics (for board members)
      try {
        const consentResponse = await api.get('/api/vote/consent-stats');
        setConsentStats(consentResponse.data);
      } catch (error) {
        console.log('Not a board member or error loading consent stats:', error);
        setConsentStats(null);
      }

      // Load all elections for archive/results view
      try {
        const electionsResponse = await api.get('/api/vote/elections');
        console.log('=== ELECTIONS API RESPONSE ===');
        console.log('Full response:', electionsResponse);
        console.log('Response data:', electionsResponse.data);
        console.log('Elections array:', electionsResponse.data?.elections);
        console.log('Elections count:', electionsResponse.data?.elections?.length);
        const electionsList = electionsResponse.data?.elections || [];
        console.log('About to setElections with:', electionsList);
        setElections(electionsList);
        console.log('setElections called successfully');
      } catch (error) {
        console.error('❌ ERROR loading elections list:', error);
        console.error('Error details:', error.response);
        setElections([]);
        console.log('Set elections to empty due to error');
      }

      // Load polls
      try {
        const pollsResponse = await api.get('/api/vote/polls');
        setPolls(pollsResponse.data?.polls || []);
      } catch (error) {
        console.error('Error loading polls:', error);
        setPolls([]);
      }

      // Load poll settings
      try {
        const settingsResponse = await api.get('/api/vote/polls/settings');
        setPollSettings(settingsResponse.data || {
          poll_creation_permissions: 'board_only',
          one_vote_per_property: false,
          require_poll_approval: false
        });
      } catch (error) {
        console.error('Error loading poll settings:', error);
      }

      // Note: Pending polls are loaded in separate useEffect that depends on isBoard state

      // Load archived polls
      try {
        const archivedResponse = await api.get('/api/vote/polls/archived');
        setArchivedPolls(archivedResponse.data?.polls || []);
      } catch (error) {
        console.error('Error loading archived polls:', error);
        setArchivedPolls([]);
      }

      // Load board members (for board users only)
      try {
        const boardResponse = await api.get('/api/vote/board-members');
        setBoardMembers(boardResponse.data?.board_members || []);
      } catch (error) {
        console.log('Not a board member or error loading board members:', error);
        setBoardMembers([]);
      }
    } catch (error) {
      console.error('Error loading voting data:', error);
      // Set default values for development
      setElection(null);
      setCandidates([]);
      setStats([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadVotingData();
    loadCommittees();
    loadHoaSettings();
    loadHoaTimezone(); // Load HOA timezone for correct date display
    loadMeetingRules(); // Load meeting rules from settings
    checkLiveVotingAccess(); // Run after initial load
    
    // Listen for direct navigation to Live Voting BOD
    const handleSwitchToLiveVotingBOD = () => {
      setActiveTab('live-bod');
    };
    
    window.addEventListener('switchToLiveVotingBOD', handleSwitchToLiveVotingBOD);
    
    return () => {
      window.removeEventListener('switchToLiveVotingBOD', handleSwitchToLiveVotingBOD);
    };
  }, [user]);

  // Load pending polls when isBoard changes
  useEffect(() => {
    const loadPendingPolls = async () => {
      if (isBoard) {
        try {
          const pendingResponse = await api.get('/api/vote/polls/pending');
          setPendingPolls(pendingResponse.data?.polls || []);
          console.log('✅ Pending polls loaded:', pendingResponse.data?.polls?.length || 0);
        } catch (error) {
          console.error('❌ Error loading pending polls:', error);
          setPendingPolls([]);
        }
      }
    };
    loadPendingPolls();
  }, [isBoard]);



  // Separate useEffect for elections to avoid closure issues
  useEffect(() => {
    const fetchElections = async () => {
      try {
        const electionsResponse = await api.get('/api/vote/elections');
        console.log('📊 Separate Elections Fetch - Success:', electionsResponse.data?.elections?.length, 'elections');
        setElections(electionsResponse.data?.elections || []);
      } catch (error) {
        console.error('📊 Separate Elections Fetch - Error:', error);
        setElections([]);
      }
    };
    
    if (user?.hoa_id) {
      console.log('📊 Fetching elections for HOA:', user.hoa_id);
      fetchElections();
    }
  }, [user?.hoa_id]);


  const updateConsent = async (newConsent) => {
    try {
      await api.post('/api/vote/consent', { consent: newConsent });
      setConsent(newConsent);
    } catch (error) {
      console.error('Error updating consent:', error);
      alert('Failed to update consent. Please try again.');
    }
  };

  const updateProxy = async (newProxy) => {
    try {
      await api.post('/api/vote/proxy', newProxy);
      setProxy(newProxy);
    } catch (error) {  
      console.error('Error updating proxy:', error);
      alert('Failed to update proxy. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading voting information...</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-indigo-900 mb-2">🗳️ Voting & Elections</h2>
            <p className="text-indigo-700">Participate in community governance and board elections</p>
          </div>
          <div className="text-6xl opacity-20">🏛️</div>
        </div>
      </div>

      {/* Tab Navigation - Mobile Friendly */}
      <div className="border-b border-gray-200">
        <div className="relative">
          {/* Mobile Tab Selector */}
          <div className="sm:hidden">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              {election && (
                <>
                  <option value="voting">🗳️ Current Election</option>
                  <option value="nominations">👥 Nominees & Profiles</option>
                </>
              )}
              <option value="device-test">🔐 Device Test</option>
              <option value="polls">📊 Polls & Documents</option>
              {liveVotingAccess.canViewBod && (
                <option value="live-bod">⚡ Live Voting - BOD</option>
              )}
              {liveVotingAccess.canViewCommittee && (
                <option value="live-committee">⚡ Live Voting - Committees</option>
              )}
              {/* Hide E-Consent for team members - they cannot vote */}
              {!isTeamMember && <option value="e-consent">🗳️ E-Consent</option>}
              <option value="archive">📚 Past Results</option>
              {(consentStats || user?.role?.toLowerCase().includes('board') || user?.role?.toLowerCase().includes('president') || user?.role?.toLowerCase().includes('treasurer') || user?.role?.toLowerCase().includes('secretary')) && (
                <option value="management">⚙️ Election Management</option>
              )}
            </select>
          </div>

          {/* Desktop Tab Navigation - Two Row Layout */}
          <div className="hidden sm:block">
            {/* First Row - Main Voting Tabs */}
            <nav className="flex -mb-px space-x-2 lg:space-x-4 xl:space-x-6 overflow-x-auto scrollbar-hide">
              {election && (
                <>
                  <button
                    onClick={() => setActiveTab('voting')}
                    className={`py-2 px-2 lg:px-3 border-b-2 font-medium text-xs lg:text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                      activeTab === 'voting'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="hidden lg:inline">🗳️ Current Election</span>
                    <span className="lg:hidden">🗳️ Election</span>
                  </button>
                  <button
                    onClick={() => setActiveTab('nominations')}
                    className={`py-2 px-2 lg:px-3 border-b-2 font-medium text-xs lg:text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                      activeTab === 'nominations'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="hidden lg:inline">👥 Nominees & Profiles</span>
                    <span className="lg:hidden">👥 Nominees</span>
                  </button>
                </>
              )}
              
              <button
                onClick={() => setActiveTab('polls')}
                className={`py-2 px-2 lg:px-3 border-b-2 font-medium text-xs lg:text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'polls'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="hidden lg:inline">📊 Polls & Documents</span>
                <span className="lg:hidden">📊 Polls</span>
              </button>

              {/* Live Voting BOD Tab - Access Control Based */}
              {liveVotingAccess.canViewBod && (
                <button
                  onClick={() => setActiveTab('live-bod')}
                  className={`py-2 px-2 lg:px-3 border-b-2 font-medium text-xs lg:text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === 'live-bod'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="hidden lg:inline">⚡ Live Voting - BOD</span>
                  <span className="lg:hidden">⚡ Live BOD</span>
                </button>
              )}

              {/* Live Voting Committee Tab - Access Control Based */}
              {liveVotingAccess.canViewCommittee && (
                <button
                  onClick={() => setActiveTab('live-committee')}
                  className={`py-2 px-2 lg:px-3 border-b-2 font-medium text-xs lg:text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === 'live-committee'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="hidden lg:inline">⚡ Live Voting - Committees</span>
                  <span className="lg:hidden">⚡ Live Committees</span>
                </button>
              )}
              
              {/* Hide E-Consent for team members - they cannot vote */}
              {!isTeamMember && (
                <button
                  onClick={() => setActiveTab('e-consent')}
                  className={`py-2 px-2 lg:px-3 border-b-2 font-medium text-xs lg:text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === 'e-consent'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="hidden lg:inline">🗳️ E-Consent</span>
                  <span className="lg:hidden">🗳️ E-Consent</span>
                </button>
              )}
            </nav>
            
            {/* Second Row - Management and Archive Tabs */}
            <nav className="flex mt-2 -mb-px space-x-2 lg:space-x-4 xl:space-x-6 overflow-x-auto scrollbar-hide border-t border-gray-100 pt-2">
              <button
                onClick={() => setActiveTab('device-test')}
                className={`py-2 px-2 lg:px-3 border-b-2 font-medium text-xs lg:text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'device-test'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="hidden lg:inline">🔐 Device Test</span>
                <span className="lg:hidden">🔐 Test</span>
              </button>
              
              <button
                onClick={() => setActiveTab('archive')}
                className={`py-2 px-2 lg:px-3 border-b-2 font-medium text-xs lg:text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeTab === 'archive'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="hidden lg:inline">📚 Archive & Results</span>
                <span className="lg:hidden">📚 Archive</span>
              </button>
              
              {(consentStats || user?.role?.toLowerCase().includes('board') || user?.role?.toLowerCase().includes('president') || user?.role?.toLowerCase().includes('treasurer') || user?.role?.toLowerCase().includes('secretary')) && (
                <button
                  onClick={() => setActiveTab('management')}
                  className={`py-2 px-2 lg:px-3 border-b-2 font-medium text-xs lg:text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === 'management'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="hidden lg:inline">⚙️ Election Management</span>
                  <span className="lg:hidden">⚙️ Management</span>
                </button>
              )}
            </nav>
          </div>
        </div>
      </div>

      {/* Content Based on Active Tab */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      ) : (
        <div>
          {/* Current Election Tab */}
          {activeTab === 'voting' && election && (
            <CurrentElectionSection 
              election={election}
              candidates={candidates}
              consent={consent}
              proxy={proxy}
              stats={stats}
              onUpdateConsent={updateConsent}
              onUpdateProxy={updateProxy}
              onRefresh={loadVotingData}
              setActiveTab={setActiveTab}
              hoaName={getHoaName()}
              hoaTimezone={hoaTimezone}
              user={user}
              onOpenCombinedBallot={() => setShowCombinedBallot(true)}
            />
          )}

          {/* Nominations Tab */}
          {activeTab === 'nominations' && (
            <NomineeProfilesSection 
              candidates={candidates}
              election={election}
              selectedCandidate={selectedCandidate}
              onSelectCandidate={setSelectedCandidate}
              onRefresh={loadVotingData}
            />
          )}


          {/* Device Test Tab */}
          {activeTab === 'device-test' && (
            <DeviceTestTab user={user} />
          )}

          {/* Polls Tab */}
          {activeTab === 'polls' && (
            <PollsSection 
              polls={polls}
              pendingPolls={pendingPolls}
              archivedPolls={archivedPolls}
              pollSettings={pollSettings}
              onRefresh={loadVotingData}
              showCreatePoll={showCreatePoll}
              setShowCreatePoll={setShowCreatePoll}
              canCreatePolls={liveVotingAccess.canCreatePolls}
              isBoard={isBoard}
              hoaTimezone={hoaTimezone}
              onSettingsUpdate={(newSettings) => setPollSettings(newSettings)}
            />
          )}

          {/* Live Voting BOD Tab */}
          {activeTab === 'live-bod' && liveVotingAccess.canViewBod && (
            <LiveVotingSection 
              boardMembers={boardMembers}
              committees={committees}
              onRefresh={loadVotingData}
              refreshTrigger={refreshTrigger}
              canInteract={liveVotingAccess.canInteractBod}
              meetingTypeFilter="BOD"
              tabType="bod"
              hoaName={getHoaName()}
              hoaSettings={hoaSettings}
              hoaData={hoaData}
              hoaTimezone={hoaTimezone}
              discussionPropertyDisplay={(liveVotingAccess && liveVotingAccess.settings && liveVotingAccess.settings.discussion_property_display) ? liveVotingAccess.settings.discussion_property_display : 'address'}
              onExport={exportFromPreview}
              meetingRules={meetingRules}
              speakingTimeMinutes={speakingTimeMinutes}
            />
          )}

          {/* Live Voting Committee Tab */}
          {activeTab === 'live-committee' && liveVotingAccess.canViewCommittee && (
            <LiveVotingSection 
              boardMembers={boardMembers}
              committees={committees}
              onRefresh={loadVotingData}
              refreshTrigger={refreshTrigger}
              canInteract={liveVotingAccess.canInteractCommittee}
              meetingTypeFilter="COMMITTEE"
              tabType="committee"
              hoaName={getHoaName()}
              hoaSettings={hoaSettings}
              hoaData={hoaData}
              hoaTimezone={hoaTimezone}
              discussionPropertyDisplay={(liveVotingAccess && liveVotingAccess.settings && liveVotingAccess.settings.discussion_property_display) ? liveVotingAccess.settings.discussion_property_display : 'address'}
              onExport={exportFromPreview}
              meetingRules={meetingRules}
              speakingTimeMinutes={speakingTimeMinutes}
            />
          )}

          {/* Device Test Tab */}
          {activeTab === 'device-test' && (
            <DeviceTestTab />
          )}

          {/* Archive/Past Results Tab */}
          {activeTab === 'archive' && (
            <div className="space-y-6">
              {/* Meeting Minutes Archive */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-purple-100 rounded-full">
                      <div className="w-6 h-6 text-purple-600">📚</div>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">Meeting Minutes Archive</h3>
                      <p className="text-gray-600">Search archived meeting minutes, discussions, and voting records</p>
                    </div>
                  </div>
                </div>
                <ArchiveManagement user={user} />
              </div>
              
              {/* Election Results Archive */}
              <ArchiveSection 
                elections={elections}
                selectedElectionResults={selectedElectionResults}
                onSelectElectionResults={setSelectedElectionResults}
                archivedPolls={archivedPolls}
                isBoard={isBoard}
                hoaTimezone={hoaTimezone}
                onExportPoll={exportPollResults}
              />
            </div>
          )}

          {/* Management Tab */}
          {activeTab === 'management' && (user?.role?.toLowerCase().includes('board') || user?.role?.toLowerCase().includes('president') || user?.role?.toLowerCase().includes('treasurer') || user?.role?.toLowerCase().includes('secretary') || consentStats) && (
            <>
              {!hasVotingAddon(hoaSettings) ? (
                <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-8 text-center">
                  <div className="mx-auto h-16 w-16 bg-gray-200 rounded-full flex items-center justify-center mb-4">
                    <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Voting Platform Add-on Required</h3>
                  <p className="text-gray-600 mb-4">
                    Unlock election management to create and manage elections, polls, and voting campaigns.
                  </p>
                  <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 inline-block">
                    <div className="text-2xl font-bold text-indigo-600 mb-1">$9/month or $100/year</div>
                    <p className="text-sm text-gray-500">Add to your subscription</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left max-w-md mx-auto">
                    <p className="text-sm text-blue-900 mb-2"><strong>✓ Already included:</strong></p>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• View polls and elections</li>
                      <li>• Participate in live voting</li>
                      <li>• Cast ballots and votes</li>
                    </ul>
                    <p className="text-sm text-blue-900 mt-3 mb-2"><strong>🔒 With add-on:</strong></p>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Create elections and polls</li>
                      <li>• Manage candidates and topics</li>
                      <li>• Configure voting settings</li>
                      <li>• Generate ballots and reports</li>
                    </ul>
                  </div>
                  <button
                    onClick={() => {
                      // Navigate to settings tab using React Router or event
                      const event = new CustomEvent('navigate-to-settings', { 
                        detail: { tab: 'subscription' } 
                      });
                      window.dispatchEvent(event);
                      
                      // Fallback to direct navigation if event doesn't work
                      setTimeout(() => {
                        const settingsTab = document.querySelector('[data-tab="settings"]');
                        if (settingsTab) {
                          settingsTab.click();
                        }
                      }, 100);
                    }}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Upgrade to Add Voting Platform
                  </button>
                </div>
              ) : (
                <ElectionsManagementSection 
                  hoaTimezone={hoaTimezone}
                  consentStats={consentStats}
                  elections={elections}
                  currentElection={election}
                  onRefresh={loadVotingData}
                  showCreateElection={showCreateElection}
                  setShowCreateElection={setShowCreateElection}
                  boardMembers={boardMembers}
                  hoaSettings={hoaSettings}
                />
              )}
            </>
          )}

          {/* E-Consent Tab */}
          {activeTab === 'e-consent' && (
            <>
              <EConsentSection 
                user={user}
                onRefresh={loadVotingData}
                hoaTimezone={hoaTimezone}
              />
              
              {/* Active Proxies Section - Filtered by Current Election */}
              <ActiveProxiesSection 
                user={user} 
                hoaTimezone={hoaTimezone}
                currentElectionId={election?.id}
                currentElectionName={election?.name}
              />
              
              {/* Proxy Management - Independent from E-Consent */}
              <ProxyManagementSection 
                user={user}
                currentElection={election}
                onProxyChange={loadVotingData}
                hoaTimezone={hoaTimezone}
              />
            </>
          )}

          {/* No Content State */}
          {!election && !consentStats && activeTab === 'voting' && (
            <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
              <div className="text-6xl mb-4">🗳️</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Active Elections</h3>
              <p className="text-gray-600 mb-4">
                There are currently no active elections or nominations in progress.
              </p>
              <p className="text-sm text-gray-500">
                Check back later or contact your board for information about upcoming elections.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Archive Management Modal */}
      {showMinutesPreview && (
        <ArchiveManagement user={user} onClose={() => setShowMinutesPreview(false)} />
      )}

      {/* Combined Ballot Modal */}
      {showCombinedBallot && election && (
        <CombinedBallotView
          electionId={election.id}
          user={user}
          onClose={() => setShowCombinedBallot(false)}
          onSubmitSuccess={() => {
            setShowCombinedBallot(false);
            loadVotingData();
          }}
        />
      )}
    </div>
    </>
  );
}

// Current Election Section
function CurrentElectionSection({ 
  election, 
  candidates, 
  consent, 
  proxy, 
  stats,
  onUpdateConsent, 
  onUpdateProxy, 
  onRefresh,
  setActiveTab,
  hoaName,
  hoaTimezone = 'America/Chicago',
  user,
  onOpenCombinedBallot
}) {
  return (
    <div className="space-y-6">
      {/* Election Overview */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 bg-indigo-100 rounded-full">
            <div className="w-6 h-6 text-indigo-600">🗳️</div>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{election.name}</h3>
            <p className="text-gray-600">
              Status: <span className={`font-medium ${getStatusColor(election.status)}`}>
                {getStatusLabel(election.status)}
              </span>
            </p>
          </div>
        </div>

        {election.description && (
          <div className="mb-4">
            <h4 className="font-medium text-gray-900 mb-2">About this Election</h4>
            <p className="text-gray-700">{election.description}</p>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-900">Nomination Period:</span>
            <p className="text-gray-700">
              {formatLocal(election.nominations_open, hoaTimezone, 'MMM d, yyyy')} - 
              {formatLocal(election.nominations_close, hoaTimezone, 'MMM d, yyyy')}
            </p>
          </div>
          <div>
            <span className="font-medium text-gray-900">Voting Period:</span>
            <p className="text-gray-700">
              {formatLocal(election.voting_open, hoaTimezone, 'MMM d, yyyy')} -{' '}
              {election.meeting_start_time
                ? formatLocal(new Date(new Date(election.meeting_start_time).getTime() + 10 * 60 * 1000).toISOString(), hoaTimezone, 'MMM d, yyyy h:mm a z')
                : election.voting_close
                  ? formatLocal(election.voting_close, hoaTimezone, 'MMM d, yyyy h:mm a z')
                  : 'Meeting start + 10 minutes'}
            </p>
          </div>
        </div>

        {/* Election Actions */}
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            onClick={() => printBallot(election, candidates, hoaName, hoaTimezone)}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors flex items-center gap-2"
          >
            🖨️ Print Ballot
          </button>
          {election.status === 'voting' && (() => {
            // Check if e-voting has closed (10 minutes after meeting start)
            const meetingStart = election.meeting_start_time ? new Date(election.meeting_start_time) : null;
            const eVotingEnd = meetingStart ? new Date(meetingStart.getTime() + 10 * 60 * 1000) : null;
            const isEVotingClosed = eVotingEnd && new Date() > eVotingEnd;
            
            if (isEVotingClosed) {
              return (
                <div className="px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-700 flex items-center gap-2">
                  <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                  E-Voting closed - In-person voting only
                </div>
              );
            }
            return (
              <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                Voting is currently open
              </div>
            );
          })()}
        </div>

        {/* Live Stats */}
        {stats && stats.length > 0 && (
          <div className="mt-6 pt-6 border-t">
            <h4 className="font-medium text-gray-900 mb-4">Live Results</h4>
            <div className="space-y-3">
              {stats.map((candidate, index) => (
                <div key={candidate.candidate_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <h5 className="font-medium text-gray-900">{candidate.candidate_name}</h5>
                    <div className="text-sm text-gray-600">
                      Online: {candidate.online_votes} | Manual: {candidate.manual_votes} | Proxy: {candidate.proxy_votes}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-indigo-600">{candidate.total_votes}</div>
                    <div className="text-sm text-gray-600">total votes</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* E-Voting Consent - Read Only Display */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-green-100 rounded-full flex-shrink-0">
            <div className="w-6 h-6 text-green-600">✓</div>
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900">E-Voting Consent Status</h3>
              <button
                onClick={() => setActiveTab('e-consent')}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
              >
                Manage in E-Consent Tab
              </button>
            </div>
            
            {consent !== null ? (
              <div className="space-y-2">
                <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                  consent ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {consent ? '✅ CONSENTED to e-voting' : '❌ DO NOT CONSENT to e-voting'}
                </div>
                <p className="text-sm text-gray-600">
                  {consent 
                    ? 'You can participate in electronic voting for this election.'
                    : 'You will need to vote by mail or in person for this election.'
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                  ⚠️ NO RESPONSE
                </div>
                <p className="text-sm text-gray-600">
                  Please provide your e-voting consent in the E-Consent tab to participate in electronic voting.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Proxy Settings - Read Only Display */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-full flex-shrink-0">
            <div className="w-6 h-6 text-blue-600">👤</div>
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900">Proxy Voting Settings</h3>
              <button
                onClick={() => setActiveTab('e-consent')}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
              >
                Manage in E-Consent Tab
              </button>
            </div>
            
            {proxy?.allow_proxy ? (
              <div className="space-y-2">
                <div className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  🤝 Proxy Enabled
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <div>
                    <strong>Proxy Type:</strong> {
                      proxy.proxy_type === 'board' ? 'Board Member' :
                      proxy.proxy_type === 'specific' ? `Specific Person (${proxy.proxy_person || 'Not specified'})` :
                      'Default Board Proxy'
                    }
                  </div>
                  {proxy.proxy_type === 'specific' && proxy.proxy_email && (
                    <div><strong>Proxy Email:</strong> {proxy.proxy_email}</div>
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  If you don't vote by the deadline, your designated proxy can vote on your behalf.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                  🚫 No Proxy
                </div>
                <p className="text-sm text-gray-600">
                  You have not enabled proxy voting. You must vote directly or your vote will not be counted.
                  You can enable proxy voting in the E-Consent tab.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Voting Section */}
      {election.status === 'voting' && consent && (
        <VotingSection
          candidates={candidates}
          election={election}
          onVote={onRefresh}
          user={user}
          hoaTimezone={hoaTimezone}
        />
      )}

      {/* Results Section */}
      {election.status === 'closed' && (
        <ResultsSection stats={stats} />
      )}
    </div>
  );
}

// Polls Section
function PollsSection({ 
  polls, 
  pendingPolls = [],
  archivedPolls = [],
  pollSettings = {},
  onRefresh, 
  showCreatePoll, 
  setShowCreatePoll, 
  canCreatePolls = true,
  isBoard = false,
  hoaTimezone = 'America/Chicago',
  onSettingsUpdate
}) {
  const [selectedPoll, setSelectedPoll] = useState(null);
  const [selectedOption, setSelectedOption] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [editingPoll, setEditingPoll] = useState(null);
  const [showPollAnalytics, setShowPollAnalytics] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const [localSettings, setLocalSettings] = useState(pollSettings);
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Load all users for specific user selection
  useEffect(() => {
    const loadAllUsers = async () => {
      try {
        // Load all homeowners and team members
        const response = await api.get('/api/vote/committees-and-members');
        setTeamMembers(response.data?.team_members || []);
      } catch (error) {
        console.error('Failed to load users:', error);
        // Fallback to old endpoint
        try {
          const teamResponse = await api.get('/api/users/search');
          setTeamMembers(teamResponse.data || []);
        } catch (fallbackError) {
          console.error('Fallback failed:', fallbackError);
        }
      }
    };
    loadAllUsers();
  }, []);

  // Approve poll
  const approvePoll = async (pollId) => {
    try {
      await api.post(`/api/vote/polls/${pollId}/approve`);
      alert('Poll approved successfully!');
      onRefresh();
    } catch (error) {
      console.error('Error approving poll:', error);
      alert('Failed to approve poll. Please try again.');
    }
  };

  // Reject poll
  const rejectPoll = async (pollId) => {
    const reason = prompt('Reason for rejection (optional):');
    try {
      await api.post(`/api/vote/polls/${pollId}/reject`, { reason });
      alert('Poll rejected.');
      onRefresh();
    } catch (error) {
      console.error('Error rejecting poll:', error);
      alert('Failed to reject poll. Please try again.');
    }
  };

  // Save poll settings
  const saveSettings = async () => {
    try {
      await api.put('/api/vote/polls/settings', localSettings);
      alert('Poll settings saved successfully!');
      setShowSettings(false);
      if (onSettingsUpdate) {
        onSettingsUpdate(localSettings);
      }
      onRefresh();
    } catch (error) {
      console.error('Error saving poll settings:', error);
      alert('Failed to save settings. Please try again.');
    }
  };


  const votePoll = async (pollId, option) => {
    try {
      const response = await api.post('/api/vote/polls/vote', {
        poll_id: pollId,
        option: option
      });
      
      // Check for override message
      if (response.data?.override && response.data?.message) {
        alert(response.data.message);
      } else {
        alert('Vote submitted successfully!');
      }
      
      onRefresh();
    } catch (error) {
      console.error('Error voting in poll:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to submit vote. Please try again.';
      alert(errorMsg);
    }
  };

  const uploadDocument = async (pollId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await api.post(`/api/vote/polls/${pollId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      alert('Document submitted successfully!');
      onRefresh();
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document. Please try again.');
    }
  };

  const loadReferenceDocuments = async (pollId) => {
    try {
      const response = await api.get(`/api/vote/polls/${pollId}/reference-documents`);
      const docs = response.data.reference_documents;
      
      if (docs.length === 0) {
        alert('No reference documents found.');
        return;
      }
      
      // Create a simple list of documents
      const docList = docs.map(doc => 
        `• ${doc.filename} (uploaded ${formatDateInHOATimezone(doc.uploaded_at, hoaTimezone, 'MMM d, yyyy')})`
      ).join('\n');
      
      const viewDoc = window.confirm(`Reference Documents:\n\n${docList}\n\nWould you like to download the first document?`);
      
      if (viewDoc && docs[0]) {
        // Download first document
        window.open(`/api/vote/polls/${pollId}/reference-documents/${docs[0].id}/download`, '_blank');
      }
    } catch (error) {
      console.error('Error loading reference documents:', error);
      alert('Failed to load reference documents.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-full">
              <div className="w-6 h-6 text-green-600">📊</div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Active & Upcoming Polls</h3>
              <p className="text-gray-600">Participate in community polls and document collection</p>
            </div>
          </div>
          
          <div className="flex gap-2">
            {isBoard && (
              <>
                <button
                  onClick={() => setShowSettings(true)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  title="Poll Settings"
                >
                  ⚙️ Settings
                </button>
                <button
                  onClick={() => setShowPending(true)}
                  className={`px-4 py-2 rounded-lg transition-colors relative ${
                    pendingPolls.length > 0 
                      ? 'bg-yellow-600 text-white hover:bg-yellow-700' 
                      : 'bg-gray-400 text-white cursor-not-allowed'
                  }`}
                  title={pendingPolls.length > 0 ? "View Pending Polls" : "No Pending Polls"}
                  disabled={pendingPolls.length === 0}
                >
                  🔔 Pending {pendingPolls.length > 0 ? `(${pendingPolls.length})` : '(0)'}
                </button>
              </>
            )}
            <button
              onClick={() => setShowCreatePoll(true)}
              disabled={!canCreatePolls}
              className={`px-4 py-2 rounded-lg transition-colors ${
                canCreatePolls 
                  ? 'bg-green-600 text-white hover:bg-green-700' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              title={!canCreatePolls ? 'You do not have permission to create polls' : 'Create a new poll'}
            >
              Create Poll
            </button>
          </div>
        </div>
      </div>

      {/* Active & Upcoming Polls */}
      {polls.filter(poll => {
        const now = new Date();
        const closesAt = new Date(poll.closes_at);
        return now <= closesAt; // Only show polls that haven't expired
      }).length === 0 ? (
        <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Active Polls</h3>
          <p className="text-gray-600">Check back later for community polls and document collection opportunities.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {polls.filter(poll => {
            const now = new Date();
            const closesAt = new Date(poll.closes_at);
            return now <= closesAt; // Only show polls that haven't expired
          }).map((poll) => {
            const now = new Date();
            const opensAt = new Date(poll.opens_at);
            const closesAt = new Date(poll.closes_at);
            const isActive = now >= opensAt && now <= closesAt;
            const hasEnded = now > closesAt;
            
            return (
              <div key={poll.id} className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">{poll.title}</h4>
                    <p className="text-sm text-gray-600">
                      {isActive && (
                        <span className="text-green-600 font-medium">
                          Closes: {formatLocal(poll.closes_at, hoaTimezone, 'MMM d, yyyy')} at {formatLocal(poll.closes_at, hoaTimezone, 'h:mm a')}
                        </span>
                      )}
                      {hasEnded && (
                        <span>Closed: {formatLocal(poll.closes_at, hoaTimezone, 'MMM d, yyyy')} at {formatLocal(poll.closes_at, hoaTimezone, 'h:mm a')}</span>
                      )}
                      {!isActive && !hasEnded && (
                        <span>Opens: {formatLocal(poll.opens_at, hoaTimezone, 'MMM d, yyyy')} at {formatLocal(poll.opens_at, hoaTimezone, 'h:mm a')}</span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Edit and Delete buttons for scheduled polls (haven't started) */}
                    {now < opensAt && isBoard && (
                      <>
                        <button
                          onClick={() => setEditingPoll(poll)}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={async () => {
                            if (window.confirm(`Are you sure you want to delete the poll "${poll.title}"? This action cannot be undone.`)) {
                              try {
                                // Delete the poll
                                await api.delete(`/api/vote/polls/${poll.id}`);
                                
                                // Show success
                                alert('✅ Poll deleted successfully');
                                
                                // Refresh polls list
                                if (onRefresh) {
                                  onRefresh();
                                }
                              } catch (error) {
                                console.error('Error deleting poll:', error);
                                alert('❌ Failed to delete poll. Please try again.');
                              }
                            }
                          }}
                          className="px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                        >
                          🗑️ Delete
                        </button>
                      </>
                    )}
                    {/* Analytics button for board members */}
                    {(isActive || hasEnded) && isBoard && (
                      <button
                        onClick={() => setShowPollAnalytics(poll)}
                        className="px-3 py-1 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        📊 Analytics
                      </button>
                    )}
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      isActive ? 'bg-green-100 text-green-800' : 
                      hasEnded ? 'bg-gray-100 text-gray-800' : 
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {isActive ? 'Active' : hasEnded ? 'Closed' : 'Scheduled'}
                    </span>
                  </div>
                </div>

                {poll.description && (
                  <p className="text-gray-700 mb-4">{poll.description}</p>
                )}

                {/* Live Vote Count for All Users */}
                {(isActive || hasEnded) && (
                  <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium text-indigo-900">📊 Current Results</h5>
                      <span className="text-sm text-indigo-600">{poll.total_votes || 0} total votes</span>
                    </div>
                    {poll.options && poll.options.map((option, idx) => {
                      const voteCount = poll.vote_counts?.[option] || 0;
                      const percentage = poll.total_votes > 0 ? ((voteCount / poll.total_votes) * 100).toFixed(1) : 0;
                      return (
                        <div key={idx} className="mb-2">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="font-medium text-gray-700">{option}</span>
                            <span className="text-indigo-600">{voteCount} votes ({percentage}%)</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Reference Documents */}
                {poll.has_reference_documents && (
                  <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <h5 className="font-medium text-blue-900 mb-2">📎 Reference Documents</h5>
                    <button
                      onClick={() => loadReferenceDocuments(poll.id)}
                      className="text-sm text-blue-700 hover:text-blue-900 underline"
                    >
                      View attached documents
                    </button>
                  </div>
                )}

                {isActive && (
                  <div className="space-y-4">
                    {/* Poll Options - Enhanced for Yes/No */}
                    {poll.options && poll.options.length > 0 && (
                      <div>
                        <h5 className="font-medium text-gray-900 mb-3">Cast your vote:</h5>
                        {poll.options.length === 2 && poll.options.includes('Yes') && poll.options.includes('No') ? (
                          // Special Yes/No Layout
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              onClick={() => votePoll(poll.id, 'Yes')}
                              className="p-4 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                            >
                              ✅ Yes
                            </button>
                            <button
                              onClick={() => votePoll(poll.id, 'No')}
                              className="p-4 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                            >
                              ❌ No
                            </button>
                          </div>
                        ) : (
                          // Standard Option Layout
                          <div className="space-y-2">
                            {poll.options.map((option, index) => (
                              <button
                                key={index}
                                onClick={() => votePoll(poll.id, option)}
                                className="w-full text-left p-3 border-2 border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors font-medium"
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Document Upload */}
                    {poll.require_document && (
                      <div className="pt-4 border-t">
                        <h5 className="font-medium text-gray-900 mb-2">Submit Document:</h5>
                        <div className="flex gap-2">
                          <input
                            type="file"
                            onChange={(e) => setUploadFile(e.target.files[0])}
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                          />
                          <button
                            onClick={() => uploadFile && uploadDocument(poll.id, uploadFile)}
                            disabled={!uploadFile}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                          >
                            Upload
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Accepted formats: PDF, JPG, PNG, DOC, DOCX (max {poll.max_documents} per person)
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {hasEnded && (
                  <div className="text-center py-4 text-gray-500">
                    <p>This poll has ended. Thank you for participating!</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Poll Modal */}
      {showCreatePoll && (
        <CreatePollModal 
          onClose={() => setShowCreatePoll(false)}
          onRefresh={onRefresh}
        />
      )}

      {/* Edit Poll Modal */}
      {editingPoll && (
        <EditPollModal 
          poll={editingPoll}
          onClose={() => setEditingPoll(null)}
          onRefresh={onRefresh}
        />
      )}

      {/* Poll Analytics Modal */}
      {showPollAnalytics && (
        <PollAnalyticsModal 
          poll={showPollAnalytics}
          onClose={() => setShowPollAnalytics(null)}
          isBoard={isBoard}
          hoaTimezone={hoaTimezone}
        />
      )}

      {/* Poll Settings Modal */}
      {showSettings && isBoard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Poll Settings</h3>
              <button onClick={() => setShowSettings(false)} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
            </div>

            <div className="space-y-6">
              {/* Poll Creation Permissions - Simplified */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Who can create polls?</label>
                <select
                  value={localSettings.poll_creation_permissions || 'board_only'}
                  onChange={(e) => setLocalSettings({...localSettings, poll_creation_permissions: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="board_only">Only Board Members</option>
                  <option value="specific_users">Specific Users</option>
                </select>
                
                {/* Specific Users Selection */}
                {localSettings.poll_creation_permissions === 'specific_users' && (
                  <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select users who can create polls:
                    </label>
                    <div className="max-h-80 overflow-y-auto border border-gray-300 rounded-lg bg-white shadow-sm">
                      {teamMembers && teamMembers.length > 0 ? (
                        teamMembers.map((member) => {
                          const memberId = member.id || member.user_id;
                          const memberName = member.name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email;
                          const isSelected = (localSettings.poll_creation_specific_users || []).includes(memberId);
                          const roleDisplay = member.role_title || member.role || '';
                          
                          return (
                            <label
                              key={memberId}
                              className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 transition-colors ${
                                isSelected ? 'bg-indigo-50 border-indigo-200' : ''
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  const currentUsers = localSettings.poll_creation_specific_users || [];
                                  const newUsers = e.target.checked
                                    ? [...currentUsers, memberId]
                                    : currentUsers.filter(id => id !== memberId);
                                  setLocalSettings({
                                    ...localSettings,
                                    poll_creation_specific_users: newUsers
                                  });
                                }}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 truncate">{memberName}</div>
                                {member.email && (
                                  <div className="text-sm text-gray-600 truncate">{member.email}</div>
                                )}
                                {roleDisplay && (
                                  <div className="text-xs text-indigo-600 font-medium mt-0.5">
                                    {roleDisplay}
                                  </div>
                                )}
                              </div>
                              {isSelected && (
                                <div className="flex-shrink-0">
                                  <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </label>
                          );
                        })
                      ) : (
                        <div className="p-6 text-center text-gray-500">
                          <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                          <p className="text-sm font-medium">No users available</p>
                          <p className="text-xs mt-1">All homeowners and team members will appear here</p>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <p className="text-xs text-gray-600">
                        <span className="font-semibold text-indigo-600">
                          {(localSettings.poll_creation_specific_users || []).length}
                        </span> user(s) selected
                      </p>
                      {(localSettings.poll_creation_specific_users || []).length > 0 && (
                        <button
                          onClick={() => setLocalSettings({...localSettings, poll_creation_specific_users: []})}
                          className="text-xs text-gray-600 hover:text-red-600 underline"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* One Vote Per Property */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localSettings.one_vote_per_property || false}
                    onChange={(e) => setLocalSettings({...localSettings, one_vote_per_property: e.target.checked})}
                    className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <div>
                    <div className="font-medium text-gray-900">One vote per property</div>
                    <div className="text-sm text-gray-600">
                      When enabled, only one vote per property is allowed. A second vote from the same property will override the first vote.
                    </div>
                  </div>
                </label>
              </div>

              {/* Require Poll Approval */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={localSettings.require_poll_approval || false}
                    onChange={(e) => setLocalSettings({...localSettings, require_poll_approval: e.target.checked})}
                    className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <div>
                    <div className="font-medium text-gray-900">Require board approval for non-board polls</div>
                    <div className="text-sm text-gray-600">
                      When enabled, polls created by non-board members must be approved before going live.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={saveSettings}
                className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
              >
                Save Settings
              </button>
              <button
                onClick={() => setShowSettings(false)}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Polls Modal */}
      {showPending && isBoard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Pending Polls for Approval</h3>
              <button onClick={() => setShowPending(false)} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
            </div>

            {pendingPolls.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No pending polls at this time.
              </div>
            ) : (
              <div className="space-y-4">
                {pendingPolls.map(poll => (
                  <div key={poll.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-lg text-gray-900">{poll.title}</h4>
                        {poll.description && (
                          <p className="text-gray-600 mt-1">{poll.description}</p>
                        )}
                        <div className="flex gap-4 mt-2 text-sm text-gray-500">
                          <span>Opens: {formatLocal(poll.opens_at, hoaTimezone, 'MMM d, yyyy')}</span>
                          <span>Closes: {formatLocal(poll.closes_at, hoaTimezone, 'MMM d, yyyy')}</span>
                        </div>
                        <div className="mt-2">
                          <span className="text-sm font-medium text-gray-700">Options:</span>
                          <div className="flex gap-2 mt-1 flex-wrap">
                            {poll.options?.map((opt, i) => (
                              <span key={i} className="px-2 py-1 bg-gray-100 rounded text-sm">{opt}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => approvePoll(poll.id)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                        >
                          ✓ Approve
                        </button>
                        <button
                          onClick={() => rejectPoll(poll.id)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                        >
                          ✗ Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowPending(false)}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Archive Section for Past Results
function ArchiveSection({ elections, selectedElectionResults, onSelectElectionResults, archivedPolls = [], isBoard = false, hoaTimezone = 'America/Chicago', onExportPoll }) {
  const [selectedElection, setSelectedElection] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeArchiveTab, setActiveArchiveTab] = useState('elections'); // 'elections' or 'polls'

  const loadElectionResults = async (electionId) => {
    setLoading(true);
    try {
      const response = await api.get(`/api/vote/election/${electionId}/results`);
      setResults(response.data);
      setSelectedElection(elections.find(e => e.id === electionId));
    } catch (error) {
      console.error('Error loading election results:', error);
      alert('Failed to load election results.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Tabs */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 bg-purple-100 rounded-full">
            <div className="w-6 h-6 text-purple-600">📚</div>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Archive & Results</h3>
            <p className="text-gray-600">View past elections and poll results</p>
          </div>
        </div>

        {/* Archive Tabs */}
        <div className="flex gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveArchiveTab('elections')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeArchiveTab === 'elections'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Elections
          </button>
          <button
            onClick={() => setActiveArchiveTab('polls')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeArchiveTab === 'polls'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Previous Polls
          </button>
        </div>
      </div>

      {/* Elections Archive Tab */}
      {activeArchiveTab === 'elections' && (
        <>
          {/* Elections List */}
          <div className="grid md:grid-cols-2 gap-4">
            {elections.filter(e => e.status === 'closed' || e.status === 'archived').map((election) => (
              <div key={election.id} className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-gray-900">{election.name}</h4>
                    <p className="text-sm text-gray-600">
                      {formatLocal(election.created_at, hoaTimezone, 'MMM d, yyyy')}
                    </p>
                  </div>
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                    Completed
                  </span>
                </div>
                
                <button
                  onClick={() => loadElectionResults(election.id)}
                  className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  View Results
                </button>
              </div>
            ))}
          </div>

          {/* Results Display */}
          {results && selectedElection && (
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">{selectedElection.name} - Results</h3>
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                  <button
                    onClick={async () => {
                      if (!window.confirm('Sync archive stats with current database values?\n\nThis will update the archived results with any manual votes or corrections made after finalization.')) {
                        return;
                      }
                      try {
                        const response = await api.post(`/api/vote/election/${selectedElection.id}/sync-archive-stats`);
                        alert('✅ Archive stats synced successfully!\n\nResults have been updated with current data.');
                        // Reload results to show updated stats
                        loadElectionResults(selectedElection.id);
                      } catch (error) {
                        alert('Failed to sync stats: ' + (error.response?.data?.detail || error.message));
                      }
                    }}
                    className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 text-sm sm:text-base bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    title="Re-sync archive with current database values"
                  >
                    🔄 Sync Stats
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const response = await api.get(`/api/vote/election/${selectedElection.id}/results-pdf`, {
                          responseType: 'blob'
                        });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `${selectedElection.name}-Official-Results.pdf`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                      } catch (error) {
                        alert('Failed to download PDF: ' + (error.response?.data?.detail || error.message));
                      }
                    }}
                    className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 text-sm sm:text-base bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    📄 Download PDF
                  </button>
                  <button
                    onClick={() => {setResults(null); setSelectedElection(null);}}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Finalization & Sync Info */}
              {results.archive && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="text-sm">
                      <span className="font-semibold text-blue-900">Finalized:</span>
                      <span className="text-blue-700 ml-2">
                        {results.archive.finalized_at 
                          ? formatLocal(results.archive.finalized_at, hoaTimezone, 'MMM d, yyyy h:mm a') 
                          : 'Unknown'}
                      </span>
                    </div>
                    {results.archive.last_synced_at && (
                      <div className="text-sm">
                        <span className="font-semibold text-green-900">Last Synced:</span>
                        <span className="text-green-700 ml-2">
                          {formatLocal(results.archive.last_synced_at, hoaTimezone, 'MMM d, yyyy h:mm a')}
                        </span>
                        <span className="text-green-600 ml-1">✓</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Turnout Statistics */}
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{results.turnout?.unique_properties_voted || 0}</div>
                  <div className="text-sm text-blue-700">Properties Voted</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{results.turnout?.online_votes_cast || 0}</div>
                  <div className="text-sm text-green-700">Online Votes</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">{results.turnout?.manual_votes_cast || 0}</div>
                  <div className="text-sm text-yellow-700">Manual Votes</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">{results.turnout?.proxy_votes_cast || 0}</div>
                  <div className="text-sm text-purple-700">Proxy Votes</div>
                </div>
              </div>

              {/* ENHANCED Proxy Breakdown - ALL Categories */}
              {(results.turnout?.total_properties_with_proxy > 0 || results.turnout?.proxy_voting_only_candidates > 0 || results.turnout?.proxy_mix_voting_and_quorum > 0) && (
                <div className="mb-6 p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg border border-teal-200">
                  <h5 className="font-semibold text-teal-900 mb-3 flex items-center gap-2">
                    🏛️ Proxy Breakdown (By Type)
                  </h5>
                  <div className="grid md:grid-cols-5 gap-3">
                    <div className="text-center p-3 bg-white rounded-lg border border-blue-200">
                      <div className="text-xl font-bold text-blue-700">{results.turnout?.proxy_voting_only_candidates || 0}</div>
                      <div className="text-xs text-blue-600">Voting Only (Candidates)</div>
                      <div className="text-[10px] text-blue-400">Kept motions & quorum</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border border-indigo-200">
                      <div className="text-xl font-bold text-indigo-700">{results.turnout?.proxy_voting_only_motions || 0}</div>
                      <div className="text-xs text-indigo-600">Voting Only (Motions)</div>
                      <div className="text-[10px] text-indigo-400">Kept candidates & quorum</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border border-teal-200">
                      <div className="text-xl font-bold text-teal-700">{results.turnout?.proxy_mix_voting_and_quorum || 0}</div>
                      <div className="text-xs text-teal-600">Mix (Voting + Quorum)</div>
                      <div className="text-[10px] text-teal-400">Gave all rights</div>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border border-purple-200">
                      <div className="text-xl font-bold text-purple-700">{results.turnout?.proxy_quorum_only || 0}</div>
                      <div className="text-xs text-purple-600">Quorum Only</div>
                      <div className="text-[10px] text-purple-400">Attendance only</div>
                    </div>
                    <div className="text-center p-3 bg-teal-100 rounded-lg border-2 border-teal-400">
                      <div className="text-xl font-bold text-teal-900">{results.turnout?.total_properties_with_proxy || 0}</div>
                      <div className="text-xs text-teal-700 font-semibold">TOTAL WITH PROXY</div>
                    </div>
                  </div>
                  <p className="text-xs text-teal-700 mt-3 text-center italic">
                    Only proxies where the holder actually voted are counted. Each property counted once.
                  </p>
                </div>
              )}

              {/* Participation Percentage */}
              <div className="mb-6 p-4 bg-indigo-50 rounded-lg">
                <div className="text-center">
                  <div className="text-3xl font-bold text-indigo-600">
                    {results.turnout?.turnout_percentage?.toFixed(1) || 0}%
                  </div>
                  <div className="text-indigo-700">Property Participation Rate</div>
                </div>
              </div>

              {/* Candidate Results */}
              {results.results && results.results.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Candidate Results</h4>
                  <div className="space-y-3">
                    {results.results.map((candidate, index) => (
                      <div key={candidate.candidate_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            index === 0 ? 'bg-yellow-100 text-yellow-800' :
                            index === 1 ? 'bg-gray-100 text-gray-800' :
                            index === 2 ? 'bg-orange-100 text-orange-800' :
                            'bg-gray-50 text-gray-600'
                          }`}>
                            {index + 1}
                          </div>
                          <div>
                            <h5 className="font-medium text-gray-900">{candidate.candidate_name}</h5>
                            <div className="text-sm text-gray-600">
                              Online: {candidate.online_votes} | Manual: {candidate.manual_votes} | Proxy: {candidate.proxy_votes}
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <div className="text-xl font-bold text-indigo-600">{candidate.total_votes}</div>
                          <div className="text-sm text-gray-600">total votes</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Motion/Topic Results */}
              {results.topics && results.topics.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Motion Results</h4>
                  <div className="space-y-3">
                    {results.topics.map((motion, index) => {
                      // Get answer options from motion data or default to Yes/No
                      const answerOptions = motion.answer_options || motion.answers || ['Yes', 'No'];
                      // Calculate total from all answer options in total_counts
                      const totalCounts = motion.total_counts || {};
                      const totalVotes = Object.values(totalCounts).reduce((sum, val) => sum + (val || 0), 0) || 
                                        (motion.approve || 0) + (motion.oppose || 0);
                      
                      // Determine if passed (first option > second option, typically Yes > No)
                      const firstOption = answerOptions[0] || 'Yes';
                      const secondOption = answerOptions[1] || 'No';
                      const firstCount = totalCounts[firstOption] || motion.approve || 0;
                      const secondCount = totalCounts[secondOption] || motion.oppose || 0;
                      const passed = firstCount > secondCount;
                      
                      return (
                        <div key={index} className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900">{motion.title}</h5>
                              {motion.description && (
                                <p className="text-sm text-gray-600 mt-1">{motion.description}</p>
                              )}
                            </div>
                            <span className={`ml-4 px-3 py-1 rounded-full text-sm font-medium ${
                              passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {passed ? '✓ Passed' : '✗ Failed'}
                            </span>
                          </div>
                          
                          {/* Dynamic answer options grid */}
                          <div className={`grid gap-3`} style={{gridTemplateColumns: `repeat(${answerOptions.length}, 1fr)`}}>
                            {answerOptions.map((option, optIdx) => {
                              const count = totalCounts[option] || 
                                           (option === 'Yes' || option === 'Approve' ? motion.approve : 
                                            option === 'No' || option === 'Oppose' ? motion.oppose : 0) || 0;
                              const isPositive = optIdx === 0;
                              const bgColor = isPositive ? 'bg-green-50' : (optIdx === 1 ? 'bg-red-50' : 'bg-gray-100');
                              const textColor = isPositive ? 'text-green-600' : (optIdx === 1 ? 'text-red-600' : 'text-gray-600');
                              const labelColor = isPositive ? 'text-green-700' : (optIdx === 1 ? 'text-red-700' : 'text-gray-700');
                              
                              return (
                                <div key={option} className={`text-center p-2 ${bgColor} rounded`}>
                                  <div className={`text-lg font-bold ${textColor}`}>{count}</div>
                                  <div className={`text-xs ${labelColor}`}>{option}</div>
                                </div>
                              );
                            })}
                          </div>
                          
                          <div className="mt-2 text-sm text-gray-600 text-center">
                            Total Votes: {totalVotes}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading results...</p>
        </div>
      )}
      </>
      )}

      {/* Previous Polls Archive Tab */}
      {activeArchiveTab === 'polls' && (
        <div className="space-y-4">
          {archivedPolls.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center">
              <div className="text-6xl mb-4">📊</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Archived Polls</h3>
              <p className="text-gray-600">Completed polls will appear here.</p>
            </div>
          ) : (
            archivedPolls.map(poll => (
              <div key={poll.id} className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-semibold text-lg text-gray-900">{poll.title}</h4>
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm font-medium">Closed</span>
                    </div>
                    {poll.description && (
                      <p className="text-gray-600 mb-2">{poll.description}</p>
                    )}
                    <div className="text-sm text-gray-500 mb-3">
                      Voting Period: {formatLocal(poll.opens_at, hoaTimezone, 'MMM d, yyyy h:mm a')} - {formatLocal(poll.closes_at, hoaTimezone, 'MMM d, yyyy h:mm a z')}
                    </div>
                    
                    {/* Results */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                      <h5 className="font-semibold text-blue-900 mb-2">Results</h5>
                      <div className="space-y-2">
                        <div className="text-sm text-blue-800">
                          Total Votes: <span className="font-semibold">{poll.results?.total_votes || 0}</span>
                        </div>
                        {poll.results?.vote_counts && Object.entries(poll.results.vote_counts).map(([option, count]) => (
                          <div key={option} className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">{option}</span>
                            <span className="font-semibold text-gray-900">{count} votes</span>
                          </div>
                        ))}
                        {poll.results?.winners && poll.results.winners.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-blue-300">
                            <span className="text-sm font-semibold text-blue-900">
                              {poll.results.is_tie ? '🏆 Tie: ' : '🏆 Winner: '}
                              {poll.results.winners.join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Export button for board members */}
                    {isBoard && onExportPoll && (
                      <button
                        onClick={() => onExportPoll(poll.id)}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm"
                      >
                        📊 Export Detailed Results
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// Create Poll Modal
function CreatePollModal({ onClose, onRefresh }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    opens_at: new Date().toISOString().slice(0, 16),
    closes_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    options: ['Yes', 'No'], // Default to Yes/No for simplicity
    require_document: false,
    max_documents: 1,
    poll_type: 'vote',
    collect_voter_data: true // New: Anonymous vs data collection
  });
  const [saving, setSaving] = useState(false);
  const [referenceDocument, setReferenceDocument] = useState(null); // New: Poll creator document upload

  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const removeOption = (index) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const updateOption = (index, value) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => i === index ? value : opt)
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const pollData = {
        ...formData,
        opens_at: new Date(formData.opens_at).toISOString(),
        closes_at: new Date(formData.closes_at).toISOString(),
        options: formData.options.filter(opt => opt.trim())
      };

      // Create the poll first
      const pollResponse = await api.post('/api/vote/polls', pollData);
      const pollId = pollResponse.data.poll_id;

      // Upload reference document if provided
      if (referenceDocument) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', referenceDocument);
        
        await api.post(`/api/vote/polls/${pollId}/attach-document`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      alert('Poll created successfully!');
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error creating poll:', error);
      alert('Failed to create poll. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-xl text-gray-900">Create New Poll</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">✕</button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Poll title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              rows="3"
              placeholder="Poll description"
            />
          </div>

          {/* Reference Document Upload */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-blue-900 mb-2">📎 Attach Reference Document (Optional)</label>
            <input
              type="file"
              onChange={(e) => setReferenceDocument(e.target.files[0])}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              className="w-full px-3 py-2 border border-blue-300 rounded-lg bg-white"
            />
            <p className="text-xs text-blue-700 mt-1">
              Upload a document for voters to review (contracts, proposals, images, etc.)
            </p>
          </div>

          {/* Privacy & Data Collection Settings */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-yellow-900">📊 Voting Privacy Settings</h4>
            
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.collect_voter_data}
                onChange={(e) => setFormData(prev => ({...prev, collect_voter_data: e.target.checked}))}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Collect Voter Information</span>
                <p className="text-xs text-gray-600">Board members can see who voted and their choices</p>
              </div>
            </label>

            {!formData.collect_voter_data && (
              <p className="text-xs text-yellow-700 bg-yellow-100 p-2 rounded">
                <strong>Anonymous Mode:</strong> Vote counts will be visible, but individual voter information will not be recorded.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Opens At</label>
              <input
                type="datetime-local"
                value={formData.opens_at}
                onChange={(e) => setFormData(prev => ({...prev, opens_at: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Closes At</label>
              <input
                type="datetime-local"
                value={formData.closes_at}
                onChange={(e) => setFormData(prev => ({...prev, closes_at: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Poll Type</label>
            <select
              value={formData.poll_type}
              onChange={(e) => setFormData(prev => ({...prev, poll_type: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="vote">Voting Poll</option>
              <option value="document_collection">Document Collection</option>
            </select>
          </div>

          {/* Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Options</label>
            {formData.options.map((option, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={option}
                  onChange={(e) => updateOption(index, e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder={`Option ${index + 1}`}
                />
                {formData.options.length > 1 && (
                  <button
                    onClick={() => removeOption(index)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={addOption}
              className="px-3 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors text-sm"
            >
              + Add Option
            </button>
          </div>

          {/* Document Requirements */}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.require_document}
                onChange={(e) => setFormData(prev => ({...prev, require_document: e.target.checked}))}
              />
              <span className="text-sm">Require document submission</span>
            </label>

            {formData.require_document && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Documents</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.max_documents}
                  onChange={(e) => setFormData(prev => ({...prev, max_documents: parseInt(e.target.value)}))}
                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !formData.title}
            className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 transition-colors"
          >
            {saving ? 'Creating...' : 'Create Poll'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Edit Poll Modal
function EditPollModal({ poll, onClose, onRefresh }) {
  const [formData, setFormData] = useState({
    title: poll.title || '',
    description: poll.description || '',
    opens_at: poll.opens_at ? new Date(poll.opens_at).toISOString().slice(0, 16) : '',
    closes_at: poll.closes_at ? new Date(poll.closes_at).toISOString().slice(0, 16) : '',
    options: poll.options || ['Yes', 'No'],
    require_document: poll.require_document || false,
    max_documents: poll.max_documents || 1,
    collect_voter_data: poll.collect_voter_data !== false // Default to true
  });
  const [saving, setSaving] = useState(false);
  const [referenceDocument, setReferenceDocument] = useState(null);

  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const removeOption = (index) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const updateOption = (index, value) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => i === index ? value : opt)
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const pollData = {
        ...formData,
        opens_at: new Date(formData.opens_at),
        closes_at: new Date(formData.closes_at),
        options: formData.options.filter(opt => opt.trim())
      };

      // Update the poll
      await api.put(`/api/vote/polls/${poll.id}`, pollData);

      // Upload reference document if provided
      if (referenceDocument) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', referenceDocument);
        
        await api.post(`/api/vote/polls/${poll.id}/attach-document`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      alert('Poll updated successfully!');
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error updating poll:', error);
      alert('Failed to update poll. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-xl text-gray-900">Edit Poll</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">✕</button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Poll title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              rows="3"
              placeholder="Poll description"
            />
          </div>

          {/* Reference Document Upload */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-blue-900 mb-2">📎 Add Reference Document</label>
            <input
              type="file"
              onChange={(e) => setReferenceDocument(e.target.files[0])}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              className="w-full px-3 py-2 border border-blue-300 rounded-lg bg-white"
            />
            <p className="text-xs text-blue-700 mt-1">
              Upload additional documents for voters to review
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Opens At</label>
              <input
                type="datetime-local"
                value={formData.opens_at}
                onChange={(e) => setFormData(prev => ({...prev, opens_at: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Closes At</label>
              <input
                type="datetime-local"
                value={formData.closes_at}
                onChange={(e) => setFormData(prev => ({...prev, closes_at: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Options</label>
            {formData.options.map((option, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={option}
                  onChange={(e) => updateOption(index, e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder={`Option ${index + 1}`}
                />
                {formData.options.length > 1 && (
                  <button
                    onClick={() => removeOption(index)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={addOption}
              className="px-3 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors text-sm"
            >
              + Add Option
            </button>
          </div>

          {/* Privacy Settings */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.collect_voter_data}
                onChange={(e) => setFormData(prev => ({...prev, collect_voter_data: e.target.checked}))}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Collect Voter Information</span>
                <p className="text-xs text-gray-600">Board members can see who voted</p>
              </div>
            </label>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.require_document}
                onChange={(e) => setFormData(prev => ({...prev, require_document: e.target.checked}))}
              />
              <span className="text-sm">Require voters to submit documents</span>
            </label>

            {formData.require_document && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Documents</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.max_documents}
                  onChange={(e) => setFormData(prev => ({...prev, max_documents: parseInt(e.target.value)}))}
                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !formData.title}
            className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 transition-colors"
          >
            {saving ? 'Updating...' : 'Update Poll'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Poll Analytics Modal
function PollAnalyticsModal({ poll, onClose, isBoard = false, hoaTimezone = 'America/Chicago' }) {
  const [analytics, setAnalytics] = useState(null);
  const [voters, setVoters] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        // Load poll statistics
        const statsResponse = await api.get(`/api/vote/polls/${poll.id}/stats`);
        setAnalytics(statsResponse.data);

        // Load voter information only for board members (if data collection is enabled)
        if (isBoard && poll.collect_voter_data !== false) {
          const votersResponse = await api.get(`/api/vote/polls/${poll.id}/voters`);
          setVoters(votersResponse.data.voters || []);
        }

        // Load submitted documents if poll requires documents (board only)
        if (isBoard && poll.require_document) {
          try {
            const docsResponse = await api.get(`/api/vote/polls/${poll.id}/documents`);
            setDocuments(docsResponse.data.documents || []);
          } catch (error) {
            console.error('Error loading poll documents:', error);
          }
        }
      } catch (error) {
        console.error('Error loading poll analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, [poll.id, poll.collect_voter_data, isBoard, poll.require_document]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-4xl bg-white rounded-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-xl text-gray-900">Poll Analytics - {poll.title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">✕</button>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading analytics...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Vote Statistics */}
            {analytics && (
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-4">📊 Vote Results</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{analytics.total_votes}</div>
                    <div className="text-sm text-gray-600">Total Votes</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{analytics.unique_voters}</div>
                    <div className="text-sm text-gray-600">Unique Voters</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{analytics.document_count || 0}</div>
                    <div className="text-sm text-gray-600">Documents</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {analytics.total_votes > 0 ? Math.round((analytics.unique_voters / analytics.total_votes) * 100) : 0}%
                    </div>
                    <div className="text-sm text-gray-600">Participation</div>
                  </div>
                </div>

                {/* Vote Breakdown */}
                <div className="mt-6">
                  <h5 className="font-medium text-gray-900 mb-3">Vote Breakdown:</h5>
                  <div className="space-y-2">
                    {Object.entries(analytics.vote_stats || {}).map(([option, count]) => (
                      <div key={option} className="flex items-center justify-between p-3 bg-white rounded-lg">
                        <span className="font-medium">{option}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xl font-bold text-indigo-600">{count}</span>
                          <span className="text-sm text-gray-500">
                            ({analytics.total_votes > 0 ? Math.round((count / analytics.total_votes) * 100) : 0}%)
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Voter Information - Only visible to board members */}
            {isBoard && poll.collect_voter_data !== false && voters.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-4">👥 Voter Information (Board View Only)</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {voters.map((voter, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">{voter.user_name}</div>
                        <div className="text-sm text-gray-600">{voter.user_email}</div>
                        {voter.property_address && (
                          <div className="text-xs text-gray-500">{voter.property_address}</div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-indigo-600">{voter.last_vote_option}</div>
                        <div className="text-xs text-gray-500">
                          {voter.voted_at ? formatLocal(voter.voted_at, hoaTimezone, 'MMM d, yyyy h:mm a') : 'Unknown date'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {poll.collect_voter_data === false && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800">
                  <strong>Anonymous Poll:</strong> Individual voter information is not collected for this poll.
                </p>
              </div>
            )}

            {/* Submitted Documents - Only visible to board members */}
            {isBoard && poll.require_document && documents.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-4">📎 Submitted Documents (Board View Only)</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {documents.map((doc, index) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{doc.user_name || 'Unknown User'}</div>
                        <div className="text-sm text-gray-600">{doc.user_email}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {doc.filename} • {(doc.size / 1024).toFixed(2)} KB
                        </div>
                        <div className="text-xs text-gray-400">
                          Uploaded: {doc.uploaded_at ? formatLocal(doc.uploaded_at, hoaTimezone, 'MMM d, yyyy h:mm a') : 'Unknown'}
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            const response = await api.get(`/api/vote/polls/${poll.id}/documents/${doc.id}/download`, {
                              responseType: 'blob'
                            });
                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', doc.filename);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(url);
                          } catch (error) {
                            console.error('Error downloading document:', error);
                            alert('Failed to download document. Please try again.');
                          }
                        }}
                        className="ml-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                      >
                        <span>📥</span>
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isBoard && poll.require_document && documents.length === 0 && !loading && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800">
                  <strong>No Documents:</strong> No documents have been submitted for this poll yet.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Live Homeowners List Component
function LiveHomeownersList({ attendance }) {
  const [homeowners, setHomeowners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadHomeowners = async () => {
      try {
        const response = await api.get('/api/vote/homeowners');
        const homeownerData = response.data?.homeowners || [];
        
        // Debug logging
        console.log('Homeowners loaded:', homeownerData.length);
        console.log('Sample homeowner:', homeownerData[0]);
        console.log('Current attendance data:', attendance);
        
        setHomeowners(homeownerData);
      } catch (error) {
        console.error('Error loading homeowners:', error);
        setHomeowners([]);
      } finally {
        setLoading(false);
      }
    };

    loadHomeowners();
  }, [attendance]); // Re-load when attendance changes

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mx-auto mb-2"></div>
        <p className="text-xs text-gray-500">Loading homeowners...</p>
      </div>
    );
  }

  if (homeowners.length === 0) {
    return null;
  }

  return (
    <div className="mt-4 pt-4 border-t border-gray-200">
      <div className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Homeowners</div>
      <div className="space-y-2">
        {homeowners.map((homeowner, index) => {
          // Use the attendance status directly from the homeowner data
          const attendanceStatus = homeowner.present || homeowner.attendance_status;
          const isPresent = attendanceStatus === true;
          const isAbsent = attendanceStatus === false;
          
          // Debug logging for troubleshooting
          console.log(`Homeowner ${homeowner.name}: Present=${homeowner.present}, Status=${attendanceStatus}, Final Present=${isPresent}, Final Absent=${isAbsent}`);
          
          return (
            <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${
                  isPresent ? 'bg-green-500' : isAbsent ? 'bg-red-500' : 'bg-gray-300'
                }`}></div>
                <span className="text-sm font-medium">{homeowner.name}</span>
                <span className="text-xs text-gray-500 px-2 py-1 bg-gray-100 rounded">
                  {homeowner.property_identifier ? `Lot ${homeowner.property_identifier}` : 
                   homeowner.unit ? `Unit ${homeowner.unit}` : 
                   homeowner.lot ? `Lot ${homeowner.lot}` : 
                   homeowner.property_number ? `Prop ${homeowner.property_number}` : 
                   'HOMEOWNER'}
                </span>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                isPresent ? 'bg-green-100 text-green-800' : 
                isAbsent ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {isPresent ? 'Present' : isAbsent ? 'Absent' : 'Unknown'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Live Voting Section - For Board Members and Committee Members
function LiveVotingSection({ 
  boardMembers, 
  committees = [], 
  onRefresh, 
  meetingTypeFilter = "BOD", 
  tabType = "bod", 
  canInteract = false,
  hoaName = 'HOA',
  hoaSettings = {},
  hoaData = null,
  hoaTimezone = 'America/Chicago',
  discussionPropertyDisplay = 'address',
  onExport,
  meetingRules = [],
  speakingTimeMinutes = 3
}) {
  const { user } = useContext(AuthContext);
  const isBoard = ['board_member', 'board', 'president', 'vice_president', 'treasurer', 'secretary'].includes(user?.role?.toLowerCase());
  // Auto-select committee for the current user (first one they belong to)
  const myCommittees = useMemo(() => {
    try {
      return (committees || []).filter(c => Array.isArray(c.members) && c.members.some(m => (m.user_id === user?.id) || (m.id === user?.id) || (m.email && user?.email && m.email.toLowerCase() === user.email.toLowerCase())));
    } catch (e) {
      return [];
    }
  }, [committees, user?.id, user?.email]);
  const selectedCommittee = useMemo(() => {
    if (tabType === 'committee') {
      if (myCommittees.length > 0) return myCommittees[0];
      return (committees && committees.length > 0) ? committees[0] : null;
    }
    return null;
  }, [tabType, committees, myCommittees]);
  // Defensive: ensure discussionPropertyDisplay always has a string value
  const safeDiscussionPropertyDisplay = (typeof discussionPropertyDisplay === 'string' && discussionPropertyDisplay.length > 0) ? discussionPropertyDisplay : 'address';
  const [activeMotions, setActiveMotions] = useState([]);
  const [showCreateMotion, setShowCreateMotion] = useState(false);
  const [showRollCall, setShowRollCall] = useState(false);
  const [attendance, setAttendance] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add state for meeting minutes preview (LiveVoting scope)
  const [showLiveMinutesPreview, setShowLiveMinutesPreview] = useState(false);
  // PER-TAB state management - separate state for BOD vs Committee  
  const [livePreviewData, setLivePreviewData] = useState(null);
  const [liveMeetingNotes, setLiveMeetingNotes] = useState('');
  const [liveActionItems, setLiveActionItems] = useState([]); // Initialize as array, not string
  const [meetingLocation, setMeetingLocation] = useState(''); // NEW: Meeting location field
  
  // Supporting documents/attachments state
  const [supportingDocuments, setSupportingDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [buildingPacket, setBuildingPacket] = useState(false);
  const [builtPacket, setBuiltPacket] = useState(null); // Stores packet info after build

  // Save/Load state modal
  const [showSaveLoadModal, setShowSaveLoadModal] = useState(false);
  const [savedStates, setSavedStates] = useState([]);
  const [saveStateName, setSaveStateName] = useState('');
  const [savingState, setSavingState] = useState(false);
  const [loadingState, setLoadingState] = useState(false);

  // Meeting session state with persistence - PER TAB
  const [meetingStartTime, setMeetingStartTime] = useState(null);
  const [meetingEndTime, setMeetingEndTime] = useState(null);
  const [meetingInProgress, setMeetingInProgress] = useState(false);
  const [meetingId, setMeetingId] = useState(null);
  
  // PER-TAB state key generator
  const getTabStateKey = (key) => `${key}_${meetingTypeFilter}`;
  
  // Load per-tab state on mount or tab change
  useEffect(() => {
    const savedLiveNotes = localStorage.getItem(getTabStateKey('liveMeetingNotes'));
    const savedActionItems = localStorage.getItem(getTabStateKey('liveActionItems'));
    const savedMeetingState = localStorage.getItem(getTabStateKey('meetingState'));
    const savedProofOfNotice = localStorage.getItem(getTabStateKey('proofOfNotice'));
    const savedManualPriorMinutes = localStorage.getItem(getTabStateKey('manualPriorMinutes'));
    const savedMeetingLocation = localStorage.getItem(getTabStateKey('meetingLocation')); // NEW
    const savedSupportingDocs = localStorage.getItem(getTabStateKey('supportingDocuments')); // NEW: Supporting documents
    
    if (savedLiveNotes) setLiveMeetingNotes(savedLiveNotes);
    if (savedActionItems) setLiveActionItems(JSON.parse(savedActionItems));
    if (savedProofOfNotice) setProofOfNotice(JSON.parse(savedProofOfNotice));
    if (savedManualPriorMinutes) setManualPriorMinutes(savedManualPriorMinutes);
    if (savedMeetingLocation) setMeetingLocation(savedMeetingLocation); // NEW
    if (savedSupportingDocs) setSupportingDocuments(JSON.parse(savedSupportingDocs)); // NEW: Load supporting documents
    
    if (savedMeetingState) {
      const state = JSON.parse(savedMeetingState);
      setMeetingInProgress(state.inProgress);
      setMeetingId(state.meetingId);
      setMeetingStartTime(state.startTime ? new Date(state.startTime) : null);
      setMeetingEndTime(state.endTime ? new Date(state.endTime) : null);
    }
    
    // Load supporting documents from backend for this meeting type
    loadSupportingDocuments();
  }, [meetingTypeFilter]);
  
  // Function to load supporting documents from backend
  const loadSupportingDocuments = async () => {
    try {
      const response = await api.get(`/api/vote/supporting-documents?meeting_type=${encodeURIComponent(meetingTypeFilter)}`);
      if (response.data?.success) {
        const backendDocs = response.data.documents || [];
        
        // Get localStorage docs
        const savedSupportingDocs = localStorage.getItem(getTabStateKey('supportingDocuments'));
        const localDocs = savedSupportingDocs ? JSON.parse(savedSupportingDocs) : [];
        
        // Merge backend docs with local docs (backend takes precedence)
        // Create a map of backend docs by ID for efficient lookup
        const backendDocsMap = new Map(backendDocs.map(doc => [doc.id, doc]));
        
        // Filter out local docs that exist in backend (to avoid duplicates)
        const uniqueLocalDocs = localDocs.filter(localDoc => !backendDocsMap.has(localDoc.id));
        
        // Combine backend docs with unique local docs
        const mergedDocs = [...backendDocs, ...uniqueLocalDocs];
        
        setSupportingDocuments(mergedDocs);
        
        // Update localStorage with merged docs
        localStorage.setItem(getTabStateKey('supportingDocuments'), JSON.stringify(mergedDocs));
      }
    } catch (error) {
      console.error('Error loading supporting documents:', error);
      // If backend fails, still load from localStorage
      const savedSupportingDocs = localStorage.getItem(getTabStateKey('supportingDocuments'));
      if (savedSupportingDocs) {
        setSupportingDocuments(JSON.parse(savedSupportingDocs));
      }
    }
  };
  
  // Save per-tab state when it changes
  useEffect(() => {
    localStorage.setItem(getTabStateKey('liveMeetingNotes'), liveMeetingNotes);
  }, [liveMeetingNotes, meetingTypeFilter]);
  
  useEffect(() => {
    localStorage.setItem(getTabStateKey('liveActionItems'), JSON.stringify(liveActionItems));
  }, [liveActionItems, meetingTypeFilter]);
  
  useEffect(() => {
    localStorage.setItem(getTabStateKey('meetingLocation'), meetingLocation);
  }, [meetingLocation, meetingTypeFilter]);
  
  useEffect(() => {
    localStorage.setItem(getTabStateKey('supportingDocuments'), JSON.stringify(supportingDocuments));
  }, [supportingDocuments, meetingTypeFilter]);
  
  // Prior meeting minutes selection state
  const [priorMinutes, setPriorMinutes] = useState([]);
  const [selectedPriorMinutesId, setSelectedPriorMinutesId] = useState(null);
  const [selectedPriorMinutesStatus, setSelectedPriorMinutesStatus] = useState('pending');
  
  // Proof of Notice state - PER TAB
  const [proofOfNotice, setProofOfNotice] = useState({
    postedOnEvents: true, // Default to checked as per user request
    emailSent: true, // Part of "Posted on events and emailed"
    mailPacket: false,
    mailPacketDate: '',
    other: false,
    otherNotes: ''
  });
  
  // Manual prior meeting minutes text
  const [manualPriorMinutes, setManualPriorMinutes] = useState('');
  
  // Per-tab meeting rules (can be edited independently for BOD vs Committee)
  const [localMeetingRules, setLocalMeetingRules] = useState([]);
  const [localSpeakingTimeMinutes, setLocalSpeakingTimeMinutes] = useState(3);
  const [savedRuleSets, setSavedRuleSets] = useState([]);
  const [showRuleSetsModal, setShowRuleSetsModal] = useState(false);
  const [newRuleSetName, setNewRuleSetName] = useState('');
  const [activeRuleSetName, setActiveRuleSetName] = useState('Custom');
  
  // Initialize local rules from passed props on mount or when they change
  useEffect(() => {
    if (meetingRules && meetingRules.length > 0) {
      const savedRules = localStorage.getItem(getTabStateKey('meetingRules'));
      if (savedRules) {
        setLocalMeetingRules(JSON.parse(savedRules));
      } else {
        setLocalMeetingRules(meetingRules);
      }
    }
  }, [meetingRules, meetingTypeFilter]);
  
  useEffect(() => {
    if (speakingTimeMinutes) {
      const savedTime = localStorage.getItem(getTabStateKey('speakingTimeMinutes'));
      if (savedTime) {
        setLocalSpeakingTimeMinutes(parseInt(savedTime));
      } else {
        setLocalSpeakingTimeMinutes(speakingTimeMinutes);
      }
    }
  }, [speakingTimeMinutes, meetingTypeFilter]);
  
  // Save local rules to localStorage
  useEffect(() => {
    if (localMeetingRules.length > 0) {
      localStorage.setItem(getTabStateKey('meetingRules'), JSON.stringify(localMeetingRules));
    }
  }, [localMeetingRules, meetingTypeFilter]);
  
  useEffect(() => {
    localStorage.setItem(getTabStateKey('speakingTimeMinutes'), localSpeakingTimeMinutes.toString());
  }, [localSpeakingTimeMinutes, meetingTypeFilter]);

  // Load saved rule sets from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('savedRuleSets');
      if (saved) {
        setSavedRuleSets(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading saved rule sets:', error);
    }
  }, []);
  
  // Load saved meeting states from backend
  const loadSavedStates = async () => {
    try {
      const response = await api.get('/api/meeting-states/list');
      setSavedStates(response.data.states || []);
    } catch (error) {
      console.error('Error loading saved states:', error);
      setSavedStates([]);
    }
  };
  
  // Load saved states when modal opens
  useEffect(() => {
    if (showSaveLoadModal) {
      loadSavedStates();
    }
  }, [showSaveLoadModal]);


  // Save proof of notice per-tab state
  useEffect(() => {
    localStorage.setItem(getTabStateKey('proofOfNotice'), JSON.stringify(proofOfNotice));
  }, [proofOfNotice, meetingTypeFilter]);
  
  // Save manual prior minutes per-tab state
  useEffect(() => {
    localStorage.setItem(getTabStateKey('manualPriorMinutes'), manualPriorMinutes);
  }, [manualPriorMinutes, meetingTypeFilter]);

  // Load meeting state from localStorage based on tab type on component mount
  useEffect(() => {
    const savedMeetingState = localStorage.getItem(`meeting_state_${tabType}`);
    if (savedMeetingState) {
      try {
        const state = JSON.parse(savedMeetingState);
        setMeetingInProgress(state.inProgress || false);
        setMeetingId(state.meetingId || null);
        
        // Convert ISO strings back to Date objects
        if (state.startTime) {
          setMeetingStartTime(new Date(state.startTime));
        }
        if (state.endTime) {
          setMeetingEndTime(new Date(state.endTime));
        }
        
        // Restore attendance/roll call if available
        if (state.attendance) {
          setAttendance(state.attendance);
        }
        
        console.log(`Restored meeting state for ${tabType}:`, state);
      } catch (error) {
        console.error('Error loading saved meeting state:', error);
      }
    }
  }, [tabType]);

  // Auto-save attendance when it changes during an active meeting
  useEffect(() => {
    if (meetingInProgress && meetingId) {
      saveMeetingState(
        meetingInProgress, 
        meetingId, 
        meetingStartTime ? meetingStartTime.toISOString() : null, 
        meetingEndTime ? meetingEndTime.toISOString() : null,
        attendance
      );
    }
  }, [attendance, meetingInProgress]);

  // Load saved rule sets for current meeting type
  useEffect(() => {
    loadSavedRuleSets();
  }, [meetingTypeFilter]);

  // Load saved rule sets from localStorage
  const loadSavedRuleSets = () => {
    try {
      const saved = localStorage.getItem('savedRuleSets');
      if (saved) {
        const allRuleSets = JSON.parse(saved);
        setSavedRuleSets(allRuleSets);
        console.log('Loaded saved rule sets from localStorage:', allRuleSets.length);
      } else {
        setSavedRuleSets([]);
      }
    } catch (error) {
      console.error('Error loading rule sets from localStorage:', error);
      setSavedRuleSets([]);
    }
  };

  const saveCurrentRuleSet = async () => {
    if (!newRuleSetName.trim()) {
      alert('Please enter a name for this rule set');
      return;
    }

    try {
      await api.post('/api/vote/rules/save', {
        name: newRuleSetName.trim(),
        meeting_type: meetingTypeFilter,
        rules: localMeetingRules
      });

      alert(`✅ Rule set "${newRuleSetName}" saved successfully!`);
      setActiveRuleSetName(newRuleSetName.trim());
      setNewRuleSetName('');
      setShowRuleSetsModal(false);
      loadSavedRuleSets();
    } catch (error) {
      console.error('Error saving rule set:', error);
      alert('❌ Failed to save rule set: ' + (error.response?.data?.detail || error.message));
    }
  };

  const loadRuleSet = async (ruleSetId, ruleSetName) => {
    try {
      const response = await api.post('/api/vote/rules/load', { rule_set_id: ruleSetId });
      setLocalMeetingRules(response.data.rule_set.rules);
      setActiveRuleSetName(ruleSetName);
      setShowRuleSetsModal(false);
      alert(`✅ Loaded rule set: "${ruleSetName}"`);
    } catch (error) {
      console.error('Error loading rule set:', error);
      alert('❌ Failed to load rule set: ' + (error.response?.data?.detail || error.message));
    }
  };

  const deleteRuleSet = async (ruleSetId, ruleSetName) => {
    if (!window.confirm(`Delete rule set "${ruleSetName}"?`)) return;

    try {
      await api.delete(`/api/vote/rules/${ruleSetId}`);
      alert(`✅ Rule set "${ruleSetName}" deleted`);
      loadSavedRuleSets();
    } catch (error) {
      console.error('Error deleting rule set:', error);
      alert('❌ Failed to delete rule set: ' + (error.response?.data?.detail || error.message));
    }
  };

  
  // Load prior meeting minutes on component mount and when tab type changes
  useEffect(() => {
    loadPriorMeetingMinutes();
  }, [tabType]);
  
  // Save meeting state to localStorage whenever it changes
  const saveMeetingState = (inProgress, id, startTime, endTime, rollCall = null) => {
    const state = {
      inProgress,
      meetingId: id,
      startTime,
      endTime,
      meetingType,
      attendance: rollCall || attendance  // Save current attendance/roll call
    };
    localStorage.setItem(`meeting_state_${tabType}`, JSON.stringify(state));
    console.log(`Saved meeting state for ${tabType}:`, state);
  };
  // Set initial meeting type based on tab type
  const [meetingType, setMeetingType] = useState(() => {
    if (tabType === "bod") {
      return 'BOD';
    } else {
      return 'ARC'; // Default to ARC for committee tab
    }
  });
  const [showMeetingTypeDropdown, setShowMeetingTypeDropdown] = useState(false);

  // Electronic signature state
  const [pendingSignatures, setPendingSignatures] = useState([]);
  const [signatureSettings, setSignatureSettings] = useState({
    BOD: true,
    ARC: true,
    FINES: true,
    ANNUAL: true,
    SPECIAL: true
  });

  // Function to determine who needs to sign based on board hierarchy
  const getSignatureHierarchy = () => {
    const presentBoardMembers = boardMembers.filter(member => {
      const memberId = member.id || member.user_id;
      return attendance[memberId] === true;
    });
    
    if (presentBoardMembers.length === 0) {
      return [];
    }
    
    if (presentBoardMembers.length === 1) {
      return [presentBoardMembers[0]];
    }
    
    // Find secretary and president
    const secretary = presentBoardMembers.find(member => 
      member.role && member.role.toLowerCase() === 'secretary'
    );
    const president = presentBoardMembers.find(member => 
      member.role && member.role.toLowerCase() === 'president'
    );
    
    if (secretary && president) {
      return [secretary, president]; // Secretary signs first, then president
    }
    
    // If no secretary or president, find most senior member
    const roleHierarchy = ['president', 'vice_president', 'secretary', 'treasurer', 'board_member'];
    const seniorMember = roleHierarchy.map(role => 
      presentBoardMembers.find(member => 
        member.role && member.role.toLowerCase() === role
      )
    ).find(member => member);
    
    return seniorMember ? [seniorMember] : [presentBoardMembers[0]];
  };

  // Function to create signature approval request
  const createSignatureApproval = async (minutesData, format) => {
    try {
      const signers = getSignatureHierarchy();
      const approvalData = {
        meeting_type: meetingType,
        meeting_date: new Date().toISOString().split('T')[0],
        minutes_data: minutesData,
        format: format,
        signers: signers.map((signer, index) => ({
          user_id: signer.id || signer.user_id,
          name: signer.name,
          role: signer.role,
          order: index + 1,
          signed: false,
          signed_at: null
        })),
        status: 'pending_signatures',
        created_at: new Date().toISOString()
      };
      
      const response = await api.post('/api/vote/meeting-minutes/approval', approvalData);
      
      if (response.data.success) {
        alert(`Meeting minutes sent for electronic signature approval. ${signers.length} signature(s) required.`);
        // Add to pending signatures list
        setPendingSignatures(prev => [...prev, response.data.approval]);
      } else {
        throw new Error('Failed to create signature approval');
      }
    } catch (error) {
      console.error('Error creating signature approval:', error);
      alert('Failed to create signature approval. Please try again.');
    }
  };

  // Function to determine presiding officer
  const getPresidingOfficer = () => {
    // Find present board members
    const presentBoardMembers = boardMembers.filter(member => {
      const memberId = member.id || member.user_id;
      return attendance[memberId] === true;
    });
    
    if (presentBoardMembers.length === 0) {
      return "No BOD member present";
    }
    
    // Priority order for presiding officer
    const roleHierarchy = ['president', 'vice_president', 'secretary', 'treasurer', 'board_member'];
    
    for (const role of roleHierarchy) {
      const officer = presentBoardMembers.find(member => 
        member.role && member.role.toLowerCase() === role
      );
      if (officer) {
        const roleTitle = role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
        return `${officer.name} (${roleTitle})`;
      }
    }
    
    // If no specific roles, use the first present board member
    return `${presentBoardMembers[0].name} (Board Member)`;
  };

  // Available meeting types based on tab type and committees
  const getMeetingTypes = () => {
    if (tabType === "bod") {
      return [
        { value: 'BOD', label: 'Board of Directors Meeting' },
        { value: 'ANNUAL', label: 'Annual Meeting' },
        { value: 'SPECIAL', label: 'Special Meeting' }
      ];
    } else {
      // Committee tab - include standard committees plus any custom ones
      const types = [
        { value: 'ARC', label: 'Architectural Review Committee' },
        { value: 'FINES', label: 'Grievance Committee Meeting' }
      ];
      
      // Add custom committees from the backend
      committees.forEach(committee => {
        if (!['ARC Committee', 'Grievance Committee'].includes(committee.name)) {
          types.push({
            value: committee.name.toUpperCase().replace(/\s+/g, '_'),
            label: `${committee.name} Meeting`
          });
        }
      });
      
      return types;
    }
  };
  
  const meetingTypes = getMeetingTypes();

  // Rollforward functionality
  const rollForwardItems = async (meetingType) => {
    try {
      // Get archived sessions with unresolved items - pass meeting_type parameter for proper permissions
      const response = await api.get(`/api/vote/archived-search?meeting_type=${meetingType}`);
      
      // Use the new rollover_items structure from backend
      const rolloverItems = response.data?.rollover_items || [];
      const rolloverCount = response.data?.rollover_count || 0;
      
      console.log(`Found ${rolloverCount} rollover items for ${meetingType}:`, rolloverItems);
      
      if (rolloverItems.length === 0) {
        alert(`No unresolved items found for ${meetingTypes.find(t => t.value === meetingType)?.label || meetingType} meetings.`);
        return;
      }
      
      // Show selection modal
      const selectedIds = await showRollForwardModal(rolloverItems);
      
      if (selectedIds && selectedIds.length > 0) {
        // Call API to roll forward selected items
        const rollForwardResponse = await api.post('/api/vote/rollover-unresolved', {
          meeting_type: meetingType,
          committee_id: (tabType === 'committee' ? (selectedCommittee?.id || selectedCommittee?.committee_id) : undefined),
          selected_items: selectedIds
        });
        
        if (rollForwardResponse.data?.success) {
          alert(`Successfully rolled forward ${rollForwardResponse.data.items_count} items to current ${meetingTypes.find(t => t.value === meetingType)?.label || meetingType} meeting.`);
          loadLiveVotingData(); // Refresh data
        }
      }
      
    } catch (error) {
      console.error('Error rolling forward items:', error);
      alert('Failed to roll forward items. Please try again.');
    }
  };

  const showRollForwardModal = (unresolvedItems) => {
    return new Promise((resolve) => {
      const modal = document.createElement('div');
      modal.className = 'fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4';
      
      const selectedIds = new Set();
      
      modal.innerHTML = `
        <div class="w-full max-w-2xl bg-white rounded-2xl max-h-[80vh] overflow-hidden">
          <div class="sticky top-0 bg-white border-b p-6">
            <h3 class="text-xl font-semibold text-gray-900">Roll Forward Unresolved Items</h3>
            <p class="text-sm text-gray-600 mt-1">Select items to bring into the current meeting</p>
          </div>
          
          <div class="overflow-y-auto max-h-96 p-6">
            <div class="space-y-3">
              ${unresolvedItems.map((item, index) => `
                <div class="border rounded-lg p-4 hover:bg-gray-50">
                  <label class="flex items-start space-x-3 cursor-pointer">
                    <input 
                      type="checkbox" 
                      class="mt-1 roll-forward-checkbox" 
                      data-id="${item.id}"
                    />
                    <div class="flex-1">
                      <div class="flex items-center gap-2">
                        <span class="text-sm font-medium ${item.type === 'motion' ? 'text-purple-600' : 'text-blue-600'}">
                          ${item.type === 'motion' ? '🗳️ Motion' : '💬 Discussion'}
                        </span>
                        <span class="px-2 py-1 text-xs rounded-full ${
                          item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                          item.status === 'tabled' ? 'bg-orange-100 text-orange-800' :
                          'bg-blue-100 text-blue-800'
                        }">
                          ${item.status}
                        </span>
                      </div>
                      <h4 class="font-medium text-gray-900 mt-1">${item.title}</h4>
                      ${item.description ? `<p class="text-sm text-gray-600 mt-1">${item.description}</p>` : ''}
                      <p class="text-xs text-gray-500 mt-2">Archived: ${formatLocal(item.archiveDate, hoaTimezone, 'MMM d, yyyy')}</p>
                    </div>
                  </label>
                </div>
              `).join('')}
            </div>
          </div>
          
          <div class="sticky bottom-0 bg-white border-t p-6 flex justify-end gap-3">
            <button id="roll-forward-cancel" class="px-4 py-2 text-gray-600 hover:text-gray-800">
              Cancel
            </button>
            <button id="roll-forward-confirm" class="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700">
              Roll Forward Selected
            </button>
          </div>
        </div>
      `;
      
      document.body.appendChild(modal);
      
      // Handle checkbox changes
      modal.addEventListener('change', (e) => {
        if (e.target.classList.contains('roll-forward-checkbox')) {
          const itemId = e.target.dataset.id;
          if (e.target.checked) {
            selectedIds.add(itemId);
          } else {
            selectedIds.delete(itemId);
          }
        }
      });
      
      // Handle cancel
      modal.querySelector('#roll-forward-cancel').addEventListener('click', () => {
        document.body.removeChild(modal);
        resolve(null);
      });
      
      // Handle confirm
      modal.querySelector('#roll-forward-confirm').addEventListener('click', () => {
        document.body.removeChild(modal);
        resolve(Array.from(selectedIds));
      });
      
      // Handle backdrop click
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          document.body.removeChild(modal);
          resolve(null);
        }
      });
    });
  };

  // Add state for forcing component refreshes
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const loadLiveVotingData = async () => {
    setLoading(true);
    try {
      // Load active motions - FILTER BY MEETING TYPE
      const motionsResponse = await api.get(`/api/vote/live-motions?meeting_type=${meetingTypeFilter}`);
      setActiveMotions(motionsResponse.data?.motions || []);

      // Load team members for roll call integration (board members only)
      try {
        const teamMembersResponse = await api.get('/api/team-members');
        setTeamMembers(teamMembersResponse.data?.team_members || []);
        console.log('Loaded team members for roll call:', teamMembersResponse.data?.team_members || []);
      } catch (error) {
        console.log('Not authorized to load team members or error:', error);
        setTeamMembers([]);
      }

      // Load attendance data - FILTER BY MEETING TYPE
      const attendanceResponse = await api.get(`/api/vote/attendance/all-records?meeting_type=${meetingTypeFilter}`);
      const attendanceRecords = attendanceResponse.data?.attendance_records || [];
      
      // Debug logging to understand the data format
      console.log('Attendance records received:', attendanceRecords);
      console.log('Board members for attendance check:', boardMembers.map(m => ({ 
        id: m.id, 
        user_id: m.user_id,
        name: m.name,
        first_name: m.first_name,
        last_name: m.last_name,
        email: m.email 
      })));
      
      // Convert attendance records to the format expected by the UI
      const formattedAttendance = {};
      attendanceRecords.forEach(record => {
        const userId = record.user_id;
        if (userId) {
          formattedAttendance[userId] = record.present;
        }
      });
      
      console.log('Formatted attendance for UI:', formattedAttendance);
      setAttendance(formattedAttendance);
    } catch (error) {
      console.error('Error loading live voting data:', error);
      setActiveMotions([]);
      setAttendance({});
    } finally {
      setLoading(false);
    }
  };

  // Load prior meeting minutes
  const loadPriorMeetingMinutes = async () => {
    try {
      // Use the actual meetingType state (ARC, FINES, BOD, etc.)
      const typeToQuery = (tabType === 'committee' ? meetingType : 'BOD');
      const response = await api.get(`/api/vote/prior-meeting-minutes?meeting_type=${typeToQuery}`);
      const priorMinutesData = response.data?.prior_minutes || [];
      setPriorMinutes(priorMinutesData);
      console.log(`Loaded ${priorMinutesData.length} prior meeting minutes for ${typeToQuery}:`, priorMinutesData);
    } catch (error) {
      console.error('Error loading prior meeting minutes:', error);
      setPriorMinutes([]);
    }
  };

  // Add prior minutes section to preview
  const addPriorMinutesSection = async () => {
    if (!selectedPriorMinutesId) {
      alert('Please select prior minutes first.');
      return;
    }
    
    try {
      const selectedMinutes = priorMinutes.find(m => m.id === selectedPriorMinutesId);
      if (!selectedMinutes) {
        alert('Selected minutes not found.');
        return;
      }
      
      // Show confirmation that prior minutes will be included in preview
      alert(`✅ Prior Minutes Selected!\n\nThe following minutes will appear in Section 4 (PRIOR BUSINESS) when you click "Preview Meeting Minutes":\n\n${selectedMinutes.title}\nDate: ${selectedMinutes.date ? formatDateInHOATimezone(selectedMinutes.date, hoaTimezone, 'MMM d, yyyy') : 'Unknown'}\n\nClick "Preview Meeting Minutes" to see the complete document.`);
      
    } catch (error) {
      console.error('Error adding prior minutes section:', error);
    }
  };

  // Helper function to generate file URL for supporting documents
  const getDocumentFileUrl = (docId) => {
    const token = localStorage.getItem('hoa_token');
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin.replace(/:\d+$/, ':8001');
    return `${backendUrl}/api/vote/supporting-document-file/${docId}?token=${encodeURIComponent(token)}`;
  };

  // Function to handle supporting document uploads
  const handleDocumentUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }
    
    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'image/png', 'image/jpeg', 'image/jpg',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload PDF, DOC, DOCX, XLS, XLSX, or image files only');
      return;
    }
    
    setUploadingDoc(true);
    
    try {
      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      
      // Upload to backend (meeting_type will be sent as query param)
      const response = await api.post(`/api/vote/upload-supporting-document?meeting_type=${encodeURIComponent(meetingTypeFilter)}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data?.success) {
        const newDoc = {
          id: response.data.document_id,
          file_name: response.data.file_name || file.name,
          file_size: response.data.file_size || file.size,
          mime_type: response.data.mime_type || file.type,
          file_url: response.data.file_url,
          uploaded_at: response.data.uploaded_at || new Date().toISOString()
        };
        
        setSupportingDocuments(prev => [...prev, newDoc]);
        alert('Document uploaded successfully!');
        
        // Reload documents from backend to ensure consistency
        loadSupportingDocuments();
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document. Please try again.');
    } finally {
      setUploadingDoc(false);
      // Reset file input
      event.target.value = '';
    }
  };
  
  // Function to remove a supporting document
  const removeSupportingDocument = async (docId) => {
    if (!window.confirm('Remove this supporting document?')) return;
    
    try {
      await api.delete(`/api/vote/supporting-document/${docId}`);
      setSupportingDocuments(prev => prev.filter(doc => doc.id !== docId));
      
      // Reload documents from backend to ensure consistency
      loadSupportingDocuments();
    } catch (error) {
      console.error('Error removing document:', error);
      alert('Failed to remove document. Please try again.');
    }
  };

  // Build Meeting Packet function
  const buildMeetingPacket = async () => {
    try {
      setBuildingPacket(true);
      
      // Prepare attachments data (only included ones, in order)
      const includedAttachments = supportingDocuments
        .filter(doc => doc.includeInPacket !== false)
        .map((doc, index) => ({
          id: doc.id,
          file_name: doc.file_name,
          file_path: doc.file_path,
          include: true,
          order: index
        }));
      
      if (includedAttachments.length === 0) {
        alert('⚠️ No attachments selected. Please select at least one document to combine.');
        setBuildingPacket(false);
        return;
      }
      
      // Call backend API to build packet (just merge documents, no minutes)
      const response = await api.post('/api/packets/build-meeting-packet', {
        meeting_type: meetingType,
        minutes_content: null,  // No minutes, just documents
        attachments: includedAttachments,
        hoa_id: user?.hoa_id || 'default',
        documents_only: true  // Flag to indicate we only want documents merged
      });
      
      if (response.data.success) {
        setBuiltPacket(response.data);
        
        // Show success message with stats
        const stats = response.data.stats;
        alert(
          `📎 Documents Combined Successfully!\n\n` +
          `📄 Total Pages: ${stats.total_pages}\n` +
          `📎 Documents Merged: ${stats.converted_files}\n` +
          `${stats.failed_files > 0 ? `⚠️ Failed: ${stats.failed_files}\n` : ''}` +
          `\nClick the "Download Packet" button below to get your combined PDF.`
        );
      } else {
        throw new Error(response.data.error || 'Unknown error');
      }
      
    } catch (error) {
      console.error('Error combining documents:', error);
      alert(`Failed to combine documents: ${error.response?.data?.detail || error.message}`);
    } finally {
      setBuildingPacket(false);
    }
  };

  // Archive Live Voting Session Function
  const archiveLiveVotingSession = async (attendance, boardMembers, motions, discussions) => {
    try {
      const now = new Date().toISOString();
      const currentDate = formatLocal(now, hoaTimezone, 'MMM d, yyyy');
      const currentTime = formatLocal(now, hoaTimezone, 'h:mm a z');
      
      // Create archive data
      const archiveData = {
        session_name: `Live Voting Minutes_${hoaName}_${new Date().toISOString().split('T')[0]}`,
        archived_at: new Date().toISOString(),
        attendance: attendance,
        board_members: boardMembers,
        motions: motions,
        discussions: discussions,
        meeting_date: meetingStartTime ? meetingStartTime.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        hoa_name: hoaName
      };

      // Save archive to backend
      const response = await api.post('/api/vote/archive-session', archiveData);
      
      if (response.data.success) {
        const archiveName = `Meeting-${new Date().toISOString().split('T')[0]}-${response.data.archive_id.slice(0, 8)}`;
        alert(`Meeting session archived successfully as: ${archiveName}`);
        
        // Clear current session data for this meeting type
        await api.post('/api/vote/clear-session', { meeting_type: meetingType });
        
        // Refresh the live voting data to show cleared state
        await loadLiveVotingData();
        
        return archiveName;
      } else {
        throw new Error('Failed to archive session');
      }
    } catch (error) {
      console.error('Error archiving session:', error);
      alert('Failed to archive session. Please try again.');
      return null;
    }
  };

  // Master Meeting Minutes Export Function (Fixed formatting)
  const exportMasterMeetingMinutes = (attendance, boardMembers, motions, discussions, notes = '', actionItems = '') => {
    const now = new Date().toISOString();
    const currentDate = formatLocal(now, hoaTimezone, 'MMM d, yyyy');
    const currentTime = formatLocal(now, hoaTimezone, 'h:mm a z');
    const meetingDate = formatLocal(now, hoaTimezone, 'EEEE, MMMM d, yyyy');
    
    // Filter present and absent members
    const presentMembers = boardMembers.filter(m => attendance[m.id] === true);
    const absentMembers = boardMembers.filter(m => attendance[m.id] === false);
    const unknownMembers = boardMembers.filter(m => attendance[m.id] !== true && attendance[m.id] !== false);
    
    // Create clean HTML content
    const minutesHTML = `<!DOCTYPE html>
<html>
<head>
  <title>HOA Board Meeting Minutes - ${currentDate}</title>
  <meta charset="UTF-8">
  <style>
    body { 
      font-family: Georgia, 'Times New Roman', serif; 
      max-width: 8.5in; 
      margin: 0 auto; 
      padding: 1in;
      background: white;
      line-height: 1.6;
      color: #333;
    }
    .header { 
      text-align: center; 
      border-bottom: 3px solid #2563eb; 
      padding-bottom: 30px; 
      margin-bottom: 40px; 
    }
    .hoa-name { 
      font-size: 28px; 
      font-weight: bold; 
      color: #1e40af;
      margin-bottom: 5px;
    }
    .meeting-title { 
      font-size: 20px; 
      color: #374151;
      margin-bottom: 10px;
    }
    .meeting-date { 
      font-size: 16px; 
      color: #6b7280;
      font-style: italic;
    }
    .section { 
      margin-bottom: 35px; 
    }
    .section-title { 
      font-size: 18px; 
      font-weight: bold; 
      color: #1e40af;
      border-bottom: 2px solid #e5e7eb;
      padding-bottom: 8px;
      margin-bottom: 20px;
    }
    .attendance-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
      margin-bottom: 20px;
    }
    .attendance-list {
      background: #f9fafb;
      padding: 20px;
      border-radius: 8px;
      border-left: 4px solid #10b981;
      min-height: 60px;
    }
    .absent-list {
      background: #fef2f2;
      padding: 20px;
      border-radius: 8px;
      border-left: 4px solid #ef4444;
      min-height: 60px;
    }
    .member-item {
      padding: 8px 0;
      border-bottom: 1px solid #e5e7eb;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .member-item:last-child {
      border-bottom: none;
    }
    .member-name {
      font-weight: 500;
    }
    .member-role {
      font-size: 12px;
      color: #6b7280;
      text-transform: capitalize;
    }
    .member-property {
      font-size: 11px;
      color: #9ca3af;
    }
    .quorum-status {
      background: #dbeafe;
      border: 1px solid #93c5fd;
      border-radius: 8px;
      padding: 15px;
      text-align: center;
      margin-top: 20px;
    }
    .quorum-achieved { background: #dcfce7; border-color: #86efac; }
    .motion-item {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 20px;
    }
    .motion-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;
    }
    .motion-title {
      font-size: 16px;
      font-weight: 600;
      color: #1e293b;
    }
    .vote-results {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 15px;
      margin-top: 15px;
    }
    .vote-count {
      text-align: center;
      padding: 10px;
      border-radius: 6px;
      background: white;
      border: 1px solid #e5e7eb;
    }
    .vote-number {
      font-size: 20px;
      font-weight: bold;
      color: #1e40af;
    }
    .vote-label {
      font-size: 12px;
      color: #6b7280;
      text-transform: uppercase;
    }
    .discussion-item {
      background: #fffbeb;
      border: 1px solid #fed7aa;
      border-radius: 8px;
      padding: 15px;
      margin-bottom: 15px;
    }
    .notes-section, .action-items-section {
      background: #f9fafb;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 20px;
    }
    .notes-content {
      white-space: pre-wrap;
      line-height: 1.8;
      min-height: 80px;
    }
    .empty-notice {
      color: #6b7280;
      font-style: italic;
      text-align: center;
      padding: 20px;
    }
    .footer {
      margin-top: 50px;
      padding-top: 30px;
      border-top: 2px solid #e5e7eb;
    }
    .signature-section {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 40px;
      margin-top: 40px;
    }
    .signature-line {
      border-bottom: 1px solid #374151;
      height: 40px;
      margin-bottom: 10px;
    }
    .signature-label {
      font-size: 12px;
      color: #6b7280;
      text-align: center;
    }
    @media print {
      body { margin: 0; padding: 0.5in; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="hoa-name">${hoaName}</div>
    <div class="meeting-title">Board of Directors Meeting Minutes</div>
    <div class="meeting-date">${meetingDate}</div>
  </div>

  <div class="section">
    <div class="section-title">Roll Call & Attendance</div>
    <div class="attendance-grid">
      <div class="attendance-list">
        <h4 style="margin: 0 0 15px 0; color: #166534;">Present Members</h4>
        ${presentMembers.length > 0 ? presentMembers.map(member => `
          <div class="member-item">
            <div>
              <div class="member-name">${member.name || 'Unknown Member'}</div>
              <div class="member-role">${(member.role || 'board member').replace('_', ' ')}</div>
              ${member.property_address ? `<div class="member-property">${member.property_address}</div>` : ''}
            </div>
          </div>
        `).join('') : '<div class="empty-notice">No members marked present</div>'}
      </div>
      <div class="absent-list">
        <h4 style="margin: 0 0 15px 0; color: #dc2626;">Absent Members</h4>
        ${absentMembers.length > 0 ? absentMembers.map(member => `
          <div class="member-item">
            <div>
              <div class="member-name">${member.name || 'Unknown Member'}</div>
              <div class="member-role">${(member.role || 'board member').replace('_', ' ')}</div>
              ${member.property_address ? `<div class="member-property">${member.property_address}</div>` : ''}
            </div>
          </div>
        `).join('') : '<div class="empty-notice">No members marked absent</div>'}
      </div>
    </div>
    
    <div class="quorum-status ${presentMembers.length >= Math.ceil(boardMembers.length / 2) ? 'quorum-achieved' : ''}">
      <strong>Quorum Status:</strong> 
      ${presentMembers.length} of ${boardMembers.length} members present
      ${presentMembers.length >= Math.ceil(boardMembers.length / 2) ? ' - QUORUM ACHIEVED' : ' - QUORUM NOT ACHIEVED'}
    </div>
  </div>

  ${motions && motions.length > 0 ? `
  <div class="section">
    <div class="section-title">Motions & Voting Results</div>
    ${motions.map((motion, index) => `
      <div class="motion-item">
        <div class="motion-header">
          <div class="motion-title">Motion ${index + 1}: ${motion.title || 'Untitled Motion'}</div>
        </div>
        
        ${motion.description ? `<p style="color: #6b7280; margin-bottom: 15px;">${motion.description}</p>` : ''}
        ${motion.discussion ? `<p style="color: #4b5563; margin-bottom: 15px;"><strong>Discussion:</strong> ${motion.discussion}</p>` : ''}
        ${motion.voting_rule === 'propose_second' && (motion.proposer_name || motion.seconder_name) ? `
          <p style="color: #6b21a8; margin-bottom: 10px;"><strong>Proposed by:</strong> ${motion.proposer_name || 'Not set'}</p>
          <p style="color: #6b21a8; margin-bottom: 15px;"><strong>Seconded by:</strong> ${motion.seconder_name || 'Not set'}</p>
        ` : ''}
        
        <div class="vote-results">
          ${(() => {
            const answerOpts = motion.answer_options || motion.answers || ['Yes', 'No'];
            const counts = motion.total_counts || motion.votes || {};
            return answerOpts.map(opt => `
              <div class="vote-count">
                <div class="vote-number">${counts[opt] || 0}</div>
                <div class="vote-label">${opt}</div>
              </div>
            `).join('');
          })()}
        </div>
        
        ${motion.individual_votes ? (() => {
          const answerOpts = motion.answer_options || motion.answers || ['Yes', 'No'];
          const indVotes = motion.individual_votes || {};
          return `
          <div style="margin-top: 15px; padding: 10px; background-color: #f9fafb; border-radius: 4px; font-size: 0.85em;">
            ${answerOpts.map(opt => {
              const voters = indVotes[opt] || [];
              return voters.length > 0 ? `<p style="margin: 5px 0;"><strong>${opt}:</strong> ${voters.map(v => v.name).join(', ')}</p>` : '';
            }).join('')}
          </div>
        `;})() : ''}
        
        <div style="margin-top: 15px; padding: 10px; background: white; border-radius: 6px;">
          ${(() => {
            const answerOpts = motion.answer_options || motion.answers || ['Yes', 'No'];
            const counts = motion.total_counts || motion.votes || {};
            const firstCount = counts[answerOpts[0]] || 0;
            const secondCount = counts[answerOpts[1]] || 0;
            return `<strong>Result:</strong> ${firstCount > secondCount ? 'PASSED' : 'FAILED'}`;
          })()}
        </div>
      </div>
    `).join('')}
  </div>
  ` : '<div class="section"><div class="section-title">Motions & Voting Results</div><div class="empty-notice">No motions were presented</div></div>'}

  ${discussions && discussions.length > 0 ? `
  <div class="section">
    <div class="section-title">Other Discussions</div>
    ${discussions.map((discussion, index) => `
      <div class="discussion-item">
        <div style="font-weight: 600; color: #92400e; margin-bottom: 5px;">${discussion.topic || 'Discussion Topic'}</div>
        <div style="font-size: 14px; color: #6b7280; margin-bottom: 10px;">
          Submitted by: ${discussion.member_name || 'Unknown Member'}${discussion.property_address ? ` (${discussion.property_address})` : ''}
        </div>
        ${discussion.ccr_rule || discussion.bylaw_rule ? `<div style="font-size: 14px; color: #6b7280; margin-bottom: 10px;"><strong>Rule cited:</strong> ${[discussion.ccr_rule, discussion.bylaw_rule].filter(Boolean).join(' • ')}</div>` : ''}
        ${discussion.notes ? `<div style="padding: 10px; background: white; border-radius: 6px;"><strong>Notes:</strong> ${discussion.notes}</div>` : ''}
      </div>
    `).join('')}
  </div>
  ` : '<div class="section"><div class="section-title">Other Discussions</div><div class="empty-notice">No additional discussions</div></div>'}

  <div class="section">
    <div class="section-title">Meeting Notes</div>
    <div class="notes-section">
      ${notes ? `<div class="notes-content">${notes}</div>` : '<div class="empty-notice">No meeting notes recorded</div>'}
    </div>
  </div>

  <div class="section">
    <div class="section-title">Action Items</div>
    <div class="action-items-section">
      ${actionItems ? `<div class="notes-content">${actionItems}</div>` : '<div class="empty-notice">No action items recorded</div>'}
    </div>
  </div>

  ${supportingDocuments && supportingDocuments.length > 0 ? `
  <div class="section">
    <div class="section-title">📎 Supporting Documents</div>
    <div class="supporting-docs-section">
      <p style="margin-bottom: 15px; font-style: italic; color: #666;">The following documents are attached as supporting materials for this meeting:</p>
      ${supportingDocuments.map((doc, index) => {
        const isImage = doc.mime_type?.startsWith('image/');
        const isPDF = doc.mime_type === 'application/pdf';
        const fileUrl = getDocumentFileUrl(doc.id);
        
        return `
          <div style="margin-bottom: 30px; page-break-inside: avoid;">
            <div style="padding: 10px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; margin-bottom: 10px;">
              <div style="display: flex; align-items: center;">
                <span style="font-size: 24px; margin-right: 12px;">📄</span>
                <div style="flex: 1;">
                  <strong>${index + 1}. ${doc.file_name}</strong>
                  <br>
                  <span style="font-size: 12px; color: #6b7280;">
                    Size: ${doc.file_size ? (doc.file_size / 1024).toFixed(1) + " KB" : "Unknown"} | 
                    Uploaded: ${formatDateInHOATimezone(doc.uploaded_at, hoaTimezone, 'MMM d, yyyy h:mm a')}
                  </span>
                </div>
              </div>
            </div>
            ${isImage ? `
              <div style="text-align: center; padding: 10px; background: #fff; border: 1px solid #ddd;">
                <img src="${fileUrl}" style="max-width: 100%; height: auto; max-height: 800px;" alt="${doc.file_name}" onerror="this.parentElement.innerHTML='<p style=color:red;>Image failed to load. URL: ${fileUrl}</p>'" />
              </div>
            ` : isPDF ? `
              <div style="border: 2px solid #ddd; padding: 10px; background: #fff; min-height: 600px;">
                <iframe src="${fileUrl}" width="100%" height="800px" style="border: none;"></iframe>
                <p style="text-align: center; margin-top: 10px; font-size: 12px; color: #666;">
                  PDF Document: ${doc.file_name}
                </p>
              </div>
            ` : `
              <div style="padding: 20px; background: #fff; border: 1px solid #ddd; text-align: center; color: #666;">
                <p>📄 Document content not displayable in preview</p>
                <p style="font-size: 12px; margin-top: 10px;">This document (${doc.file_name}) is available for download</p>
                <a href="${fileUrl}" download="${doc.file_name}" style="display: inline-block; margin-top: 15px; padding: 10px 20px; background: #6366f1; color: white; text-decoration: none; border-radius: 6px; font-size: 14px;">
                  📥 Download ${doc.file_name}
                </a>
              </div>
            `}
          </div>
        `;
      }).join('')}
      <p style="margin-top: 15px; font-size: 12px; color: #666; font-style: italic;">
        Total: ${supportingDocuments.length} supporting document${supportingDocuments.length !== 1 ? 's' : ''}
      </p>
    </div>
  </div>
  ` : ''}

  <div class="footer">
    <p style="color: #6b7280; margin-bottom: 40px; text-align: center;">
      These minutes were generated on ${currentDate} at ${currentTime}
    </p>
    
    <div class="signature-section">
      <div>
        <div class="signature-line"></div>
        <div class="signature-label">Board Secretary Signature & Date</div>
      </div>
      <div>
        <div class="signature-line"></div>
        <div class="signature-label">Board President Signature & Date</div>
      </div>
    </div>
  </div>

  <div class="no-print" style="text-align: center; margin-top: 30px;">
    <button onclick="window.print()" style="padding: 12px 24px; font-size: 16px; background: #2563eb; color: white; border: none; border-radius: 8px; cursor: pointer; margin-right: 10px;">Print Minutes</button>
    <button onclick="window.close()" style="padding: 12px 24px; font-size: 16px; background: #6b7280; color: white; border: none; border-radius: 8px; cursor: pointer;">Close</button>
  </div>
</body>
</html>`;
    
    // Try to open new window for printing, with fallback to download
    try {
      const printWindow = window.open('', '_blank');
      
      if (printWindow && printWindow.document) {
        // Window opened successfully
        printWindow.document.write(minutesHTML);
        printWindow.document.close();
        console.log('Meeting minutes opened in new window');
      } else {
        // Fallback to download as HTML file
        console.log('Popup blocked, downloading as file instead');
        downloadHTMLFile(minutesHTML, `meeting-minutes-${new Date().toISOString().split('T')[0]}.html`);
      }
    } catch (error) {
      console.error('Error opening print window:', error);
      // Fallback to download
      downloadHTMLFile(minutesHTML, `meeting-minutes-${new Date().toISOString().split('T')[0]}.html`);
    }
  };

  // Helper function to download HTML file
  const downloadHTMLFile = (htmlContent, filename) => {
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    alert('Meeting minutes downloaded as HTML file. Open it in your browser to print.');
  };

  const markAttendance = async (isPresent) => {
    try {
      await api.post('/api/vote/attendance/mark', { present: isPresent });
      await loadLiveVotingData();
      alert(isPresent ? 'Marked as present' : 'Marked as absent');
    } catch (error) {
      console.error('Error marking attendance:', error);
      alert('Failed to mark attendance');
    }
  };

  // Add state for meeting minutes preview
  const [showMinutesPreview, setShowMinutesPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [meetingNotes, setMeetingNotes] = useState('');
  const [actionItems, setActionItems] = useState('');
  const [editingStartTime, setEditingStartTime] = useState(false);
  const [editingEndTime, setEditingEndTime] = useState(false);
  const [tempStartTime, setTempStartTime] = useState('');
  const [tempEndTime, setTempEndTime] = useState('');

  // Function to prepare and show meeting minutes preview
  const showMeetingMinutesPreview = async (attendance, boardMembers, motions) => {
    try {
      // Get discussions from the API with meeting_type filter to prevent cross-contamination
      // FIX: Add meeting_type parameter to prevent BOD discussions showing in Committee and vice versa
      const response = await api.get(`/api/vote/discussions?meeting_type=${meetingTypeFilter}`);
      const discussions = response.data?.discussions || [];
      
      // Get homeowners attendance data
      let homeowners = [];
      try {
        const homeownerResponse = await api.get('/api/vote/homeowners');
        homeowners = homeownerResponse.data?.homeowners || [];
        
        // Also get external attendees and team members from attendance records
        const attendanceResponse = await api.get(`/api/vote/attendance/all-records?meeting_type=${meetingTypeFilter}`);
        const attendanceRecords = attendanceResponse.data?.attendance_records || [];
        
        // Add external attendees to homeowners array for minutes
        const externalAttendees = attendanceRecords.filter(r => r.type === 'external').map(record => ({
          id: record.user_id,
          name: record.name,
          email: record.email || '',
          property_address: record.property || '',
          type: 'external',
          externalRole: record.externalRole,
          present: record.present,
          attendance_status: record.present
        }));

        // Add team members to homeowners array for minutes
        const teamMemberAttendees = attendanceRecords.filter(r => r.type === 'team_member').map(record => ({
          id: record.user_id,
          name: record.name,
          email: record.email || '',
          property_address: record.property || '',
          type: 'team_member',
          role: record.role,
          present: record.present,
          attendance_status: record.present
        }));
        
        homeowners = [...homeowners, ...externalAttendees, ...teamMemberAttendees];
        console.log('Homeowners + External attendees + Team members for preview:', homeowners.length, 'total');
      } catch (error) {
        console.error('Error loading homeowners for preview:', error);
      }
      
      // Include prior minutes approval if selected
      let priorMinutesApproval = null;
      if (selectedPriorMinutesId) {
        const selectedMinutes = priorMinutes.find(m => m.id === selectedPriorMinutesId);
        if (selectedMinutes) {
          priorMinutesApproval = {
            title: selectedMinutes.title,
            date: selectedMinutes.date || selectedMinutes.created_at,
            status: selectedPriorMinutesStatus, // Use the status from dropdown
            votes: null // Will be updated when voted
          };
        }
      }

      // CRITICAL FIX: Add presence status to boardMembers from attendance object
      const boardMembersWithPresence = boardMembers.map(bm => ({
        ...bm,
        present: attendance[bm.id] === true,
        attendance_status: attendance[bm.id] === true
      }));
      
      setLivePreviewData({
        attendance,
        boardMembers: boardMembersWithPresence,
        motions,
        discussions,
        homeowners,
        meetingDate: formatLocal(new Date().toISOString(), hoaTimezone, 'MMM d, yyyy'),
        meetingType: meetingType,
        meetingTypeLabel: meetingTypes.find(t => t.value === meetingType)?.label || 'Board Meeting',
        startTime: meetingStartTime ? formatLocal(meetingStartTime.toISOString(), hoaTimezone, 'h:mm a') : null,
        endTime: meetingEndTime ? formatLocal(meetingEndTime.toISOString(), hoaTimezone, 'h:mm a') : null,
        prior_minutes_approval: priorMinutesApproval,
        // NEW: Add proof of notice, rules, manual prior minutes, meeting location
        proofOfNotice: proofOfNotice,
        rulesOfConduct: localMeetingRules,
        speakingTimeMinutes: localSpeakingTimeMinutes,
        manualPriorMinutes: manualPriorMinutes,
        meetingLocation: meetingLocation // NEW: Meeting location
      });
      setShowLiveMinutesPreview(true);
    } catch (error) {
      console.error('Error loading discussions for preview:', error);
      // Include prior minutes approval if selected (error case)
      let priorMinutesApproval = null;
      if (selectedPriorMinutesId) {
        const selectedMinutes = priorMinutes.find(m => m.id === selectedPriorMinutesId);
        if (selectedMinutes) {
          priorMinutesApproval = {
            title: selectedMinutes.title,
            date: selectedMinutes.date || selectedMinutes.created_at,
            status: selectedPriorMinutesStatus, // Use the status from dropdown
            votes: null
          };
        }
      }

      setLivePreviewData({
        attendance,
        boardMembers,
        motions,
        discussions: [],
        homeowners: [],
        meetingDate: formatLocal(new Date().toISOString(), hoaTimezone, 'MMM d, yyyy'),
        meetingType: meetingType,
        meetingTypeLabel: meetingTypes.find(t => t.value === meetingType)?.label || 'Board Meeting',
        startTime: meetingStartTime ? formatLocal(meetingStartTime.toISOString(), hoaTimezone, 'h:mm a') : null,
        endTime: meetingEndTime ? formatLocal(meetingEndTime.toISOString(), hoaTimezone, 'h:mm a') : null,
        prior_minutes_approval: priorMinutesApproval,
        // NEW: Add proof of notice, rules, manual prior minutes, meeting location
        proofOfNotice: proofOfNotice,
        rulesOfConduct: localMeetingRules,
        speakingTimeMinutes: localSpeakingTimeMinutes,
        manualPriorMinutes: manualPriorMinutes,
        meetingLocation: meetingLocation // NEW: Meeting location
      });
      setShowLiveMinutesPreview(true);
    }
  };

  // Helper function to generate HTML for meeting minutes
  const generateMeetingMinutesHTML = (previewData, notes, actionItems) => {
    const currentDate = formatLocal(new Date().toISOString(), hoaTimezone, 'MMMM d, yyyy');
    
    // Helper to format member display with property identifier (privacy)
    const formatMemberDisplay = (member) => {
      const memberName = member.name || member.user_id || 'Unknown';
      const lot = member.property_identifier || member.unit || member.lot || member.property_number || '';
      return lot ? `${memberName} (Lot ${lot})` : memberName;
    };
    
    return `
<!DOCTYPE html>
<html>
<head>
    <title>Meeting Minutes - ${currentDate}</title>
    <style>
        body { font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; }
        .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
        .section { margin-bottom: 20px; }
        .section-title { font-weight: bold; font-size: 16px; color: #333; margin-bottom: 10px; }
        .attendance-item { padding: 5px 0; border-bottom: 1px solid #eee; }
        .motion-item { padding: 10px; border: 1px solid #ddd; margin-bottom: 10px; border-radius: 5px; }
        .rule-item { padding: 5px 0; margin-left: 20px; }
        @media print { body { margin: 0; padding: 15px; } }
    </style>
</head>
<body>
    <div class="header">
        <h1>MEETING MINUTES</h1>
        <p>${hoaName}</p>
        <p>Date: ${currentDate}</p>
        <p>Start: ${meetingStartTime ? formatLocal(meetingStartTime.toISOString(), hoaTimezone, 'h:mm a z') : 'Not recorded'}</p>
        <p>End: ${meetingEndTime ? formatLocal(meetingEndTime.toISOString(), hoaTimezone, 'h:mm a z') : 'Not recorded'}</p>
    </div>
    
    ${previewData.proofOfNotice && (previewData.proofOfNotice.selections || previewData.proofOfNotice.date || previewData.proofOfNotice.notes) ? `
    <div class="section">
        <div class="section-title">PROOF OF NOTICE</div>
        ${previewData.proofOfNotice.selections && previewData.proofOfNotice.selections.length > 0 ? `
          <div>Methods of Notice:</div>
          <ul>
            ${previewData.proofOfNotice.selections.map(s => `<li>${s}</li>`).join('')}
          </ul>
        ` : ''}
        ${previewData.proofOfNotice.date ? `<div><strong>Date Posted:</strong> ${previewData.proofOfNotice.date}</div>` : ''}
        ${previewData.proofOfNotice.notes ? `<div><strong>Notes:</strong> ${previewData.proofOfNotice.notes}</div>` : ''}
    </div>
    ` : ''}
    
    ${previewData.manualPriorMinutes ? `
    <div class="section">
        <div class="section-title">APPROVAL OF PRIOR MEETING MINUTES</div>
        <p>${previewData.manualPriorMinutes.replace(/\n/g, '<br>')}</p>
    </div>
    ` : ''}
    
    ${previewData.rulesOfConduct && previewData.rulesOfConduct.length > 0 ? `
    <div class="section">
        <div class="section-title">RULES OF CONDUCT FOR THIS MEETING</div>
        <ol>
            ${previewData.rulesOfConduct.map(rule => `<li class="rule-item">${rule}</li>`).join('')}
        </ol>
    </div>
    ` : ''}
    
    <div class="section">
        <div class="section-title">ATTENDANCE</div>
        <div><strong>Board Members:</strong></div>
        ${boardMembers.map(member => {
          const memberId = member.id || member.user_id;
          const status = attendance[memberId] === true ? 'PRESENT' : 'ABSENT';
          const role = member.role?.replace('_', ' ').toUpperCase() || 'BOARD MEMBER';
          return `<div class="attendance-item">${member.name} [${role}] - ${status}</div>`;
        }).join('')}
    </div>
    
    <div class="section">
        <div class="section-title">MOTIONS & VOTING</div>
        ${previewData.motions && previewData.motions.length > 0 ? 
          previewData.motions.map((motion, index) => {
            const votingRuleDisplay = motion.voting_rule === 'unanimous' ? '🤝 Unanimous' : 
                                     motion.voting_rule === 'propose_second' ? '✋ Propose & Second' : 
                                     '📊 Majority';
            return `
            <div class="motion-item">
                <strong>${index + 1}. ${motion.title}</strong><br>
                Description: ${motion.description || 'N/A'}<br>
                ${motion.discussion ? `<strong>Discussion:</strong> ${motion.discussion}<br>` : ''}
                <strong>Voting Rule:</strong> ${votingRuleDisplay}<br>
                Status: ${motion.status || 'Unknown'}<br>
                ${motion.voting_rule === 'propose_second' && (motion.proposer_id || motion.seconder_id) ? 
                  `Proposed by: ${motion.proposer_name || motion.proposer_id || 'Not set'}<br>
                   Seconded by: ${motion.seconder_name || motion.seconder_id || 'Not set'}<br>` : 
                  ''}
                ${(motion.vote_counts || motion.total_counts) ? (() => {
                  const answerOpts = motion.answer_options || motion.answers || ['Yes', 'No'];
                  const counts = motion.total_counts || motion.vote_counts || {};
                  return `Votes: ${answerOpts.map(opt => `${opt}=${counts[opt] || 0}`).join(', ')}`;
                })() : ''}
            </div>
          `;
          }).join('') : 
          '<p>No motions presented.</p>'
        }
    </div>
    
    ${notes ? `
    <div class="section">
        <div class="section-title">MEETING NOTES</div>
        <p>${notes.replace(/\n/g, '<br>')}</p>
    </div>
    ` : ''}
    
    ${actionItems.length > 0 ? `
    <div class="section">
        <div class="section-title">ACTION ITEMS</div>
        <ol>
            ${actionItems.map(item => `<li>${item}</li>`).join('')}
        </ol>
    </div>
    ` : ''}
</body>
</html>`;
  };

  // Removed problematic generateProfessionalMeetingMinutesHTML function - replaced with inline HTML generation

  // Function to export from live preview  
  async function exportFromLivePreview(format = 'text', dataToUse = null, notesOverride = null, itemsOverride = null, sectionVisibilityOverride = null) {
    console.log('exportFromLivePreview called with format:', format);
    console.log('sectionVisibilityOverride:', sectionVisibilityOverride);
    console.log('meetingRules available:', typeof meetingRules, meetingRules);
    
    // Helper function to format member display - USE PROPERTY IDENTIFIERS ONLY (privacy)
    const formatMemberDisplay = (member) => {
      const memberName = member.name || member.user_id || 'Unknown';
      const lot = member.property_identifier || member.unit || member.lot || member.property_number || '';
      // ALWAYS use property identifier, never address for privacy
      return lot ? `${memberName} (Lot ${lot})` : memberName;
    };
    
    // Use provided data or fall back to live preview data
    const exportData = dataToUse || livePreviewData;
    const exportNotes = notesOverride !== null ? notesOverride : liveMeetingNotes;
    const exportItems = itemsOverride !== null ? itemsOverride : liveActionItems;
    
    // Use section visibility from override or default to all visible
    const sectionVisibility = sectionVisibilityOverride || {
      meetingInfo: true,
      proofOfNotice: true,
      rules: true,
      attendance: true,
      priorBusiness: true,
      manualPriorMinutes: true,
      motions: true,
      discussions: true,
      notes: true,
      actionItems: true
    };
    
    console.log('exportData:', exportData);
    
    if (!exportData) {
      alert('No preview data available');
      return;
    }

    const currentDate = formatLocal(new Date().toISOString(), hoaTimezone, 'MMMM d, yyyy');
    const meetingTypeLabel = meetingTypes.find(t => t.value === meetingType)?.label || 'Board Meeting';
    
    if (format === 'text') {
      // Generate professional meeting minutes in text format
      let minutesText = `${meetingTypeLabel.toUpperCase()}\n`;
      minutesText += `${hoaName}\n`;
      minutesText += `${'='.repeat(60)}\n\n`;
      
      // Meeting Type at the top
      minutesText += `MEETING TYPE: ${meetingTypeLabel.toUpperCase()}\n\n`;
      
      // 1. MEETING CALLED TO ORDER
      minutesText += `1. MEETING CALLED TO ORDER\n`;
      minutesText += `${'-'.repeat(40)}\n`;
      minutesText += `Date: ${currentDate}\n`;
      minutesText += `Called to Order: ${exportData.startTime || (meetingStartTime ? formatLocal(meetingStartTime.toISOString(), hoaTimezone, 'h:mm a z') : 'Not recorded')}\n`;
      if (exportData.meetingLocation || exportData.meeting_location) {
        minutesText += `Location: ${exportData.meetingLocation || exportData.meeting_location}\n`;
      }
      minutesText += `\n`;
      
      // 1a. PROOF OF NOTICE
      if (exportData.proofOfNotice || exportData.proof_of_notice) {
        const proofData = exportData.proofOfNotice || exportData.proof_of_notice;
        minutesText += `1a. PROOF OF NOTICE\n`;
        minutesText += `${'-'.repeat(40)}\n`;
        if (proofData.postedOnEvents && proofData.emailSent) {
          minutesText += `✓ Notice posted on events calendar and emailed to all homeowners\n`;
        }
        if (proofData.mailPacket) {
          minutesText += `✓ Notice sent via mail packet`;
          if (proofData.mailPacketDate) {
            minutesText += ` on ${formatDateInHOATimezone(proofData.mailPacketDate, hoaTimezone, 'MMM d, yyyy')}`;
          }
          minutesText += `\n`;
        }
        if (proofData.other && proofData.otherNotes) {
          minutesText += `✓ Other: ${proofData.otherNotes}\n`;
        }
        minutesText += `\n`;
      }
      
      // 2. ROLL CALL
      minutesText += `2. ROLL CALL\n`;
      minutesText += `${'-'.repeat(40)}\n`;
      
      minutesText += `Members Present:\n`;
      let memberCount = 1;
      
      // Board Members - only show present members
      const presentBoardMembers = boardMembers.filter(member => {
        const memberId = member.id || member.user_id;
        return attendance[memberId] === true;
      });
      
      if (presentBoardMembers.length > 0) {
        presentBoardMembers.forEach(member => {
          const role = member.role?.replace('_', ' ').toUpperCase() || 'BOARD MEMBER';
          const displayName = formatMemberDisplay(member);
          minutesText += `${memberCount}. ${displayName} - ${role}\n`;
          memberCount++;
        });
      }
      
      // Homeowners - only show present members (excluding external)
      if (exportData.homeowners && exportData.homeowners.length > 0) {
        const presentHomeowners = exportData.homeowners.filter(homeowner => {
          const attendanceStatus = homeowner.present || homeowner.attendance_status;
          return attendanceStatus === true && homeowner.type !== 'external';
        });
        
        if (presentHomeowners.length > 0) {
          presentHomeowners.forEach(homeowner => {
            const displayName = formatMemberDisplay(homeowner);
            minutesText += `${memberCount}. ${displayName} - Homeowner\n`;
            memberCount++;
          });
        }
        
        // External Attendees - only show present members
        const presentExternal = exportData.homeowners.filter(homeowner => {
          const attendanceStatus = homeowner.present || homeowner.attendance_status;
          return attendanceStatus === true && homeowner.type === 'external';
        });
        
        if (presentExternal.length > 0) {
          presentExternal.forEach(attendee => {
            const roleLabels = {
              attorney: 'Attorney',
              accountant: 'Accountant',
              staff: 'Staff', 
              maintenance: 'Maintenance',
              security: 'Security',
              other: 'Other'
            };
            const roleLabel = roleLabels[attendee.externalRole] || 'External';
            minutesText += `${memberCount}. ${attendee.name} - ${roleLabel}\n`;
            memberCount++;
          });
        }
      }
      
      if (memberCount === 1) {
        minutesText += `No members present\n`;
      }
      
      const presentCount = Object.values(attendance).filter(status => status === true).length;
      const totalCount = boardMembers.length + (exportData.homeowners?.length || 0);
      // Removed attendance summary per user request
      minutesText += `Board Quorum: ${presentCount >= Math.ceil(boardMembers.length / 2) ? 'ACHIEVED' : 'NOT ACHIEVED'}\n\n`;
      
      // 3. RULES OF THE MEETING — use rules from export data (EDITED RULES FOR THIS MEETING)
      minutesText += `3. RULES OF THE MEETING\n`;
      minutesText += `${'-'.repeat(40)}\n`;
      
      // Use meeting rules from export data if available (these are the edited rules for THIS meeting)
      const rulesToUseForExport = (exportData.rulesOfConduct && exportData.rulesOfConduct.length > 0) 
        ? exportData.rulesOfConduct 
        : (exportData.rules_of_conduct && exportData.rules_of_conduct.length > 0)
        ? exportData.rules_of_conduct
        : [
            "Members of the HOA will be allotted 3 minutes to speak, address issues, concerns or ask questions that are listed on the agenda, during 'Open to the Floor'.",
            "Board members have no time restriction for responses.",
            "Speakers will be reminded to state their name and address or identifier, speak calmly, no yelling, no profanity or smearing of other individuals.",
            "Anyone who has an outburst, speaks out of turn and refuses to adhere to the rules will be politely asked to leave to include the BOD or ARC. If individuals refuse to leave, then other legal means will be taken to have those individuals escorted off the property or call.",
            "To transact such other business as may properly come before the Board or any Adjournment or Postponement thereof."
          ];
      
      rulesToUseForExport.forEach((rule, index) => {
        minutesText += `${String.fromCharCode(97 + index)}.) ${rule}\n`;
      });
      minutesText += `\n`;
      
      // 4. PRIOR BUSINESS (APPROVAL OF PRIOR MEETING MINUTES)
      if (exportData.prior_minutes_approval || exportData.manualPriorMinutes || exportData.manual_prior_minutes) {
        minutesText += `4. PRIOR BUSINESS\n`;
        minutesText += `${'-'.repeat(40)}\n`;
        
        if (exportData.prior_minutes_approval) {
          minutesText += `Approval of Prior Meeting Minutes:\n`;
          minutesText += `  Meeting: ${exportData.prior_minutes_approval.title}\n`;
          minutesText += `  Date: ${exportData.prior_minutes_approval.date ? formatLocal(exportData.prior_minutes_approval.date, hoaTimezone, 'MMM d, yyyy') : 'Unknown'}\n`;
          minutesText += `  Motion: To approve the prior meeting minutes as presented.\n`;
          minutesText += `  Status: ${exportData.prior_minutes_approval.status || 'Pending approval during this meeting'}\n\n`;
        }
        
        // Add manual prior minutes notes if present
        if (exportData.manualPriorMinutes || exportData.manual_prior_minutes) {
          const manualNotes = exportData.manualPriorMinutes || exportData.manual_prior_minutes;
          if (manualNotes && manualNotes.trim()) {
            minutesText += `Additional Prior Meeting Minutes Notes:\n`;
            minutesText += `${manualNotes}\n`;
          }
        }
        minutesText += `\n`;
      }
      
      // 5. CURRENT BUSINESS (MOTIONS) - Renumbered from 4
      minutesText += `${exportData.prior_minutes_approval ? '5' : '4'}. CURRENT BUSINESS\n`;
      minutesText += `${'-'.repeat(40)}\n`;
      if (exportData.motions && exportData.motions.length > 0) {
        exportData.motions.forEach((motion, index) => {
          minutesText += `Motion ${index + 1}: ${motion.title}\n`;
          minutesText += `Description: ${motion.description || 'No description provided'}\n`;
          if (motion.discussion) {
            minutesText += `Discussion: ${motion.discussion}\n`;
          }
          if (motion.voting_rule === 'propose_second' && (motion.proposer_name || motion.seconder_name)) {
            minutesText += `Proposed by: ${motion.proposer_name || 'Not set'}\n`;
            minutesText += `Seconded by: ${motion.seconder_name || 'Not set'}\n`;
          }
          minutesText += `Status: ${motion.status || 'Pending'}\n`;
          if (motion.vote_counts || motion.votes || motion.total_counts) {
            const answerOpts = motion.answer_options || motion.answers || ['Yes', 'No'];
            const counts = motion.total_counts || motion.vote_counts || motion.votes || {};
            minutesText += `Vote Results: ${answerOpts.map(opt => `${opt}=${counts[opt] || 0}`).join(', ')}\n`;
          }
          if (motion.individual_votes) {
            const answerOpts = motion.answer_options || motion.answers || ['Yes', 'No'];
            const indVotes = motion.individual_votes || {};
            answerOpts.forEach(opt => {
              const voters = indVotes[opt] || [];
              if (voters.length > 0) {
                minutesText += `  ${opt}: ${voters.map(v => v.name).join(', ')}\n`;
              }
            });
          }
          minutesText += `\n`;
        });
      } else {
        minutesText += `No motions presented during this meeting.\n\n`;
      }
      
      // 6. HOA OFFICIAL MEANS OF COMMUNICATIONS - Renumbered from 5
      minutesText += `${exportData.prior_minutes_approval ? '6' : '5'}. HOA OFFICIAL MEANS OF COMMUNICATIONS\n`;
      minutesText += `${'-'.repeat(40)}\n`;
      minutesText += `${hoaSettings?.hoa_name || hoaSettings?.name || getHoaName()} Account: Available to all residents\n`;
      minutesText += `HOA Mailing Address: ${hoaSettings?.mailing_address || 'Not configured'}\n`;
      minutesText += `HOA Official Email: ${hoaSettings?.hoa_email || 'Not configured'}\n\n`;
      
      // 7. OPEN DISCUSSION - Renumbered from 6
      minutesText += `${exportData.prior_minutes_approval ? '7' : '6'}. OPEN DISCUSSION\n`;
      minutesText += `${'-'.repeat(40)}\n`;
      if (exportData.discussions && exportData.discussions.length > 0) {
        exportData.discussions.forEach((discussion, index) => {
          minutesText += `Discussion ${index + 1}: ${discussion.topic || 'General Discussion'}\n`;
          minutesText += `Submitted by: ${discussion.member_name || 'Unknown'}\n`;
          if (discussion.property_address) {
            minutesText += `Property: ${discussion.property_address}\n`;
          }
          if (discussion.ccr_rule || discussion.bylaw_rule) {
            const citations = [discussion.ccr_rule, discussion.bylaw_rule].filter(Boolean).join(' • ');
            minutesText += `Rule cited: ${citations}\n`;
          }
          if (discussion.notes) {
            minutesText += `Notes: ${discussion.notes}\n`;
          }
          minutesText += `Status: ${discussion.status || 'Pending'}\n\n`;
        });
      } else {
        minutesText += `No additional discussions recorded.\n\n`;
      }
      
      // 8. NOTES FROM SECRETARY - Renumbered from 7
      minutesText += `${exportData.prior_minutes_approval ? '8' : '7'}. NOTES FROM SECRETARY\n`;
      minutesText += `${'-'.repeat(40)}\n`;
      if (exportNotes.trim()) {
        minutesText += `${exportNotes}\n\n`;
      } else {
        minutesText += `No additional notes recorded.\n\n`;
      }
      
      // Action Items
      if (exportItems.length > 0) {
        minutesText += `ACTION ITEMS:\n`;
        minutesText += `${'-'.repeat(20)}\n`;
        exportItems.forEach((item, index) => {
          minutesText += `${index + 1}. ${item}\n`;
        });
        minutesText += `\n`;
      }
      
      // Supporting Documents
      if (supportingDocuments && supportingDocuments.length > 0) {
        minutesText += `SUPPORTING DOCUMENTS:\n`;
        minutesText += `${'-'.repeat(20)}\n`;
        minutesText += `The following documents are attached as supporting materials:\n\n`;
        supportingDocuments.forEach((doc, index) => {
          const fileUrl = getDocumentFileUrl(doc.id);
          minutesText += `${index + 1}. ${doc.file_name}\n`;
          minutesText += `   Size: ${doc.file_size ? (doc.file_size / 1024).toFixed(1) + ' KB' : 'Unknown'}\n`;
          minutesText += `   Uploaded: ${formatLocal(doc.uploaded_at, hoaTimezone, 'MMM d, yyyy h:mm a z')}\n`;
          minutesText += `   Download: ${fileUrl}\n\n`;
        });
        minutesText += `Total: ${supportingDocuments.length} supporting document${supportingDocuments.length !== 1 ? 's' : ''}\n\n`;
      }
      
      // 9. MEETING ADJOURNED - Renumbered from 8
      minutesText += `${exportData.prior_minutes_approval ? '9' : '8'}. MEETING ADJOURNED\n`;
      minutesText += `${'-'.repeat(40)}\n`;
      minutesText += `Meeting adjourned: ${meetingEndTime ? formatLocal(meetingEndTime.toISOString(), hoaTimezone, 'h:mm a z') : 'Not recorded'}\n\n`;
      
      minutesText += `Minutes prepared by: ${hoaSettings?.hoa_name || hoaSettings?.name || getHoaName()}\n`;
      minutesText += `Date of preparation: ${currentDate}\n`;
      
      // Download as text file
      const blob = new Blob([minutesText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${meetingType}-meeting-minutes-${currentDate.replace(/\//g, '-')}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      alert('Meeting minutes downloaded as text file!');
      
    } else if (format === 'pdf') {
      // For PDF, create HTML and open in new window for printing (no signature approval for now)
      console.log('Generating PDF for live meeting minutes');
      // Generate HTML content using available scope variables
      const htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <title>${meetingTypeLabel} Minutes - ${currentDate}</title>
    <style>
        body { font-family: 'Times New Roman', serif; max-width: 8.5in; margin: 0 auto; padding: 40px 20px; line-height: 1.6; }
        .header { text-align: center; border-bottom: 3px double #333; padding-bottom: 20px; margin-bottom: 30px; }
        .header h1 { font-size: 24px; margin: 0; font-weight: bold; }
        .header h2 { font-size: 18px; margin: 5px 0; font-weight: normal; }
        .section { margin-bottom: 25px; page-break-inside: avoid; }
        .section-title { font-weight: bold; font-size: 16px; color: #000; margin-bottom: 10px; border-bottom: 1px solid #333; padding-bottom: 5px; }
        .subsection { margin-left: 20px; margin-bottom: 15px; }
        .attendance-list { margin-left: 20px; }
        .attendance-list ol { padding-left: 0; }
        .attendance-list li { list-style: decimal; margin-bottom: 5px; padding-left: 5px; }
        .motion-item { border: 1px solid #ddd; padding: 15px; margin-bottom: 15px; border-radius: 5px; background: #f9f9f9; }
        .rules-list { margin-left: 20px; }
        .rules-list li { margin-bottom: 10px; }
        .signature-section { margin-top: 40px; display: grid; grid-template-columns: 1fr 1fr; gap: 40px; }
        .signature-line { border-bottom: 1px solid #000; padding-bottom: 2px; margin-bottom: 5px; height: 30px; }
        @media print { 
            body { margin: 0; padding: 20px; font-size: 12px; }
            * { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            .section { page-break-inside: avoid; }
            img { max-width: 100%; height: auto; page-break-inside: avoid; }
            iframe { display: none; } /* Hide iframes in print as they won't render */
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>${meetingTypeLabel.toUpperCase()}</h1>
        <h2>${(hoaSettings?.hoa_name || hoaSettings?.name || hoaName || 'HOMEOWNERS ASSOCIATION')}</h2>
        ${(hoaSettings?.mailing_address || hoaSettings?.hoa_email || hoaSettings?.phone) ? `<p style="font-size: 12px; margin: 5px 0;">
          ${hoaSettings?.mailing_address || ''}${hoaSettings?.hoa_email ? (hoaSettings?.mailing_address ? ' | ' : '') + hoaSettings.hoa_email : ''}${hoaSettings?.phone ? (hoaSettings?.mailing_address || hoaSettings?.hoa_email ? ' | ' : '') + hoaSettings.phone : ''}
        </p>` : ''}
        <p style="font-size: 14px; margin: 5px 0;"><strong>Date:</strong> ${currentDate}${exportData.startTime ? ` | <strong>Time:</strong> ${exportData.startTime}` : ''}</p>
    </div>
    
    <div class="section">
        <div class="section-title">1. MEETING CALLED TO ORDER</div>
        <p><strong>Called to Order:</strong> ${meetingStartTime ? formatLocal(meetingStartTime.toISOString(), hoaTimezone, 'h:mm a z') : 'Not recorded'}</p>
    </div>
    
    ${(exportData.meetingLocation || exportData.meeting_location) ? `
    <div class="section">
        <div class="section-title">1a. MEETING LOCATION</div>
        <p>${exportData.meetingLocation || exportData.meeting_location}</p>
    </div>
    ` : ''}
    
    ${exportData.proofOfNotice && (exportData.proofOfNotice.postedOnEvents || exportData.proofOfNotice.emailSent || exportData.proofOfNotice.mailPacket || exportData.proofOfNotice.other) ? `
    <div class="section">
        <div class="section-title">${(exportData.meetingLocation || exportData.meeting_location) ? '1b' : '1a'}. PROOF OF NOTICE</div>
        <div class="subsection">
            ${exportData.proofOfNotice.postedOnEvents || exportData.proofOfNotice.emailSent ? `<p>✓ Posted on events calendar and emailed to all homeowners</p>` : ''}
            ${exportData.proofOfNotice.mailPacket ? `<p>✓ Mail packet${exportData.proofOfNotice.mailPacketDate ? ` (Date: ${exportData.proofOfNotice.mailPacketDate})` : ''}</p>` : ''}
            ${exportData.proofOfNotice.other && exportData.proofOfNotice.otherNotes ? `<p>✓ Other: ${exportData.proofOfNotice.otherNotes}</p>` : ''}
        </div>
    </div>
    ` : ''}
    
    ${sectionVisibility.attendance !== false ? `
    <div class="section">
        <div class="section-title">2. ROLL CALL</div>
        <div class="subsection">
            <p><strong>Members Present:</strong></p>
            <div class="attendance-list">
                <ol>
                    ${boardMembers.filter(member => (attendance[member.id || member.user_id]) === true).map(member => {
                      const role = member.role?.replace('_', ' ').toUpperCase() || 'BOARD MEMBER';
                      const displayName = formatMemberDisplay(member);
                      return `<li>${displayName} - ${role}</li>`;
                    }).join('')}
                    ${exportData.homeowners ? exportData.homeowners.filter(h => (h.present || h.attendance_status) === true && h.type !== 'external').map(h => {
                      const displayName = formatMemberDisplay(h);
                      return `<li>${displayName} - Homeowner</li>`;
                    }).join('') : ''}
                    ${exportData.homeowners ? exportData.homeowners.filter(h => (h.present || h.attendance_status) === true && h.type === 'external').map(h => {
                      const roleLabels = {
                        attorney: 'Attorney',
                        accountant: 'Accountant', 
                        staff: 'Staff',
                        maintenance: 'Maintenance',
                        security: 'Security',
                        other: 'Other'
                      };
                      const roleLabel = roleLabels[h.externalRole] || 'External';
                      return `<li>${h.name} - ${roleLabel}</li>`;
                    }).join('') : ''}
                </ol>
                ${boardMembers.filter(member => (attendance[member.id || member.user_id]) === true).length === 0 && (!exportData.homeowners || exportData.homeowners.filter(h => (h.present || h.attendance_status) === true).length === 0) ? '<p>No members present</p>' : ''}
            </div>
        </div>
        <div class="subsection">
            <!-- Removed attendance summary per user request -->
            <p><strong>Board Quorum:</strong> ${Object.values(attendance).filter(s => s === true).length >= Math.ceil(boardMembers.length / 2) ? 'ACHIEVED' : 'NOT ACHIEVED'}</p>
        </div>
    </div>
    ` : ''}
    
    ${sectionVisibility.rules !== false ? `
    <div class="section">
        <div class="section-title">3. RULES OF THE MEETING</div>
        <ol class="rules-list">
            ${(exportData.rulesOfConduct && exportData.rulesOfConduct.length > 0 
               ? exportData.rulesOfConduct 
               : localMeetingRules && localMeetingRules.length > 0 
               ? localMeetingRules 
               : meetingRules && meetingRules.length > 0
               ? meetingRules
               : [
                  "Members of the HOA will be allotted 3 minutes to speak, address issues, concerns or ask questions that are listed on the agenda, during 'Open to the Floor'.",
                  "Board members have no time restriction for responses.",
                  "Speakers will be reminded to state their name and address or identifier, speak calmly, no yelling, no profanity or smearing of other individuals.",
                  "Anyone who has an outburst, speaks out of turn and refuses to adhere to the rules will be politely asked to leave to include the BOD or ARC. If individuals refuse to leave, then other legal means will be taken to have those individuals escorted off the property or call.",
                  "To transact such other business as may properly come before the Board or any Adjournment or Postponement thereof."
                ]).map(rule => `<li>${rule}</li>`).join('')}
        </ol>
    </div>
    ` : ''}
    
    ${(sectionVisibility.priorBusiness !== false || sectionVisibility.manualPriorMinutes !== false) && (exportData.prior_minutes_approval || exportData.manualPriorMinutes || exportData.manual_prior_minutes) ? `
    <div class="section">
        <div class="section-title">4. PRIOR BUSINESS</div>
        ${exportData.prior_minutes_approval ? `
        <div class="subsection">
            <p><strong>Approval of Prior Meeting Minutes</strong></p>
            <p><strong>Meeting:</strong> ${exportData.prior_minutes_approval.title}</p>
            <p><strong>Date:</strong> ${exportData.prior_minutes_approval.date ? formatLocal(exportData.prior_minutes_approval.date, hoaTimezone, 'MMM d, yyyy') : 'Unknown'}</p>
            <p><strong>Motion:</strong> To approve the prior meeting minutes as presented.</p>
            <p><strong>Status:</strong> ${exportData.prior_minutes_approval.status || 'Pending approval during this meeting'}</p>
        </div>
        ` : ''}
        ${(exportData.manualPriorMinutes || exportData.manual_prior_minutes) ? `
        <div class="subsection" style="margin-top: 15px; padding: 12px; background-color: #f0f8ff; border-left: 4px solid #007cba;">
            <p><strong>Additional Prior Meeting Minutes Notes:</strong></p>
            <p style="white-space: pre-wrap;">${exportData.manualPriorMinutes || exportData.manual_prior_minutes}</p>
        </div>
        ` : ''}
    </div>
    ` : ''}
    
    ${true ? `
    <div class="section">
        <div class="section-title">${exportData.prior_minutes_approval ? '5' : '4'}. CURRENT BUSINESS</div>
        ${exportData.motions && exportData.motions.length > 0 ? 
          exportData.motions.map((motion, index) => `
            <div class="motion-item">
                <p><strong>Motion ${index + 1}:</strong> ${motion.title}</p>
                <p><strong>Description:</strong> ${motion.description || 'No description provided'}</p>
                ${motion.discussion ? `<p><strong>Discussion:</strong> ${motion.discussion}</p>` : ''}
                ${motion.voting_rule === 'propose_second' && (motion.proposer_name || motion.seconder_name) ? `
                  <p><strong>Proposed by:</strong> ${motion.proposer_name || 'Not set'}</p>
                  <p><strong>Seconded by:</strong> ${motion.seconder_name || 'Not set'}</p>
                ` : ''}
                <p><strong>Status:</strong> ${motion.status || 'Pending'}</p>
                ${(motion.vote_counts || motion.votes || motion.total_counts) ? (() => {
                  const answerOpts = motion.answer_options || motion.answers || ['Yes', 'No'];
                  const counts = motion.total_counts || motion.vote_counts || motion.votes || {};
                  return `<p><strong>Vote Results:</strong> ${answerOpts.map(opt => `${opt}: ${counts[opt] || 0}`).join(', ')}</p>`;
                })() : ''}
                ${motion.individual_votes ? (() => {
                  const answerOpts = motion.answer_options || motion.answers || ['Yes', 'No'];
                  const indVotes = motion.individual_votes || {};
                  return `
                  <div style="margin-left: 20px; margin-top: 10px;">
                    ${answerOpts.map(opt => {
                      const voters = indVotes[opt] || [];
                      return voters.length > 0 ? `<p><strong>${opt}:</strong> ${voters.map(v => v.name).join(', ')}</p>` : '';
                    }).join('')}
                  </div>
                `;})() : ''}
            </div>
          `).join('') : 
          '<p>No motions presented during this meeting.</p>'
        }
    </div>
    ` : ''}
    
    ${sectionVisibility.discussions !== false ? `
    <div class="section">
        <div class="section-title">${exportData.prior_minutes_approval ? '6' : '5'}. OPEN DISCUSSION</div>
        ${exportData.discussions && exportData.discussions.length > 0 ? 
          exportData.discussions.map((discussion, index) => `
            <div class="subsection">
                <p><strong>Discussion ${index + 1}:</strong> ${discussion.topic || 'General Discussion'}</p>
                <p><strong>Submitted by:</strong> ${discussion.member_name || 'Unknown'}</p>
                ${discussion.property_address ? `<p><strong>Property:</strong> ${discussion.property_address}</p>` : ''}
                ${discussion.ccr_rule || discussion.bylaw_rule ? `<p><strong>Rule cited:</strong> ${[discussion.ccr_rule, discussion.bylaw_rule].filter(Boolean).join(' • ')}</p>` : ''}
                ${discussion.notes ? `<p><strong>Notes:</strong> ${discussion.notes}</p>` : ''}
                <p><strong>Status:</strong> ${discussion.status || 'Pending'}</p>
            </div>
          `).join('') : 
          '<p>No additional discussions recorded.</p>'
        }
    </div>
    ` : ''}
    
    ${true ? `
    <div class="section">
        <div class="section-title">${exportData.prior_minutes_approval ? '7' : '6'}. NOTES FROM SECRETARY</div>
        ${true ? (liveMeetingNotes.trim() ? `<p>${liveMeetingNotes.replace(/\n/g, '<br>')}</p>` : '<p>No additional notes recorded.</p>') : ''}
        ${sectionVisibility.actionItems !== false ? (liveActionItems.length > 0 ? `
        <div class="subsection">
            <p><strong>Action Items:</strong></p>
            <ol>
                ${liveActionItems.map(item => `<li>${item}</li>`).join('')}
            </ol>
        </div>
        ` : '') : ''}
    </div>
    ` : ''}
    
    ${supportingDocuments && supportingDocuments.length > 0 ? `
    <div class="section">
        <div class="section-title">📎 SUPPORTING DOCUMENTS</div>
        ${exportData.use_combined_packet && exportData.combined_packet_id ? `
          <p style="font-style: italic; color: #666; margin-bottom: 15px;">All supporting documents have been combined into a single comprehensive packet.</p>
          <div style="margin-bottom: 30px; padding: 20px; background: #f0fdf4; border: 2px solid #10b981; border-radius: 8px; text-align: center;">
            <div style="margin-bottom: 15px;">
              <span style="font-size: 48px;">📦</span>
            </div>
            <h3 style="margin: 0 0 10px 0; color: #059669;">Combined Supporting Documents Packet</h3>
            <p style="margin: 0 0 15px 0; color: #6b7280; font-size: 14px;">
              ${supportingDocuments.length} documents merged into single PDF (${supportingDocuments.map(d => d.file_name).join(', ')})
            </p>
            <a 
              href="${process.env.REACT_APP_BACKEND_URL || window.location.origin.replace(/:\d+$/, ':8001')}/api/packets/download/${exportData.combined_packet_id}?token=${localStorage.getItem('hoa_token')}" 
              download="Combined_Supporting_Documents.pdf"
              style="display: inline-block; padding: 12px 24px; background: #10b981; color: white; text-decoration: none; border-radius: 6px; font-weight: bold; font-size: 16px;"
              target="_blank"
            >
              📥 Download Combined Packet (${supportingDocuments.length} documents)
            </a>
            <p style="margin: 15px 0 0 0; font-size: 12px; color: #6b7280; font-style: italic;">
              Note: All supporting documents have been combined into a single PDF file for your convenience. Click above to download.
            </p>
          </div>
        ` : `
          <p style="font-style: italic; color: #666; margin-bottom: 15px;">The following documents are attached as supporting materials for this meeting:</p>
          ${supportingDocuments.map((doc, index) => {
          const isImage = doc.mime_type?.startsWith('image/');
          const isPDF = doc.mime_type === 'application/pdf';
          const fileUrl = getDocumentFileUrl(doc.id);
          
          return `
            <div style="margin-bottom: 30px; page-break-inside: avoid;">
              <div style="padding: 10px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; margin-bottom: 10px;">
                <div style="display: flex; align-items: center;">
                  <span style="font-size: 20px; margin-right: 10px;">📄</span>
                  <div>
                    <strong>${index + 1}. ${doc.file_name}</strong><br>
                    <span style="font-size: 11px; color: #6b7280;">
                      Size: ${doc.file_size ? (doc.file_size / 1024).toFixed(1) + " KB" : "Unknown"} | 
                      Uploaded: ${formatLocal(doc.uploaded_at, hoaTimezone, 'MMM d, yyyy h:mm a z')}
                    </span>
                  </div>
                </div>
              </div>
              ${isImage ? `
                <div style="text-align: center; padding: 10px; background: #fff; border: 1px solid #ddd;">
                  <img src="${fileUrl}" style="max-width: 100%; height: auto; max-height: 800px;" alt="${doc.file_name}" onerror="this.parentElement.innerHTML='<p style=color:red;>Image failed to load. URL: ${fileUrl}</p>'" />
                </div>
              ` : isPDF ? `
                <div style="border: 2px solid #ddd; padding: 10px; background: #fff; min-height: 600px;">
                  <iframe src="${fileUrl}" width="100%" height="800px" style="border: none;"></iframe>
                  <p style="text-align: center; margin-top: 10px; font-size: 11px; color: #666;">
                    PDF Document: ${doc.file_name}
                  </p>
                </div>
              ` : `
                <div style="padding: 20px; background: #fff; border: 1px solid #ddd; text-align: center; color: #666;">
                  <p>📄 Document content not displayable in PDF export</p>
                  <p style="font-size: 11px; margin-top: 10px;">This document (${doc.file_name}) can be downloaded separately</p>
                  <p style="font-size: 11px; margin-top: 8px;"><a href="${fileUrl}" style="color: #6366f1; text-decoration: underline;" download="${doc.file_name}">Click here to download: ${doc.file_name}</a></p>
                </div>
              `}
            </div>
          `;
          }).join('')}
          <p style="font-size: 11px; color: #666; font-style: italic; margin-top: 10px;">
              Total: ${supportingDocuments.length} supporting document${supportingDocuments.length !== 1 ? 's' : ''}
          </p>
        `}
    </div>
    ` : ''}
    
    <div class="section">
        <div class="section-title">${exportData.prior_minutes_approval ? '8' : '7'}. MEETING ADJOURNED</div>
        <p><strong>Meeting adjourned:</strong> ${meetingEndTime ? formatLocal(meetingEndTime.toISOString(), hoaTimezone, 'h:mm a z') : 'Not recorded'}</p>
    </div>
    
    <div class="signature-section">
        <div>
            <div class="signature-line"></div>
            <p style="text-align: center; margin: 0;"><strong>Secretary Signature</strong></p>
            <p style="text-align: center; margin: 0; font-size: 12px;">Date: ___________</p>
        </div>
        <div>
            <div class="signature-line"></div>
            <p style="text-align: center; margin: 0;"><strong>President Signature</strong></p>
            <p style="text-align: center; margin: 0; font-size: 12px;">Date: ___________</p>
        </div>
    </div>
    
    <div style="margin-top: 30px; font-size: 12px; color: #666; text-align: center;">
        <p>Minutes prepared by: ${hoaSettings?.hoa_name || hoaSettings?.name || getHoaName()} | Date of preparation: ${currentDate}</p>
    </div>
</body>
</html>`;
      
      try {
        // Remove supporting documents section from HTML (will be merged as PDF)
        let htmlWithoutAttachments = htmlContent;
        
        // More precise regex to remove supporting documents section
        if (supportingDocuments && supportingDocuments.length > 0) {
          htmlWithoutAttachments = htmlContent.replace(
            /<div class="section">\s*<div class="section-title">📎 SUPPORTING DOCUMENTS<\/div>[\s\S]*?(?=<div class="section">|<\/body>)/,
            ''
          );
        }
        
        console.log('Export Data:', exportData);
        console.log('Combined Packet ID:', exportData.combined_packet_id);
        console.log('Has Supporting Docs:', supportingDocuments?.length > 0);
        
        // Call backend to generate merged PDF
        const token = localStorage.getItem('hoa_token');
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin.replace(/:\d+$/, ':8001');
        
        const payload = {
          html_content: htmlWithoutAttachments,
          combined_packet_id: exportData.combined_packet_id || null,
          meeting_type: meetingType || 'BOD',
          meeting_date: currentDate
        };
        
        console.log('Sending PDF generation request:', payload);
        
        const response = await fetch(`${backendUrl}/api/minutes-pdf/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Backend error response:', errorText);
          throw new Error(`PDF generation failed (${response.status}): ${errorText}`);
        }
        
        // Get PDF blob and open in new tab
        const pdfBlob = await response.blob();
        console.log('PDF Blob received:', pdfBlob.size, 'bytes, type:', pdfBlob.type);
        
        if (pdfBlob.size === 0) {
          throw new Error('Received empty PDF from server');
        }
        
        if (!pdfBlob.type.includes('pdf')) {
          console.error('Invalid content type:', pdfBlob.type);
          const text = await pdfBlob.text();
          console.error('Response content:', text);
          throw new Error(`Server returned ${pdfBlob.type} instead of PDF`);
        }
        
        const pdfUrl = URL.createObjectURL(pdfBlob);
        console.log('Opening PDF:', pdfUrl);
        
        // Try to open in new window
        const pdfWindow = window.open(pdfUrl, '_blank');
        
        if (!pdfWindow || pdfWindow.closed || typeof pdfWindow.closed === 'undefined') {
          // Popup was blocked, offer download instead
          console.warn('Popup blocked, offering download');
          const a = document.createElement('a');
          a.href = pdfUrl;
          a.download = `meeting_minutes_${meetingType}_${currentDate.replace(/\//g, '-')}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          
          const hasAttachments = exportData.combined_packet_id ? true : false;
          alert(`✅ Complete meeting minutes PDF generated!\n\nThe PDF includes:\n• Meeting minutes${hasAttachments ? '\n• All supporting documents combined' : ''}\n\nDownloading now...`);
        } else {
          const hasAttachments = exportData.combined_packet_id ? true : false;
          alert(`✅ Complete meeting minutes PDF generated!\n\nThe PDF includes:\n• Meeting minutes${hasAttachments ? '\n• All supporting documents combined' : ''}\n\nOpened in new tab.`);
        }
        
      } catch (error) {
        console.error('Error with PDF export:', error);
        alert(`PDF export failed: ${error.message}\n\nPlease check the console for more details.`);
      }
    }
    
    setShowLiveMinutesPreview(false);
  };

  // Helper function to create a test motion for testing vote casting
  const createTestMotion = async () => {
    try {
      const testMotion = {
        title: "Test Motion - Fix Vote Casting",
        description: "This is a test motion to verify vote casting functionality works correctly.",
        timing_type: "immediate",
        duration_minutes: 15,
        requires_quorum: false
      };

      const response = await api.post('/api/vote/live-motions/bulk', {
        motions: [testMotion],
        meeting_type: meetingTypeFilter  // FIXED: Per-tab test motion
      });

      if (response.data.success) {
        alert("Test motion created successfully! You can now test vote casting.");
        await loadLiveVotingData();
      }
    } catch (error) {
      console.error('Error creating test motion:', error);
      alert('Failed to create test motion. Check console for details.');
    }
  }

  // Meeting session management
  const startMeeting = () => {
    const currentTime = new Date();
    const newMeetingId = `meeting_${Date.now()}_${tabType}`;
    
    setMeetingStartTime(currentTime);
    setMeetingInProgress(true);
    setMeetingId(newMeetingId);
    
    // Save to localStorage for persistence (including current attendance)
    saveMeetingState(true, newMeetingId, currentTime.toISOString(), null, attendance);
    
    alert(`${meetingTypes.find(t => t.value === meetingType)?.label || 'Meeting'} called to order at ${formatLocal(currentTime.toISOString(), hoaTimezone, 'h:mm a z')}`);
  };

  const endMeeting = () => {
    const currentTime = new Date();
    
    setMeetingEndTime(currentTime);
    setMeetingInProgress(false);
    
    // Save to localStorage for persistence (including current attendance)
    saveMeetingState(false, meetingId, meetingStartTime ? meetingStartTime.toISOString() : null, currentTime.toISOString(), attendance);
    
    alert(`Meeting adjourned at ${formatLocal(currentTime.toISOString(), hoaTimezone, 'h:mm a z')}`);
  };

  const clearMeeting = async () => {
    if (!confirm('Are you sure you want to clear all meeting data? This action cannot be undone.')) {
      return;
    }
    
    // Ask about rules before clearing
    const keepRules = confirm(
      'Would you like to keep the current meeting rules for the next meeting?\n\n' +
      '• Click OK to KEEP current rules (recommended if this meeting used custom rules)\n' +
      '• Click Cancel to RESET to standard rules from Settings'
    );
    
    try {
      // Clear discussions for this meeting type
      await api.delete('/api/vote/discussions/clear', {
        params: { meeting_type: meetingType }
      });

      // Also clear motions and attendance for this meeting type
      await api.post('/api/vote/clear-session', { 
        meeting_type: meetingType,
        clear_all: meetingTypeFilter === 'BOD'  // BOD clears all, Committee clears only its own
      });
      
      // Clear local state
      setMeetingStartTime(null);
      setMeetingEndTime(null);
      setMeetingInProgress(false);
      setMeetingId(null);
      setAttendance({});
      setActiveMotions([]);
      setLiveMeetingNotes('');
      setLiveActionItems([]);
      setSelectedPriorMinutesId(null);
      setSelectedPriorMinutesStatus('pending');
      setManualPriorMinutes('');
      setMeetingLocation(''); // NEW: Clear meeting location
      
      // Reset rules based on user choice
      if (!keepRules) {
        // User chose to reset to standard rules
        setLocalMeetingRules(meetingRules || []);
        setLocalSpeakingTimeMinutes(speakingTimeMinutes || 3);
        localStorage.removeItem(getTabStateKey('meetingRules'));
        localStorage.removeItem(getTabStateKey('speakingTimeMinutes'));
      }
      // If keepRules is true, don't reset or remove from localStorage
      
      // Reset proof of notice to default (first option checked)
      setProofOfNotice({
        postedOnEvents: true,
        emailSent: true,
        mailPacket: false,
        mailPacketDate: '',
        other: false,
        otherNotes: ''
      });
      
      // Clear from localStorage (except rules if keeping them)
      localStorage.removeItem(`meeting_state_${tabType}`);
      localStorage.removeItem(getTabStateKey('liveMeetingNotes'));
      localStorage.removeItem(getTabStateKey('meetingLocation')); // NEW
      localStorage.removeItem(getTabStateKey('liveActionItems'));
      localStorage.removeItem(getTabStateKey('proofOfNotice'));
      localStorage.removeItem(getTabStateKey('manualPriorMinutes'));
      
      // Refresh data to show updated state
      await loadLiveVotingData();
      
      const rulesMessage = keepRules ? 'Current rules kept for next meeting.' : 'Rules reset to standard settings.';
      alert(`Meeting data cleared successfully!\n${rulesMessage}`);
    } catch (error) {
      console.error('Error clearing meeting:', error);
      alert('Failed to clear meeting data. Please try again.');
    }
  };

  const castLiveVote = async (motionId, vote) => {
    try {
      // Find the motion to check if it's still active
      const motion = activeMotions.find(m => m.id === motionId);
      if (!motion) {
        alert('Motion not found. Please refresh the page.');
        return;
      }
      
      // Check if motion is still active (not expired)
      if (motion.status !== 'voting') {
        alert('This motion is no longer accepting votes. Please refresh to see current status.');
        await loadLiveVotingData();
        return;
      }
      
      // Check expiration time if available
      if (motion.expires_at) {
        const expirationTime = new Date(motion.expires_at);
        const currentTime = new Date();
        if (currentTime > expirationTime) {
          alert('This motion has expired and is no longer accepting votes.');
          await loadLiveVotingData();
          return;
        }
      }

      await api.post('/api/vote/live-motions/vote', {
        motion_id: motionId,
        vote: vote,
        meeting_type: (tabType === 'committee' ? 'COMMITTEE' : 'BOD'),
        committee_id: (tabType === 'committee' ? (selectedCommittee?.id || selectedCommittee?.committee_id) : undefined)
      });
      await loadLiveVotingData();
      alert('Vote cast successfully!');
    } catch (error) {
      console.error('Error casting live vote:', error);
      
      // Provide more specific error messages
      if (error.response?.status === 404) {
        alert('Motion not found or has expired. The page will refresh to show current motions.');
        await loadLiveVotingData();
      } else if (error.response?.status === 400) {
        alert('Invalid vote data. Please try again.');
      } else if (error.response?.data?.detail) {
        alert(`Vote failed: ${error.response.data.detail}`);
      } else {
        alert('Failed to cast vote. Please check your connection and try again.');
      }
    }
  };

  const handleProposeMotion = async (motionId) => {
    try {
      await api.post(`/api/vote/live-motions/${motionId}/propose-second`, {
        proposer_id: user.id,
        meeting_type: (tabType === 'committee' ? 'COMMITTEE' : 'BOD')
      });
      
      await loadLiveVotingData();
      alert('✅ You have proposed this motion!');
    } catch (error) {
      console.error('Error proposing motion:', error);
      alert('Failed to propose motion: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSecondMotion = async (motionId) => {
    try {
      await api.post(`/api/vote/live-motions/${motionId}/propose-second`, {
        seconder_id: user.id,
        meeting_type: (tabType === 'committee' ? 'COMMITTEE' : 'BOD')
      });
      
      await loadLiveVotingData();
      alert('✅ You have seconded this motion!');
    } catch (error) {
      console.error('Error seconding motion:', error);
      alert('Failed to second motion: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleManualProposeSecond = async (motionId, type, userId) => {
    if (!userId) return;
    
    try {
      const payload = {
        meeting_type: (tabType === 'committee' ? 'COMMITTEE' : 'BOD')
      };
      
      if (type === 'proposer') {
        payload.proposer_id = userId;
      } else {
        payload.seconder_id = userId;
      }
      
      await api.post(`/api/vote/live-motions/${motionId}/propose-second`, payload);
      
      await loadLiveVotingData();
      alert(`✅ ${type === 'proposer' ? 'Proposer' : 'Seconder'} set successfully!`);
    } catch (error) {
      console.error(`Error setting ${type}:`, error);
      alert(`Failed to set ${type}: ` + (error.response?.data?.detail || error.message));
    }
  };

  // Debounce timer for discussion updates
  const discussionTimerRef = useRef({});

  const handleMotionDiscussionChange = async (motionId, discussionText) => {
    // Update local state immediately for responsive UI
    setActiveMotions(prevMotions => 
      prevMotions.map(m => 
        m.id === motionId ? { ...m, discussion: discussionText } : m
      )
    );

    // Clear existing timer for this motion
    if (discussionTimerRef.current[motionId]) {
      clearTimeout(discussionTimerRef.current[motionId]);
    }

    // Set new timer to save after 1 second of no typing
    discussionTimerRef.current[motionId] = setTimeout(async () => {
      try {
        await api.put(`/api/vote/live-motions/${motionId}/discussion`, {
          discussion: discussionText
        });
        console.log(`Discussion notes saved for motion ${motionId}`);
      } catch (error) {
        console.error('Error saving discussion notes:', error);
        // Don't alert on auto-save errors, just log them
      }
    }, 1000);
  };

  const handleProposeSecond = async (motionId, action) => {
    try {
      const motion = activeMotions.find(m => m.id === motionId);
      if (!motion) {
        alert('Motion not found. Please refresh the page.');
        return;
      }

      const endpoint = action === 'propose' ? 'propose' : 'second';
      await api.post(`/api/vote/live-motions/${motionId}/${endpoint}`, {
        meeting_type: (tabType === 'committee' ? 'COMMITTEE' : 'BOD'),
        committee_id: (tabType === 'committee' ? (selectedCommittee?.id || selectedCommittee?.committee_id) : undefined)
      });
      
      await loadLiveVotingData();
      alert(`Motion ${action}d successfully!`);
    } catch (error) {
      console.error(`Error ${action}ing motion:`, error);
      
      if (error.response?.status === 400) {
        alert(`Cannot ${action} motion: ${error.response.data?.detail || 'Invalid request'}`);
      } else if (error.response?.status === 403) {
        alert(`You don't have permission to ${action} this motion.`);
      } else if (error.response?.data?.detail) {
        alert(`Failed to ${action} motion: ${error.response.data.detail}`);
      } else {
        alert(`Failed to ${action} motion. Please try again.`);
      }
    }
  };

  // Export Functions
  const exportAttendance = async (attendance, boardMembers) => {
    const now = new Date().toISOString();
    const currentDate = formatLocal(now, hoaTimezone, 'MMM d, yyyy');
    const currentTime = formatLocal(now, hoaTimezone, 'h:mm a z');
    let attendanceReport = '';
    
    try {
      // Fetch complete attendance records - FILTER BY MEETING TYPE
      const response = await api.get(`/api/vote/attendance/all-records?meeting_type=${meetingTypeFilter}`);
      const allAttendanceRecords = response.data?.attendance_records || [];
      
      attendanceReport = `MEETING ATTENDANCE REPORT\n`;
      attendanceReport += `Generated: ${currentDate} at ${currentTime}\n`;
      attendanceReport += `HOA: ${hoaName}\n\n`;
      
      // Separate board members and homeowners
      const boardMemberRecords = allAttendanceRecords.filter(record => 
        record.type === 'board_member' || ['board_member', 'president', 'treasurer', 'secretary', 'vice_president'].includes(record.role)
      );
      const homeownerRecords = allAttendanceRecords.filter(record => 
        record.type === 'homeowner' || record.role === 'homeowner'
      );
      
      // Board Members Section
      attendanceReport += `BOARD MEMBERS:\n`;
      attendanceReport += `${'-'.repeat(60)}\n`;
      
      if (boardMemberRecords.length > 0) {
        boardMemberRecords.forEach(record => {
          const status = record.present ? 'PRESENT' : 'ABSENT';
          const role = record.role?.replace('_', ' ').toUpperCase() || 'BOARD MEMBER';
          attendanceReport += `${record.name.padEnd(25)} ${role.padEnd(15)} ${status}\n`;
        });
      } else {
        // Fallback to boardMembers array if no records available
        boardMembers.forEach(member => {
          const memberId = member.id || member.user_id;
          const status = attendance[memberId] === true ? 'PRESENT' : 
                         attendance[memberId] === false ? 'ABSENT' : 'UNKNOWN';
          attendanceReport += `${member.name.padEnd(25)} ${member.role?.replace('_', ' ').toUpperCase().padEnd(15)} ${status}\n`;
        });
      }
      
      // Homeowners Section
      if (homeownerRecords.length > 0) {
        attendanceReport += `\nHOMEOWNERS:\n`;
        attendanceReport += `${'-'.repeat(60)}\n`;
        
        homeownerRecords.forEach(record => {
          const status = record.present ? 'PRESENT' : 'ABSENT';
          const property = record.property ? ` (${record.property})` : '';
          attendanceReport += `${record.name.padEnd(25)} HOMEOWNER${property.padEnd(15)} ${status}\n`;
        });
      }
      
      // Summary
      const totalBoardPresent = boardMemberRecords.filter(r => r.present).length;
      const totalHomeownersPresent = homeownerRecords.filter(r => r.present).length;
      const totalPresent = totalBoardPresent + totalHomeownersPresent;
      const totalBoard = boardMemberRecords.length || boardMembers.length;
      const totalHomeowners = homeownerRecords.length;
      
      attendanceReport += `\nSUMMARY:\n`;
      attendanceReport += `${'-'.repeat(30)}\n`;
      attendanceReport += `Board Members Present: ${totalBoardPresent} of ${totalBoard}\n`;
      if (totalHomeowners > 0) {
        attendanceReport += `Homeowners Present: ${totalHomeownersPresent} of ${totalHomeowners}\n`;
      }
      attendanceReport += `Total Present: ${totalPresent}\n`;
      attendanceReport += `QUORUM: ${totalBoardPresent >= Math.ceil(totalBoard / 2) ? 'ACHIEVED' : 'NOT ACHIEVED'}\n`;
      
    } catch (error) {
      console.error('Error fetching complete attendance records:', error);
      
      // Fallback to original logic if API fails
      attendanceReport = `MEETING ATTENDANCE REPORT\n`;
      attendanceReport += `Generated: ${currentDate} at ${currentTime}\n`;
      attendanceReport += `HOA: ${hoaName}\n\n`;
      
      attendanceReport += `BOARD MEMBERS:\n`;
      attendanceReport += `${'-'.repeat(50)}\n`;
      
      boardMembers.forEach(member => {
        const memberId = member.id || member.user_id;
        const status = attendance[memberId] === true ? 'PRESENT' : 
                       attendance[memberId] === false ? 'ABSENT' : 'UNKNOWN';
        attendanceReport += `${member.name.padEnd(25)} ${member.role?.replace('_', ' ').toUpperCase().padEnd(15)} ${status}\n`;
      });
      
      const presentCount = Object.values(attendance).filter(status => status === true).length;
      const totalCount = boardMembers.length;
      attendanceReport += `\nSUMMARY: ${presentCount} of ${totalCount} board members present\n`;
      attendanceReport += `QUORUM: ${presentCount >= Math.ceil(totalCount / 2) ? 'ACHIEVED' : 'NOT ACHIEVED'}\n`;
    }
    
    // Download as text file
    const blob = new Blob([attendanceReport], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `meeting-attendance-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportMotionResults = (motions) => {
    const now = new Date().toISOString();
    const currentDate = formatLocal(now, hoaTimezone, 'MMM d, yyyy');
    const currentTime = formatLocal(now, hoaTimezone, 'h:mm a z');
    
    let motionReport = `MEETING MOTIONS & VOTING RESULTS\n`;
    motionReport += `Generated: ${currentDate} at ${currentTime}\n`;
    motionReport += `HOA: ${hoaName}\n\n`;
    
    motions.forEach((motion, index) => {
      motionReport += `MOTION #${index + 1}: ${motion.title}\n`;
      motionReport += `${'-'.repeat(50)}\n`;
      motionReport += `Description: ${motion.description || 'No description provided'}\n`;
      if (motion.discussion) {
        motionReport += `Discussion: ${motion.discussion}\n`;
      }
      if (motion.voting_rule === 'propose_second' && (motion.proposer_name || motion.seconder_name)) {
        motionReport += `Proposed by: ${motion.proposer_name || 'Not set'}\n`;
        motionReport += `Seconded by: ${motion.seconder_name || 'Not set'}\n`;
      }
      motionReport += `Status: ${motion.status.toUpperCase()}\n`;
      motionReport += `Required Quorum: ${motion.requires_quorum ? 'YES' : 'NO'}\n`;
      
      if (motion.votes || motion.total_counts) {
        const answerOpts = motion.answer_options || motion.answers || ['Yes', 'No'];
        const counts = motion.total_counts || motion.votes || {};
        const total = answerOpts.reduce((sum, opt) => sum + (counts[opt] || 0), 0);
        motionReport += `\nVOTING RESULTS:\n`;
        answerOpts.forEach(opt => {
          motionReport += `  ${opt}: ${counts[opt] || 0}\n`;
        });
        motionReport += `  Total Votes: ${total}\n`;
        
        if (motion.individual_votes) {
          const indVotes = motion.individual_votes || {};
          answerOpts.forEach(opt => {
            const voters = indVotes[opt] || [];
            if (voters.length > 0) {
              motionReport += `\n  ${opt}: ${voters.map(v => v.name).join(', ')}\n`;
            }
          });
        }
        
        if (total > 0) {
          const firstOpt = answerOpts[0];
          const secondOpt = answerOpts[1] || answerOpts[0];
          const firstPercent = Math.round(((counts[firstOpt] || 0) / total) * 100);
          motionReport += `  ${firstOpt} Rate: ${firstPercent}%\n`;
          
          const result = (counts[firstOpt] || 0) > (counts[secondOpt] || 0) ? 'PASSED' : 'FAILED';
          motionReport += `  RESULT: ${result}\n`;
        }
      }
      motionReport += `\n`;
    });
    
    // Download as text file
    const blob = new Blob([motionReport], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `meeting-motions-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    loadLiveVotingData();
  }, []);

  // Listen for attendance updates to refresh data
  useEffect(() => {
    const handleAttendanceUpdate = () => {
      console.log('Attendance update event received - refreshing data');
      loadLiveVotingData();
    };
    
    window.addEventListener('attendanceUpdated', handleAttendanceUpdate);
    
    return () => {
      window.removeEventListener('attendanceUpdated', handleAttendanceUpdate);
    };
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading live voting session...</p>
      </div>
    );
  }

  // Create portal modals to render at document.body level (avoids z-index issues)
  const rulesModal = showRuleSetsModal ? ReactDOM.createPortal(
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black/50" 
      style={{ zIndex: 9999 }}
      onClick={() => {
        console.log('Rules modal overlay clicked, closing');
        setShowRuleSetsModal(false);
      }}
    >
      <div 
        className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto" 
        onClick={e => {
          e.stopPropagation();
          console.log('Rules modal content clicked, staying open');
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-900">📋 Meeting Rules Manager</h3>
          <button onClick={() => setShowRuleSetsModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
        </div>

        <div className="space-y-6">
          {/* Current Active Rule Set */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="font-semibold text-purple-900 mb-2">Current Rule Set</h4>
            <p className="text-purple-700 font-medium">{activeRuleSetName}</p>
          </div>

          {/* Meeting Rules Display */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Active Meeting Rules:</h4>
            {localMeetingRules && localMeetingRules.length > 0 ? (
              <ul className="space-y-2">
                {localMeetingRules.map((rule, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-purple-600 font-bold">{idx + 1}.</span>
                    <span className="flex-1">{rule}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500 italic">No meeting rules configured.</p>
            )}
          </div>

          {/* Save Current Rules as New Set */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-3">Save Current Rules as Template</h4>
            <div className="flex gap-2">
              <input
                type="text"
                value={newRuleSetName}
                onChange={(e) => setNewRuleSetName(e.target.value)}
                placeholder="Enter template name (e.g., 'Board Meetings', 'Committee Rules')"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={async () => {
                  if (!newRuleSetName.trim()) {
                    alert('Please enter a name for this rule set');
                    return;
                  }
                  
                  const newRuleSet = {
                    id: Date.now().toString(),
                    name: newRuleSetName,
                    rules: localMeetingRules,
                    speakingTime: localSpeakingTimeMinutes,
                    createdAt: new Date().toISOString()
                  };
                  
                  const updatedSets = [...savedRuleSets, newRuleSet];
                  setSavedRuleSets(updatedSets);
                  localStorage.setItem('savedRuleSets', JSON.stringify(updatedSets));
                  
                  alert(`✅ Rule set "${newRuleSetName}" saved successfully!`);
                  setNewRuleSetName('');
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium whitespace-nowrap"
              >
                💾 Save Template
              </button>
            </div>
            <p className="text-xs text-blue-700 mt-2">
              Save the current rules as a reusable template for future meetings
            </p>
          </div>

          {/* Saved Rule Sets */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Saved Templates ({savedRuleSets.length})</h4>
            {savedRuleSets.length > 0 ? (
              <div className="space-y-2">
                {savedRuleSets.map((ruleSet) => (
                  <div key={ruleSet.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{ruleSet.name}</p>
                      <p className="text-xs text-gray-500">{ruleSet.rules?.length || 0} rules • Speaking time: {ruleSet.speakingTime}min</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          if (window.confirm(`Load "${ruleSet.name}" rules for this meeting?`)) {
                            setLocalMeetingRules(ruleSet.rules);
                            setLocalSpeakingTimeMinutes(ruleSet.speakingTime);
                            setActiveRuleSetName(ruleSet.name);
                            localStorage.setItem(getTabStateKey('meetingRules'), JSON.stringify(ruleSet.rules));
                            localStorage.setItem(getTabStateKey('speakingTimeMinutes'), ruleSet.speakingTime.toString());
                            alert(`✅ Loaded "${ruleSet.name}" rules`);
                          }
                        }}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                      >
                        Load
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete "${ruleSet.name}" template?`)) {
                            const updatedSets = savedRuleSets.filter(rs => rs.id !== ruleSet.id);
                            setSavedRuleSets(updatedSets);
                            localStorage.setItem('savedRuleSets', JSON.stringify(updatedSets));
                            alert(`🗑️ Deleted "${ruleSet.name}"`);
                          }
                        }}
                        className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic text-center py-4">
                No saved templates yet. Save your current rules to reuse them later.
              </p>
            )}
          </div>

          {/* Instructions */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-900 mb-2">💡 How to Use</h4>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• <strong>Save Template:</strong> Save current rules to reuse in future meetings</li>
              <li>• <strong>Load Template:</strong> Apply saved rules to the current meeting</li>
              <li>• <strong>Edit Rules:</strong> Close this modal and edit rules in the main meeting view</li>
              <li>• <strong>Templates are saved locally</strong> in your browser for quick access</li>
            </ul>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={() => setShowRuleSetsModal(false)}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  const saveLoadModal = showSaveLoadModal ? ReactDOM.createPortal(
    <div 
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" 
      style={{ zIndex: 9999 }}
      onClick={() => {
        console.log('Modal overlay clicked, closing');
        setShowSaveLoadModal(false);
      }}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" 
        onClick={e => {
          e.stopPropagation();
          console.log('Modal content clicked, staying open');
        }}
      >
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">💾 Meeting State Manager</h2>
            <button
              onClick={() => setShowSaveLoadModal(false)}
              className="text-white hover:bg-white/20 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
            >
              ✕
            </button>
          </div>
          <p className="text-purple-100 text-sm mt-2">Save and restore meeting states • Auto-expires after 30 days</p>
        </div>

        <div className="p-6 space-y-6">
          {/* Save Current State Section */}
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-5">
            <h3 className="text-lg font-semibold text-purple-900 mb-3 flex items-center gap-2">
              <span className="text-2xl">💾</span>
              Save Current State
            </h3>
            <p className="text-sm text-purple-700 mb-4">Save all motions, attendance, discussions, notes, and meeting data</p>
            
            <input
              type="text"
              value={saveStateName}
              onChange={(e) => setSaveStateName(e.target.value)}
              placeholder="e.g., Board Meeting Nov 7 - Draft"
              className="w-full px-4 py-3 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-3"
            />
            
            <button
              onClick={async () => {
                if (!saveStateName.trim()) {
                  alert('Please enter a name for this state');
                  return;
                }
                
                setSavingState(true);
                try {
                  // Fetch current discussions before saving
                  let currentDiscussions = [];
                  try {
                    const discussionsResp = await api.get(`/api/vote/discussions?meeting_type=${meetingTypeFilter}`);
                    currentDiscussions = discussionsResp.data?.discussions || [];
                  } catch (err) {
                    console.error('Error fetching discussions for save:', err);
                  }
                  
                  const meetingData = {
                    meeting_type: meetingTypeFilter,
                    committee_id: selectedCommittee?.id,
                    attendance: attendance,
                    meeting_start_time: meetingStartTime,
                    meeting_end_time: meetingEndTime,
                    motions: activeMotions,
                    discussions: currentDiscussions,
                    meeting_notes: liveMeetingNotes,
                    action_items: liveActionItems,
                    meeting_location: meetingLocation,
                    meeting_rules: localMeetingRules,
                    speaking_time_minutes: localSpeakingTimeMinutes,
                    proof_of_notice: proofOfNotice,
                    manual_prior_minutes: manualPriorMinutes,
                    preview_data: livePreviewData,
                    supporting_documents: supportingDocuments
                  };
                  
                  const response = await api.post('/api/meeting-states/save', {
                    name: saveStateName.trim(),
                    data: meetingData
                  });
                  
                  alert('✅ Meeting state saved successfully!\nExpires: ' + formatLocal(response.data.expires_at, hoaTimezone, 'MMM d, yyyy'));
                  setSaveStateName('');
                  loadSavedStates();
                } catch (error) {
                  console.error('Error saving state:', error);
                  alert('❌ Failed to save meeting state. Please try again.');
                } finally {
                  setSavingState(false);
                }
              }}
              disabled={savingState || !saveStateName.trim()}
              className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium transition-colors"
            >
              {savingState ? 'Saving...' : '💾 Save State'}
            </button>
          </div>

          {/* Load Saved States Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
            <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <span className="text-2xl">📂</span>
              Load Saved State
            </h3>
            
            <button
              onClick={loadSavedStates}
              className="mb-4 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm"
            >
              🔄 Refresh List
            </button>
            
            {savedStates.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p className="text-4xl mb-2">📭</p>
                <p>No saved states yet</p>
                <p className="text-sm">Save your current meeting to resume later</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {savedStates.map((state) => (
                  <div key={state.id} className="bg-white border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{state.name}</h4>
                        <div className="text-xs text-gray-500 mt-1 space-y-1">
                          <div>📅 Saved: {formatLocal(state.saved_at, hoaSettings?.timezone || 'US/Central', 'MMM d, yyyy h:mm a')}</div>
                          <div>📊 {state.motion_count} motion(s), {state.discussion_count} discussion(s)</div>
                          <div>⏰ Expires in {state.days_until_expiry} day(s)</div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 ml-3">
                        <button
                          onClick={async () => {
                            if (window.confirm(`Load "${state.name}"?\n\nThis will replace your current meeting data.`)) {
                              setLoadingState(true);
                              try {
                                const response = await api.get(`/api/meeting-states/${state.id}`);
                                const data = response.data.state.data;
                                
                                setAttendance(data.attendance || []);
                                setActiveMotions(data.motions || []);
                                setLiveMeetingNotes(data.meeting_notes || '');
                                setLiveActionItems(data.action_items || []);
                                setMeetingLocation(data.meeting_location || '');
                                setLocalMeetingRules(data.meeting_rules || []);
                                setLocalSpeakingTimeMinutes(data.speaking_time_minutes || 3);
                                setProofOfNotice(data.proof_of_notice || {uploaded: false});
                                setManualPriorMinutes(data.manual_prior_minutes || '');
                                setLivePreviewData(data.preview_data || null);
                                setSupportingDocuments(data.supporting_documents || []);
                                
                                // Restore meeting times if they exist
                                if (data.meeting_start_time) {
                                  setMeetingStartTime(new Date(data.meeting_start_time));
                                }
                                if (data.meeting_end_time) {
                                  setMeetingEndTime(new Date(data.meeting_end_time));
                                }
                                
                                alert(`✅ State "${state.name}" loaded successfully!`);
                                setShowSaveLoadModal(false);
                              } catch (error) {
                                console.error('Error loading state:', error);
                                alert('❌ Failed to load meeting state. It may have expired.');
                              } finally {
                                setLoadingState(false);
                              }
                            }
                          }}
                          disabled={loadingState}
                          className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                        >
                          📂 Load
                        </button>
                        <button
                          onClick={async () => {
                            if (window.confirm(`Delete "${state.name}"?\n\nThis action cannot be undone.`)) {
                              try {
                                await api.delete(`/api/meeting-states/${state.id}`);
                                alert('✅ State deleted');
                                loadSavedStates();
                              } catch (error) {
                                console.error('Error deleting state:', error);
                                alert('❌ Failed to delete state');
                              }
                            }
                          }}
                          className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info Section */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <h4 className="font-semibold text-yellow-900 mb-2">ℹ️ How It Works</h4>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• <strong>Save</strong>: Store your current meeting progress</li>
              <li>• <strong>Load</strong>: Resume a saved meeting anytime</li>
              <li>• <strong>Expires</strong>: Saved states automatically delete after 30 days</li>
              <li>• <strong>Not Saved</strong>: File attachments (can be re-added)</li>
            </ul>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t p-4 rounded-b-2xl">
          <button
            onClick={() => setShowSaveLoadModal(false)}
            className="w-full px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      {rulesModal}
      {saveLoadModal}
      <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 shadow-sm border border-purple-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-full">
              <div className="w-6 h-6 text-purple-600">⚡</div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Live Board Voting</h3>
              <p className="text-gray-600">Real-time voting for board members on immediate issues</p>
            </div>
          </div>
          
          <button
            onClick={() => setShowCreateMotion(true)}
            disabled={!canInteract}
            className={`px-4 py-2 ${canInteract ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-300 cursor-not-allowed'} text-white rounded-lg transition-colors`}
            title={!canInteract ? 'You do not have permission to interact with live voting' : ''}
          >
            New Motions
          </button>
        </div>
      </div>

      {/* Meeting Session Controls */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">🕐 Meeting Session</h4>
        
        <div className="grid md:grid-cols-5 gap-4">
          {/* Meeting Type Selector */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Type</label>
            <button
              onClick={() => setShowMeetingTypeDropdown(!showMeetingTypeDropdown)}
              disabled={meetingInProgress}
              className={`w-full px-3 py-2 text-left border rounded-lg bg-white shadow-sm flex items-center justify-between ${
                meetingInProgress ? 'bg-gray-100 cursor-not-allowed' : 'hover:bg-gray-50'
              }`}
            >
              <span className="text-sm">{meetingTypes.find(t => t.value === meetingType)?.label || 'Select Type'}</span>
              <span className="text-gray-400">▼</span>
            </button>
            
            {showMeetingTypeDropdown && !meetingInProgress && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {meetingTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => {
                      setMeetingType(type.value);
                      setShowMeetingTypeDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Roll Forward Button */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Roll Forward</label>
            <button
              onClick={() => rollForwardItems(meetingType)}
              disabled={meetingInProgress}
              className={`w-full px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
                meetingInProgress 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-orange-600 text-white hover:bg-orange-700'
              }`}
            >
              🔄 Roll Forward
            </button>
          </div>
          
          {/* Meeting Status */}
          <div className="text-center">
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium ${
              meetingInProgress ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              <div className={`w-2 h-2 rounded-full mr-2 ${
                meetingInProgress ? 'bg-green-500' : 'bg-gray-400'
              }`}></div>
              {meetingInProgress ? 'In Progress' : 'Not Started'}
            </div>
            {meetingStartTime && (
              <div className="mt-2 text-sm text-gray-600">
                Called to Order: {formatLocal(meetingStartTime.toISOString(), hoaTimezone, 'h:mm a z')}
              </div>
            )}
            {meetingEndTime && (
              <div className="mt-1 text-sm text-gray-600">
                Adjourned: {formatLocal(meetingEndTime.toISOString(), hoaTimezone, 'h:mm a z')}
              </div>
            )}
          </div>
          
          {/* Start Meeting Button */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Call to Order</label>
            <button
              onClick={startMeeting}
              disabled={meetingInProgress || !canInteract}
              className={`w-full px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
                (meetingInProgress || !canInteract)
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
              title={!canInteract ? 'You do not have permission to interact with live voting' : ''}
            >
              ▶️ Call Meeting to Order
            </button>
          </div>
          
          {/* End Meeting Button */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Adjourn</label>
            <button
              onClick={endMeeting}
              disabled={!meetingInProgress || !canInteract}
              className={`w-full px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
                (!meetingInProgress || !canInteract)
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
              title={!canInteract ? 'You do not have permission to interact with live voting' : ''}
            >
              ⏹️ Adjourn Meeting
            </button>
          </div>
        </div>
      </div>
      
      {/* Meeting Location */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">📍 Meeting Location</h4>
        <input
          type="text"
          value={meetingLocation}
          onChange={(e) => setMeetingLocation(e.target.value)}
          placeholder="Enter meeting location (e.g., Community Center, Virtual via Zoom, etc.)"
          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          disabled={!canInteract}
        />
        <p className="text-xs text-gray-500 mt-2">
          This location will appear in meeting minutes, approvals, and all exported documents
        </p>
      </div>
      
      {/* Supporting Documents/Attachments */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">📎 Supporting Documents</h4>
        <p className="text-sm text-gray-600 mb-4">
          Upload documents to attach as supporting materials for this meeting (e.g., financial reports, proposals, etc.)
        </p>
        
        {/* Upload Button */}
        <div className="mb-4">
          <input
            type="file"
            id="supporting-doc-upload"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
            onChange={handleDocumentUpload}
            className="hidden"
            disabled={!canInteract || uploadingDoc}
          />
          <label
            htmlFor="supporting-doc-upload"
            className={`inline-block px-4 py-2 rounded-lg font-medium transition-colors cursor-pointer ${
              canInteract && !uploadingDoc
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {uploadingDoc ? '⏳ Uploading...' : '📎 Upload Document'}
          </label>
          <p className="text-xs text-gray-500 mt-2">
            Max 10MB. Supported: PDF, DOC, DOCX, XLS, XLSX, Images
          </p>
        </div>
        
        {/* List of Uploaded Documents - Enhanced for Packet Building */}
        {supportingDocuments.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-700">Attached Documents ({supportingDocuments.length}):</p>
              <p className="text-xs text-gray-500">Use toggles to select which documents to include in packet</p>
            </div>
            
            {supportingDocuments.map((doc, index) => {
              // Determine file type and badge color
              const ext = doc.file_name.split('.').pop().toLowerCase();
              let fileTypeBadge = '';
              let badgeColor = '';
              
              if (['pdf'].includes(ext)) {
                fileTypeBadge = 'PDF';
                badgeColor = 'bg-red-100 text-red-700';
              } else if (['doc', 'docx'].includes(ext)) {
                fileTypeBadge = 'DOC';
                badgeColor = 'bg-blue-100 text-blue-700';
              } else if (['xls', 'xlsx'].includes(ext)) {
                fileTypeBadge = 'XLS';
                badgeColor = 'bg-green-100 text-green-700';
              } else if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
                fileTypeBadge = 'IMG';
                badgeColor = 'bg-purple-100 text-purple-700';
              } else {
                fileTypeBadge = ext.toUpperCase();
                badgeColor = 'bg-gray-100 text-gray-700';
              }
              
              return (
                <div key={doc.id} className="flex items-center gap-3 p-4 bg-white rounded-xl border-2 border-gray-200 hover:border-blue-300 transition-all shadow-sm">
                  {/* File Type Badge */}
                  <div className={`px-3 py-1 rounded-lg text-xs font-bold ${badgeColor}`}>
                    {fileTypeBadge}
                  </div>
                  
                  {/* File Info */}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{doc.file_name}</p>
                    <p className="text-xs text-gray-500">
                      {doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB • ` : ''}Uploaded {formatLocal(doc.uploaded_at, hoaTimezone, 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                  
                  {/* Reorder Buttons */}
                  <div className="flex flex-col gap-1">
                    {index > 0 && (
                      <button
                        onClick={() => {
                          const newDocs = [...supportingDocuments];
                          [newDocs[index], newDocs[index - 1]] = [newDocs[index - 1], newDocs[index]];
                          setSupportingDocuments(newDocs);
                        }}
                        className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded transition-colors"
                        title="Move up"
                      >
                        ▲
                      </button>
                    )}
                    {index < supportingDocuments.length - 1 && (
                      <button
                        onClick={() => {
                          const newDocs = [...supportingDocuments];
                          [newDocs[index], newDocs[index + 1]] = [newDocs[index + 1], newDocs[index]];
                          setSupportingDocuments(newDocs);
                        }}
                        className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded transition-colors"
                        title="Move down"
                      >
                        ▼
                      </button>
                    )}
                  </div>
                  
                  {/* Include Toggle */}
                  <label className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={doc.includeInPacket !== false}
                      onChange={(e) => {
                        const newDocs = supportingDocuments.map(d => 
                          d.id === doc.id ? { ...d, includeInPacket: e.target.checked } : d
                        );
                        setSupportingDocuments(newDocs);
                      }}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-xs font-medium text-blue-700">
                      {doc.includeInPacket !== false ? 'Included' : 'Excluded'}
                    </span>
                  </label>
                  
                  {/* Remove Button */}
                  <button
                    onClick={() => removeSupportingDocument(doc.id)}
                    className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium"
                    disabled={!canInteract}
                    title="Remove document"
                  >
                    🗑️
                  </button>
                </div>
              );
            })}
            
            {/* Combine Documents Button */}
            <div className="mt-6 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h5 className="text-lg font-bold text-gray-900">📎 Combine Documents</h5>
                  <p className="text-sm text-gray-600 mt-1">
                    Merge all selected attachments into a single PDF (converts DOCX, images, etc.)
                  </p>
                </div>
              </div>
              
              {!builtPacket ? (
                <>
                  <div className="flex gap-3">
                    <button
                      onClick={() => buildMeetingPacket()}
                      disabled={buildingPacket}
                      className={`flex-1 px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105 ${
                        !buildingPacket
                          ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg hover:shadow-xl'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {buildingPacket ? '⏳ Combining...' : '📎 Combine into Single PDF'}
                    </button>
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    ✨ Merges selected documents into one PDF • Available anytime • Perfect for distribution
                  </p>
                </>
              ) : (
                <div className="space-y-4">
                  {/* Success Message */}
                  <div className="p-4 bg-green-50 border-2 border-green-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">✅</span>
                      <div className="flex-1">
                        <p className="font-bold text-green-900">Packet Built Successfully!</p>
                        <p className="text-sm text-green-700 mt-1">
                          Total Pages: {builtPacket.stats?.total_pages} • Documents: {builtPacket.stats?.converted_files + 1}
                          {builtPacket.stats?.failed_files > 0 && ` • Failed: ${builtPacket.stats.failed_files}`}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Download Buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${builtPacket.download_url}?token=${localStorage.getItem('hoa_token')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                    >
                      📥 Download Packet
                    </a>
                    
                    <button
                      onClick={() => {
                        window.open(`${process.env.REACT_APP_BACKEND_URL}${builtPacket.download_url}?token=${localStorage.getItem('hoa_token')}`, '_blank');
                      }}
                      className="flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                    >
                      🖨️ Print Packet
                    </button>
                  </div>
                  
                  {/* PDF Preview */}
                  <div className="mt-6 border rounded-lg overflow-hidden bg-gray-50">
                    <div className="bg-gray-100 px-4 py-2 border-b flex items-center justify-between">
                      <h4 className="font-semibold text-gray-700">📄 PDF Preview</h4>
                      <p className="text-sm text-gray-600">Pages: {builtPacket.stats?.total_pages || '?'}</p>
                    </div>
                    <iframe
                      src={`${process.env.REACT_APP_BACKEND_URL}${builtPacket.download_url}?token=${localStorage.getItem('hoa_token')}`}
                      className="w-full h-[600px] bg-white"
                      title="Combined Documents Preview"
                    />
                  </div>
                  
                  {/* Build Another Packet Button */}
                  <button
                    onClick={() => setBuiltPacket(null)}
                    className="w-full px-4 py-2 mt-4 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    🔄 Combine Different Documents
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        
        {supportingDocuments.length === 0 && (
          <p className="text-sm text-gray-500 italic">No supporting documents attached yet</p>
        )}
      </div>

      {/* Prior Meeting Minutes Approval */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">📋 Approval of Prior Meeting Minutes</h4>
        
        <div className="rounded-lg border p-4 mb-4 bg-gray-50">
          <label className="block text-sm font-semibold mb-2">Prior Minutes Selection</label>
          <select
            className="w-full border rounded-lg px-3 py-2 mb-3 bg-white"
            value={selectedPriorMinutesId || ""}
            onChange={(e) => setSelectedPriorMinutesId(e.target.value || null)}
          >
            <option value="">— Select prior minutes —</option>
            {priorMinutes.map(m => (
              <option key={m.id} value={m.id}>
                {m.title} ({m.created_at ? formatLocal(m.created_at, hoaTimezone, 'MMM d, yyyy') : 'No date'})
              </option>
            ))}
          </select>
          
          <div className="flex gap-3">
            <button 
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                canInteract && selectedPriorMinutesId 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              onClick={addPriorMinutesSection}
              disabled={!canInteract || !selectedPriorMinutesId}
              title={!canInteract ? 'You do not have permission to interact with live voting' : ''}
            >
              ➕ Insert into Preview
            </button>
            <button 
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedPriorMinutesId
                  ? (selectedPriorMinutesStatus === 'approved' 
                      ? 'bg-green-600 text-white hover:bg-green-700' 
                      : 'bg-yellow-600 text-white hover:bg-yellow-700')
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              onClick={() => {
                if (selectedPriorMinutesId) {
                  setSelectedPriorMinutesStatus(selectedPriorMinutesStatus === 'pending' ? 'approved' : 'pending');
                }
              }}
              disabled={!selectedPriorMinutesId}
              title="Toggle approval status"
            >
              {selectedPriorMinutesStatus === 'approved' ? '✓ Approved' : '⏳ Pending'}
            </button>
            <button 
              className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              onClick={() => {
                setSelectedPriorMinutesId(null);
                setSelectedPriorMinutesStatus('pending'); // Reset status when clearing
              }}
            >
              Clear Selection
            </button>
          </div>
          
          <p className="text-xs text-gray-600 mt-2">
            Select prior meeting minutes to add approval section to the meeting preview
          </p>

          
          {/* Manual Prior Minutes Notes */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Prior Meeting Minutes Notes
            </label>
            <textarea
              value={manualPriorMinutes}
              onChange={(e) => setManualPriorMinutes(e.target.value)}
              placeholder="Enter any additional prior meeting minutes text (optional)..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
              rows="4"
            />
            <p className="text-xs text-gray-500 mt-1">
              Add manual notes if minutes are not in the system or for additional context
            </p>
          </div>

        </div>


      {/* Proof of Notice Section */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">📢 Proof of Notice</h4>
        
        <div className="space-y-4">
          <p className="text-sm text-gray-600 mb-3">
            Select how notice was provided for this meeting:
          </p>
          
          {/* Posted on Events and Emailed */}
          <label className={`flex items-start space-x-3 p-3 rounded-lg ${canInteract ? 'hover:bg-gray-50 cursor-pointer' : 'bg-gray-50 cursor-not-allowed'}`}>
            <input
              type="checkbox"
              checked={proofOfNotice.postedOnEvents && proofOfNotice.emailSent}
              onChange={(e) => setProofOfNotice(prev => ({
                ...prev,
                postedOnEvents: e.target.checked,
                emailSent: e.target.checked
              }))}
              className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              disabled={!canInteract}
            />
            <div className="flex-1">
              <span className="font-medium text-gray-900">Posted on events calendar and emailed</span>
              <p className="text-xs text-gray-500 mt-1">Notice was posted to the HOA events calendar and sent via email to all homeowners</p>
            </div>
          </label>
          
          {/* Mail Packet */}
          <label className={`flex items-start space-x-3 p-3 rounded-lg ${canInteract ? 'hover:bg-gray-50 cursor-pointer' : 'bg-gray-50 cursor-not-allowed'}`}>
            <input
              type="checkbox"
              checked={proofOfNotice.mailPacket}
              onChange={(e) => setProofOfNotice(prev => ({
                ...prev,
                mailPacket: e.target.checked
              }))}
              className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              disabled={!canInteract}
            />
            <div className="flex-1">
              <span className="font-medium text-gray-900">Sent via mail packet on</span>
              {proofOfNotice.mailPacket && (
                <div className="mt-2">
                  <input
                    type="date"
                    value={proofOfNotice.mailPacketDate}
                    onChange={(e) => setProofOfNotice(prev => ({
                      ...prev,
                      mailPacketDate: e.target.value
                    }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={!canInteract}
                  />
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">Notice was included in a physical mail packet sent to homeowners</p>
            </div>
          </label>
          
          {/* Other */}
          <label className={`flex items-start space-x-3 p-3 rounded-lg ${canInteract ? 'hover:bg-gray-50 cursor-pointer' : 'bg-gray-50 cursor-not-allowed'}`}>
            <input
              type="checkbox"
              checked={proofOfNotice.other}
              onChange={(e) => setProofOfNotice(prev => ({
                ...prev,
                other: e.target.checked
              }))}
              className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              disabled={!canInteract}
            />
            <div className="flex-1">
              <span className="font-medium text-gray-900">Other</span>
              {proofOfNotice.other && (
                <div className="mt-2">
                  <textarea
                    value={proofOfNotice.otherNotes}
                    onChange={(e) => setProofOfNotice(prev => ({
                      ...prev,
                      otherNotes: e.target.value
                    }))}
                    placeholder="Describe how notice was provided..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
                    rows="3"
                    disabled={!canInteract}
                  />
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">Specify any other method used to provide notice</p>
            </div>
          </label>
        </div>
      </div>


      {/* Meeting Rules Section */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-900">📋 Meeting Rules</h4>
          <button
            onClick={() => {
              if (!canInteract) return;
              const newRule = prompt('Enter new meeting rule:');
              if (newRule && newRule.trim()) {
                setLocalMeetingRules([...localMeetingRules, newRule.trim()]);
              }
            }}
            className={`px-3 py-1 text-white text-sm rounded-lg transition-colors ${
              canInteract ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-300 cursor-not-allowed'
            }`}
            disabled={!canInteract}
          >
            + Add Rule
          </button>
        </div>
        
        <div className="space-y-3">
          {localMeetingRules && localMeetingRules.length > 0 ? (
            localMeetingRules.map((rule, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg group">
                <span className="font-semibold text-purple-600 mt-0.5 min-w-[24px]">{index + 1}.</span>
                <p className="flex-1 text-sm text-gray-900">{rule}</p>
                {canInteract && (
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => {
                        const updatedRule = prompt('Edit rule:', rule);
                        if (updatedRule !== null && updatedRule.trim()) {
                          const newRules = [...localMeetingRules];
                          newRules[index] = updatedRule.trim();
                          setLocalMeetingRules(newRules);
                        }
                      }}
                      className="px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs rounded transition-colors"
                      title="Edit rule"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Delete rule ${index + 1}?`)) {
                          setLocalMeetingRules(localMeetingRules.filter((_, i) => i !== index));
                        }
                      }}
                      className="px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 text-xs rounded transition-colors"
                      title="Delete rule"
                    >
                      🗑️
                    </button>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500 italic mb-2">No meeting rules configured.</p>
              {canInteract && (
                <button
                  onClick={() => {
                    const newRule = prompt('Enter first meeting rule:');
                    if (newRule && newRule.trim()) {
                      setLocalMeetingRules([newRule.trim()]);
                    }
                  }}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg transition-colors"
                >
                  Add First Rule
                </button>
              )}
            </div>
          )}
          
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800">
              <strong>Tip:</strong> Add a rule for speaking time limit. Example: "Speaking time limited to 3 minutes per person (Board members have no time restriction)". Rules are loaded from HOA Settings but changes here only affect this specific meeting.
            </p>
          </div>
        </div>
      </div>


      </div>

      {/* Attendance Tracking */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">📋 Meeting Attendance</h4>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Quick Attendance */}
          <div>
            <h5 className="font-medium text-gray-900 mb-3">Meeting Roll Call</h5>
            <button
              onClick={() => setShowRollCall(true)}
              disabled={!canInteract}
              className={`w-full px-4 py-3 ${canInteract ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-300 cursor-not-allowed'} text-white rounded-lg transition-colors flex items-center justify-center gap-2`}
              title={!canInteract ? 'You do not have permission to interact with live voting' : ''}
            >
              📋 Conduct Roll Call
            </button>
            <p className="text-xs text-gray-600 mt-2">
              Mark attendance for all board members and homeowners
            </p>
          </div>

          {/* Current Attendance */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h5 className="font-medium text-gray-900">Current Attendance</h5>
              <button
                onClick={() => exportAttendance(attendance, boardMembers)}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
              >
                📋 Export
              </button>
            </div>
            
            {/* Live Attendance List */}
            <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
              <div className="space-y-2">
                <div className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">Board Members</div>
                {boardMembers.map((member, index) => {
                  const memberId = member.id || member.user_id;
                  const attendanceStatus = attendance[memberId];
                  const isPresent = attendanceStatus === true;
                  const isAbsent = attendanceStatus === false;
                  
                  return (
                    <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          isPresent ? 'bg-green-500' : isAbsent ? 'bg-red-500' : 'bg-gray-300'
                        }`}></div>
                        <span className="text-sm font-medium">{member.name}</span>
                        <span className="text-xs text-gray-500 px-2 py-1 bg-blue-100 rounded">
                          {member.role?.replace('_', ' ').toUpperCase() || 'BOARD'}
                        </span>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        isPresent ? 'bg-green-100 text-green-800' : 
                        isAbsent ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {isPresent ? 'Present' : isAbsent ? 'Absent' : 'Unknown'}
                      </span>
                    </div>
                  );
                })}
                
                {/* Homeowners Section */}
                <LiveHomeownersList attendance={attendance} />
              </div>
              
              {/* Summary */}
              <div className="mt-4 pt-3 border-gray-200">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-xl font-bold text-green-600">
                      {Object.values(attendance).filter(status => status === true).length}
                    </div>
                    <div className="text-xs text-gray-600">Present</div>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-gray-600">
                      {Object.keys(attendance).length}
                    </div>
                    <div className="text-xs text-gray-600">Total</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active Motions */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-900">🗳️ Active Motions</h4>
          {activeMotions.length > 0 && (
            <button
              onClick={() => exportMotionResults(activeMotions)}
              className="px-3 py-1 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 transition-colors"
            >
              📊 Export Results
            </button>
          )}
        </div>
        
        {activeMotions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">⚡</div>
            <p>No active motions. Create new motions to start live voting.</p>
            <button
              onClick={createTestMotion}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              🧪 Create Test Motion
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {activeMotions.map((motion) => (
              <div key={motion.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1">
                    <h5 className="font-medium text-gray-900">{motion.title}</h5>
                    <div className="flex items-center gap-2 mt-1">
                      {/* Voting Rule Display */}
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        motion.voting_rule === 'unanimous' 
                          ? 'bg-orange-100 text-orange-800' 
                          : motion.voting_rule === 'propose_second'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {motion.voting_rule === 'unanimous' ? '🤝 Unanimous' : motion.voting_rule === 'propose_second' ? '✋ Propose & Second' : '📊 Majority'}
                      </span>
                      
                      {/* Motion Result Status */}
                      {motion.result && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          motion.result.status === 'passed' ? 'bg-green-100 text-green-800' :
                          motion.result.status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {motion.result.status === 'passed' ? '✅ Passed' :
                           motion.result.status === 'failed' ? '❌ Failed' :
                           '⏳ Pending'}
                        </span>
                      )}
                    </div>
                    {/* Result Reason */}
                    {motion.result && motion.result.reason && (
                      <p className="text-xs text-gray-600 mt-1">{motion.result.reason}</p>
                    )}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    motion.status === 'voting' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {motion.status === 'voting' ? 'Live' : motion.status}
                  </span>
                </div>
                
                {motion.description && (
                  <p className="text-gray-700 mb-4">{motion.description}</p>
                )}

                {/* Discussion Notes Section - Secretary can add notes during meeting */}
                <div className="mb-4 border-t border-gray-200 pt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    📝 Discussion Notes (Secretary)
                  </label>
                  <textarea
                    value={motion.discussion || ''}
                    onChange={(e) => handleMotionDiscussionChange(motion.id, e.target.value)}
                    placeholder="Add discussion notes as members discuss this motion during the meeting... (editable anytime)"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    These notes will appear in preview minutes, PDF, TXT, approvals, archive, and documents
                  </p>
                </div>

                {motion.status === 'voting' && (
                  <div className="space-y-3">
                    {/* Voting Buttons */}
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        onClick={() => castLiveVote(motion.id, 'approve')}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        ✅ Approve
                      </button>
                      <button
                        onClick={() => castLiveVote(motion.id, 'oppose')}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        ❌ Oppose
                      </button>
                      <button
                        onClick={() => castLiveVote(motion.id, 'abstain')}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                      >
                        ⏸️ Abstain
                      </button>
                    </div>

                    {/* Live Results */}
                    {(motion.votes || motion.total_counts) && (() => {
                      const answerOpts = motion.answer_options || motion.answers || ['Yes', 'No'];
                      const counts = motion.total_counts || motion.votes || {};
                      return (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="grid gap-4 text-center" style={{gridTemplateColumns: `repeat(${answerOpts.length}, 1fr)`}}>
                          {answerOpts.map((opt, idx) => (
                            <div key={opt}>
                              <div className={`text-xl font-bold ${idx === 0 ? 'text-green-600' : idx === 1 ? 'text-red-600' : 'text-gray-600'}`}>{counts[opt] || 0}</div>
                              <div className="text-xs text-gray-600">{opt}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      );
                    })()}

                    {/* Propose & Second Selection (for propose_second voting rule) */}
                    {motion.voting_rule === 'propose_second' && (
                      <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                        <h5 className="font-medium text-purple-900 mb-3">✋ Propose & Second Tracking</h5>
                        
                        {/* Display current status */}
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <div className="text-sm font-medium text-purple-800">Proposed by:</div>
                            <div className="text-sm text-purple-600">
                              {motion.proposer_name || motion.proposed_by || 'Not yet proposed'}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm font-medium text-purple-800">Seconded by:</div>
                            <div className="text-sm text-purple-600">
                              {motion.seconder_name || motion.seconded_by || (motion.proposer_id ? 'Waiting for second' : 'Waiting for proposal first')}
                            </div>
                          </div>
                        </div>
                        
                        {/* Interactive buttons for board members to click */}
                        {canInteract && (
                          <div className="space-y-2">
                            {!motion.proposer_id && (
                              <button
                                onClick={() => handleProposeMotion(motion.id)}
                                className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                              >
                                👆 I Propose This Motion
                              </button>
                            )}
                            
                            {motion.proposer_id && !motion.seconder_id && (
                              <button
                                onClick={() => handleSecondMotion(motion.id)}
                                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                              >
                                ✌️ I Second This Motion
                              </button>
                            )}
                            
                            {motion.proposer_id && motion.seconder_id && (
                              <div className="text-center text-sm text-green-700 font-medium py-2 bg-green-50 rounded">
                                ✅ Motion Proposed & Seconded - Ready to Proceed
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Secretary Manual Override */}
                        <div className="mt-3 pt-3 border-t border-purple-200">
                          <details className="text-sm">
                            <summary className="cursor-pointer text-purple-700 font-medium hover:text-purple-900">
                              📝 Secretary Override (Manual Selection)
                            </summary>
                            <div className="mt-2 space-y-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Proposer:</label>
                                <select
                                  value={motion.proposer_id || ''}
                                  onChange={(e) => handleManualProposeSecond(motion.id, 'proposer', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                                >
                                  <option value="">Select proposer...</option>
                                  {boardMembers.map(member => (
                                    <option key={member.id || member.user_id} value={member.id || member.user_id}>
                                      {member.name} - {member.role}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Seconder:</label>
                                <select
                                  value={motion.seconder_id || ''}
                                  onChange={(e) => handleManualProposeSecond(motion.id, 'seconder', e.target.value)}
                                  disabled={!motion.proposer_id}
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100"
                                >
                                  <option value="">Select seconder...</option>
                                  {boardMembers.filter(m => (m.id || m.user_id) !== motion.proposer_id).map(member => (
                                    <option key={member.id || member.user_id} value={member.id || member.user_id}>
                                      {member.name} - {member.role}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              {!motion.proposer_id && (
                                <p className="text-xs text-gray-500 italic">Select proposer first before selecting seconder</p>
                              )}
                            </div>
                          </details>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Other Discussions Section */}
      <OtherDiscussionsSection 
        boardMembers={boardMembers}
        onRefresh={loadLiveVotingData}
        refreshTrigger={refreshTrigger}
        meetingType={meetingTypeFilter}
        tabType={tabType}
        hoaName={hoaName}
        hoaTimezone={hoaTimezone}
        discussionPropertyDisplay={discussionPropertyDisplay}
      />

      {/* Master Meeting Minutes Export */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-6 shadow-sm border border-green-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-full">
              <div className="w-6 h-6 text-green-600">📋</div>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-900">Meeting Minutes Export & Archive</h4>
              <p className="text-sm text-gray-600">Generate comprehensive meeting minutes and archive session data</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 relative z-10">
            <button
              onClick={() => showMeetingMinutesPreview(attendance, boardMembers, activeMotions)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2 w-full"
            >
              📄 Preview & Export Minutes
            </button>
            
            <button
              onClick={() => {
                const confirmClear = window.confirm(
                  'Clear Current Session?\n\n' +
                  'This will:\n' +
                  '• Clear all current meeting data (attendance, motions, discussions)\n' +
                  '• Reset the session for a new meeting\n' +
                  '• NOT save any data permanently\n\n' +
                  '⚠️ WARNING: All current session data will be lost!\n\n' +
                  'Continue?'
                );
                
                if (confirmClear) {
                  // Ask about meeting rules preservation
                  const keepRules = window.confirm(
                    '📋 Meeting Rules Preference\n\n' +
                    'Do you want to KEEP the current meeting rules for future meetings?\n\n' +
                    '✅ Click OK to KEEP current rules\n' +
                    '❌ Click Cancel to RESET to standard rules\n\n' +
                    '(This only affects meeting rules - all session data will be cleared)'
                  );
                  
                  api.post('/api/vote/clear-session', { 
                    meeting_type: meetingTypeFilter,
                    clear_all: false  // Each tab clears only its own data
                  }).then(() => {
                    // Handle rules based on user choice
                    if (!keepRules) {
                      // User chose to reset rules - clear localStorage and reset to standard
                      localStorage.removeItem(getTabStateKey('meetingRules'));
                      setLocalMeetingRules(meetingRules); // Reset to standard rules from HOA settings
                      alert(`✅ ${meetingTypeFilter} meeting cleared and rules reset to standard - other meetings preserved`);
                    } else {
                      // User chose to keep rules - do nothing, rules stay in localStorage
                      alert(`✅ ${meetingTypeFilter} meeting cleared with rules preserved - other meetings preserved`);
                    }
                    
                    // Clear ALL frontend state for meeting data
                    setMeetingStartTime(null);
                    setMeetingEndTime(null);
                    setLiveMeetingNotes('');
                    setLiveActionItems([]);
                    setManualPriorMinutes('');
                    setMeetingLocation('');
                    setAttendance([]);
                    setProofOfNotice({ uploaded: false });
                    setLivePreviewData(null);
                    setSupportingDocuments([]);
                    
                    // Clear localStorage for all meeting-related data
                    localStorage.removeItem(getTabStateKey('manualPriorMinutes'));
                    localStorage.removeItem(getTabStateKey('meetingLocation'));
                    localStorage.removeItem(getTabStateKey('meetingNotes'));
                    localStorage.removeItem(getTabStateKey('actionItems'));
                    
                    loadLiveVotingData();
                    setRefreshTrigger(prev => prev + 1);
                  }).catch(error => {
                    console.error('Error:', error);
                    alert('❌ Failed to clear.');
                  });
                }
              }}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium flex items-center justify-center gap-2 w-full"
            >
              🗑️ Clear Session
            </button>
            
            {console.log('[DEBUG] Live Voting Buttons - isBoard:', isBoard, 'canInteract:', canInteract, 'meetingTypeFilter:', meetingTypeFilter, 'tabType:', tabType)}
            
            {/* Show Rules and Save/Load for board members on BOD tab, or committee members with interact permission on Committee tab */}
            {((tabType === 'bod' && isBoard) || (tabType === 'committee' && canInteract)) && (
              <button
                onClick={() => {
                  console.log('Rules button clicked!');
                  setShowRuleSetsModal(true);
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center justify-center gap-2 cursor-pointer w-full"
                title="Save and manage meeting rules sets"
                style={{ pointerEvents: 'auto' }}
              >
                📋 Custom Rules
              </button>
            )}
            
            {((tabType === 'bod' && isBoard) || (tabType === 'committee' && canInteract)) && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Save/Load State button clicked!');
                  setShowSaveLoadModal(true);
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center justify-center gap-2 cursor-pointer w-full"
                style={{ pointerEvents: 'auto' }}
              >
                💾 Save/Load State
              </button>
            )}
          </div>
        </div>
        
        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            <span>Roll call & attendance</span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
            <span>Motion results & voting</span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
            <span>Member discussions</span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
            <span>Archive & clear session</span>
          </div>
        </div>
      </div>

      {/* Create Motion Modal */}
      {showCreateMotion && (
        <BulkCreateMotionModal
          onClose={() => setShowCreateMotion(false)}
          onRefresh={loadLiveVotingData}
          meetingType={meetingTypeFilter}
          hoaTimezone={hoaTimezone}
        />
      )}

      {/* Roll Call Modal */}
      {showRollCall && (
        <RollCallModal
          boardMembers={boardMembers}
          attendance={attendance}
          onClose={() => setShowRollCall(false)}
          onRefresh={loadLiveVotingData}
          meetingType={meetingTypeFilter}
          teamMembers={teamMembers}
        />
      )}

      {/* Meeting Minutes Preview Modal */}
      {showLiveMinutesPreview && (
        <MeetingMinutesPreviewModal
          previewData={livePreviewData}
          meetingNotes={liveMeetingNotes}
          setMeetingNotes={setLiveMeetingNotes}
          actionItems={liveActionItems}
          setActionItems={setLiveActionItems}
          proofOfNotice={proofOfNotice}
          meetingRules={meetingRules}
          manualPriorMinutes={manualPriorMinutes}
          setManualPriorMinutes={setManualPriorMinutes}
          speakingTimeMinutes={speakingTimeMinutes}
          meetingType={meetingType}
          hoaName={hoaName}
          hoaTimezone={hoaTimezone}
          supportingDocuments={supportingDocuments}
          user={user}
          hoaId={user?.hoa_id}
          hoaData={hoaData}
          hoaSettings={hoaSettings}
          onClose={() => setShowLiveMinutesPreview(false)}
          onExport={(format, sectionVisibility, combinedPacketData) => {
            // Pass combined packet data if available
            const dataWithPacket = {
              ...livePreviewData,
              use_combined_packet: combinedPacketData?.useCombinedDocs,
              combined_packet_id: combinedPacketData?.combinedPacketId,
              combined_packet_url: combinedPacketData?.combinedPacketUrl
            };
            exportFromLivePreview(format, dataWithPacket, liveMeetingNotes, liveActionItems, sectionVisibility);
          }}
        />
      )}
      </div>
    </>
  );
}

// Bulk Create Motion Modal
function BulkCreateMotionModal({ onClose, onRefresh, meetingType = "BOD", hoaTimezone = 'America/Chicago' }) {
  const [motions, setMotions] = useState([{
    id: Date.now(),
    title: '',
    description: '',
    timing_type: 'immediate', // 'immediate' or 'scheduled'
    duration_minutes: 10,
    scheduled_start: new Date().toISOString().slice(0, 16),
    scheduled_end: new Date(Date.now() + 10 * 60 * 1000).toISOString().slice(0, 16),
    requires_quorum: true,
    voting_rule: 'majority' // 'majority' or 'unanimous'
  }]);
  const [saving, setSaving] = useState(false);

  const addMotion = () => {
    setMotions(prev => [...prev, {
      id: Date.now() + Math.random(),
      title: '',
      description: '',
      timing_type: 'immediate',
      duration_minutes: 10,
      scheduled_start: new Date().toISOString().slice(0, 16),
      scheduled_end: new Date(Date.now() + 10 * 60 * 1000).toISOString().slice(0, 16),
      requires_quorum: true,
      voting_rule: 'majority'
    }]);
  };

  const removeMotion = (id) => {
    setMotions(prev => prev.filter(m => m.id !== id));
  };

  const updateMotion = (id, field, value) => {
    setMotions(prev => prev.map(m => 
      m.id === id ? { ...m, [field]: value } : m
    ));
  };

  const updateMotionTiming = (id, timing_type) => {
    setMotions(prev => prev.map(m => {
      if (m.id === id) {
        if (timing_type === 'immediate') {
          // Set to start now with duration
          return {
            ...m,
            timing_type,
            scheduled_start: new Date().toISOString().slice(0, 16),
            scheduled_end: new Date(Date.now() + m.duration_minutes * 60 * 1000).toISOString().slice(0, 16)
          };
        } else {
          // Keep existing scheduled times or set defaults
          return { ...m, timing_type };
        }
      }
      return m;
    }));
  };

  const updateDuration = (id, duration_minutes) => {
    setMotions(prev => prev.map(m => {
      if (m.id === id) {
        const updated = { ...m, duration_minutes };
        if (m.timing_type === 'immediate') {
          // Update end time based on new duration
          updated.scheduled_end = new Date(Date.now() + duration_minutes * 60 * 1000).toISOString().slice(0, 16);
        }
        return updated;
      }
      return m;
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      // Validate all motions
      const validMotions = motions.filter(m => m.title.trim());
      if (validMotions.length === 0) {
        alert('Please add at least one motion with a title.');
        return;
      }

      // Create all motions
      const promises = validMotions.map(motion => {
        const motionData = {
          title: motion.title,
          description: motion.description,
          duration_minutes: motion.duration_minutes,
          requires_quorum: motion.requires_quorum,
          voting_rule: motion.voting_rule,
          scheduled_start: motion.scheduled_start,
          scheduled_end: motion.scheduled_end,
          timing_type: motion.timing_type,
          meeting_type: meetingType,  // FIXED: Per-tab motion creation
          // Meeting location data
          meeting_location: {
            is_online: motion.meeting_location?.includes('zoom') || motion.meeting_location?.includes('teams') || motion.meeting_location?.includes('meet'),
            platform: motion.meeting_location || '',
            url: motion.meeting_location || '',
            is_in_person: !!motion.meeting_address,
            address: motion.meeting_address || ''
          },
          include_location_in_minutes: true,
          // Propose & Second data
          proposer_id: motion.proposer_id || null,
          seconder_id: motion.seconder_id || null
        };
        // Attach committee_id for committee tab
        if (meetingType === 'COMMITTEE') {
          motionData.committee_id = (selectedCommittee?.id || selectedCommittee?.committee_id);
        }
        return api.post('/api/vote/live-motions/bulk', motionData);
      });

      await Promise.all(promises);
      
      alert(`Successfully created ${validMotions.length} motion${validMotions.length > 1 ? 's' : ''}!`);
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error creating motions:', error);
      alert('Failed to create motions. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-4xl bg-white rounded-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-xl text-gray-900">Create Meeting Motions</h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">✕</button>
          </div>
          <p className="text-sm text-gray-600 mt-1">Add multiple motions for your board meeting</p>
        </div>

        <div className="p-6 space-y-6">
          {motions.map((motion, index) => (
            <div key={motion.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900">Motion #{index + 1}</h4>
                {motions.length > 1 && (
                  <button
                    onClick={() => removeMotion(motion.id)}
                    className="text-red-600 hover:bg-red-50 p-2 rounded-lg transition-colors"
                  >
                    🗑️ Remove
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Title */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Motion Title</label>
                  <input
                    type="text"
                    value={motion.title}
                    onChange={(e) => updateMotion(motion.id, 'title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="e.g., Approve emergency roof repair budget"
                  />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={motion.description}
                    onChange={(e) => updateMotion(motion.id, 'description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    rows="2"
                    placeholder="Provide details about the motion..."
                  />
                </div>

                {/* Timing Type */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Voting Schedule</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => updateMotionTiming(motion.id, 'immediate')}
                      className={`p-3 rounded-lg border-2 transition-colors ${
                        motion.timing_type === 'immediate'
                          ? 'border-green-500 bg-green-50 text-green-800'
                          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">🚀 Start Now</div>
                      <div className="text-xs">Begin voting immediately</div>
                    </button>
                    <button
                      onClick={() => updateMotionTiming(motion.id, 'scheduled')}
                      className={`p-3 rounded-lg border-2 transition-colors ${
                        motion.timing_type === 'scheduled'
                          ? 'border-blue-500 bg-blue-50 text-blue-800'
                          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">⏰ Schedule</div>
                      <div className="text-xs">Set specific start/end times</div>
                    </button>
                  </div>
                </div>

                {/* Immediate Timing Options */}
                {motion.timing_type === 'immediate' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Voting Duration</label>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      {[5, 10, 15, 30, 60].map(minutes => (
                        <button
                          key={minutes}
                          onClick={() => updateDuration(motion.id, minutes)}
                          className={`p-2 rounded-lg text-sm transition-colors ${
                            motion.duration_minutes === minutes
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {minutes === 60 ? '1 hour' : `${minutes} min`}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      Voting will end at: {formatLocal(new Date(Date.now() + motion.duration_minutes * 60 * 1000).toISOString(), hoaTimezone, 'h:mm a z')}
                    </p>
                  </div>
                )}

                {/* Scheduled Timing Options */}
                {motion.timing_type === 'scheduled' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                      <input
                        type="datetime-local"
                        value={motion.scheduled_start}
                        onChange={(e) => updateMotion(motion.id, 'scheduled_start', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                      <input
                        type="datetime-local"
                        value={motion.scheduled_end}
                        onChange={(e) => updateMotion(motion.id, 'scheduled_end', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                  </>
                )}

                {/* Quorum Requirement */}
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={motion.requires_quorum}
                      onChange={(e) => updateMotion(motion.id, 'requires_quorum', e.target.checked)}
                      className="text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">Requires quorum to pass</span>
                  </label>
                </div>

                {/* Voting Rule */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Voting Rule</label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => updateMotion(motion.id, 'voting_rule', 'majority')}
                      className={`p-3 rounded-lg border-2 transition-colors ${
                        motion.voting_rule === 'majority'
                          ? 'border-green-500 bg-green-50 text-green-800'
                          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">📊 Majority</div>
                      <div className="text-xs">Passes with &gt;50% approval</div>
                    </button>
                    <button
                      onClick={() => updateMotion(motion.id, 'voting_rule', 'unanimous')}
                      className={`p-3 rounded-lg border-2 transition-colors ${
                        motion.voting_rule === 'unanimous'
                          ? 'border-orange-500 bg-orange-50 text-orange-800'
                          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">🤝 Unanimous</div>
                      <div className="text-xs">Requires all approval</div>
                    </button>
                    <button
                      onClick={() => updateMotion(motion.id, 'voting_rule', 'propose_second')}
                      className={`p-3 rounded-lg border-2 transition-colors ${
                        motion.voting_rule === 'propose_second'
                          ? 'border-purple-500 bg-purple-50 text-purple-800'
                          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">✋ Propose & Second</div>
                      <div className="text-xs">Requires propose + second</div>
                    </button>
                  </div>
                  
                  {/* Proposer/Seconder Selection (only for propose_second) */}
                  {/* NOTE: Proposer/Seconder selection happens during LIVE VOTING, not during creation */}
                </div>
              </div>
            </div>
          ))}

          {/* Add Motion Button */}
          <button
            onClick={addMotion}
            className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-purple-400 hover:text-purple-600 transition-colors"
          >
            + Add Another Motion
          </button>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-white border-t p-6">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || motions.every(m => !m.title.trim())}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition-colors"
            >
              {saving ? 'Creating...' : `Create ${motions.filter(m => m.title.trim()).length} Motion${motions.filter(m => m.title.trim()).length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Keep all the existing components (ConsentCard, ProxyCard, VotingSection, etc.)
// They remain the same...

// Consent Card Component
function ConsentCard({ consent, canChange, onUpdate }) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-green-100 rounded-full flex-shrink-0">
          <div className="w-6 h-6 text-green-600">✓</div>
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">E-Voting Consent</h3>
          <p className="text-gray-600 mb-4">
            Florida law requires your consent to participate in electronic voting. 
            You can change this at any time before voting begins.
          </p>
          
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="consent"
                checked={consent === true}
                onChange={() => canChange && onUpdate(true)}
                disabled={!canChange}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-gray-900">
                Yes, I consent to electronic voting
              </span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="consent"
                checked={consent === false}
                onChange={() => canChange && onUpdate(false)}
                disabled={!canChange}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-gray-900">
                No, I prefer paper ballots
              </span>
            </label>
          </div>
          
          {!canChange && (
            <p className="text-xs text-amber-600 mt-2">
              Consent settings are locked during this phase of the election.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// Proxy Card Component  
function ProxyCard({ proxy, canChange, onUpdateProxy }) {
  const [showProxyForm, setShowProxyForm] = useState(false);
  const [proxyData, setProxyData] = useState({
    type: proxy?.type || 'default',
    assignee_name: proxy?.assignee_name || '',
    assignee_email: proxy?.assignee_email || ''
  });

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-blue-100 rounded-full flex-shrink-0">
          <div className="w-6 h-6 text-blue-600">👤</div>
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Proxy Voting</h3>
          <p className="text-gray-600 mb-4">
            Choose how you want to vote or assign your vote to another eligible member.
          </p>
          
          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="proxy_type"
                value="default"
                checked={proxyData.type === 'default'}
                onChange={() => setProxyData({ ...proxyData, type: 'default' })}
                disabled={!canChange}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-gray-900">
                I will vote myself
              </span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="proxy_type"
                value="board"
                checked={proxyData.type === 'board'}
                onChange={() => setProxyData({ ...proxyData, type: 'board' })}
                disabled={!canChange}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-gray-900">
                Assign proxy to Board
              </span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="proxy_type"
                value="homeowner"
                checked={proxyData.type === 'homeowner'}
                onChange={() => {
                  setProxyData({ ...proxyData, type: 'homeowner' });
                  setShowProxyForm(true);
                }}
                disabled={!canChange}
                className="text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm font-medium text-gray-900">
                Assign proxy to another homeowner
              </span>
            </label>
          </div>

          {proxyData.type === 'homeowner' && showProxyForm && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">Proxy Assignment Details</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assignee Name
                  </label>
                  <input
                    type="text"
                    value={proxyData.assignee_name}
                    onChange={(e) => setProxyData({ ...proxyData, assignee_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Full name of proxy holder"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assignee Email
                  </label>
                  <input
                    type="email"
                    value={proxyData.assignee_email}
                    onChange={(e) => setProxyData({ ...proxyData, assignee_email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="email@example.com"
                  />
                </div>
              </div>
            </div>
          )}

          {canChange && (
            <button
              onClick={() => onUpdateProxy(proxyData)}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Update Proxy Settings
            </button>
          )}
          
          {!canChange && (
            <p className="text-xs text-amber-600 mt-2">
              Proxy settings are locked during this phase of the election.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// Voting Section Component
function VotingSection({ candidates, election, onVote, user, hoaTimezone = 'America/Chicago' }) {
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [topicAnswers, setTopicAnswers] = useState({}); // User's own answers
  const [proxyAnswers, setProxyAnswers] = useState({}); // Format: {proxyId: {topicIndex: answer}}
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [proxiesHeld, setProxiesHeld] = useState([]);
  const [proxiesGiven, setProxiesGiven] = useState([]);
  const [loadingProxies, setLoadingProxies] = useState(true);
  const [myVote, setMyVote] = useState(null);
  const [loadingVote, setLoadingVote] = useState(true);
  const [voterToken, setVoterToken] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [loadingEligibility, setLoadingEligibility] = useState(true);

  // Check if this is a topics-only election
  const isTopicsOnly = election.not_member_election || (!candidates || candidates.length === 0);
  const hasSpecialTopics = election.special_topics && election.special_topics.length > 0;

  // Check voting eligibility
  useEffect(() => {
    const checkEligibility = async () => {
      try {
        const response = await api.get(`/api/vote/election/${election.id}/voting-eligibility`);
        setEligibility(response.data);
      } catch (error) {
        console.error('Error checking eligibility:', error);
        setEligibility({ can_vote: false, reason: 'error', message: 'Error checking eligibility' });
      } finally {
        setLoadingEligibility(false);
      }
    };
    checkEligibility();
  }, [election.id]);

  // Load proxies held by current user AND proxies given away
  useEffect(() => {
    const loadProxies = async () => {
      try {
        const response = await api.get('/api/proxy/active');
        const relevantProxiesHeld = (response.data.proxies_received || []).filter(
          p => p.election_id === election.id && p.status === 'active'
        );
        const relevantProxiesGiven = (response.data.proxies_given || []).filter(
          p => p.election_id === election.id && p.status === 'active'
        );
        setProxiesHeld(relevantProxiesHeld);
        setProxiesGiven(relevantProxiesGiven);
      } catch (error) {
        console.error('Error loading proxies:', error);
      } finally {
        setLoadingProxies(false);
      }
    };
    loadProxies();
  }, [election.id]);

  // Check if user has already voted
  useEffect(() => {
    const checkMyVote = async () => {
      try {
        const response = await api.get(`/api/vote/election/${election.id}/my-vote`);
        if (response.data.has_voted) {
          setMyVote(response.data);
        }
      } catch (error) {
        console.error('Error checking vote status:', error);
      } finally {
        setLoadingVote(false);
      }
    };
    checkMyVote();
  }, [election.id]);

  const handleAuthenticated = (token) => {
    setVoterToken(token);
  };

  const handleCandidateToggle = (candidateId) => {
    // Calculate total seats available from all positions
    const totalSeats = (election.positions_up_for_election || []).reduce(
      (sum, pos) => sum + (pos.count || 1), 0
    ) || election.positions || 1;
    
    setSelectedCandidates(prev => {
      if (prev.includes(candidateId)) {
        return prev.filter(id => id !== candidateId);
      } else if (prev.length < totalSeats) {
        return [...prev, candidateId];
      }
      return prev;
    });
  };

  const handleTopicAnswer = (topicIndex, answer) => {
    setTopicAnswers(prev => ({
      ...prev,
      [topicIndex]: answer
    }));
  };

  // Handler for proxy-specific answers
  const handleProxyAnswer = (proxyId, topicIndex, answer) => {
    setProxyAnswers(prev => ({
      ...prev,
      [proxyId]: {
        ...(prev[proxyId] || {}),
        [topicIndex]: answer
      }
    }));
  };

  // Check if user has given away a general proxy (can't vote at all)
  const hasGeneralProxyGiven = proxiesGiven.some(p => p.proxy_type === 'general');

  // Check if a specific motion has been proxied away for VOTING (not quorum-only)
  // Quorum-only proxies do NOT take away voting rights
  const isMotionProxied = (motionName) => {
    return proxiesGiven.some(p => 
      p.proxy_type === 'limited' && 
      p.specific_motion === motionName &&
      p.purpose !== 'quorum_only'  // Quorum-only doesn't block voting
    );
  };

  // Check if a specific position has been proxied away for VOTING
  const isPositionProxied = (positionName) => {
    return proxiesGiven.some(p => 
      p.proxy_type === 'limited' && 
      p.specific_motion === positionName &&
      p.purpose !== 'quorum_only'  // Quorum-only doesn't block voting
    );
  };

  // Check if ANY candidate position has been proxied away for VOTING (user can't vote on candidates for that position)
  const getProxiedPositions = () => {
    const positionNames = (election?.positions_up_for_election || []).map(p => p.label || p.role);
    return proxiesGiven.filter(p => 
      p.proxy_type === 'limited' && 
      positionNames.includes(p.specific_motion) &&
      p.purpose !== 'quorum_only'  // Quorum-only doesn't block voting
    );
  };

  // Check if ALL positions have been proxied away (user can't vote on any candidates)
  const allPositionsProxied = () => {
    const positionNames = (election?.positions_up_for_election || []).map(p => p.label || p.role);
    if (positionNames.length === 0) return false;
    return positionNames.every(posName => 
      proxiesGiven.some(p => p.proxy_type === 'limited' && p.specific_motion === posName)
    );
  };

  const submitVote = async () => {
    // NO VALIDATION - User can submit even with zero selections
    // Requirements: Authentication (voterToken) + E-consent + Eligibility
    // User can participate for quorum only without making any selections
    
    setIsSubmitting(true);
    try {
      // Format selections: combine candidates and topics into single selections object
      const selections = {};
      
      // Add candidate selections
      selectedCandidates.forEach((candidateId, idx) => {
        selections[`candidate_${idx}`] = candidateId;
      });
      
      // Add topic/motion answers
      Object.entries(topicAnswers).forEach(([topicIndex, answer]) => {
        selections[`topic_${topicIndex}`] = answer;
      });
      
      // Submit vote with voter token from OTP authentication
      const response = await api.post('/api/vote/ballot/cast', {
        voter_token: voterToken,
        election_id: election.id,
        selections: selections
      });
      
      const data = response.data;
      
      // Immediately mark as voted to prevent re-voting
      setMyVote({
        has_voted: true,
        voted_at: new Date().toISOString(),
        ballot_receipt: data
      });
      
      const votesMessage = '✅ Your ballot has been cast successfully!\n\nA receipt has been emailed to you.';
      alert(votesMessage);
      
      onVote(); // Refresh the parent component data
      
    } catch (error) {
      console.error('Error submitting vote:', error);
      alert(error.response?.data?.detail || 'Failed to submit vote. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = () => {
    // Requirements to cast vote:
    // 1. Must be authenticated (have voter token) - THIS IS THE REAL REQUIREMENT
    // 2. That's it! User doesn't need to answer anything - they can show up for quorum only
    
    // The presence of voterToken means user is authenticated
    // They can cast vote even with no selections (valid for quorum participation)
    return true;
  };

  // If user HAS voted, show congratulations instead of anything else
  // CRITICAL: Only show if myVote is explicitly set AND has has_voted=true
  if (!loadingVote && myVote && myVote.has_voted === true) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-6">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-6xl">✅</span>
            </div>
            <h2 className="text-4xl font-bold text-green-900 mb-4">🎉 Congratulations!</h2>
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Your Vote Has Been Cast</h3>
            <p className="text-lg text-gray-700 mb-2">
              Thank you for participating in this election.
            </p>
            <p className="text-md text-gray-600">
              You voted on: <strong>{myVote.voted_at_hoa || formatLocal(myVote.voted_at, hoaTimezone, 'MMMM d, yyyy') + ' at ' + formatLocal(myVote.voted_at, hoaTimezone, 'h:mm a z')}</strong>
            </p>
          </div>

          <div className="bg-green-50 border-2 border-green-400 rounded-xl p-6 mb-6">
            <h4 className="font-bold text-green-900 mb-3 text-lg">✓ Your Vote is Recorded</h4>
            <ul className="text-sm text-green-800 space-y-2">
              <li>• Your ballot has been securely recorded</li>
              <li>• A receipt has been emailed to you</li>
              <li>• Your vote is final and cannot be changed</li>
              <li>• Results will be announced after the election closes</li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-300 rounded-lg p-4 text-center">
            <p className="text-sm text-blue-900">
              Thank you for being an active member of your HOA community! 🏘️
            </p>
          </div>
        </div>
      </div>
    );
  }

  // If still loading eligibility, show loading
  if (loadingEligibility) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking voting eligibility...</p>
        </div>
      </div>
    );
  }

  // If user is NOT eligible to vote, show the reason
  if (eligibility && !eligibility.can_vote) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-5xl">🚫</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Cannot Vote Electronically</h2>
          </div>

          <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-6 rounded-r-lg">
            <h3 className="font-semibold text-red-900 mb-3">
              {eligibility.reason === 'property_already_voted' && '✅ Property Vote Already Cast'}
              {eligibility.reason === 'not_primary_voter' && '👤 Not Primary Voter'}
              {eligibility.reason === 'missing_consent' && '📋 E-Consent Required'}
              {eligibility.reason === 'general_proxy_given' && '📝 General Proxy Assigned'}
              {eligibility.reason === 'proxy_used' && '✓ Proxy Already Used'}
              {eligibility.reason === 'all_motions_proxied' && '📝 All Motions Proxied'}
              {!['property_already_voted', 'not_primary_voter', 'missing_consent', 'general_proxy_given', 'proxy_used', 'all_motions_proxied'].includes(eligibility.reason) && '❌ Not Eligible'}
            </h3>
            <p className="text-red-800">{eligibility.message}</p>
            
            {eligibility.reason === 'general_proxy_given' && (
              <p className="text-sm text-red-700 mt-3">
                To vote directly, go to the Proxy Management section and revoke your proxy (only if the holder hasn't voted yet).
              </p>
            )}
            
            {eligibility.reason === 'not_primary_voter' && (
              <p className="text-sm text-red-700 mt-3">
                Contact the primary voter or have them transfer the primary voter designation to you in the E-Consent section.
              </p>
            )}
            
            {eligibility.reason === 'missing_consent' && (
              <p className="text-sm text-red-700 mt-3">
                All property owners must complete their e-consent in the E-Consent tab before the property can vote electronically.
              </p>
            )}
          </div>

          <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              If you believe this is in error, please contact your board to verify your voting eligibility or to submit a paper ballot.
            </p>
            {eligibility.reason === 'general_proxy_given' && (
              <p className="text-sm text-gray-600 mt-2">
                Note: Your proxy holder ({eligibility.proxy_holder}) can vote on your behalf.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Check if e-voting period has closed (10 minutes after meeting start)
  const isEVotingClosed = () => {
    if (!election.meeting_start_time) return false;
    const meetingStart = new Date(election.meeting_start_time);
    const eVotingEnd = new Date(meetingStart.getTime() + 10 * 60 * 1000); // +10 minutes
    return new Date() > eVotingEnd;
  };

  // If e-voting has closed, show message
  if (isEVotingClosed() && !myVote) {
    const meetingStart = new Date(election.meeting_start_time);
    const eVotingEnd = new Date(meetingStart.getTime() + 10 * 60 * 1000);
    
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-5xl">⏰</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">E-Voting Period Has Ended</h2>
          </div>

          <div className="bg-orange-50 border-l-4 border-orange-500 p-6 mb-6 rounded-r-lg">
            <p className="text-orange-800">
              Electronic voting closed at <strong>{eVotingEnd.toLocaleString()}</strong> (10 minutes after the meeting started).
            </p>
            <p className="text-orange-700 mt-3 text-sm">
              The in-person meeting is now in progress. If you haven't voted yet, please vote in person at the meeting or submit a paper ballot.
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              If you believe this is in error, please contact your board.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // If user hasn't voted yet and no voter token, show OTP authentication
  if (!loadingVote && !myVote && !voterToken) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-5xl">🔐</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Voter Authentication Required</h2>
            <p className="text-gray-600">
              To cast your vote, you must authenticate using the code sent to your email.
            </p>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-6 rounded-r-lg">
            <h3 className="font-semibold text-blue-900 mb-3">How Authentication Works:</h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>• A secure code will be sent to your registered email</li>
              <li>• Enter the code below to verify your identity</li>
              <li>• Once authenticated, you can cast your vote (including any proxy votes you hold)</li>
              <li>• Your vote will count when the meeting starts</li>
            </ul>
          </div>

          <OTPGate
            electionId={election.id}
            userEmail={user?.email}
            onAuthenticated={handleAuthenticated}
            isTest={false}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      <div className="flex items-center gap-4 mb-6">
        <div className="p-3 bg-purple-100 rounded-full">
          <div className="w-6 h-6 text-purple-600">🗳️</div>
        </div>
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Cast Your Vote</h3>
          <p className="text-gray-600">
            {isTopicsOnly 
              ? 'Vote on each motion below'
              : `Select up to ${election.positions || 1} candidate${(election.positions || 1) > 1 ? 's' : ''} for this election.`
            }
          </p>
        </div>
      </div>

      {/* Proxy Voting Indicator */}
      {!loadingProxies && proxiesHeld.length > 0 && !myVote && (
        <div className="mb-6">
          <div className="space-y-4">
            <div className="p-6 bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-500 rounded-xl shadow-lg">
              <h4 className="font-bold text-purple-900 mb-4 flex items-center gap-2 text-xl">
                <span>🤝</span> YOU ARE HOLDING {proxiesHeld.length} PROXY/PROXIES
              </h4>
              <div className="text-sm text-purple-900 space-y-3">
                {(() => {
                  // Count UNIQUE proxy originators who gave voting proxies (not quorum-only)
                  const votingProxies = proxiesHeld.filter(p => p.purpose !== 'quorum_only');
                  const uniqueVotingOriginators = [...new Set(votingProxies.map(p => p.originator_email || p.originator_id))];
                  const quorumOnlyProxies = proxiesHeld.filter(p => p.purpose === 'quorum_only');
                  const uniqueQuorumOriginators = [...new Set(quorumOnlyProxies.map(p => p.originator_email || p.originator_id))];
                  
                  // Total votes = your vote + unique people who gave you voting proxies
                  const totalVotes = 1 + uniqueVotingOriginators.length;
                  
                  return (
                    <div className="bg-white rounded-lg p-4 border border-purple-200">
                      <p className="font-bold text-lg mb-2">📊 TOTAL VOTES YOU WILL CAST:</p>
                      <div className="text-3xl font-black text-purple-600">
                        {totalVotes}
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        (Your vote + {uniqueVotingOriginators.length} proxy vote{uniqueVotingOriginators.length !== 1 ? 's' : ''} from {uniqueVotingOriginators.length} person{uniqueVotingOriginators.length !== 1 ? 's' : ''})
                      </p>
                      {uniqueQuorumOriginators.length > 0 && (
                        <p className="text-xs text-orange-600 mt-2 font-semibold">
                          ⚠️ Plus {uniqueQuorumOriginators.length} quorum-only proxy (counts for quorum, but NO vote cast)
                        </p>
                      )}
                    </div>
                  );
                })()}

                <div className="space-y-2">
                  <p className="font-bold text-base">Who you're voting for:</p>
                  <ul className="ml-4 space-y-2">
                    <li className="flex items-start gap-2 bg-green-50 p-2 rounded border border-green-200">
                      <span className="text-green-600 text-lg">✓</span>
                      <div>
                        <p className="font-bold text-green-900">YOUR PERSONAL VOTE</p>
                        <p className="text-xs text-green-700">This is YOUR vote based on YOUR selections below</p>
                      </div>
                    </li>
                    {proxiesHeld.map((p, idx) => (
                      <li key={idx} className="flex items-start gap-2 bg-blue-50 p-2 rounded border border-blue-200">
                        <span className="text-blue-600 text-lg">🤝</span>
                        <div className="flex-1">
                          <p className="font-bold text-blue-900">
                            PROXY FROM: {p.originator_name}
                          </p>
                          <p className="text-xs text-blue-700">
                            Email: {p.originator_email} | Signed: {formatLocal(p.created_at, hoaTimezone, 'MMM d, yyyy')}
                          </p>
                          {p.purpose === 'quorum_only' ? (
                            <p className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded mt-1 inline-block font-semibold">
                              QUORUM ONLY - No vote will be cast (counts for quorum only)
                            </p>
                          ) : p.proxy_type === 'limited' && p.specific_motion ? (
                            <p className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded mt-1 inline-block">
                              LIMITED: Only for "{p.specific_motion}"
                              {p.specific_answer && ` - Must vote: ${p.specific_answer}`}
                            </p>
                          ) : (
                            <p className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded mt-1 inline-block">
                              DELEGATE/GENERAL: Will use YOUR selections
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-yellow-50 border border-yellow-300 rounded p-3 mt-4">
                  <p className="text-xs text-yellow-900 font-semibold">
                    ⚠️ IMPORTANT: You will submit ONE ballot, but it will cast {(() => {
                      const votingProxies = proxiesHeld.filter(p => p.purpose !== 'quorum_only');
                      const uniqueVotingOriginators = [...new Set(votingProxies.map(p => p.originator_email || p.originator_id))];
                      return 1 + uniqueVotingOriginators.length;
                    })()} votes automatically.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Already Voted Display */}
      {myVote && (
        <div className="mb-6 p-6 bg-green-50 border-2 border-green-300 rounded-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-600 text-white rounded-full">✓</div>
            <div>
              <h4 className="text-lg font-semibold text-green-900">Vote Already Cast</h4>
              <p className="text-sm text-green-700">
                You voted on {formatLocal(myVote.voted_at, hoaTimezone, 'MMM d, yyyy h:mm a z')}
              </p>
            </div>
          </div>

          {myVote.candidate_votes && myVote.candidate_votes.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Your Candidate Votes:</p>
              <ul className="space-y-1">
                {myVote.candidate_votes.map((name, idx) => (
                  <li key={idx} className="text-sm text-gray-900">✓ {name}</li>
                ))}
              </ul>
            </div>
          )}

          {myVote.topic_votes && myVote.topic_votes.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Your Motion Votes:</p>
              <div className="space-y-2">
                {myVote.topic_votes.map((topic, idx) => (
                  <div key={idx} className="bg-white rounded p-3 border border-green-200">
                    <p className="text-sm font-medium text-gray-900">{topic.topic_name}</p>
                    <p className="text-sm text-green-700">Your vote: <strong>{topic.your_answer}</strong></p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 p-3 bg-green-100 rounded border border-green-300">
            <p className="text-xs text-green-800">
              ✓ Your vote has been recorded and cannot be changed. Thank you for participating!
            </p>
          </div>
        </div>
      )}

      {/* General Proxy Warning */}
      {!myVote && hasGeneralProxyGiven && (
        <div className="mb-6 p-4 bg-red-50 border-2 border-red-400 rounded-xl">
          <div className="flex items-start gap-3">
            <span className="text-2xl">🚫</span>
            <div>
              <h4 className="font-bold text-red-900 mb-2">You Cannot Vote - General Proxy Assigned</h4>
              <p className="text-sm text-red-800 mb-2">
                You have assigned a <strong>general proxy</strong> for this election. 
                {proxiesGiven.find(p => p.proxy_type === 'general')?.holder_name && (
                  <span> <strong>{proxiesGiven.find(p => p.proxy_type === 'general').holder_name}</strong> will cast all votes on your behalf.</span>
                )}
              </p>
              <p className="text-xs text-red-700">
                To vote directly, you must first revoke the general proxy in the E-Consent tab.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ========== YOUR OWN VOTE SECTION (ALWAYS AT TOP) ========== */}
      {!myVote && !hasGeneralProxyGiven && (
        <div className="mb-8 p-6 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-500 rounded-xl">
          <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span>✅</span> YOUR PERSONAL VOTE
          </h3>
          <p className="text-sm text-gray-600 mb-6">These are the items YOU can vote on based on your proxy status.</p>

          {/* Candidate Voting Section - YOUR VOTE */}
          {!isTopicsOnly && candidates && candidates.length > 0 && (
            <div className="space-y-4 mb-6">
              {(() => {
                // Calculate total seats available
                const totalSeats = (election.positions_up_for_election || []).reduce(
                  (sum, pos) => sum + (pos.count || 1), 0
                ) || election.positions || 1;
                const positionNames = (election.positions_up_for_election || []).map(p => p.label || p.role).join(', ');
                
                // Check if ANY positions are proxied away
                const proxiedPositionsList = getProxiedPositions();
                const allProxied = allPositionsProxied();
                
                return (
                  <>
                    <h4 className="font-bold text-gray-900 text-lg border-b pb-2">Board Election (Your Vote):</h4>
                    
                    {/* Show warning if positions are proxied */}
                    {proxiedPositionsList.length > 0 && (
                      <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-4">
                        <p className="text-sm text-yellow-800 font-medium mb-2">
                          🤝 <strong>Proxy Assigned for Candidate Positions:</strong>
                        </p>
                        <ul className="text-sm text-yellow-700 list-disc list-inside mb-2">
                          {proxiedPositionsList.map((proxy, i) => (
                            <li key={i}>
                              <strong>{proxy.specific_motion}</strong> → {proxy.holder_name}
                              {proxy.purpose !== 'quorum_only' && ' (voting rights transferred)'}
                            </li>
                          ))}
                        </ul>
                        {allProxied ? (
                          <p className="text-sm text-red-700 font-bold">
                            ❌ You cannot vote for candidates - all positions have been proxied away.
                          </p>
                        ) : (
                          <p className="text-xs text-yellow-600">
                            ⚠️ Your proxy holder(s) will vote on your behalf for the proxied positions.
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Only show voting UI if NOT all positions are proxied */}
                    {!allProxied && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-blue-800">
                          <strong>📋 How Voting Works:</strong> There {totalSeats === 1 ? 'is' : 'are'} <strong>{totalSeats} seat{totalSeats > 1 ? 's' : ''}</strong> available 
                          {positionNames && <> ({positionNames})</>}. 
                          You may vote for <strong>up to {totalSeats} candidate{totalSeats > 1 ? 's' : ''}</strong>. 
                          The top {totalSeats} candidate{totalSeats > 1 ? 's' : ''} with the most votes will be elected to the board.
                        </p>
                        <p className="text-xs text-blue-700 mt-1">
                          You've selected: <strong>{selectedCandidates.length} of {totalSeats}</strong> allowed
                        </p>
                      </div>
                    )}
                  </>
                );
              })()}
              
              {/* Only show candidates if NOT all positions are proxied */}
              {!allPositionsProxied() && candidates.map((candidate) => (
                <div
                  key={candidate.id}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    selectedCandidates.includes(candidate.id)
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleCandidateToggle(candidate.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <input
                        type="checkbox"
                        checked={selectedCandidates.includes(candidate.id)}
                        onChange={() => handleCandidateToggle(candidate.id)}
                    className="w-5 h-5 text-indigo-600 focus:ring-indigo-500 rounded"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{candidate.name}</h4>
                    {candidate.bio && (
                      <p className="text-sm text-gray-600 line-clamp-2">{candidate.bio}</p>
                    )}
                  </div>
                </div>
                
                {selectedCandidates.includes(candidate.id) && (
                  <div className="text-indigo-600">
                    ✓ Selected
                  </div>
                )}
              </div>
              
              {candidate.platform && (
                <p className="mt-3 text-sm text-gray-700 pl-9"><strong>Platform:</strong> {candidate.platform}</p>
              )}
            </div>
          ))}
            </div>
          )}

          {/* Motions/Topics - YOUR VOTE */}
          {hasSpecialTopics && (
            <div className="space-y-6 mb-6">
          <h4 className="font-medium text-gray-900">Motions:</h4>
          {election.special_topics.map((topic, index) => {
            const motionProxied = isMotionProxied(topic.name);
            const proxiedTo = proxiesGiven.find(p => p.specific_motion === topic.name);
            
            return (
              <div key={index} className={`border-2 rounded-xl p-4 ${motionProxied ? 'bg-yellow-50 border-yellow-400' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-start justify-between mb-2">
                  <h5 className="font-semibold text-gray-900">{topic.name}</h5>
                  {motionProxied && (
                    <span className="px-2 py-1 bg-yellow-200 text-yellow-800 text-xs font-medium rounded-full">
                      🤝 Proxy Assigned
                    </span>
                  )}
                </div>
                
                {motionProxied && (
                  <div className="mb-4 p-3 bg-yellow-100 border border-yellow-300 rounded-lg">
                    <p className="text-sm font-medium text-yellow-900 mb-1">
                      ⚠️ You have assigned a proxy for this motion
                    </p>
                    <p className="text-xs text-yellow-800">
                      <strong>{proxiedTo?.holder_name}</strong> will vote on your behalf.
                      {proxiedTo?.specific_answer && ` Your specified vote: ${proxiedTo.specific_answer}`}
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      You cannot vote on this motion. To vote directly, revoke the proxy first.
                    </p>
                  </div>
                )}
                
                {topic.description && (
                  <p className="text-sm text-gray-600 mb-4">{topic.description}</p>
                )}
                
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700 mb-2">Select your vote:</p>
                  {topic.answers && topic.answers.length > 0 ? (
                    topic.answers.map((answer, ansIdx) => (
                      <label 
                        key={ansIdx}
                        className={`flex items-center p-3 border-2 rounded-lg transition-all ${
                          motionProxied 
                            ? 'cursor-not-allowed opacity-50 border-gray-200 bg-gray-100' 
                            : `cursor-pointer ${
                                topicAnswers[index] === answer
                                  ? 'border-indigo-500 bg-indigo-50'
                                  : 'border-gray-300 hover:border-gray-400'
                              }`
                        }`}
                      >
                        <input
                          type="radio"
                          name={`topic_${index}`}
                          value={answer}
                          checked={topicAnswers[index] === answer}
                          onChange={() => !motionProxied && handleTopicAnswer(index, answer)}
                          disabled={motionProxied}
                          className="w-5 h-5 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <span className="ml-3 font-medium text-gray-900">{answer}</span>
                        {topicAnswers[index] === answer && !motionProxied && (
                          <span className="ml-auto text-indigo-600">✓</span>
                        )}
                      </label>
                    ))
                  ) : (
                    <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        ⚠️ This motion has no voting options configured. Please contact the board administrator.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
            </div>
          )}
        </div>
      )}

      {/* ========== PROXY VOTES SECTIONS (ONE PER PROXY RECEIVED) ========== */}
      {!myVote && proxiesHeld.length > 0 && proxiesHeld.map((proxy, proxyIdx) => {
        // Skip quorum-only proxies in the voting UI (they don't cast votes)
        if (proxy.purpose === 'quorum_only') return null;

        return (
          <div key={proxy.id} className="mb-8 p-6 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-500 rounded-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span>🤝</span> PROXY VOTE FOR: {proxy.originator_name}
            </h3>
            <div className="mb-4 p-3 bg-purple-100 border border-purple-300 rounded-lg">
              <p className="text-sm text-purple-900">
                <strong>Email:</strong> {proxy.originator_email}
              </p>
              <p className="text-sm text-purple-900">
                <strong>Proxy Type:</strong> {proxy.proxy_type === 'limited' ? `Limited to: ${proxy.specific_motion}` : 'General (all items)'}
              </p>
              {proxy.specific_answer && (
                <p className="text-sm text-purple-900">
                  <strong>Specified Answer:</strong> {proxy.specific_answer} (pre-selected below)
                </p>
              )}
            </div>

            {/* Check if this is a position proxy (not a motion) */}
            {(() => {
              const positionNames = (election.positions_up_for_election || []).map(p => p.label || p.role);
              const isPositionProxy = proxy.proxy_type === 'limited' && positionNames.includes(proxy.specific_motion);
              const isGeneralProxy = proxy.proxy_type === 'general';
              
              // Show candidates section if this is a general proxy OR a limited proxy for a POSITION
              if ((isGeneralProxy || isPositionProxy) && !isTopicsOnly && candidates && candidates.length > 0) {
                return (
                  <div className="space-y-4 mb-6">
                    <h4 className="font-bold text-gray-900 text-lg border-b pb-2">
                      🗳️ Board Election ({proxy.originator_name}'s Proxy Vote){isPositionProxy ? ` - ${proxy.specific_motion}` : ''}:
                    </h4>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-blue-800">
                        <strong>📋 How Voting Works:</strong> Select candidates on behalf of <strong>{proxy.originator_name}</strong>. 
                        Your selections will count as their vote for the board election.
                      </p>
                    </div>
                    <p className="text-sm font-medium text-purple-700 bg-purple-100 p-2 rounded">
                      ✓ Uses YOUR candidate selections from your personal vote above (delegate proxy)
                    </p>
                  </div>
                );
              }
              return null;
            })()}

            {/* Motions for this proxy */}
            {hasSpecialTopics && (() => {
              // For limited proxies, check if it's a motion (not a position)
              const positionNames = (election.positions_up_for_election || []).map(p => p.label || p.role);
              const isPositionProxy = proxy.proxy_type === 'limited' && positionNames.includes(proxy.specific_motion);
              
              // If this is a LIMITED proxy for a POSITION, don't show motions section
              if (isPositionProxy) return null;
              
              return (
              <div className="space-y-4">
                <h4 className="font-bold text-gray-900 text-lg border-b pb-2">
                  Motions ({proxy.originator_name}'s Proxy Vote):
                </h4>
                {election.special_topics.map((topic, topicIdx) => {
                  // For limited proxies, only show the specific motion
                  if (proxy.proxy_type === 'limited' && proxy.specific_motion !== topic.name) {
                    return null;
                  }

                  const hasSpecificAnswer = proxy.specific_answer && proxy.specific_answer.trim() !== '';

                  return (
                    <div key={topicIdx} className="border-2 rounded-xl p-4 bg-white border-purple-300">
                      <h5 className="font-semibold text-gray-900 mb-2">{topic.name}</h5>
                      
                      {hasSpecificAnswer && (
                        <div className="mb-3 p-2 bg-green-50 border border-green-300 rounded">
                          <p className="text-sm text-green-900 font-medium">
                            ✓ Pre-selected answer: <strong>{proxy.specific_answer}</strong>
                          </p>
                          <p className="text-xs text-green-700">
                            This vote is already specified and will be cast automatically.
                          </p>
                        </div>
                      )}

                      {topic.description && (
                        <p className="text-sm text-gray-600 mb-4">{topic.description}</p>
                      )}

                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          {hasSpecificAnswer ? 'Specified vote:' : 'Select vote on behalf of ' + proxy.originator_name + ':'}
                        </p>
                        {topic.answers && topic.answers.length > 0 ? (
                          topic.answers.map((answer, ansIdx) => (
                            <label 
                              key={ansIdx}
                              className={`flex items-center p-3 border-2 rounded-lg ${
                                hasSpecificAnswer
                                  ? answer === proxy.specific_answer
                                    ? 'border-green-500 bg-green-50 cursor-default'
                                    : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                  : `cursor-pointer ${
                                      proxyAnswers[proxy.id]?.[topicIdx] === answer
                                        ? 'border-purple-500 bg-purple-50'
                                        : 'border-gray-300 hover:border-gray-400'
                                    }`
                              }`}
                            >
                              <input
                                type="radio"
                                name={`proxy_${proxy.id}_topic_${topicIdx}`}
                                value={answer}
                                checked={
                                  hasSpecificAnswer 
                                    ? answer === proxy.specific_answer
                                    : proxyAnswers[proxy.id]?.[topicIdx] === answer
                                }
                                onChange={() => !hasSpecificAnswer && handleProxyAnswer(proxy.id, topicIdx, answer)}
                                disabled={hasSpecificAnswer}
                                className="w-5 h-5 text-purple-600 focus:ring-purple-500"
                              />
                              <span className="ml-3 font-medium text-gray-900">{answer}</span>
                              {((hasSpecificAnswer && answer === proxy.specific_answer) || 
                                (!hasSpecificAnswer && proxyAnswers[proxy.id]?.[topicIdx] === answer)) && (
                                <span className="ml-auto text-purple-600">✓</span>
                              )}
                            </label>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 italic">No voting options for this motion.</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              );
            })()}
          </div>
        );
      })}

      {!myVote && (
        <div className="pt-6 border-t-4 border-indigo-500">
          <div className="mb-4 p-4 bg-blue-50 border border-blue-300 rounded-lg">
            <h4 className="font-bold text-blue-900 mb-2">✅ Ready to Submit</h4>
            <p className="text-sm text-blue-800 mb-2">
              You are authenticated and ready to cast your ballot.
            </p>
            <div className="text-sm text-blue-900 space-y-1">
              <p>• Your vote {proxiesGiven.length > 0 ? `(${proxiesGiven.length} item${proxiesGiven.length > 1 ? 's' : ''} given away)` : ''}</p>
              {(() => {
                const votingProxies = proxiesHeld.filter(p => p.purpose !== 'quorum_only');
                const uniqueVotingOriginators = [...new Set(votingProxies.map(p => p.originator_email || p.originator_id))];
                const quorumOnlyProxies = proxiesHeld.filter(p => p.purpose === 'quorum_only');
                const uniqueQuorumOriginators = [...new Set(quorumOnlyProxies.map(p => p.originator_email || p.originator_id))];
                return (
                  <>
                    {uniqueVotingOriginators.length > 0 && (
                      <p>• {uniqueVotingOriginators.length} proxy vote{uniqueVotingOriginators.length > 1 ? 's' : ''} you will cast (from {uniqueVotingOriginators.length} person{uniqueVotingOriginators.length > 1 ? 's' : ''})</p>
                    )}
                    {uniqueQuorumOriginators.length > 0 && (
                      <p>• {uniqueQuorumOriginators.length} quorum-only prox{uniqueQuorumOriginators.length > 1 ? 'ies' : 'y'} (no vote cast)</p>
                    )}
                  </>
                );
              })()}
            </div>
            <p className="text-xs text-blue-700 mt-3 italic">
              Note: You can submit even without selecting anything (valid for quorum participation).
            </p>
          </div>
          
          <button
            onClick={submitVote}
            disabled={isSubmitting || !canSubmit()}
            className="w-full px-6 py-4 bg-indigo-600 text-white font-bold text-lg rounded-xl hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors shadow-lg"
          >
            {isSubmitting ? '⏳ Submitting Ballot...' : '🗳️ CAST VOTE (Submit Ballot)'}
          </button>
        </div>
      )}
    </div>
  );
}

// Results Section Component  
function ResultsSection({ stats }) {
  if (!stats || stats.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
        <div className="text-4xl mb-4">📊</div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Results Pending</h3>
        <p className="text-gray-600">Election results will be available once voting is complete.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      <div className="flex items-center gap-4 mb-6">
        <div className="p-3 bg-green-100 rounded-full">
          <div className="w-6 h-6 text-green-600">🏆</div>
        </div>
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Election Results</h3>
          <p className="text-gray-600">Final results for this election</p>
        </div>
      </div>

      <div className="space-y-4">
        {stats.map((candidate, index) => (
          <div key={candidate.candidate_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                index === 0 ? 'bg-yellow-100 text-yellow-800' :
                index === 1 ? 'bg-gray-100 text-gray-800' :
                index === 2 ? 'bg-orange-100 text-orange-800' :
                'bg-gray-50 text-gray-600'
              }`}>
                {index + 1}
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">{candidate.candidate_name}</h4>
                <div className="text-sm text-gray-600">
                  Online: {candidate.online_votes} | Manual: {candidate.manual_votes} | Proxy: {candidate.proxy_votes}
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <div className="text-xl font-bold text-gray-900">{candidate.total_votes}</div>
              <div className="text-sm text-gray-600">votes</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Nominee Profiles Section Component
function NomineeProfilesSection({ candidates, election, selectedCandidate, onSelectCandidate, onRefresh }) {
  const [showNominateForm, setShowNominateForm] = useState(false);

  if (!candidates || candidates.length === 0) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
          <div className="text-6xl mb-4">👥</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Nominees Yet</h3>
          <p className="text-gray-600 mb-4">
            Candidate nominations are not yet available for this election.
          </p>
          
          {/* Show positions up for nomination */}
          {election?.positions_up_for_election && election.positions_up_for_election.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4 text-left max-w-md mx-auto">
              <h4 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                📋 Positions Open for Nomination:
              </h4>
              <div className="flex flex-wrap gap-2">
                {election.positions_up_for_election.map((pos, idx) => (
                  <span 
                    key={idx} 
                    className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium"
                  >
                    {pos.label || pos.role} {pos.count > 1 ? `(${pos.count})` : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {election && election.status === 'nominating' && (
            <button
              onClick={() => setShowNominateForm(true)}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Nominate a Candidate
            </button>
          )}
          
          <p className="text-sm text-gray-500 mt-4">
            Check back during the nomination period to see candidate profiles and information.
          </p>
        </div>

        {showNominateForm && (
          <NominateForm
            election={election}
            onClose={() => setShowNominateForm(false)}
            onRefresh={onRefresh}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <div className="w-6 h-6 text-blue-600">👥</div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Nominee Profiles</h3>
              <p className="text-gray-600">
                Get to know the candidates running in this election ({candidates.length} nominees)
              </p>
            </div>
          </div>
          
          {election && election.status === 'nominating' && (
            <button
              onClick={() => setShowNominateForm(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Nominate Candidate
            </button>
          )}
        </div>
        
        {/* Show positions up for nomination */}
        {election?.positions_up_for_election && election.positions_up_for_election.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <h4 className="text-sm font-medium text-gray-700 mb-2">📋 Positions in this election:</h4>
            <div className="flex flex-wrap gap-2">
              {election.positions_up_for_election.map((pos, idx) => (
                <span 
                  key={idx} 
                  className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium"
                >
                  {pos.label || pos.role} {pos.count > 1 ? `(${pos.count} seats)` : ''}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Candidates List */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {candidates.map((candidate) => (
          <div
            key={candidate.id}
            className={`bg-white rounded-2xl p-6 shadow-sm cursor-pointer transition-all ${
              selectedCandidate?.id === candidate.id
                ? 'ring-2 ring-indigo-500 bg-indigo-50'
                : 'hover:shadow-md'
            }`}
            onClick={() => onSelectCandidate(candidate)}
          >
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full mx-auto mb-3 flex items-center justify-center">
                <div className="text-2xl">👤</div>
              </div>
              <h4 className="font-semibold text-lg text-gray-900">{candidate.name}</h4>
              {candidate.position && (
                <p className="text-sm text-indigo-600 font-medium">{candidate.position}</p>
              )}
            </div>

            {candidate.bio && (
              <p className="text-sm text-gray-700 line-clamp-3 mb-4">{candidate.bio}</p>
            )}

            <button className="w-full px-4 py-2 text-sm bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors">
              View Full Profile
            </button>
          </div>
        ))}
      </div>

      {/* Selected Candidate Details */}
      {selectedCandidate && (
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center">
              <div className="text-3xl">👤</div>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900">{selectedCandidate.name}</h3>
              {selectedCandidate.position && (
                <p className="text-lg text-indigo-600 font-medium">{selectedCandidate.position}</p>
              )}
            </div>
          </div>

          {/* Helper to get actual question text */}
          {(() => {
            const getQuestion = (questionId) => {
              if (election?.nomination_questions) {
                const q = election.nomination_questions.find(nq => nq.id === questionId);
                return q?.question || null;
              }
              return null;
            };

            return (
              <div className="space-y-4">
                {/* Dynamically render ALL questions from nomination_questions */}
                {election?.nomination_questions && election.nomination_questions.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-6">
                    {election.nomination_questions.map((question) => {
                      // Skip name questions (shown in header)
                      const questionLower = (question.question || '').toLowerCase();
                      if (questionLower.includes("nominee's name") || questionLower.includes("candidate name")) {
                        return null;
                      }
                      
                      // Get value from candidate using question_X or legacy fields
                      const fieldKey = `question_${question.id}`;
                      let value = selectedCandidate[fieldKey];
                      
                      // Try legacy field mapping
                      if (!value) {
                        const legacyMap = {
                          'property': 'property_identifier',
                          'bio': 'bio',
                          'biography': 'bio',
                          'platform': 'platform',
                          'experience': 'experience',
                          'skills': 'skills',
                          'priorities': 'priorities',
                          'previous': 'previous_service',
                          'accomplishments': 'accomplishments',
                          'time': 'time_commitment',
                          'commitment': 'time_commitment'
                        };
                        for (const [key, field] of Object.entries(legacyMap)) {
                          if (questionLower.includes(key)) {
                            value = selectedCandidate[field];
                            break;
                          }
                        }
                      }
                      
                      // Try nomination_responses
                      if (!value && selectedCandidate.nomination_responses) {
                        value = selectedCandidate.nomination_responses[fieldKey];
                      }
                      
                      // Skip if no value
                      if (!value) return null;
                      
                      // Determine emoji based on question type
                      const getEmoji = () => {
                        if (questionLower.includes('bio')) return '📝';
                        if (questionLower.includes('platform') || questionLower.includes('goal')) return '🎯';
                        if (questionLower.includes('experience')) return '💼';
                        if (questionLower.includes('skills')) return '🛠️';
                        if (questionLower.includes('priorities')) return '⭐';
                        if (questionLower.includes('previous') || questionLower.includes('service')) return '🏛️';
                        if (questionLower.includes('accomplishments')) return '🏆';
                        if (questionLower.includes('time') || questionLower.includes('commitment')) return '⏰';
                        return '📋';
                      };
                      
                      return (
                        <div key={question.id} className={question.type === 'textarea' ? 'md:col-span-2' : ''}>
                          <h4 className="font-semibold text-gray-900 mb-2">{getEmoji()} {question.question.replace(' *', '')}</h4>
                          <p className="text-gray-700 leading-relaxed">{value}</p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  // Fallback for legacy data without nomination_questions
                  <div className="grid md:grid-cols-2 gap-6">
                    {selectedCandidate.bio && (
                      <div className="md:col-span-2">
                        <h4 className="font-semibold text-gray-900 mb-2">📝 Biography</h4>
                        <p className="text-gray-700 leading-relaxed">{selectedCandidate.bio}</p>
                      </div>
                    )}
                    {selectedCandidate.platform && (
                      <div className="md:col-span-2">
                        <h4 className="font-semibold text-gray-900 mb-2">🎯 Platform</h4>
                        <p className="text-gray-700 leading-relaxed">{selectedCandidate.platform}</p>
                      </div>
                    )}
                    {selectedCandidate.experience && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">💼 Experience</h4>
                        <p className="text-gray-700 leading-relaxed">{selectedCandidate.experience}</p>
                      </div>
                    )}
                    {selectedCandidate.skills && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">🛠️ Skills</h4>
                        <p className="text-gray-700 leading-relaxed">{selectedCandidate.skills}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          <button
            onClick={() => onSelectCandidate(null)}
            className="mt-6 px-6 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close Profile
          </button>
        </div>
      )}

      {/* Nominate Form Modal */}
      {showNominateForm && (
        <NominateForm
          election={election}
          onClose={() => setShowNominateForm(false)}
          onRefresh={onRefresh}
        />
      )}
    </div>
  );
}

// Nominate Form Component - Fixed Dynamic Questions
function NominateForm({ election, onClose, onRefresh }) {
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [nominationQuestions, setNominationQuestions] = useState([]);

  // Debug: Log election data to console
  console.log('NominateForm received election:', election);

  // Fetch election with nomination questions
  const fetchElectionQuestions = async () => {
    if (!election?.id) {
      console.log('No election ID provided');
      setQuestionsLoading(false);
      return;
    }

    try {
      // Try to get updated election data with nomination questions
      const response = await api.get(`/api/vote/elections`);
      const elections = response.data;
      const currentElection = elections.find(e => e.id === election.id);
      
      console.log('Fetched election data:', currentElection);
      console.log('Nomination questions from API:', currentElection?.nomination_questions);

      if (currentElection?.nomination_questions && Array.isArray(currentElection.nomination_questions)) {
        // Handle array of question objects (from EditElectionModal)
        const questions = currentElection.nomination_questions;
        const sectionsMap = {
          identification: [],
          background: [],
          vision: [],
          community: [],
          custom: []
        };

        questions.forEach(q => {
          const section = q.section || 'custom';
          if (sectionsMap[section]) {
            sectionsMap[section].push(q);
          }
        });

        // Convert to section format
        const questionSections = Object.keys(sectionsMap)
          .filter(key => sectionsMap[key].length > 0)
          .map(key => ({
            section: key,
            questions: sectionsMap[key]
          }));

        setNominationQuestions(questionSections);
        console.log('Processed question sections:', questionSections);
      } else {
        // Use default comprehensive questions
        setNominationQuestions(getDefaultNominationQuestions());
        console.log('Using default nomination questions');
      }
    } catch (error) {
      console.error('Error fetching election questions:', error);
      // Fallback to default questions
      setNominationQuestions(getDefaultNominationQuestions());
    } finally {
      setQuestionsLoading(false);
    }
  };

  const getDefaultNominationQuestions = () => [
    {
      section: 'identification',
      questions: [
        { id: 1, question: "Nominee's Name", type: 'text', required: true, public: true },
        { id: 2, question: 'Nominated By', type: 'radio', options: ['Self', 'Another Member'], required: true, public: false },
        { id: 3, question: 'Property Address / Identifier', type: 'text', required: true, public: false }
      ]
    },
    {
      section: 'background',
      questions: [
        { id: 4, question: 'Brief Biography (2-3 sentences): Tell us a little about yourself, your background, and your connection to the community.', type: 'textarea', required: true, public: true },
        { id: 5, question: 'Relevant Experience: Any board, committee, volunteer, or professional experience that helps you serve the association.', type: 'textarea', required: true, public: true }
      ]
    },
    {
      section: 'vision',
      questions: [
        { id: 6, question: 'Why are you interested in serving on the Board? (what is your motivation)', type: 'textarea', required: true, public: true },
        { id: 7, question: 'What are your top 2-3 priorities or areas you would like the Association to improve?', type: 'textarea', required: true, public: true },
        { id: 8, question: 'What skills or perspectives will you bring to the Board?', type: 'textarea', required: true, public: true }
      ]
    },
    {
      section: 'community',
      questions: [
        { id: 9, question: 'Have you served on an HOA committee or Board before? If yes, which one(s)?', type: 'textarea', required: true, public: true },
        { id: 10, question: 'Any previous accomplishments or contributions in community service?', type: 'textarea', required: false, public: true },
        { id: 11, question: 'Are you willing to commit the time required for Board meetings, committee work, and community events?', type: 'radio', options: ['Yes', 'No'], required: true, public: true }
      ]
    }
  ];

  // Initialize form data when questions are loaded
  React.useEffect(() => {
    fetchElectionQuestions();
  }, [election?.id]);

  React.useEffect(() => {
    if (nominationQuestions.length > 0) {
      const initialData = {};
      nominationQuestions.forEach(section => {
        section.questions?.forEach(question => {
          initialData[`question_${question.id}`] = '';
        });
      });
      setFormData(initialData);
      console.log('Initialized form data:', initialData);
    }
  }, [nominationQuestions]);

  const updateFormData = (questionId, value) => {
    setFormData(prev => ({
      ...prev,
      [`question_${questionId}`]: value
    }));
  };

  const validateForm = () => {
    const errors = [];
    nominationQuestions.forEach(section => {
      section.questions?.forEach(question => {
        if (question.required && !formData[`question_${question.id}`]?.trim()) {
          errors.push(`${question.question.substring(0, 50)}... is required`);
        }
      });
    });
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (errors.length > 0) {
      alert('Please fill in all required fields:\n\n' + errors.join('\n'));
      return;
    }

    setSubmitting(true);
    try {
      // Convert form data to structured nomination data
      const nominationData = {
        election_id: election.id,
        candidate_name: formData['question_1'] || 'Unknown Candidate',
        candidate_bio: formData['question_4'] || '',
        candidate_platform: formData['question_6'] || '',
        candidate_experience: formData['question_5'] || '',
        nomination_responses: formData,
        nominated_by_type: formData['question_2'] || 'Self',
        property_identifier: formData['question_3'] || '',
        priorities: formData['question_7'] || '',
        skills: formData['question_8'] || '',
        previous_service: formData['question_9'] || '',
        accomplishments: formData['question_10'] || '',
        time_commitment: formData['question_11'] || 'Yes'
      };
      
      console.log('Submitting nomination data:', nominationData);
      
      await api.post('/api/vote/nominate', nominationData);
      
      alert('🎉 Candidate nominated successfully!\n\nThe nomination has been submitted and will be reviewed by the board.');
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error nominating candidate:', error);
      alert(error.response?.data?.detail || 'Failed to nominate candidate. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question) => {
    const questionId = question.id;
    const value = formData[`question_${questionId}`] || '';

    switch (question.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => updateFormData(questionId, e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={question.placeholder || 'Enter your response...'}
            required={question.required}
          />
        );
      
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => updateFormData(questionId, e.target.value)}
            rows="4"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
            placeholder={question.placeholder || 'Enter your detailed response...'}
            required={question.required}
          />
        );
      
      case 'radio':
        return (
          <div className="space-y-2">
            {question.options?.map((option, index) => (
              <label key={index} className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name={`question_${questionId}`}
                  value={option}
                  checked={value === option}
                  onChange={(e) => updateFormData(questionId, e.target.value)}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  required={question.required}
                />
                <span className="text-gray-700">{option}</span>
              </label>
            ))}
          </div>
        );
      
      case 'checkbox':
        return (
          <div className="space-y-2">
            {question.options?.map((option, index) => (
              <label key={index} className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  value={option}
                  checked={value.includes(option)}
                  onChange={(e) => {
                    const currentValues = value ? value.split(',') : [];
                    if (e.target.checked) {
                      updateFormData(questionId, [...currentValues, option].join(','));
                    } else {
                      updateFormData(questionId, currentValues.filter(v => v !== option).join(','));
                    }
                  }}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-700">{option}</span>
              </label>
            ))}
          </div>
        );
      
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => updateFormData(questionId, e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required={question.required}
          >
            <option value="">Select...</option>
            {question.options?.map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
        );
      
      case 'boolean':
      case 'yes_no':
        return (
          <select
            value={value}
            onChange={(e) => updateFormData(questionId, e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required={question.required}
          >
            <option value="">Select...</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        );
      
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => updateFormData(questionId, e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter your response..."
            required={question.required}
          />
        );
    }
  };

  const getSectionTitle = (sectionKey) => {
    const titles = {
      identification: '👤 Candidate Identification',
      background: '📋 Background & Experience',
      vision: '🎯 Vision & Motivation',
      community: '🤝 Community Involvement',
      custom: '📝 Additional Questions'
    };
    return titles[sectionKey] || '❓ Questions';
  };

  if (questionsLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading nomination form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl flex flex-col" style={{ maxHeight: '85vh' }} onClick={e => e.stopPropagation()}>
        {/* Fixed Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 flex-shrink-0">
          <div>
            <h3 className="font-bold text-2xl text-gray-900">🗳️ Candidate Nomination</h3>
            <p className="text-gray-600 mt-1">{election?.name || 'Election'}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">✕</button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-8">
            {/* Positions Up for Election */}
            {election?.positions_up_for_election && election.positions_up_for_election.length > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-5">
                <h4 className="text-lg font-semibold text-purple-900 mb-3 flex items-center gap-2">
                  📋 Positions Open for Nomination
                </h4>
                <p className="text-sm text-purple-700 mb-3">
                  The following positions are available for nomination in this election:
                </p>
                <div className="flex flex-wrap gap-2">
                  {election.positions_up_for_election.map((pos, idx) => (
                    <span 
                      key={idx} 
                      className="px-3 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium border border-purple-300"
                    >
                      {pos.label || pos.role} {pos.count > 1 ? `(${pos.count} seats)` : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {nominationQuestions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600">No nomination questions configured for this election.</p>
                <p className="text-sm text-gray-500 mt-2">Please contact the board to set up nomination questions.</p>
              </div>
            ) : (
              nominationQuestions.map((section, sectionIndex) => (
                <div key={sectionIndex} className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    {getSectionTitle(section.section)}
                  </h4>
                  
                  <div className="space-y-6">
                    {section.questions?.map((question) => (
                      <div key={question.id} className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="flex items-start justify-between mb-3">
                          <label className="block text-sm font-medium text-gray-700 flex-1">
                            {question.question}
                            {question.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {!question.public && (
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded ml-2">Internal Use</span>
                          )}
                        </div>
                        
                        {renderQuestion(question)}
                        
                        {question.required && (
                          <p className="text-xs text-gray-500 mt-1">This field is required</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="text-sm text-gray-600">
            <span className="text-red-500">*</span> Required fields
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || nominationQuestions.length === 0}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Submitting...
                </>
              ) : (
                <>
                  🗳️ Submit Nomination
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Enhanced E-Voting Consent System Component
function EnhancedEVotingConsentSection({ consentStats, onRefresh, hoaTimezone = 'America/Chicago' }) {
  const [detailedStats, setDetailedStats] = useState(null);
  const [showDetailedView, setShowDetailedView] = useState(false);
  const [showMismatchModal, setShowMismatchModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchDetailedStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/vote/e-voting-consent/statistics');
      setDetailedStats(response.data);
    } catch (error) {
      console.error('Error fetching detailed consent stats:', error);
      alert('Failed to load detailed consent statistics.');
    } finally {
      setLoading(false);
    }
  };

  const exportConsentPDF = async () => {
    try {
      const response = await api.post('/api/vote/e-voting-consent/export-csv', {}, { responseType: 'blob' });
      
      // Create downloadable CSV
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `e_voting_consents_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert('✅ Consent list exported as CSV successfully!');
    } catch (error) {
      console.error('Error exporting consent PDF:', error);
      alert('Failed to export consent list.');
    }
  };

  const sendMismatchNotice = async (userEmail, mismatchNotes) => {
    try {
      await api.post('/api/vote/e-voting-consent/send-mismatch-notice', {
        user_email: userEmail,
        mismatch_notes: mismatchNotes
      });
      
      alert('✅ Mismatch notice sent successfully!');
      await fetchDetailedStats(); // Refresh data
      setShowMismatchModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error sending mismatch notice:', error);
      alert('Failed to send mismatch notice.');
    }
  };

  return (
    <section className="bg-white rounded-2xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900">📊 Enhanced E-Voting Consent System</h3>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setShowDetailedView(!showDetailedView);
              if (!showDetailedView && !detailedStats) {
                fetchDetailedStats();
              }
            }}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
          >
            {showDetailedView ? '📊 Summary View' : '📋 Detailed View'}
          </button>
          {showDetailedView && (
            <button
              onClick={exportConsentPDF}
              className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
            >
              📊 Export CSV
            </button>
          )}
        </div>
      </div>

      {!showDetailedView ? (
        // Summary Statistics View
        <div>
          <div className="grid md:grid-cols-5 gap-4 mb-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{consentStats.total_properties}</div>
              <div className="text-sm text-blue-700">Total Properties</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{consentStats.consented || 0}</div>
              <div className="text-sm text-green-700">Homeowners Consented</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{detailedStats?.statistics?.board_members?.consented || 0}</div>
              <div className="text-sm text-purple-700">Board Members Consented</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{consentStats.not_consented || consentStats.total_properties}</div>
              <div className="text-sm text-red-700">Not Consented</div>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">{consentStats.consent_percentage?.toFixed(1) || '0.0'}%</div>
              <div className="text-sm text-orange-700">Overall Rate</div>
            </div>
          </div>
          
          {/* Proxy Statistics Summary */}
          {detailedStats?.proxy_statistics && (
            <div className="mb-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
              <h4 className="font-semibold text-indigo-900 mb-3">📋 Proxy Status Summary</h4>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-xl font-bold text-indigo-600">{detailedStats.proxy_statistics.active_proxies || 0}</div>
                  <div className="text-xs text-indigo-700">Active Proxies</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-green-600">{detailedStats.proxy_statistics.general_active || 0}</div>
                  <div className="text-xs text-green-700">General (Active)</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-purple-600">{detailedStats.proxy_statistics.limited_active || 0}</div>
                  <div className="text-xs text-purple-700">Limited (Active)</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-gray-600">{detailedStats.proxy_statistics.expired_proxies || 0}</div>
                  <div className="text-xs text-gray-700">Expired/Revoked</div>
                </div>
              </div>
              
              {/* 90-Day Window Warning */}
              {detailedStats.proxy_statistics.days_until_expiration !== null && (
                <div className={`mt-3 p-2 rounded text-sm ${
                  detailedStats.proxy_statistics.is_expired ? 'bg-red-100 text-red-800' :
                  detailedStats.proxy_statistics.is_expiring_soon ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  <strong>90-Day Window:</strong> {' '}
                  {detailedStats.proxy_statistics.is_expired ? (
                    <>⚠️ EXPIRED - General proxies from original meeting ({detailedStats.proxy_statistics.first_meeting_time?.split('T')[0]}) have exceeded the {detailedStats.proxy_statistics.proxy_validity_days}-day limit</>
                  ) : detailedStats.proxy_statistics.is_expiring_soon ? (
                    <>⏰ Expiring in {detailedStats.proxy_statistics.days_until_expiration} days (on {detailedStats.proxy_statistics.proxy_expiration_date?.split('T')[0]})</>
                  ) : (
                    <>✅ {detailedStats.proxy_statistics.days_until_expiration} days remaining until proxy expiration ({detailedStats.proxy_statistics.proxy_expiration_date?.split('T')[0]})</>
                  )}
                </div>
              )}
            </div>
          )}
          
          {consentStats.message && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">{consentStats.message}</p>
            </div>
          )}
        </div>
      ) : (
        // Detailed View
        <div>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading detailed consent data...</p>
            </div>
          ) : detailedStats ? (
            <div className="space-y-6">
              {/* Detailed Statistics */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-3">🏠 Homeowners</h4>
                  <div className="space-y-2 text-sm">
                    <div>Total: <strong>{detailedStats.statistics.homeowners.total}</strong></div>
                    <div>Consented: <strong className="text-green-600">{detailedStats.statistics.homeowners.consented}</strong></div>
                    <div>Not Consented: <strong className="text-red-600">{detailedStats.statistics.homeowners.not_consented}</strong></div>
                    <div>No Response: <strong className="text-gray-600">{detailedStats.statistics.homeowners.no_response}</strong></div>
                    <div>Consent Rate: <strong>{detailedStats.statistics.homeowners.percentage_consented}%</strong></div>
                  </div>
                </div>
                
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-900 mb-3">👥 Board Members</h4>
                  <div className="space-y-2 text-sm">
                    <div>Total: <strong>{detailedStats.statistics.board_members.total}</strong></div>
                    <div>Consented: <strong className="text-green-600">{detailedStats.statistics.board_members.consented}</strong></div>
                    <div>Not Consented: <strong className="text-red-600">{detailedStats.statistics.board_members.not_consented}</strong></div>
                    <div>No Response: <strong className="text-gray-600">{detailedStats.statistics.board_members.no_response}</strong></div>
                    <div>Consent Rate: <strong>{detailedStats.statistics.board_members.percentage_consented}%</strong></div>
                  </div>
                </div>
              </div>

              {/* Detailed Consent List */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">📋 Individual Consent Records</h4>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 px-4 py-2 text-left">Name</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Role</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Status</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Date</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Expires</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Proxy</th>
                        <th className="border border-gray-300 px-4 py-2 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detailedStats.detailed_consent_records || detailedStats.detailed_consents || []).map((consent, index) => (
                        <tr key={index} className={`${
                          consent.is_expired ? 'bg-red-50' :
                          consent.consent_status === 'consent' ? 'bg-green-50' :
                          consent.consent_status === 'do_not_consent' ? 'bg-red-50' :
                          'bg-gray-50'
                        }`}>
                          <td className="border border-gray-300 px-4 py-2">{consent.name || 'N/A'}</td>
                          <td className="border border-gray-300 px-4 py-2">
                            <span className={`px-2 py-1 text-xs rounded ${
                              consent.role === 'homeowner' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                            }`}>
                              {consent.role}
                            </span>
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <span className={`px-2 py-1 text-xs rounded font-medium ${
                              consent.consent_status === 'consent' ? 'bg-green-100 text-green-800' :
                              consent.consent_status === 'do_not_consent' ? 'bg-red-100 text-red-800' :
                              consent.is_expired ? 'bg-orange-100 text-orange-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {consent.is_expired ? 'EXPIRED' : consent.consent_status.toUpperCase().replace('_', ' ')}
                            </span>
                            {consent.change_requested && (
                              <div className="text-xs text-yellow-600 mt-1">Change Requested</div>
                            )}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-sm">
                            {consent.consent_date ? formatLocal(consent.consent_date, hoaTimezone, 'MMM d, yyyy') : 'N/A'}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-sm">
                            {consent.expires_at ? formatLocal(consent.expires_at, hoaTimezone, 'MMM d, yyyy') : 'N/A'}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-sm">
                            {consent.proxy_status === 'none' ? (
                              <span className="text-gray-400">None</span>
                            ) : consent.proxy_status === 'given' ? (
                              <div>
                                <span className={`px-2 py-0.5 text-xs rounded ${
                                  consent.proxy_info?.type === 'general' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'
                                }`}>
                                  {consent.proxy_info?.type === 'general' ? 'General' : 'Limited'} → 
                                </span>
                                <div className="text-xs text-gray-600 mt-1">{consent.proxy_info?.holder_name}</div>
                              </div>
                            ) : consent.proxy_status === 'holding' ? (
                              <div>
                                <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800">
                                  Holding {consent.proxy_info?.holding_count || 0}
                                </span>
                                <div className="text-xs text-gray-600 mt-1">
                                  From: {consent.proxy_info?.holding_from?.join(', ')}
                                </div>
                              </div>
                            ) : consent.proxy_status === 'given_and_holding' ? (
                              <div>
                                <span className="px-2 py-0.5 text-xs rounded bg-orange-100 text-orange-800">
                                  Given + Holding {consent.proxy_info?.holding_count || 0}
                                </span>
                              </div>
                            ) : consent.proxy_status === 'expired' ? (
                              <div>
                                <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-800">
                                  ⚠️ Expired
                                </span>
                                <div className="text-xs text-gray-500 mt-1">{consent.proxy_info?.expired_reason}</div>
                              </div>
                            ) : (
                              <span className="text-gray-400">{consent.proxy_settings?.type || 'None'}</span>
                            )}
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <button
                              onClick={() => {
                                setSelectedUser(consent);
                                setShowMismatchModal(true);
                              }}
                              className="px-2 py-1 bg-orange-600 text-white text-xs rounded hover:bg-orange-700"
                            >
                              Report Mismatch
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* All Proxies Section - Board View (Always visible) */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-4">📄 All Proxy Documents (All Elections)</h4>
                  
                  {/* Active Proxies (excluding used limited proxies) */}
                  <div className="mb-4">
                    <h5 className="text-sm font-medium text-green-800 mb-2">✅ Current Active Proxies ({(detailedStats?.all_proxies || []).filter(p => p.status === 'active' && !p.vote_cast).length})</h5>
                    {(detailedStats?.all_proxies || []).filter(p => p.status === 'active' && !p.vote_cast).length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-green-200 text-sm">
                          <thead>
                            <tr className="bg-green-50">
                              <th className="border border-green-200 px-3 py-2 text-left">Election</th>
                              <th className="border border-green-200 px-3 py-2 text-left">Type</th>
                              <th className="border border-green-200 px-3 py-2 text-left">Giver</th>
                              <th className="border border-green-200 px-3 py-2 text-left">Holder (Receiver)</th>
                              <th className="border border-green-200 px-3 py-2 text-left">Purpose</th>
                              <th className="border border-green-200 px-3 py-2 text-left">Created</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(detailedStats?.all_proxies || []).filter(p => p.status === 'active' && !p.vote_cast).map((proxy, idx) => (
                              <tr key={proxy.id || idx} className="hover:bg-green-25">
                                <td className="border border-green-200 px-3 py-2 text-xs">
                                  {proxy.election_name || 'Unknown'}
                                </td>
                                <td className="border border-green-200 px-3 py-2">
                                  <span className={`px-2 py-0.5 text-xs rounded ${
                                    proxy.proxy_type === 'general' ? 'bg-green-100 text-green-800' : 
                                    proxy.proxy_type === 'delegate' ? 'bg-blue-100 text-blue-800' :
                                    'bg-purple-100 text-purple-800'
                                  }`}>
                                    {proxy.proxy_type === 'general' ? 'General' : 
                                     proxy.proxy_type === 'delegate' ? 'Delegate' : 'Limited'}
                                  </span>
                                </td>
                                <td className="border border-green-200 px-3 py-2">
                                  <div>{proxy.originator_name}</div>
                                  <div className="text-xs text-gray-500">{proxy.originator_email}</div>
                                </td>
                                <td className="border border-green-200 px-3 py-2">
                                  <div>{proxy.holder_name}</div>
                                  <div className="text-xs text-gray-500">{proxy.holder_email}</div>
                                </td>
                                <td className="border border-green-200 px-3 py-2">
                                  {proxy.purpose === 'voting_quorum' ? 'Voting & Quorum' : 
                                   proxy.purpose === 'quorum_only' ? 'Quorum Only' : proxy.purpose || 'N/A'}
                                </td>
                                <td className="border border-green-200 px-3 py-2 text-xs">
                                  {proxy.created_at ? formatLocal(proxy.created_at, hoaTimezone, 'MMM d, yyyy h:mm a') : 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm italic">No active proxies found across any election</p>
                    )}
                  </div>
                  
                  {/* Past/Expired/Used Proxies */}
                  <div className="mt-4">
                    <h5 className="text-sm font-medium text-gray-600 mb-2">📜 Past Proxies (Used/Expired/Revoked) ({(detailedStats?.all_proxies || []).filter(p => p.status !== 'active' || p.vote_cast).length})</h5>
                    {(detailedStats?.all_proxies || []).filter(p => p.status !== 'active' || p.vote_cast).length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-200 text-sm">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="border border-gray-200 px-3 py-2 text-left">Election</th>
                              <th className="border border-gray-200 px-3 py-2 text-left">Type</th>
                              <th className="border border-gray-200 px-3 py-2 text-left">Giver</th>
                              <th className="border border-gray-200 px-3 py-2 text-left">Holder</th>
                              <th className="border border-gray-200 px-3 py-2 text-left">Status</th>
                              <th className="border border-gray-200 px-3 py-2 text-left">Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(detailedStats?.all_proxies || []).filter(p => p.status !== 'active' || p.vote_cast).map((proxy, idx) => (
                              <tr key={proxy.id || idx} className="hover:bg-gray-25">
                                <td className="border border-gray-200 px-3 py-2 text-xs">
                                  {proxy.election_name || 'Unknown'}
                                </td>
                                <td className="border border-gray-200 px-3 py-2">
                                  <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-600">
                                    {proxy.proxy_type === 'general' ? 'General' : 
                                     proxy.proxy_type === 'delegate' ? 'Delegate' : 'Limited'}
                                  </span>
                                </td>
                                <td className="border border-gray-200 px-3 py-2">{proxy.originator_name}</td>
                                <td className="border border-gray-200 px-3 py-2">{proxy.holder_name}</td>
                                <td className="border border-gray-200 px-3 py-2">
                                  <span className={`px-2 py-0.5 text-xs rounded ${
                                    proxy.vote_cast ? 'bg-blue-100 text-blue-800' :
                                    proxy.status === 'expired' ? 'bg-orange-100 text-orange-800' :
                                    proxy.status === 'revoked' ? 'bg-red-100 text-red-800' :
                                    proxy.status === 'invalidated_postponed' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-600'
                                  }`}>
                                    {proxy.vote_cast ? '✅ Used/Completed' :
                                     proxy.status === 'invalidated_postponed' ? 'Invalidated (Postponed)' : 
                                     proxy.status_display || (proxy.status || 'unknown').charAt(0).toUpperCase() + (proxy.status || 'unknown').slice(1)}
                                  </span>
                                </td>
                                <td className="border border-gray-200 px-3 py-2 text-xs text-gray-500">
                                  {proxy.invalidation_reason || 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm italic">No past/expired proxies found</p>
                    )}
                  </div>
                  
                  {/* 90-Day Window Info */}
                  {detailedStats?.proxy_statistics && (
                    <div className={`mt-4 p-3 rounded-lg text-sm ${
                      detailedStats.proxy_statistics.is_expired ? 'bg-red-50 border border-red-200' :
                      detailedStats.proxy_statistics.is_expiring_soon ? 'bg-yellow-50 border border-yellow-200' :
                      'bg-blue-50 border border-blue-200'
                    }`}>
                      <strong>📅 Proxy Validity Timeline (Current Election):</strong>
                      <ul className="mt-2 list-disc list-inside text-gray-700">
                        <li>First Meeting Date: {detailedStats.proxy_statistics.first_meeting_time?.split('T')[0] || 'Not set'}</li>
                        <li>Validity Period: {detailedStats.proxy_statistics.proxy_validity_days || 90} days</li>
                        <li>Expiration Date: {detailedStats.proxy_statistics.proxy_expiration_date?.split('T')[0] || 'N/A'}</li>
                        <li>Days Remaining: {detailedStats.proxy_statistics.days_until_expiration ?? 'N/A'} days</li>
                      </ul>
                    </div>
                  )}
                  
                  {/* QUORUM PROXY TRACKING SECTION - For Official Documentation */}
                  <div className="mt-6 pt-4 border-t-2 border-teal-300">
                    <h4 className="font-semibold text-teal-900 mb-3 flex items-center gap-2">
                      🏛️ Proxy Summary
                      <span className="text-xs font-normal text-teal-600 bg-teal-100 px-2 py-0.5 rounded">See PDF for details</span>
                    </h4>
                    
                    {/* Simplified Proxy Summary */}
                    {(() => {
                      const allProxies = detailedStats?.all_proxies || [];
                      // Get active proxies only (already filtered by backend based on context)
                      const activeProxies = allProxies.filter(p => p.status === 'active');
                      
                      // Count by property (1 per property, not per proxy)
                      const propertiesWithProxy = new Set();
                      const votingProxies = [];
                      const quorumOnlyProxies = [];
                      
                      activeProxies.forEach(p => {
                        const propKey = p.originator_property || p.originator_email;
                        if (!propertiesWithProxy.has(propKey)) {
                          propertiesWithProxy.add(propKey);
                          if (p.purpose === 'quorum_only') {
                            quorumOnlyProxies.push(p);
                          } else {
                            votingProxies.push(p);
                          }
                        }
                      });
                      
                      return (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="grid md:grid-cols-4 gap-3 mb-4">
                            <div className="bg-white rounded p-3 border text-center">
                              <div className="text-2xl font-bold text-teal-700">{propertiesWithProxy.size}</div>
                              <div className="text-xs text-gray-600">Properties with Proxy</div>
                            </div>
                            <div className="bg-blue-50 rounded p-3 border border-blue-200 text-center">
                              <div className="text-2xl font-bold text-blue-700">{votingProxies.length}</div>
                              <div className="text-xs text-blue-600">Voting Proxies</div>
                              <div className="text-[10px] text-blue-500">Also count for quorum</div>
                            </div>
                            <div className="bg-purple-50 rounded p-3 border border-purple-200 text-center">
                              <div className="text-2xl font-bold text-purple-700">{quorumOnlyProxies.length}</div>
                              <div className="text-xs text-purple-600">Quorum-Only</div>
                              <div className="text-[10px] text-purple-500">No vote cast</div>
                            </div>
                            <div className="bg-green-50 rounded p-3 border border-green-200 text-center">
                              <div className="text-2xl font-bold text-green-700">{votingProxies.length + quorumOnlyProxies.length}</div>
                              <div className="text-xs text-green-600">Total for Quorum</div>
                            </div>
                          </div>
                          
                          <p className="text-xs text-gray-500 italic">
                            💡 This shows online proxies. The PDF report shows detailed proxy usage (only counts proxies where the holder actually voted).
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Click "Detailed View" to load comprehensive consent data.
            </div>
          )}
        </div>
      )}

      {/* Mismatch Notice Modal */}
      {showMismatchModal && selectedUser && (
        <MismatchNoticeModal
          user={selectedUser}
          onClose={() => {
            setShowMismatchModal(false);
            setSelectedUser(null);
          }}
          onSend={sendMismatchNotice}
        />
      )}
    </section>
  );
}

// Mismatch Notice Modal Component
function MismatchNoticeModal({ user, onClose, onSend }) {
  const [mismatchNotes, setMismatchNotes] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!mismatchNotes.trim()) {
      alert('Please describe the mismatch issue.');
      return;
    }

    setSending(true);
    try {
      await onSend(user.email, mismatchNotes);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold">⚠️ Report Consent Mismatch</h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">✕</button>
          </div>
          <p className="text-gray-600 mt-2">
            Report mismatch for <strong>{user.name}</strong> ({user.email})
          </p>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Describe the Mismatch Issue
              </label>
              <textarea
                value={mismatchNotes}
                onChange={(e) => setMismatchNotes(e.target.value)}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Example: Name on consent form doesn't match records, property address is incorrect, signature appears invalid, etc."
              />
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-medium text-yellow-900 mb-2">What happens next:</h4>
              <ul className="text-sm text-yellow-800 space-y-1">
                <li>• User will receive an email notification about the mismatch</li>
                <li>• Their consent form will be unlocked for re-submission</li>
                <li>• They can click "Request Change" to submit corrected information</li>
                <li>• New submission will appear in this consent list for review</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !mismatchNotes.trim()}
            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:bg-gray-400"
          >
            {sending ? 'Sending...' : '📧 Send Mismatch Notice'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Elections Management Section (Board Members Only)
function ElectionsManagementSection({ 
  hoaTimezone = 'America/Chicago',
  consentStats, 
  elections, 
  currentElection,
  onRefresh,
  showCreateElection,
  setShowCreateElection,
  boardMembers,
  hoaSettings = {}
}) {
  const [showPrintableBallot, setShowPrintableBallot] = useState(false);
  const [showBoardTerms, setShowBoardTerms] = useState(false);
  const [editingElection, setEditingElection] = useState(null);

  const publishElection = async (electionId) => {
    try {
      await api.put(`/api/vote/election/${electionId}/publish`);
      alert('Election published successfully!');
      onRefresh();
    } catch (error) {
      console.error('Error publishing election:', error);
      alert('Failed to publish election. Please try again.');
    }
  };

  const deleteElection = async (electionId) => {
    if (!confirm('Are you sure you want to delete this draft election? This action cannot be undone.')) {
      return;
    }
    
    try {
      await api.delete(`/api/vote/election/${electionId}`);
      alert('Election deleted successfully!');
      onRefresh();
    } catch (error) {
      console.error('Error deleting election:', error);
      alert('Failed to delete election. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-indigo-100 rounded-full">
              <div className="w-6 h-6 text-indigo-600">⚙️</div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Election Management</h3>
              <p className="text-gray-600">Create and manage elections (Board Members Only)</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4 flex-wrap">
            <button
              onClick={() => setShowCreateElection(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Create Election
            </button>
          </div>
        </div>
      </div>

      {/* Board Members & Terms */}
      {boardMembers && boardMembers.length > 0 && (
        <section className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-900">👥 Current Board Members</h3>
            <button
              onClick={() => setShowBoardTerms(!showBoardTerms)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {showBoardTerms ? 'Hide Terms' : 'Manage Terms'}
            </button>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {boardMembers.map((member) => (
              <div key={member.user_id} className={`p-4 rounded-lg border-2 ${
                member.is_expiring_soon ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200 bg-gray-50'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-900">{member.name}</h4>
                  {member.is_expiring_soon && (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                      Expiring Soon
                    </span>
                  )}
                </div>
                <p className="text-sm text-indigo-600 font-medium mb-1">{member.position_title}</p>
                <p className="text-xs text-gray-600">{member.email}</p>
                {member.term_end && (
                  <p className="text-xs text-gray-500 mt-2">
                    Term ends: {formatLocal(member.term_end, hoaTimezone, 'MMM d, yyyy')}
                  </p>
                )}
                {!member.term_end && (
                  <p className="text-xs text-amber-600 mt-2">No term set</p>
                )}
              </div>
            ))}
          </div>

          {showBoardTerms && (
            <BoardTermsManager 
              boardMembers={boardMembers}
              onRefresh={onRefresh}
              onClose={() => setShowBoardTerms(false)}
            />
          )}
        </section>
      )}

      {/* Enhanced E-Voting Consent System */}
      {consentStats && (
        <EnhancedEVotingConsentSection 
          consentStats={consentStats}
          onRefresh={onRefresh}
          hoaTimezone={hoaTimezone}
        />
      )}

      {/* Property Identifier Notes */}
      <section className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-6 shadow-sm border border-yellow-200">
        <div className="flex items-start gap-4">
          <div className="text-3xl">🏠</div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-orange-900 mb-2">Property Identifier Information</h3>
            <div className="text-sm text-orange-800 space-y-2">
              <p><strong>For Board Members:</strong> Property identifiers help track voting eligibility and ensure one vote per property unit.</p>
              <p><strong>Property ID Format:</strong> Each property has a unique identifier (e.g., Unit 101, Lot 15, Building A-205) that corresponds to the physical address.</p>
              <p><strong>Voting Rights:</strong> Each property gets exactly one vote, regardless of the number of occupants or homeowners associated with that property.</p>
              <p><strong>Proxy Assignment:</strong> If multiple homeowners are listed for one property, they must coordinate who casts the vote or assign a proxy.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Board Term Transitions */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 shadow-sm border border-blue-200">
        <div className="flex items-start gap-4">
          <div className="text-3xl">⚡</div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Board Term Transitions</h3>
            <div className="text-sm text-blue-800 space-y-2">
              <p><strong>Transition Period:</strong> Generally state rules require a 5-day transition period. Ensure you know your state's specific requirements; however, in practice it may take longer to transition - an accepted practice in some states.</p>
              <p><strong>Adding New Board Members:</strong> Add new board members through User Management in Settings Dashboard using "Invite Board Member" if they are not in the system.</p>
              <p><strong>Upgrading Existing Users:</strong> If the new board member is already a homeowner in the system, use the "Upgrade to Board" button in the Homeowners tab to assign their board position.</p>
              <p><strong>Transitioning Outgoing Members:</strong> For users no longer serving as board members, once ready to transition, select "Downgrade to Homeowner" to remove board access.</p>
            </div>
            {boardMembers && boardMembers.some(m => m.is_expiring_soon) && (
              <div className="mt-4 p-3 bg-white bg-opacity-50 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">📋 Upcoming Term Expirations:</h4>
                <div className="space-y-1">
                  {boardMembers.filter(m => m.is_expiring_soon).map((member, index) => (
                    <div key={index} className="text-sm text-blue-700">
                      <strong>{member.name}</strong> ({member.role}) - Term ends: {member.term_end ? formatLocal(member.term_end, hoaTimezone, 'MMM d, yyyy') : 'Not set'}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Elections List - Redesigned */}
      <section className="bg-white rounded-2xl p-6 shadow-sm">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">📚 Elections Management</h3>
        
        {elections.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No elections found.</p>
        ) : (
          <div className="space-y-3">
            {elections.map((election) => (
              <div key={election.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h4 className="font-medium text-gray-900">{election.name}</h4>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getPhaseColor(election.status)}`}>
                        {getPhaseLabel(election.status)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      <span>Created: {formatLocal(election.created_at, hoaTimezone, 'MMM d, yyyy')}</span>
                      {election.description && (
                        <span className="ml-3 text-gray-500">• {election.description}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingElection(election)}
                      className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      ⚙️ Edit & Manage
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Create Election Modal */}
      {showCreateElection && (
        <CreateElectionModal
          onClose={() => setShowCreateElection(false)}
          onRefresh={onRefresh}
          boardMembers={boardMembers}
          hoaTimezone={hoaTimezone}
        />
      )}

      {/* Edit Election Modal */}
      {editingElection && (
        <EditElectionModal
          election={editingElection}
          onClose={() => setEditingElection(null)}
          onRefresh={onRefresh}
          boardMembers={boardMembers}
          hoaTimezone={hoaTimezone}
          hoaSettings={hoaSettings}
        />
      )}
    </div>
  );
}

// Create Election Modal
function CreateElectionModal({ onClose, onRefresh, boardMembers, hoaTimezone = 'America/Chicago' }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    nominations_open: new Date().toISOString().slice(0, 16),
    nominations_close: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    proxy_collection_start: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),  // NEW: When proxy collection opens
    voting_open: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    meeting_start_time: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),  // Changed from voting_close
    positions_up_for_election: [],
    special_topics: [], // NEW: Special topics for voting
    is_condo: false, // NEW: HOA vs Condo distinction
    not_member_election: false, // NEW: Checkbox to disable officer positions
    votes_per_property: 1,
    per_property_weight: 1,
    multiple_votes_per_property: false,
    default_proxy_role: 'board',
    // Import from previous election
    import_from_election_id: null,
    import_proxies: false,
    import_motions: false
  });
  const [saving, setSaving] = useState(false);
  const [referenceDocument, setReferenceDocument] = useState(null);
  
  // Import from previous election state
  const [importableElections, setImportableElections] = useState([]);
  const [loadingImportable, setLoadingImportable] = useState(false);
  const [showImportSection, setShowImportSection] = useState(false);
  const [selectedImportElection, setSelectedImportElection] = useState(null);
  
  // Load importable elections when import section is opened
  useEffect(() => {
    if (showImportSection && importableElections.length === 0) {
      loadImportableElections();
    }
  }, [showImportSection]);
  
  const loadImportableElections = async () => {
    setLoadingImportable(true);
    try {
      const response = await api.get('/api/vote/elections/importable');
      setImportableElections(response.data.elections || []);
    } catch (error) {
      console.error('Error loading importable elections:', error);
    } finally {
      setLoadingImportable(false);
    }
  };
  
  const handleSelectImportElection = (election) => {
    setSelectedImportElection(election);
    setFormData(prev => ({
      ...prev,
      import_from_election_id: election.id,
      import_proxies: election.can_import_proxies,
      import_motions: election.can_import_motions
    }));
  };
  
  const handleImportMotionsPreview = () => {
    if (selectedImportElection && selectedImportElection.motions) {
      // Add motions from selected election to special_topics
      // CRITICAL: Each topic MUST have a unique ID for answer management to work correctly
      const importedTopics = selectedImportElection.motions.map((motion, idx) => ({
        id: `imported_${Date.now()}_${idx}`,  // Unique ID for each topic
        name: motion.name,
        description: motion.description || '',
        answers: [...(motion.answer_options || motion.answers || ['Yes', 'No'])],  // Copy answers, don't share reference
        proxy_settings: { allow_general: true, allow_limited: true, board_receiver: true },
        imported: true
      }));
      setFormData(prev => ({
        ...prev,
        special_topics: [...prev.special_topics, ...importedTopics]
      }));
      alert(`Imported ${importedTopics.length} motion(s) from "${selectedImportElection.name}"`);
    }
  };
  const [availablePositions] = useState([
    { value: 'president', label: 'President', description: 'Chief Executive Officer of the HOA' },
    { value: 'vice_president', label: 'Vice President', description: 'Assists the President and acts in their absence' },
    { value: 'treasurer', label: 'Treasurer', description: 'Manages HOA finances and budgets' },
    { value: 'secretary', label: 'Secretary', description: 'Maintains records and meeting minutes' },
    { value: 'board_member', label: 'Board Member', description: 'General board member position' }
  ]);
  
  // Pre-configured topic templates
  const [topicTemplates] = useState([
    { 
      type: 'amendment_ccr_bylaws', 
      label: 'Amendment to CCR\'s & Bylaws',
      proxy: { allow_general: false, allow_limited: true, board_receiver: true }
    },
    { 
      type: 'condo_amendment_docs', 
      label: 'Condo Amendment to Governing Documents',
      proxy: { allow_general: false, allow_limited: true, board_receiver: false }
    },
    { 
      type: 'budget_assessment', 
      label: 'Budget or Special Assessment',
      proxy: { allow_general: false, allow_limited: true, board_receiver: true },
      requiresDetails: true
    },
    { 
      type: 'recall_director', 
      label: 'Recall Vote of Director',
      proxy: { allow_general: true, allow_limited: true, board_receiver: true }
    },
    { 
      type: 'dissolution_hoa', 
      label: 'Dissolution of HOA',
      proxy: { allow_general: false, allow_limited: true, board_receiver: true }
    },
    { 
      type: 'custom', 
      label: 'Custom Topic',
      proxy: { allow_general: true, allow_limited: true, board_receiver: true }
    }
  ]);

  const addPosition = (positionValue) => {
    const position = availablePositions.find(p => p.value === positionValue);
    if (position && !formData.positions_up_for_election.find(p => p.role === positionValue)) {
      setFormData(prev => ({
        ...prev,
        positions_up_for_election: [...prev.positions_up_for_election, {
          role: positionValue,
          label: position.label,
          count: 1,
          current_holder: getCurrentHolder(positionValue),
          // Proxy settings for officer positions
          proxy_settings: {
            allow_general: !formData.is_condo, // Condo: no general for officers
            allow_limited: true,
            board_receiver: true,
            purpose: 'voting_and_quorum'
          }
        }]
      }));
    }
  };

  const removePosition = (positionRole) => {
    setFormData(prev => ({
      ...prev,
      positions_up_for_election: prev.positions_up_for_election.filter(p => p.role !== positionRole)
    }));
  };

  const updatePositionCount = (positionRole, count) => {
    setFormData(prev => ({
      ...prev,
      positions_up_for_election: prev.positions_up_for_election.map(p =>
        p.role === positionRole ? { ...p, count: parseInt(count) || 1 } : p
      )
    }));
  };
  
  const updatePositionProxy = (positionRole, proxyField, value) => {
    setFormData(prev => ({
      ...prev,
      positions_up_for_election: prev.positions_up_for_election.map(p =>
        p.role === positionRole ? {
          ...p,
          proxy_settings: { ...p.proxy_settings, [proxyField]: value }
        } : p
      )
    }));
  };
  
  // Check if any proxies are enabled
  const hasProxiesEnabled = () => {
    const positionProxies = formData.positions_up_for_election.some(p => 
      p.proxy_settings?.allow_general || p.proxy_settings?.allow_limited
    );
    const topicProxies = formData.special_topics.some(t => 
      t.proxy_settings?.allow_general || t.proxy_settings?.allow_limited
    );
    return positionProxies || topicProxies;
  };
  
  // NEW: Topic management functions
  const addTopic = (template) => {
    const topicId = `topic_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setFormData(prev => ({
      ...prev,
      special_topics: [...prev.special_topics, {
        id: topicId,
        type: template.type,
        name: template.label,
        description: '',
        assessment_details: '',
        answers: ['Yes', 'No', 'Abstain'],  // Each topic gets its own array instance
        proxy_settings: {
          ...template.proxy,
          allow_quorum_only: false,
          purpose: 'voting_and_quorum'
        }
      }]
    }));
  };
  
  const removeTopic = (topicId) => {
    setFormData(prev => ({
      ...prev,
      special_topics: prev.special_topics.filter(t => t.id !== topicId)
    }));
  };
  
  const updateTopic = (topicId, field, value) => {
    setFormData(prev => ({
      ...prev,
      special_topics: prev.special_topics.map(t =>
        t.id === topicId ? { ...t, [field]: value } : t
      )
    }));
  };
  
  const updateTopicProxy = (topicId, proxyField, value) => {
    setFormData(prev => ({
      ...prev,
      special_topics: prev.special_topics.map(t =>
        t.id === topicId ? {
          ...t,
          proxy_settings: { ...t.proxy_settings, [proxyField]: value }
        } : t
      )
    }));
  };

  const addTopicAnswer = (topicId, answer) => {
    if (!answer.trim()) return;
    setFormData(prev => ({
      ...prev,
      special_topics: prev.special_topics.map(t =>
        t.id === topicId ? {
          ...t,
          answers: [...(t.answers || []), answer]
        } : t
      )
    }));
  };

  const removeTopicAnswer = (topicId, answerIndex) => {
    setFormData(prev => ({
      ...prev,
      special_topics: prev.special_topics.map(t =>
        t.id === topicId ? {
          ...t,
          answers: t.answers.filter((_, i) => i !== answerIndex)
        } : t
      )
    }));
  };

  const getCurrentHolder = (role) => {
    const member = boardMembers?.find(m => m.role === role);
    return member ? {
      name: member.name,
      email: member.email,
      term_end: member.term_end,
      is_expiring: member.is_expiring_soon
    } : null;
  };

  const getExpiringPositions = () => {
    return boardMembers?.filter(m => m.is_expiring_soon) || [];
  };

  const calculateTransitionPeriod = (electionEndDate) => {
    const transitionStart = new Date(electionEndDate);
    const transitionEnd = new Date(transitionStart);
    transitionEnd.setDate(transitionEnd.getDate() + 30); // 30-day transition period
    return { start: transitionStart, end: transitionEnd };
  };

  const suggestElectionName = () => {
    const year = new Date().getFullYear();
    const expiringPositions = getExpiringPositions();
    
    if (expiringPositions.length > 0) {
      const positionTitles = expiringPositions.map(p => p.position_title).join(', ');
      return `${year} ${positionTitles} Election`;
    }
    
    return `${year} Board Election`;
  };

  const handleSubmit = async () => {
    // If not member election, only topics required
    if (formData.not_member_election) {
      if (formData.special_topics.length === 0) {
        alert('Please add at least one special topic for voting.');
        return;
      }
    } else {
      // If member election, need positions OR topics
      if (formData.positions_up_for_election.length === 0 && formData.special_topics.length === 0) {
        alert('Please select at least one position or add a special topic for the election.');
        return;
      }
    }

    setSaving(true);
    try {
      const totalPositions = formData.positions_up_for_election.reduce((sum, pos) => sum + pos.count, 0);
      
      // CRITICAL: Send datetime strings WITHOUT timezone conversion
      // The backend will interpret these as being in the HOA's timezone
      // If we convert to Date objects, axios will auto-convert to UTC which breaks scheduling
      const electionData = {
        ...formData,
        nominations_open: formData.not_member_election ? null : formData.nominations_open,
        nominations_close: formData.not_member_election ? null : formData.nominations_close,
        proxy_collection_start: formData.proxy_collection_start,
        voting_open: formData.voting_open,
        meeting_start_time: formData.meeting_start_time,
        // Auto-calculate voting_close: meeting start + 10 minutes (as string to avoid conversion)
        voting_close: null, // Backend will calculate this
        total_positions: totalPositions,
        // Include special topics and proxy settings
        special_topics: formData.special_topics,
        is_condo: formData.is_condo,
        not_member_election: formData.not_member_election,
        // Import from previous election
        import_from_election_id: formData.import_from_election_id,
        import_proxies: formData.import_proxies,
        import_motions: formData.import_motions
      };

      const response = await api.post('/api/vote/election', electionData);
      
      // Show import results if applicable
      let successMessage = 'Election created successfully!';
      if (response.data.imported_motions > 0 || response.data.imported_proxies > 0) {
        successMessage += `\n\nImported from previous election:\n`;
        if (response.data.imported_motions > 0) {
          successMessage += `• ${response.data.imported_motions} motion(s)\n`;
        }
        if (response.data.imported_proxies > 0) {
          successMessage += `• ${response.data.imported_proxies} proxy(s)`;
        }
      }
      alert(successMessage);
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error creating election:', error);
      alert('Failed to create election. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-xl text-gray-900">Create New Election</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">✕</button>
        </div>

        {/* Quick Setup for Expiring Terms */}
        {getExpiringPositions().length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-yellow-900 mb-2">⚠️ Terms Expiring Soon</h4>
            <p className="text-sm text-yellow-800 mb-3">
              The following positions have terms expiring within 6 months:
            </p>
            <div className="space-y-2">
              {getExpiringPositions().map(member => (
                <div key={member.user_id} className="flex items-center justify-between bg-yellow-100 rounded p-2">
                  <div>
                    <span className="font-medium text-yellow-900">{member.position_title}</span>
                    <span className="text-yellow-700 ml-2">({member.name})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-yellow-600">
                      Expires: {member.term_end ? formatLocal(member.term_end, hoaTimezone, 'MMM d, yyyy') : 'No date set'}
                    </span>
                    <button
                      onClick={() => addPosition(member.role)}
                      className="px-2 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700"
                    >
                      Add to Election
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => {
                setFormData(prev => ({ ...prev, name: suggestElectionName() }));
                getExpiringPositions().forEach(member => addPosition(member.role));
              }}
              className="mt-3 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-sm"
            >
              Quick Setup: Add All Expiring Positions
            </button>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Election Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g., 2025 Board Elections"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              rows="3"
              placeholder="Election description"
            />
          </div>

          {/* NEW: Import from Previous Election */}
          <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <button
              type="button"
              onClick={() => setShowImportSection(!showImportSection)}
              className="flex items-center gap-2 text-sm font-medium text-blue-900 w-full"
            >
              <span className="text-lg">{showImportSection ? '▼' : '▶'}</span>
              <span>📥 Import from Previous Election</span>
              {selectedImportElection && (
                <span className="ml-auto text-xs bg-blue-200 px-2 py-1 rounded">
                  Importing from: {selectedImportElection.name}
                </span>
              )}
            </button>
            
            {showImportSection && (
              <div className="mt-4 space-y-3">
                <p className="text-xs text-blue-700">
                  Import motions and/or valid general proxies from a previous election. Proxies are valid for 90 days from the original meeting date.
                </p>
                
                {loadingImportable ? (
                  <div className="text-center py-4 text-blue-600">Loading previous elections...</div>
                ) : importableElections.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">No previous elections available to import from.</div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {importableElections.map(election => (
                      <div 
                        key={election.id}
                        onClick={() => handleSelectImportElection(election)}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedImportElection?.id === election.id 
                            ? 'border-blue-500 bg-blue-100' 
                            : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">{election.name}</div>
                            <div className="text-xs text-gray-500">Status: {election.status}</div>
                          </div>
                          <div className="text-right text-xs">
                            <div className={election.can_import_motions ? 'text-green-600' : 'text-gray-400'}>
                              📋 {election.motions_count} motion(s)
                            </div>
                            <div className={election.can_import_proxies ? 'text-green-600' : 'text-gray-400'}>
                              👤 {election.active_general_proxies} proxy(s)
                              {election.proxies_expired && ' (expired)'}
                              {!election.proxies_expired && election.proxy_days_remaining > 0 && 
                                ` (${election.proxy_days_remaining}d left)`}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {selectedImportElection && (
                  <div className="mt-4 p-3 bg-white border border-blue-200 rounded-lg space-y-3">
                    <div className="font-medium text-blue-900">Import Options:</div>
                    
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.import_motions}
                        onChange={(e) => setFormData(prev => ({...prev, import_motions: e.target.checked}))}
                        disabled={!selectedImportElection.can_import_motions}
                        className="rounded border-blue-300"
                      />
                      <span className={`text-sm ${selectedImportElection.can_import_motions ? 'text-gray-900' : 'text-gray-400'}`}>
                        Import {selectedImportElection.motions_count} motion(s)
                      </span>
                      {formData.import_motions && (
                        <button
                          type="button"
                          onClick={handleImportMotionsPreview}
                          className="ml-auto text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                          Preview & Add Now
                        </button>
                      )}
                    </label>
                    
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.import_proxies}
                        onChange={(e) => setFormData(prev => ({...prev, import_proxies: e.target.checked}))}
                        disabled={!selectedImportElection.can_import_proxies}
                        className="rounded border-blue-300"
                      />
                      <span className={`text-sm ${selectedImportElection.can_import_proxies ? 'text-gray-900' : 'text-gray-400'}`}>
                        Import {selectedImportElection.active_general_proxies} general proxy(s)
                        {selectedImportElection.proxies_expired && ' (expired - not available)'}
                        {!selectedImportElection.proxies_expired && selectedImportElection.proxy_days_remaining > 0 && 
                          ` (${selectedImportElection.proxy_days_remaining} days remaining)`}
                      </span>
                    </label>
                    
                    {selectedImportElection.proxies_expired && (
                      <p className="text-xs text-amber-600">
                        ⚠️ Proxies from this election have expired (past 90-day window from first meeting).
                      </p>
                    )}
                    
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedImportElection(null);
                        setFormData(prev => ({
                          ...prev,
                          import_from_election_id: null,
                          import_proxies: false,
                          import_motions: false
                        }));
                      }}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Clear Selection
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* NEW: HOA vs Condo Selection */}
          <div className="col-span-2 bg-purple-50 border border-purple-200 rounded-lg p-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_condo}
                onChange={(e) => {
                  const isCondo = e.target.checked;
                  setFormData(prev => ({
                    ...prev,
                    is_condo: isCondo,
                    // Update all officer positions to disable general proxy if condo
                    positions_up_for_election: prev.positions_up_for_election.map(p => ({
                      ...p,
                      proxy_settings: {
                        ...p.proxy_settings,
                        allow_general: !isCondo
                      }
                    }))
                  }));
                }}
                className="rounded border-purple-300"
              />
              <span className="text-sm font-medium text-purple-900">This is a Condo (different proxy rules)</span>
            </label>
            {formData.is_condo && (
              <p className="text-xs text-purple-700 mt-2 ml-6">
                ⚠️ General proxies will NOT be allowed for officer elections (Condo rule)
              </p>
            )}
          </div>

          {/* NEW: Not a Member Election Checkbox */}
          <div className="col-span-2 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.not_member_election}
                onChange={(e) => {
                  const notMemberElection = e.target.checked;
                  setFormData(prev => ({
                    ...prev,
                    not_member_election: notMemberElection,
                    // Clear positions if not member election
                    positions_up_for_election: notMemberElection ? [] : prev.positions_up_for_election
                  }));
                }}
                className="rounded border-yellow-300"
              />
              <span className="text-sm font-medium text-yellow-900">Not a Member Election (special topics only)</span>
            </label>
            {formData.not_member_election && (
              <p className="text-xs text-yellow-700 mt-2 ml-6">
                ℹ️ Officer positions and nominations will be disabled. Use special topics section below for voting items.
              </p>
            )}
          </div>

          <div className={formData.not_member_election ? 'opacity-40 pointer-events-none' : ''}>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nominations Open</label>
            <input
              type="datetime-local"
              value={formData.nominations_open}
              onChange={(e) => setFormData(prev => ({...prev, nominations_open: e.target.value}))}
              disabled={formData.not_member_election}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className={formData.not_member_election ? 'opacity-40 pointer-events-none' : ''}>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nominations Close</label>
            <input
              type="datetime-local"
              value={formData.nominations_close}
              onChange={(e) => setFormData(prev => ({...prev, nominations_close: e.target.value}))}
              disabled={formData.not_member_election}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Best Practice Warning for Phase Scheduling */}
          <div className="col-span-2 bg-blue-50 border border-blue-300 rounded-lg p-3">
            <p className="text-sm text-blue-800 flex items-start gap-2">
              <span className="text-blue-600 text-lg">💡</span>
              <span>
                <strong>Recommended:</strong> Spread election phases across different dates (not all on the same day). 
                This gives members adequate time for nominations, proxy collection, and voting. 
                A typical schedule spans 2-4 weeks.
              </span>
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Proxy Collection Starts
              <span className="text-xs text-gray-500 ml-2">(When homeowners can begin submitting proxies)</span>
            </label>
            <input
              type="datetime-local"
              value={formData.proxy_collection_start}
              onChange={(e) => setFormData(prev => ({...prev, proxy_collection_start: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Voting Opens (E-Voting Start)
              <span className="text-xs text-gray-500 ml-2">(When online voting becomes available)</span>
            </label>
            <input
              type="datetime-local"
              value={formData.voting_open}
              onChange={(e) => setFormData(prev => ({...prev, voting_open: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Meeting Start Time
              <span className="text-xs text-gray-500 ml-2">(E-voting auto-closes 10 min after meeting starts)</span>
            </label>
            <input
              type="datetime-local"
              value={formData.meeting_start_time}
              onChange={(e) => setFormData(prev => ({...prev, meeting_start_time: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Legal Notice Warning */}
          <div className="col-span-2 bg-amber-50 border border-amber-300 rounded-lg p-3">
            <p className="text-sm text-amber-800 flex items-center gap-2">
              <span className="text-amber-600">⚠️</span>
              <strong>Important:</strong> Voting may not begin before notice is sent to all eligible voters.
            </p>
          </div>

          {/* Position Selection */}
          <div className={`col-span-2 ${formData.not_member_election ? 'opacity-40 pointer-events-none' : ''}`}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Positions Up for Election
              {formData.not_member_election && <span className="ml-2 text-xs text-yellow-600">(Disabled for non-member elections)</span>}
            </label>
            
            {/* Selected Positions */}
            {formData.positions_up_for_election.length > 0 && (
              <div className="space-y-2 mb-4">
                {formData.positions_up_for_election.map((position) => (
                  <div key={position.role} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{position.label}</span>
                          <span className="text-sm text-gray-600">({position.count} position{position.count > 1 ? 's' : ''})</span>
                        </div>
                        {position.current_holder && (
                          <div className="text-xs text-gray-700 mt-1">
                            Current: {position.current_holder.name}
                            {position.current_holder.is_expiring && (
                              <span className="ml-2 px-1 py-0.5 bg-yellow-100 text-yellow-800 rounded text-xs">
                                Term Expiring
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          max="5"
                          value={position.count}
                          onChange={(e) => updatePositionCount(position.role, e.target.value)}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                        <button
                          onClick={() => removePosition(position.role)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                        >
                          ✕
                        </button>
                      </div>
                    </div>

                    {/* Proxy Settings for this Position */}
                    <div className={`bg-blue-50 border border-blue-200 rounded p-3 ${formData.is_condo ? 'opacity-50' : ''}`}>
                      <h5 className="text-xs font-semibold text-blue-900 mb-2">
                        🗳️ Proxy Settings for {position.label}:
                        {formData.is_condo && <span className="ml-2 text-red-600">(Limited for Condo)</span>}
                      </h5>
                      
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <label className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={position.proxy_settings?.allow_general || false}
                            onChange={(e) => updatePositionProxy(position.role, 'allow_general', e.target.checked)}
                            disabled={formData.is_condo}
                            className="rounded border-blue-300"
                          />
                          <span className="text-blue-900">Allow General Proxy (90-day)</span>
                        </label>
                        <label className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={position.proxy_settings?.allow_limited || false}
                            onChange={(e) => updatePositionProxy(position.role, 'allow_limited', e.target.checked)}
                            className="rounded border-blue-300"
                          />
                          <span className="text-blue-900">Allow Limited Proxy (single-use)</span>
                        </label>
                      </div>

                      <label className="flex items-center gap-2 text-xs mb-2">
                        <input
                          type="checkbox"
                          checked={position.proxy_settings?.board_receiver || false}
                          onChange={(e) => updatePositionProxy(position.role, 'board_receiver', e.target.checked)}
                          className="rounded border-blue-300"
                        />
                        <span className="text-blue-900">Allow board members to receive proxy</span>
                      </label>

                      <div>
                        <label className="block text-xs font-medium text-blue-900 mb-1">Proxy Purpose:</label>
                        <select
                          value={position.proxy_settings?.purpose || 'voting_and_quorum'}
                          onChange={(e) => updatePositionProxy(position.role, 'purpose', e.target.value)}
                          className="w-full px-2 py-1 border border-blue-300 rounded text-xs"
                        >
                          <option value="voting_and_quorum">Voting & Quorum</option>
                          <option value="quorum_only">Quorum Only (no voting)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Position */}
            <div className="border border-gray-200 rounded-lg p-3">
              <h5 className="text-sm font-medium text-gray-700 mb-2">Add Position to Election:</h5>
              <div className="grid grid-cols-2 gap-2">
                {availablePositions.map((position) => {
                  const isSelected = formData.positions_up_for_election.find(p => p.role === position.value);
                  const currentHolder = getCurrentHolder(position.value);
                  
                  return (
                    <button
                      key={position.value}
                      onClick={() => !isSelected && !formData.not_member_election && addPosition(position.value)}
                      disabled={isSelected || formData.not_member_election}
                      className={`p-2 text-left rounded-lg text-sm transition-colors ${
                        isSelected || formData.not_member_election
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                          : 'bg-gray-50 hover:bg-indigo-50 hover:text-indigo-700'
                      }`}
                    >
                      <div className="font-medium">{position.label}</div>
                      {currentHolder && (
                        <div className="text-xs text-gray-600">
                          Current: {currentHolder.name}
                          {currentHolder.is_expiring && (
                            <span className="ml-1 text-yellow-600">⚠️</span>
                          )}
                        </div>
                      )}
                      {isSelected && (
                        <div className="text-xs text-green-600 mt-1">✓ Added</div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {formData.positions_up_for_election.length === 0 && (
              <p className="text-sm text-gray-500 mt-2">
                Select the board positions that will be up for election. You can add multiple positions and specify how many seats are available for each.
              </p>
            )}
          </div>

          {/* NEW: Special Topics Section */}
          <div className="col-span-2 border-t pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">Special Topics / Motions</label>
              <span className="text-xs text-gray-500">Add topics for members to vote on (beyond officer elections)</span>
            </div>

            {/* Selected Topics */}
            {formData.special_topics.length > 0 && (
              <div className="space-y-3 mb-4">
                {formData.special_topics.map((topic) => (
                  <div key={topic.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={topic.name}
                          onChange={(e) => updateTopic(topic.id, 'name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium"
                          placeholder="Topic name"
                        />
                        {topic.type === 'budget_assessment' && (
                            <input
                              type="text"
                              value={topic.assessment_details}
                              onChange={(e) => updateTopic(topic.id, 'assessment_details', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-2"
                              placeholder="Assessment amount and purpose (e.g., '$50,000 for pool renovation')"
                            />
                          )}
                        </div>
                        <button
                          onClick={() => removeTopic(topic.id)}
                          className="ml-2 p-1 text-red-600 hover:bg-red-50 rounded"
                        >
                          ✕
                        </button>
                      </div>

                      {/* Answer Options */}
                      <div className="bg-white border border-gray-200 rounded p-3 mb-3">
                        <h5 className="text-xs font-semibold text-gray-900 mb-2">📝 Vote Answers:</h5>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {topic.answers?.map((answer, index) => (
                            <div key={`${topic.id}_answer_${index}`} className="flex items-center gap-1 bg-blue-50 px-2 py-1 rounded text-sm">
                              <span className="text-blue-900">{answer}</span>
                              <button
                                onClick={() => removeTopicAnswer(topic.id, index)}
                                className="text-red-600 hover:text-red-800 text-xs ml-1"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            placeholder="Add answer option (e.g., 'Approve', 'Reject')"
                            className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                const input = e.target;
                                if (input.value.trim()) {
                                  addTopicAnswer(topic.id, input.value.trim());
                                  input.value = '';
                                }
                              }
                            }}
                          />
                          <button
                            onClick={(e) => {
                              const input = e.target.previousElementSibling;
                              if (input.value.trim()) {
                                addTopicAnswer(topic.id, input.value.trim());
                                input.value = '';
                              }
                            }}
                            className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                          >
                            Add
                          </button>
                        </div>
                      </div>

                      {/* Proxy Settings for this topic */}
                      <div className="bg-blue-50 border border-blue-200 rounded p-3">
                      <h5 className="text-xs font-semibold text-blue-900 mb-2">🗳️ Proxy Settings for this Topic:</h5>
                      
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <label className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={topic.proxy_settings.allow_general}
                            onChange={(e) => updateTopicProxy(topic.id, 'allow_general', e.target.checked)}
                            className="rounded border-blue-300"
                          />
                          <span className="text-blue-900">Allow General Proxy (90-day)</span>
                        </label>
                        <label className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={topic.proxy_settings.allow_limited}
                            onChange={(e) => updateTopicProxy(topic.id, 'allow_limited', e.target.checked)}
                            className="rounded border-blue-300"
                          />
                          <span className="text-blue-900">Allow Limited Proxy (single-use)</span>
                        </label>
                      </div>

                      <label className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={topic.proxy_settings.board_receiver}
                          onChange={(e) => updateTopicProxy(topic.id, 'board_receiver', e.target.checked)}
                          className="rounded border-blue-300"
                        />
                        <span className="text-blue-900">Allow board members to receive proxy for this topic</span>
                      </label>
                      
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-blue-900 mb-1">Proxy Purpose:</label>
                        <select
                          value={topic.proxy_settings.purpose}
                          onChange={(e) => updateTopicProxy(topic.id, 'purpose', e.target.value)}
                          className="w-full px-2 py-1 border border-blue-300 rounded text-xs"
                        >
                          <option value="voting_and_quorum">Voting & Quorum</option>
                          <option value="quorum_only">Quorum Only (no voting)</option>
                        </select>
                        <p className="text-xs text-blue-700 mt-1">
                          {topic.proxy_settings.purpose === 'quorum_only' 
                            ? '📊 Counts toward quorum %, but proxy holder cannot vote' 
                            : '✅ Counts toward quorum AND proxy holder can vote'}
                        </p>
                      </div>

                      {!topic.proxy_settings.allow_general && !topic.proxy_settings.allow_limited && (
                        <p className="text-xs text-red-600 mt-2">⚠️ No proxy types selected - proxies disabled for this topic</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Topic Button */}
            <div className="border border-dashed border-gray-300 rounded-lg p-3">
              <h5 className="text-xs font-medium text-gray-700 mb-2">➕ Add Topic:</h5>
              <div className="grid grid-cols-2 gap-2">
                {topicTemplates.map((template) => (
                  <button
                    key={template.type}
                    onClick={() => addTopic(template)}
                    className="p-2 text-left rounded-lg text-xs bg-gray-50 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                  >
                    <div className="font-medium">{template.label}</div>
                    <div className="text-gray-500 text-xs mt-1">
                      {template.proxy.allow_general && template.proxy.allow_limited ? 'General or Limited' :
                       template.proxy.allow_general ? 'General Only' :
                       template.proxy.allow_limited ? 'Limited Only' : 'No Proxy'}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Votes per Property</label>
            <input
              type="number"
              min="1"
              max="10"
              value={formData.votes_per_property}
              onChange={(e) => setFormData(prev => ({...prev, votes_per_property: parseInt(e.target.value)}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Default Proxy section removed - proxy assignment now handled in E-Consent */}
          
          <div className="col-span-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.multiple_votes_per_property}
                onChange={(e) => setFormData(prev => ({...prev, multiple_votes_per_property: e.target.checked}))}
              />
              <span className="text-sm">Allow multiple votes per property</span>
            </label>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !formData.name}
            className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 transition-colors"
          >
            {saving ? 'Creating...' : 'Create Election'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Enhanced E-Consent Section Component (includes E-Voting Consent + Proxy Settings)
// Active Proxies Section Component
// Shows proxies for CURRENT ELECTION only - other election proxies shown separately
function ActiveProxiesSection({ user, hoaTimezone = 'America/Chicago', currentElectionId, currentElectionName }) {
  const [currentProxiesGiven, setCurrentProxiesGiven] = useState([]);
  const [currentProxiesReceived, setCurrentProxiesReceived] = useState([]);
  const [otherProxiesGiven, setOtherProxiesGiven] = useState([]);
  const [otherProxiesReceived, setOtherProxiesReceived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [showOtherElections, setShowOtherElections] = useState(false);

  useEffect(() => {
    loadActiveProxies();
  }, [currentElectionId]);

  const loadActiveProxies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/proxy/active');
      const allGiven = response.data.proxies_given || [];
      const allReceived = response.data.proxies_received || [];
      
      // Separate proxies by current election vs other elections
      if (currentElectionId) {
        setCurrentProxiesGiven(allGiven.filter(p => p.election_id === currentElectionId));
        setCurrentProxiesReceived(allReceived.filter(p => p.election_id === currentElectionId));
        setOtherProxiesGiven(allGiven.filter(p => p.election_id !== currentElectionId));
        setOtherProxiesReceived(allReceived.filter(p => p.election_id !== currentElectionId));
      } else {
        // No current election - show all as "other"
        setCurrentProxiesGiven([]);
        setCurrentProxiesReceived([]);
        setOtherProxiesGiven(allGiven);
        setOtherProxiesReceived(allReceived);
      }
    } catch (error) {
      console.error('Error loading active proxies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (proxyId) => {
    if (!window.confirm('Are you sure you want to revoke this proxy? This will restore your voting rights.')) {
      return;
    }

    try {
      setRevoking(proxyId);
      await api.post(`/api/proxy/${proxyId}/revoke`);
      alert('Proxy revoked successfully! Your voting rights have been restored.');
      loadActiveProxies(); // Reload
    } catch (error) {
      console.error('Error revoking proxy:', error);
      alert('Failed to revoke proxy: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRevoking(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return formatLocal(dateStr, hoaTimezone, 'MMM d, yyyy');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h4 className="text-lg font-medium text-gray-900 mb-4">🗳️ Active Proxies</h4>
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-3 text-gray-600">Loading active proxies...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-lg font-medium text-gray-900">🗳️ Active Proxies</h4>
        <button
          onClick={loadActiveProxies}
          className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Current Election Header */}
      {currentElectionName && (
        <div className="mb-4 p-2 bg-blue-100 rounded-lg">
          <p className="text-sm text-blue-800 font-medium">
            📌 Showing proxies for: <strong>{currentElectionName}</strong>
          </p>
        </div>
      )}

      {currentProxiesGiven.length === 0 && currentProxiesReceived.length === 0 && (
        <div className="p-4 bg-gray-50 rounded-lg text-center">
          <p className="text-gray-600 text-sm">No active proxies for the current election. Create a proxy in the section above.</p>
        </div>
      )}

      {/* Proxies Given for CURRENT ELECTION */}
      {currentProxiesGiven.length > 0 && (
        <div className="mb-6">
          <h5 className="text-sm font-semibold text-gray-700 mb-3">Proxies You've Given (Current Election):</h5>
          <div className="space-y-3">
            {currentProxiesGiven.map((proxy) => (
              <div key={proxy.id} className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        proxy.proxy_type === 'general' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {proxy.proxy_type === 'general' ? 'General Proxy' : 'Limited Proxy'}
                      </span>
                      {/* Show PURPOSE badge - this determines if they can VOTE */}
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        proxy.purpose === 'quorum_only' 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {proxy.purpose === 'quorum_only' ? '🔒 Quorum Only' : '🗳️ Voting & Quorum'}
                      </span>
                      <span className="text-xs text-gray-600">
                        {proxy.status === 'active' ? '✅ Active' : '⏸️ ' + proxy.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-900 mb-1">
                      <strong>Holder:</strong> {proxy.holder_name}
                    </p>
                    <p className="text-sm text-gray-700 mb-1">
                      <strong>Election:</strong> {proxy.election_name}
                    </p>
                    {proxy.specific_motion && (
                      <p className="text-sm text-gray-700 mb-1">
                        <strong>Motion:</strong> {proxy.specific_motion}
                        {proxy.specific_answer && <span> (Answer: {proxy.specific_answer})</span>}
                      </p>
                    )}
                    {proxy.purpose === 'quorum_only' && (
                      <p className="text-xs text-yellow-700 mt-1">
                        ⚠️ This proxy only counts for quorum - holder cannot vote on your behalf
                      </p>
                    )}
                    <p className="text-xs text-gray-600 mt-2">
                      <strong>Meeting:</strong> {formatDate(proxy.meeting_date)} | 
                      <strong> Expires:</strong> {formatDate(proxy.expiration_date)}
                    </p>
                  </div>
                  {proxy.status === 'active' && (
                    <button
                      onClick={() => handleRevoke(proxy.id)}
                      disabled={revoking === proxy.id}
                      className="ml-4 px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400"
                    >
                      {revoking === proxy.id ? 'Revoking...' : '🗑️ Revoke'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Proxies Received for CURRENT ELECTION */}
      {currentProxiesReceived.length > 0 && (
        <div className="mb-6">
          <h5 className="text-sm font-semibold text-gray-700 mb-3">Proxies You've Received (Current Election):</h5>
          <div className="space-y-3">
            {currentProxiesReceived.map((proxy) => (
              <div key={proxy.id} className="border border-green-200 bg-green-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    proxy.proxy_type === 'general' 
                      ? 'bg-purple-100 text-purple-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {proxy.proxy_type === 'general' ? 'General Proxy' : 'Limited Proxy'}
                  </span>
                  {/* Show PURPOSE badge - this determines if you can VOTE with this proxy */}
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    proxy.purpose === 'quorum_only' 
                      ? 'bg-yellow-100 text-yellow-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {proxy.purpose === 'quorum_only' ? '🔒 Quorum Only' : '🗳️ Can Vote'}
                  </span>
                  <span className="text-xs text-gray-600">
                    {proxy.status === 'active' ? '✅ Active' : '⏸️ ' + proxy.status}
                  </span>
                </div>
                <p className="text-sm text-gray-900 mb-1">
                  <strong>From:</strong> {proxy.originator_name}
                </p>
                {proxy.specific_motion && (
                  <p className="text-sm text-gray-700 mb-1">
                    <strong>Motion:</strong> {proxy.specific_motion}
                    {proxy.specific_answer && <span> (Their answer: {proxy.specific_answer})</span>}
                  </p>
                )}
                {proxy.purpose === 'quorum_only' && (
                  <p className="text-xs text-yellow-700 mt-1">
                    ⚠️ Quorum only - you cannot vote on behalf of {proxy.originator_name} for this item
                  </p>
                )}
                {proxy.purpose !== 'quorum_only' && !proxy.specific_answer && (
                  <p className="text-xs text-green-700 mt-1">
                    ✅ You can vote on behalf of {proxy.originator_name} for this item
                  </p>
                )}
                <p className="text-xs text-gray-600 mt-2">
                  <strong>Meeting:</strong> {formatDate(proxy.meeting_date)} | 
                  <strong> Expires:</strong> {formatDate(proxy.expiration_date)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Other Elections Proxies - Collapsed Section */}
      {(otherProxiesGiven.length > 0 || otherProxiesReceived.length > 0) && (
        <div className="mt-6 border-t border-gray-200 pt-4">
          <button
            onClick={() => setShowOtherElections(!showOtherElections)}
            className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-600 hover:text-gray-800"
          >
            <span>📁 Proxies from Other Elections ({otherProxiesGiven.length + otherProxiesReceived.length})</span>
            <span>{showOtherElections ? '▼' : '▶'}</span>
          </button>
          
          {showOtherElections && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-3">
                These proxies are for other elections and do not affect the current vote.
              </p>
              
              {/* Other Elections - Given */}
              {otherProxiesGiven.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-xs font-semibold text-gray-600 mb-2">Given:</h6>
                  <div className="space-y-2">
                    {otherProxiesGiven.map((proxy) => (
                      <div key={proxy.id} className="border border-gray-200 bg-white rounded p-2 text-xs">
                        <div className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded ${
                            proxy.proxy_type === 'general' ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {proxy.proxy_type === 'general' ? 'General' : 'Limited'}
                          </span>
                          <span className="text-gray-600">→ {proxy.holder_name}</span>
                        </div>
                        <div className="mt-1 text-gray-500">
                          <strong>Election:</strong> {proxy.election_name || 'Unknown'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Other Elections - Received */}
              {otherProxiesReceived.length > 0 && (
                <div>
                  <h6 className="text-xs font-semibold text-gray-600 mb-2">Received:</h6>
                  <div className="space-y-2">
                    {otherProxiesReceived.map((proxy) => (
                      <div key={proxy.id} className="border border-gray-200 bg-white rounded p-2 text-xs">
                        <div className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded ${
                            proxy.proxy_type === 'general' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {proxy.proxy_type === 'general' ? 'General' : 'Limited'}
                          </span>
                          <span className="text-gray-600">from {proxy.originator_name}</span>
                        </div>
                        <div className="mt-1 text-gray-500">
                          <strong>Election:</strong> {proxy.election_name || 'Unknown'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-800">
          <strong>Note:</strong> You can only revoke proxies before the vote is cast. 
          {currentProxiesGiven.some(p => p.proxy_type === 'general') && ' General proxies disable all your voting rights for that election.'}
          {currentProxiesGiven.some(p => p.proxy_type === 'limited') && ' Limited proxies only affect specific motions - you can still vote on other items.'}
        </p>
      </div>
    </div>
  );
}

function EConsentSection({ user, onRefresh, hoaTimezone = 'America/Chicago' }) {
  const [consentData, setConsentData] = useState({
    consent_type: user?.e_voting_consent || '',
    full_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
    // FIX: Use local date string to avoid timezone shifts - show actual current date  
    consent_date: (() => {
      const today = new Date();
      return today.getFullYear() + '-' + 
             String(today.getMonth() + 1).padStart(2, '0') + '-' + 
             String(today.getDate()).padStart(2, '0');
    })()
  });
  const [submitting, setSubmitting] = useState(false);
  const [isLocked, setIsLocked] = useState(!!user?.e_voting_consent && !user?.e_voting_consent_change_requested);
  const [showRequestChange, setShowRequestChange] = useState(false);
  const [coOwners, setCoOwners] = useState([]);
  const [primaryVoter, setPrimaryVoter] = useState(null);
  const [loadingPrimary, setLoadingPrimary] = useState(true);

  // Check if consent is expired
  const isExpired = user?.e_voting_consent_expires && 
    new Date(user.e_voting_consent_expires) < new Date();

  // Load co-owners and primary voter for property
  React.useEffect(() => {
    if (user?.property_address) {
      loadCoOwnersAndPrimary();
    }
  }, [user?.property_address]);

  const loadCoOwnersAndPrimary = async () => {
    try {
      setLoadingPrimary(true);
      // Get all users with same property address from HOA members
      // We need to fetch from a different endpoint that returns all HOA members
      // For now, use a simpler approach - just show option to set current user as primary
      // In a production system, this would query all users with the same property_address
      
      // For MVP: If user has property address, they can set themselves as primary
      if (user?.property_address) {
        // Try to get current primary voter (voting-only)
        try {
          const primaryResponse = await api.get(`/api/vote/property-primary-voter?property_address=${encodeURIComponent(user.property_address)}&hoa_id=${user.hoa_id}`);
          setPrimaryVoter(primaryResponse.data.primary_voter);
          
          // For now, show only current user as co-owner
          // In full implementation, query all users with same property_address
          setCoOwners([user]);
        } catch (err) {
          console.log('No primary voter set yet');
          setPrimaryVoter(null);
          setCoOwners([user]);
        }
      }
    } catch (error) {
      console.error('Error loading primary voter:', error);
    } finally {
      setLoadingPrimary(false);
    }
  };

  // Load co-owner consent status
  const [coOwnerConsent, setCoOwnerConsent] = useState({ co_owners: [], all_consented: true });
  
  React.useEffect(() => {
    loadCoOwnerConsent();
  }, [user?.id]);
  
  const loadCoOwnerConsent = async () => {
    try {
      const response = await api.get('/api/vote/e-voting-consent/co-owners');
      setCoOwnerConsent(response.data);
    } catch (error) {
      console.error('Error loading co-owner consent:', error);
    }
  };

  const handleSetPrimaryVoter = async (userId) => {
    try {
      await api.put('/api/vote/property-primary-voter', null, {
        params: {
          property_address: user.property_address,
          new_primary_user_id: userId,
          hoa_id: user.hoa_id
        }
      });
      alert('✅ Primary voter set successfully!\n\nThis designation is ONLY for voting/elections and does not affect other system features.');
      loadCoOwnersAndPrimary();
    } catch (error) {
      console.error('Error setting primary voter:', error);
      alert('❌ Failed to set primary voter: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Update lock status when user data changes
  React.useEffect(() => {
    const hasConsent = !!user?.e_voting_consent;
    const changeRequested = !!user?.e_voting_consent_change_requested;
    const expired = isExpired;
    
    setIsLocked(hasConsent && !changeRequested && !expired);
    
    if (expired) {
      setShowRequestChange(true);
    }
  }, [user?.e_voting_consent, user?.e_voting_consent_change_requested, isExpired]);

  const handleSubmitConsent = async () => {
    // Validate required fields
    if (!consentData.consent_type) {
      alert('Please select your e-voting consent preference.');
      return;
    }
    
    if (!consentData.full_name.trim()) {
      alert('Please enter your full name.');
      return;
    }
    
    if (!consentData.consent_date) {
      alert('Please enter the consent date.');
      return;
    }

    // Confirm submission
    const confirmMessage = `
🗳️ SUBMIT E-VOTING CONSENT TO HOA

You are about to formally submit your e-voting consent:

Name: ${consentData.full_name}
Consent Type: ${consentData.consent_type === 'consent' ? 'I CONSENT to e-voting' : 'I DO NOT CONSENT to e-voting'}
Date: ${consentData.consent_date}
${consentData.consent_type === 'consent' ? 'Valid for: 1 year from today' : ''}

This formal submission will:
• Be recorded by the HOA board
• Send you an email confirmation
• ${consentData.consent_type === 'consent' ? 'Enable your e-voting rights for 1 year' : 'Require you to vote by mail or in person'}

NOTE: Proxy assignments are now managed separately per election/motion.

Are you sure you want to submit this consent?
    `;

    if (!confirm(confirmMessage)) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/api/vote/e-voting-consent', {
        consent_type: consentData.consent_type,
        full_name: consentData.full_name,
        consent_date: consentData.consent_date
      });

      // Update local state to reflect submission
      setIsLocked(true);
      setShowRequestChange(false);

      alert(`✅ E-VOTING CONSENT SUBMITTED SUCCESSFULLY!\n\n${response.data.message}\n\nYou will receive an email confirmation shortly.`);
      
      // Refresh data
      if (onRefresh) {
        onRefresh();
      }
      
    } catch (error) {
      console.error('Error submitting e-voting consent:', error);
      alert('❌ Failed to submit e-voting consent. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestChange = async () => {
    if (!confirm('Request to change your e-voting consent?\n\nThis will allow you to modify your consent status and will notify the board of your change request.')) {
      return;
    }

    try {
      await api.post('/api/vote/e-voting-consent/request-change');
      
      setIsLocked(false);
      setShowRequestChange(false);
      
      alert('✅ Change request submitted successfully!\n\nYou can now modify your e-voting consent. The board has been notified of your request.');
      
    } catch (error) {
      console.error('Error requesting consent change:', error);
      alert('❌ Failed to request consent change. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-100 rounded-full">
            <div className="w-6 h-6 text-blue-600">🗳️</div>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">E-Consent Management</h3>
            <p className="text-gray-600">Manage your electronic voting consent and proxy settings</p>
          </div>
        </div>
      </div>

      {/* PRIMARY VOTER SECTION - PROMINENT PLACEMENT */}
      {user?.property_address && (
        <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl p-6 shadow-lg text-white">
          <h4 className="text-2xl font-bold mb-3 flex items-center gap-2">
            👤 Primary Voter Designation
          </h4>
          <p className="text-blue-100 mb-4">
            For Voting/Elections Only - Does NOT affect other system features
          </p>
          
          {loadingPrimary ? (
            <div className="text-white">Loading...</div>
          ) : (
            <div className="space-y-4">
              <div className="bg-white/10 backdrop-blur rounded-lg p-4 border border-white/20">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-white mb-1">
                      Property: <span className="font-bold">{user.property_address}</span>
                    </div>
                    {primaryVoter ? (
                      <div className="text-green-300 font-medium flex items-center gap-2">
                        <span className="text-2xl">✅</span>
                        Primary Voter: {primaryVoter.name}
                      </div>
                    ) : (
                      <div className="text-yellow-300 font-medium flex items-center gap-2">
                        <span className="text-2xl">⚠️</span>
                        No primary voter designated yet
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => handleSetPrimaryVoter(user.id)}
                className={`w-full px-6 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 ${
                  primaryVoter?.user_id === user.id
                    ? 'bg-green-500 text-white cursor-default shadow-lg'
                    : 'bg-white text-blue-600 hover:bg-blue-50 shadow-lg'
                }`}
                disabled={primaryVoter?.user_id === user.id}
              >
                {primaryVoter?.user_id === user.id ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="text-2xl">✅</span> You are the Primary Voter
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <span className="text-2xl">👤</span> Set Yourself as Primary Voter
                  </span>
                )}
              </button>
              
              <div className="bg-blue-900/30 rounded-lg p-4 border border-blue-400/30">
                <p className="text-blue-100 font-medium mb-2 flex items-center gap-2">
                  <span className="text-xl">ℹ️</span> What is Primary Voter?
                </p>
                <ul className="text-blue-200 text-sm space-y-1 ml-6">
                  <li>• Only primary voter can submit electronic votes for this property</li>
                  <li>• Only primary voter can manage proxies for this property</li>
                  <li>• Ensures one vote per property in elections</li>
                  <li>• Does NOT affect payments, documents, or other features</li>
                  <li>• Can be changed if there are no active votes</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* E-Voting Consent Section */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h4 className="text-lg font-medium text-gray-900 mb-4">🗳️ Electronic Voting Consent</h4>
        
        {/* Current Status Display */}
        {isLocked && !isExpired && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="font-medium text-green-900">✅ E-Consent Status: Active</h5>
                <div className="text-sm text-green-800 mt-2 space-y-1">
                  <div><strong>Current Status:</strong> {user?.e_voting_consent === 'consent' ? 'CONSENTED to e-voting' : 'DO NOT CONSENT to e-voting'}</div>
                  <div><strong>Last Consent Date:</strong> {user?.e_voting_consent_date ? formatLocal(user.e_voting_consent_date, hoaTimezone, 'MMM d, yyyy') : 'Unknown'}</div>
                  <div><strong>Expires On:</strong> {user?.e_voting_consent_expires ? formatLocal(user.e_voting_consent_expires, hoaTimezone, 'MMM d, yyyy') : 'Unknown'}</div>
                  <div className="text-xs text-green-700 mt-2 p-2 bg-green-100 rounded">
                    Your consent is locked and active. Use "Request Change" below if you need to modify it.
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowRequestChange(true)}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                Request Change
              </button>
            </div>
          </div>
        )}

        {/* Expired Status */}
        {isExpired && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <h5 className="font-medium text-red-900">⚠️ E-Voting Consent Expired</h5>
            <p className="text-sm text-red-800 mt-1">
              Your e-voting consent expired on {formatLocal(user?.e_voting_consent_expires, hoaTimezone, 'MMM d, yyyy')}. 
              Please renew your consent to continue e-voting.
            </p>
          </div>
        )}

        {/* Co-Owner Consent Status - Show if multiple owners on property */}
        {coOwnerConsent.co_owners?.length > 1 && (
          <div className={`mb-6 p-4 rounded-lg border-2 ${coOwnerConsent.all_consented ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'}`}>
            <h5 className={`font-medium mb-3 flex items-center gap-2 ${coOwnerConsent.all_consented ? 'text-green-900' : 'text-orange-900'}`}>
              👥 Property Co-Owner Consent Status
              {coOwnerConsent.all_consented ? (
                <span className="text-sm bg-green-200 text-green-800 px-2 py-0.5 rounded-full">✅ All Consented</span>
              ) : (
                <span className="text-sm bg-orange-200 text-orange-800 px-2 py-0.5 rounded-full">⚠️ Pending Consent</span>
              )}
            </h5>
            
            {!coOwnerConsent.all_consented && (
              <div className="bg-white/50 rounded p-3 mb-3 text-sm text-orange-800">
                <strong>⚠️ Important:</strong> All property owners must provide e-consent before any owner can cast an e-vote. 
                Please ensure all owners listed below have consented.
              </div>
            )}
            
            <div className="space-y-2">
              {coOwnerConsent.co_owners?.map((owner, idx) => (
                <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${owner.is_current_user ? 'bg-blue-100 border border-blue-300' : 'bg-white'}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{owner.has_consented ? '✅' : '❌'}</span>
                    <div>
                      <div className={`font-medium ${owner.is_current_user ? 'text-blue-900' : 'text-gray-900'}`}>
                        {owner.name} {owner.is_current_user && <span className="text-xs bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded ml-1">You</span>}
                      </div>
                      <div className="text-xs text-gray-500">{owner.email}</div>
                    </div>
                  </div>
                  <div className={`text-sm font-medium ${owner.has_consented ? 'text-green-700' : 'text-red-700'}`}>
                    {owner.has_consented ? 'Consented' : 'Not Consented'}
                  </div>
                </div>
              ))}
            </div>
            
            <p className="text-xs text-gray-600 mt-3">
              Property: {coOwnerConsent.property_identifier || 'N/A'}
            </p>
          </div>
        )}

        {/* Consent Form */}
        {(!isLocked || isExpired) ? (
          <div className="space-y-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h5 className="font-medium text-yellow-900 mb-2">📋 Formal E-Voting Consent</h5>
              <p className="text-sm text-yellow-800">
                This is a formal legal document. Your consent status will be recorded by the HOA board and maintained for official records.
                {isExpired && ' Your previous consent has expired and must be renewed.'}
              </p>
            </div>

            {/* Full Name Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Legal Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={consentData.full_name}
                onChange={(e) => setConsentData({...consentData, full_name: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your full legal name as it appears on official documents"
                required
              />
              <p className="text-xs text-gray-500 mt-1">This must match your legal name for voting verification</p>
            </div>

            {/* Consent Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Consent Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={consentData.consent_date}
                onChange={(e) => setConsentData({...consentData, consent_date: e.target.value})}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Date cannot be in the past</p>
            </div>

            {/* Consent Options */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                E-Voting Consent Choice <span className="text-red-500">*</span>
              </label>
              <div className="space-y-4">
                <label className="flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-colors hover:bg-blue-50 hover:border-blue-300">
                  <input
                    type="radio"
                    name="consent_type"
                    value="consent"
                    checked={consentData.consent_type === 'consent'}
                    onChange={(e) => setConsentData({...consentData, consent_type: e.target.value})}
                    className="mt-1 w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <div className="font-medium text-gray-900">✅ I CONSENT to E-Voting</div>
                    <div className="text-sm text-gray-600 mt-1">
                      I agree to participate in electronic voting for HOA elections and important matters.
                      This consent is valid for one (1) year from the date above.
                    </div>
                  </div>
                </label>

                <label className="flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-colors hover:bg-red-50 hover:border-red-300">
                  <input
                    type="radio"
                    name="consent_type"
                    value="do_not_consent"
                    checked={consentData.consent_type === 'do_not_consent'}
                    onChange={(e) => setConsentData({...consentData, consent_type: e.target.value})}
                    className="mt-1 w-4 h-4 text-red-600 focus:ring-red-500"
                  />
                  <div>
                    <div className="font-medium text-gray-900">❌ I DO NOT CONSENT to E-Voting</div>
                    <div className="text-sm text-gray-600 mt-1">
                      I prefer to vote by mail or in person only. I will not participate in electronic voting.
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6 opacity-50 pointer-events-none">
            <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
              <h5 className="font-medium text-gray-700 mb-2">📋 E-Voting Consent Form (Locked)</h5>
              <p className="text-sm text-gray-600">
                Your consent has been submitted and is currently locked. Use "Request Change" above to modify your consent.
              </p>
            </div>

            {/* Greyed out form fields showing current values */}
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-2">
                Full Legal Name
              </label>
              <input
                type="text"
                value={`${user?.first_name || ''} ${user?.last_name || ''}`.trim()}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                disabled
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-2">
                Consent Date
              </label>
              <input
                type="date"
                // FIX: Use local date string to avoid timezone shifts - display actual consent date
                value={user?.e_voting_consent_date ? (() => {
                  const date = new Date(user.e_voting_consent_date + 'T00:00:00'); // Treat as local date
                  return date.getFullYear() + '-' + 
                         String(date.getMonth() + 1).padStart(2, '0') + '-' + 
                         String(date.getDate()).padStart(2, '0');
                })() : ''}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                disabled
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-4">
                E-Voting Consent Choice
              </label>
              <div className="space-y-4">
                <label className="flex items-start space-x-3 p-4 border-2 border-gray-300 rounded-lg bg-gray-100">
                  <input
                    type="radio"
                    checked={user?.e_voting_consent === 'consent'}
                    className="mt-1 w-4 h-4 text-gray-400"
                    disabled
                  />
                  <div>
                    <div className="font-medium text-gray-500">✅ I CONSENT to E-Voting</div>
                    <div className="text-sm text-gray-400 mt-1">
                      I agree to participate in electronic voting for HOA elections and important matters.
                    </div>
                  </div>
                </label>

                <label className="flex items-start space-x-3 p-4 border-2 border-gray-300 rounded-lg bg-gray-100">
                  <input
                    type="radio"
                    checked={user?.e_voting_consent === 'do_not_consent'}
                    className="mt-1 w-4 h-4 text-gray-400"
                    disabled
                  />
                  <div>
                    <div className="font-medium text-gray-500">❌ I DO NOT CONSENT to E-Voting</div>
                    <div className="text-sm text-gray-400 mt-1">
                      I prefer to vote by mail or in person only. I will not participate in electronic voting.
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Request Change Modal */}
        {showRequestChange && isLocked && !isExpired && (
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h5 className="font-medium text-yellow-900 mb-2">🔄 Request Consent Change</h5>
            <p className="text-sm text-yellow-800 mb-4">
              Your current consent is locked. Click below to request permission to change your e-voting consent status.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleRequestChange}
                className="px-4 py-2 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700 transition-colors"
              >
                Request Change Permission
              </button>
              <button
                onClick={() => setShowRequestChange(false)}
                className="px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Submit Section (E-Consent Only) */}
      {(!isLocked || isExpired) && (
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="border-t border-gray-200 pt-6">
            <button
              onClick={handleSubmitConsent}
              disabled={submitting || !consentData.consent_type || !consentData.full_name.trim() || !consentData.consent_date}
              className="w-full px-6 py-4 bg-blue-600 text-white font-medium text-lg rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Submitting to HOA Board...
                </div>
              ) : (
                '📋 Submit E-Consent to HOA'
              )}
            </button>
            
            <p className="text-xs text-center text-gray-500 mt-3">
              By submitting, you confirm this information is accurate and consent to formal recording by the HOA board.
              You will receive an email confirmation upon successful submission.
              <br />
              <strong>Note:</strong> Proxy voting is now managed separately per election/motion in the Voting section.
            </p>
          </div>
        </div>
      )}

      {/* Download Blank Proxy Form */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">📄 Download Blank Proxy Form</h4>
            <p className="text-sm text-gray-600">
              Print this professional proxy form for offline proxy assignments. Completed forms can be uploaded to the portal.
            </p>
          </div>
          <button
            onClick={() => {
              // Get HOA name and current election if available
              const hoaName = user?.hoa_name || 'HOA';
              const electionName = 'Upcoming Election';
              const meetingDate = formatLocal(new Date().toISOString(), hoaTimezone, 'MMM d, yyyy');
              generateBlankProxyForm(hoaName, electionName, meetingDate);
            }}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-colors shadow-md flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download Blank Form
          </button>
        </div>
      </div>
    </div>
  );
}

// Proxy Management Section - Independent from E-Consent
function ProxyManagementSection({ user, currentElection, onProxyChange, hoaTimezone = 'America/Chicago' }) {
  const [proxies, setProxies] = useState({ general_proxy: null, limited_proxies: {} });
  const [motionSettings, setMotionSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedMotion, setSelectedMotion] = useState(null);
  const [proxyType, setProxyType] = useState('limited');
  const [holderEmail, setHolderEmail] = useState('');
  const [holderName, setHolderName] = useState('');
  const [specificAnswer, setSpecificAnswer] = useState('');
  const [proxyMode, setProxyMode] = useState('specific'); // 'specific' or 'delegate'
  
  // Bulk proxy modal state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkHolderEmail, setBulkHolderEmail] = useState('');
  const [bulkHolderName, setBulkHolderName] = useState('');
  const [bulkMode, setBulkMode] = useState('delegate'); // 'delegate' or 'specific'
  const [bulkAnswer, setBulkAnswer] = useState(''); // For specific answer mode
  const [bulkPurpose, setBulkPurpose] = useState('quorum_and_vote');
  const [bulkHolder, setBulkHolder] = useState(null);
  const [searchingHolder, setSearchingHolder] = useState(false);

  // Get motions only (for specific answer mode)
  const motionsOnly = (currentElection?.special_topics || []).map(t => t.name || t.title || 'Unnamed Motion');
  
  // Get positions only (for delegate mode - can include nominee positions)
  const positionsOnly = (currentElection?.positions_up_for_election || []).map(p => p.label || p.role);
  
  // Combined list for delegate mode (positions + motions)
  const allItemsForDelegate = [...positionsOnly, ...motionsOnly];
  
  // For display and specific answer mode, use motions only
  // For delegate mode, we'll use allItemsForDelegate
  const allMotions = motionsOnly;

  // Load current proxies and motion settings
  const loadProxies = async () => {
    if (!currentElection?.id) return;
    
    try {
      const response = await api.get(`/api/proxy/election/${currentElection.id}/my-proxies`);
      setProxies(response.data);
    } catch (error) {
      console.error('Error loading proxies:', error);
    }
  };

  const loadMotionSettings = async () => {
    if (!currentElection?.id) return;
    
    try {
      const response = await api.get(`/api/proxy/election/${currentElection.id}/motion-settings`);
      setMotionSettings(response.data.motion_settings || {});
    } catch (error) {
      console.error('Error loading motion settings:', error);
    }
  };

  React.useEffect(() => {
    if (currentElection?.id) {
      console.log('🔍 Loading proxies and motion settings for election:', currentElection.id);
      loadProxies();
      loadMotionSettings();
    }
  }, [currentElection?.id]);

  const handleOpenCreateModal = (motion = null) => {
    console.log('🔍 Opening create proxy modal for motion:', motion);
    console.log('🔍 Current motion settings:', motionSettings);
    setSelectedMotion(motion);
    setProxyType(motion ? 'limited' : 'limited');
    setShowCreateModal(true);
  };

  // Analyze motions to determine what bulk options are available
  const analyzeBulkOptions = () => {
    const analysis = {
      allQuorumOnly: true,          // Are ALL motions quorum-only?
      anyVotingAllowed: false,      // Does ANY motion allow voting?
      allSameAnswers: true,         // Do ALL motions have same answer options?
      commonAnswers: [],            // The common answer options (if all same)
      answerMismatchReason: null,   // Why answers don't match
    };
    
    let firstAnswerSet = null;
    
    for (const motion of allMotions) {
      const cfg = motionSettings[motion] || {};
      const purpose = cfg.purpose || 'voting_quorum';
      const answers = cfg.vote_options || cfg.answers || [];
      
      // Check quorum vs voting
      if (purpose !== 'quorum_only') {
        analysis.allQuorumOnly = false;
        analysis.anyVotingAllowed = true;
      }
      
      // Check answer consistency (only matters for voting motions)
      if (purpose !== 'quorum_only' && answers.length > 0) {
        const answerKey = JSON.stringify(answers.sort());
        
        if (firstAnswerSet === null) {
          firstAnswerSet = answerKey;
          analysis.commonAnswers = [...answers];
        } else if (answerKey !== firstAnswerSet) {
          analysis.allSameAnswers = false;
          analysis.answerMismatchReason = `Motion "${motion}" has different answer options than others`;
        }
      }
    }
    
    // If all are quorum only, answers don't matter
    if (analysis.allQuorumOnly) {
      analysis.allSameAnswers = true;
      analysis.commonAnswers = [];
    }
    
    return analysis;
  };

  // Open bulk proxy modal
  const handleOpenBulkModal = () => {
    if (allMotions.length === 0) {
      alert('No motions available to create proxies for.');
      return;
    }
    
    const analysis = analyzeBulkOptions();
    
    setBulkHolderEmail('');
    setBulkHolderName('');
    setBulkMode('delegate');
    setBulkAnswer('');
    // Default to quorum_only if all motions are quorum only, otherwise voting
    setBulkPurpose(analysis.allQuorumOnly ? 'quorum_only' : 'quorum_and_vote');
    setBulkHolder(null);
    setShowBulkModal(true);
  };
  
  // Get current bulk analysis (recalculate when modal is open)
  const bulkAnalysis = showBulkModal ? analyzeBulkOptions() : null;

  // Search for holder in bulk modal
  const handleBulkHolderSearch = async () => {
    if (!bulkHolderEmail) {
      alert('Please enter proxy holder email.');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(bulkHolderEmail)) {
      alert('Please enter a valid email address.');
      return;
    }

    setSearchingHolder(true);
    try {
      const response = await api.get(`/api/proxy/search-user?email=${encodeURIComponent(bulkHolderEmail)}`);
      if (response.data.success && response.data.user) {
        setBulkHolder(response.data.user);
        setBulkHolderName(response.data.user.name || `${response.data.user.first_name || ''} ${response.data.user.last_name || ''}`.trim());
      } else {
        alert(`User with email ${bulkHolderEmail} not found in HOA.`);
        setBulkHolder(null);
      }
    } catch (error) {
      alert('Failed to verify proxy holder: ' + (error.response?.data?.detail || error.message));
      setBulkHolder(null);
    } finally {
      setSearchingHolder(false);
    }
  };

  // Execute bulk proxy creation
  const handleCreateBulkProxies = async () => {
    if (!bulkHolder) {
      alert('Please search and verify the proxy holder first.');
      return;
    }

    const analysis = analyzeBulkOptions();
    
    // Validate specific answer only if needed
    if (!analysis.allQuorumOnly && bulkPurpose === 'quorum_and_vote' && bulkMode === 'specific' && !bulkAnswer) {
      alert('Please select an answer for all motions.');
      return;
    }

    setLoading(true);
    let successCount = 0;
    let failCount = 0;
    let skippedDuplicates = 0;

    // IMPORTANT: Determine which items to create proxies for based on mode
    // - Delegate mode: Include BOTH positions (nominees) AND motions
    // - Specific Answer mode: ONLY motions (can't vote Yes/No for a person)
    const itemsToProcess = bulkMode === 'delegate' ? allItemsForDelegate : motionsOnly;
    
    console.log(`📋 Bulk proxy mode: ${bulkMode}, processing ${itemsToProcess.length} items`);
    console.log(`   - Positions: ${positionsOnly.length}, Motions: ${motionsOnly.length}`);

    for (const item of itemsToProcess) {
      const isPosition = positionsOnly.includes(item);
      const isMotion = motionsOnly.includes(item);
      
      // ============================================
      // GET THE ACTUAL PROXY SETTINGS FROM THE ELECTION CONFIG
      // DO NOT HARDCODE ANYTHING - READ FROM SOURCE
      // ============================================
      let actualPurpose = 'quorum_and_vote'; // Default
      let actualSettings = null;
      
      if (isPosition) {
        // Find the position's proxy settings directly from election config
        const positionData = (currentElection?.positions_up_for_election || []).find(
          p => (p.label || p.role) === item
        );
        if (positionData?.proxy_settings) {
          actualSettings = positionData.proxy_settings;
          // Use the ACTUAL purpose from the config
          actualPurpose = actualSettings.purpose || 'voting_and_quorum';
          // Normalize the purpose value
          if (actualPurpose === 'voting_and_quorum') actualPurpose = 'quorum_and_vote';
        }
        console.log(`   Position "${item}": actualPurpose=${actualPurpose} from config:`, actualSettings);
      } else if (isMotion) {
        // Find the motion's proxy settings directly from election config
        const motionData = (currentElection?.special_topics || []).find(
          t => (t.name || t.title) === item
        );
        if (motionData?.proxy_settings) {
          actualSettings = motionData.proxy_settings;
          // Use the ACTUAL purpose from the config
          actualPurpose = actualSettings.purpose || 'quorum_and_vote';
          // Normalize the purpose value
          if (actualPurpose === 'voting_and_quorum') actualPurpose = 'quorum_and_vote';
        }
        console.log(`   Motion "${item}": actualPurpose=${actualPurpose} from config:`, actualSettings);
      }
      
      // The ACTUAL purpose from the election config is the source of truth
      const itemPurpose = actualPurpose;
      
      // Only set specific vote if:
      // 1. This item allows voting (not quorum-only)
      // 2. User selected specific mode
      // 3. It's a motion (not a position - can't vote Yes/No for a person)
      const specificVote = (itemPurpose !== 'quorum_only' && bulkMode === 'specific' && isMotion) ? bulkAnswer : null;
      
      // For positions, always use delegate mode (can't have specific answer for a person)
      const mode = isPosition ? 'delegate' : (itemPurpose === 'quorum_only' ? 'delegate' : bulkMode);
      
      try {
        const response = await api.post(`/api/proxy/election/${currentElection.id}/create`, {
          holder_id: bulkHolder.id,
          holder_email: bulkHolderEmail,
          holder_name: bulkHolderName,
          proxy_type: 'limited',
          specific_motion: item,
          purpose: itemPurpose,
          specific_vote: specificVote,
          mode: mode
        });
        
        if (response.data?.skipped_duplicate) {
          skippedDuplicates++;
          console.log(`   ⚠️ Skipped duplicate proxy for ${item}`);
        } else {
          successCount++;
          console.log(`   ✅ Created proxy for ${item} - purpose: ${itemPurpose}, mode: ${mode}`);
        }
      } catch (error) {
        // Check if it's a duplicate error (409 Conflict or 400 with duplicate message)
        const isDuplicate = error.response?.status === 409 || 
                           error.response?.status === 400 && 
                           (error.response?.data?.detail?.toLowerCase().includes('duplicate') ||
                            error.response?.data?.detail?.toLowerCase().includes('already have'));
        
        if (isDuplicate) {
          skippedDuplicates++;
          console.log(`   ⚠️ Duplicate proxy for ${item}`);
        } else {
          console.error(`   ❌ Failed to create proxy for ${item}:`, error);
          failCount++;
        }
      }
    }

    setLoading(false);
    setShowBulkModal(false);
    
    let message = `✅ Bulk Proxy Creation Complete\n\nSuccess: ${successCount}`;
    if (skippedDuplicates > 0) {
      message += `\nSkipped (already exists): ${skippedDuplicates}`;
    }
    if (failCount > 0) {
      message += `\nFailed: ${failCount}`;
    }
    
    alert(message);
    
    if (successCount > 0 || skippedDuplicates > 0) {
      loadProxies();
      if (onProxyChange) onProxyChange();
    }
  };

  const handleCreateProxy = async () => {
    if (!holderEmail || !holderName) {
      alert('Please enter proxy holder name and email.');
      return;
    }

    console.log('🔍 Creating proxy - Email:', holderEmail, 'Name:', holderName);

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(holderEmail)) {
      alert('Please enter a valid email address.');
      return;
    }

    // Find holder user by email
    let holder;
    try {
      // Search for user by email
      const response = await api.get(`/api/proxy/search-user?email=${encodeURIComponent(holderEmail)}`);
      console.log('🔍 User search response:', response.data);
      
      if (!response.data.success || !response.data.user) {
        alert(`User with email ${holderEmail} not found in HOA.\n\nPlease ensure:\n• The email is correct\n• The person is registered as a homeowner or board member\n• They have completed initial setup`);
        return;
      }
      
      holder = response.data.user;
      console.log('✅ Found holder:', holder);
    } catch (error) {
      console.error('❌ Error finding holder:', error);
      alert('Failed to verify proxy holder: ' + (error.response?.data?.detail || error.message || 'Please try again.'));
      return;
    }
    
    // Get motion settings
    const motionCfg = selectedMotion ? motionSettings[selectedMotion] : null;
    
    console.log('🔍 Motion config for proxy creation:', motionCfg);
    
    // Build confirmation message
    let confirmMsg = `🤝 CREATE PROXY ASSIGNMENT\n\n`;
    
    if (proxyType === 'general') {
      confirmMsg += `GENERAL PROXY\nCovers ALL motions/positions\n`;
    } else if (motionCfg?.purpose === 'quorum_only') {
      confirmMsg += `PROXY FOR QUORUM ONLY\nMotion: ${selectedMotion}\n⚠️ This proxy can only be used to establish quorum, not for voting.\n`;
    } else {
      confirmMsg += `LIMITED PROXY\nMotion: ${selectedMotion}\n`;
      if (proxyMode === 'specific' && specificAnswer) {
        confirmMsg += `Your Answer: ${specificAnswer} (Proxy holder MUST vote this way)\n`;
      } else if (proxyMode === 'delegate') {
        confirmMsg += `Mode: Delegate (Proxy holder can choose any answer)\n`;
      }
    }
    
    confirmMsg += `\nProxy Holder: ${holderName} (${holderEmail})\n\n⚠️ IMPORTANT: Once assigned, this proxy becomes part of the official HOA record and will generate a legal document visible ONLY to you and the proxy holder.\n\nAre you sure you want to create this proxy?`;

    if (!confirm(confirmMsg)) return;

    setLoading(true);
    try {
      console.log('🔍 Sending proxy creation request:', {
        election_id: currentElection.id,
        proxy_type: proxyType,
        holder_id: holder.id,
        specific_motion: proxyType === 'limited' ? selectedMotion : null,
        specific_answer: (proxyMode === 'specific' && specificAnswer) ? specificAnswer : null,
        purpose: motionCfg?.purpose || 'voting_quorum'
      });
      
      const response = await api.post('/api/proxy/create', {
        election_id: currentElection.id,
        proxy_type: proxyType,
        holder_id: holder.id,
        specific_motion: proxyType === 'limited' ? selectedMotion : null,
        specific_answer: (proxyMode === 'specific' && specificAnswer) ? specificAnswer : null,
        purpose: motionCfg?.purpose || 'voting_quorum'
      });

      console.log('✅ Proxy created:', response.data);
      
      alert(`✅ Proxy created successfully!\n\n- Document ID: ${response.data.document_id}\n- Proxy ID: ${response.data.proxy_id}\n- Status: ${response.data.status}\n\nThe proxy document has been generated and is visible ONLY to you and ${holder.first_name} ${holder.last_name} in the Documents tab (Legal category).`);
      
      setShowCreateModal(false);
      setSelectedMotion(null);
      setHolderEmail('');
      setHolderName('');
      setSpecificAnswer('');
      setProxyMode('specific');
      await loadProxies();
      
      // Refresh parent component to update ActiveProxiesSection
      if (onProxyChange) {
        onProxyChange();
      }
    } catch (error) {
      console.error('❌ Error creating proxy:', error);
      console.error('❌ Error response:', error.response?.data);
      alert('❌ Failed to create proxy: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeProxy = async (proxyId, motion) => {
    const confirmMsg = `
🔄 REVOKE PROXY

Are you sure you want to revoke your proxy for:
${motion || 'ALL MOTIONS (General Proxy)'}?

This will restore your voting rights for this item.
    `;

    if (!confirm(confirmMsg)) return;

    setLoading(true);
    try {
      await api.post(`/api/proxy/${proxyId}/revoke`);
      alert('✅ Proxy revoked successfully! Your voting rights have been restored.');
      await loadProxies();
    } catch (error) {
      console.error('Error revoking proxy:', error);
      alert('❌ Failed to revoke proxy: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  if (!currentElection) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <h4 className="text-lg font-medium text-gray-900 mb-4">🤝 Proxy Management</h4>
        <div className="p-4 bg-gray-50 rounded-lg text-center">
          <p className="text-gray-600">No active election available. Proxies are created per election/motion.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-medium text-gray-900">🤝 Proxy Management</h4>
            <p className="text-sm text-gray-600 mt-1">
              Assign or revoke proxies for <strong>{currentElection.name}</strong>
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleOpenCreateModal()}
              disabled={!currentElection || !['proxy_collection', 'nominating', 'queued', 'voting'].includes(currentElection.status)}
              className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              ➕ Create Single Proxy
            </button>
            <button
              onClick={handleOpenBulkModal}
              disabled={!currentElection || !['proxy_collection', 'nominating', 'queued', 'voting'].includes(currentElection.status) || (allMotions.length === 0 && positionsOnly.length === 0)}
              className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              title="Create proxies for all positions and motions at once"
            >
              ✨ Add All Items
            </button>
          </div>
        </div>
        
        {/* Status Message */}
        {currentElection && !['proxy_collection', 'nominating', 'queued', 'voting'].includes(currentElection.status) && (
          <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
            ⚠️ Proxy creation is only available during Proxy Collection, Nominations, or Voting phases.
          </div>
        )}
        
        {/* Important Notice */}
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-xs text-yellow-800">
            ⚠️ <strong>Important:</strong> Once a proxy is assigned, it becomes part of the official HOA record and generates a legal document.
          </p>
        </div>
      </div>

      {/* Overall Voting Status - SIMPLIFIED AND ACCURATE */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border-2 border-indigo-500">
        <h5 className="font-bold text-gray-900 mb-4 text-lg">📊 YOUR VOTING STATUS</h5>
        
        {proxies.general_proxy ? (
          <div className="p-4 bg-red-50 border-2 border-red-500 rounded-lg">
            <div className="flex items-start gap-3 mb-3">
              <span className="text-3xl">🚫</span>
              <div className="flex-1">
                <h6 className="font-bold text-red-900 text-lg mb-2">GENERAL PROXY - YOU CANNOT VOTE</h6>
                <p className="text-sm text-red-800 font-medium mb-2">
                  <strong>{proxies.general_proxy.holder_name}</strong> will vote on your behalf for EVERYTHING in this election.
                </p>
                <p className="text-xs text-red-700 mb-2">
                  Created: {formatLocal(proxies.general_proxy.created_at, hoaTimezone, 'MMM d, yyyy h:mm a z')}
                </p>
              </div>
            </div>
            <button
              onClick={() => handleRevokeProxy(proxies.general_proxy.id, null)}
              disabled={loading}
              className="w-full px-4 py-3 bg-red-600 text-white font-bold rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400"
            >
              🔄 REVOKE TO RESTORE YOUR VOTING RIGHTS
            </button>
          </div>
        ) : Object.keys(proxies.limited_proxies).length > 0 ? (
          (() => {
            const proxiedCount = Object.keys(proxies.limited_proxies).length;
            // Total items = positions + motions
            const totalItems = allItemsForDelegate.length;
            const canVoteCount = totalItems - proxiedCount;
            const hasPositions = (currentElection?.positions_up_for_election?.length > 0 || currentElection?.candidates?.length > 0);
            
            // Check if ALL items are proxied away
            const allProxiedAway = canVoteCount <= 0;
            
            return allProxiedAway ? (
              <div className="p-4 bg-red-50 border-2 border-red-500 rounded-lg">
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-3xl">🚫</span>
                  <div className="flex-1">
                    <h6 className="font-bold text-red-900 text-lg mb-2">ALL VOTING RIGHTS PROXIED AWAY</h6>
                    <div className="space-y-1 text-sm text-red-900">
                      <p className="font-medium">
                        ❌ <strong>CANNOT VOTE</strong> on any items - you gave away all {proxiedCount} proxies
                      </p>
                      <p className="text-xs mt-2">
                        Your proxy holder(s) will vote on your behalf. You can revoke proxies below to restore your voting rights.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-yellow-50 border-2 border-yellow-500 rounded-lg">
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-3xl">⚠️</span>
                  <div className="flex-1">
                    <h6 className="font-bold text-yellow-900 text-lg mb-2">LIMITED PROXIES - MIXED STATUS</h6>
                    <div className="space-y-1 text-sm text-yellow-900">
                      <p className="font-medium">
                        ❌ <strong>CANNOT VOTE</strong> on {proxiedCount} item{proxiedCount > 1 ? 's' : ''} (you gave proxy away)
                      </p>
                      {canVoteCount > 0 && (
                        <p className="font-medium">
                          ✅ <strong>CAN VOTE</strong> on {canVoteCount} item{canVoteCount > 1 ? 's' : ''} (you kept rights)
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()
        ) : (
          <div className="p-4 bg-green-50 border-2 border-green-500 rounded-lg">
            <div className="flex items-start gap-3">
              <span className="text-3xl">✅</span>
              <div className="flex-1">
                <h6 className="font-bold text-green-900 text-lg mb-2">FULL VOTING RIGHTS</h6>
                <p className="text-sm text-green-800 font-medium">
                  No proxies assigned. You can vote on everything.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Proxied Motions Only - Show ONLY motions with proxies */}
      {Object.keys(proxies.limited_proxies).length > 0 && (
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h5 className="font-bold text-gray-900 mb-4 text-lg">🚫 Motions You CANNOT Vote On (Proxied)</h5>
          <div className="space-y-3">
            {Object.entries(proxies.limited_proxies).map(([motion, proxy], idx) => (
              <div key={idx} className="p-4 border-2 rounded-lg bg-red-50 border-red-400">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h6 className="font-bold text-gray-900 text-base mb-2">📋 {motion}</h6>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-800">
                        <strong>Proxy Holder:</strong> {proxy.holder_name}
                      </p>
                      {proxy.specific_answer && (
                        <p className="text-sm text-purple-800">
                          <strong>Specified Answer:</strong> {proxy.specific_answer}
                        </p>
                      )}
                      <p className="text-xs text-gray-600">
                        Created: {formatLocal(proxy.created_at, hoaTimezone, 'MMM d, yyyy h:mm a z')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRevokeProxy(proxy.id, motion)}
                    disabled={loading}
                    className="px-4 py-2 bg-red-600 text-white text-sm font-bold rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400 whitespace-nowrap"
                  >
                    🔄 Revoke
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Proxy Modal */}
      {showCreateModal && (() => {
        const motionCfg = selectedMotion ? motionSettings[selectedMotion] : null;
        
        console.log('🔍 PROXY MODAL - Selected Motion:', selectedMotion);
        console.log('🔍 PROXY MODAL - Motion Config:', motionCfg);
        
        const allowGeneral = motionCfg?.allow_general === true;
        const allowLimited = motionCfg?.allow_limited === true;
        const purpose = motionCfg?.purpose || 'voting_quorum';
        const voteOptions = motionCfg?.vote_options || [];
        
        console.log('🔍 PROXY MODAL - Allow General:', allowGeneral);
        console.log('🔍 PROXY MODAL - Allow Limited:', allowLimited);
        console.log('🔍 PROXY MODAL - Purpose:', purpose);
        console.log('🔍 PROXY MODAL - Vote Options:', voteOptions);
        
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-semibold mb-4">
                {proxyType === 'general' ? '📋 Create General Proxy' : '📍 Create Proxy'}
              </h3>

              {/* Motion Display */}
              {selectedMotion && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800"><strong>Motion:</strong> {selectedMotion}</p>
                  {purpose === 'quorum_only' && (
                    <p className="text-xs text-yellow-800 mt-2">⚠️ Quorum Only: This proxy is only for establishing meeting quorum.</p>
                  )}
                </div>
              )}

              {/* Proxy Type Selection - Based on Motion Settings */}
              {!selectedMotion && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Proxy Type</label>
                  <div className="space-y-2">
                    {allowLimited && (
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          value="limited"
                          checked={proxyType === 'limited'}
                          onChange={(e) => setProxyType(e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Limited (Single Motion)</span>
                      </label>
                    )}
                    {allowGeneral && (
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          value="general"
                          checked={proxyType === 'general'}
                          onChange={(e) => setProxyType(e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">General (All Motions)</span>
                      </label>
                    )}
                    {!allowGeneral && !allowLimited && (
                      <p className="text-sm text-red-600">⚠️ No proxy types allowed for this motion</p>
                    )}
                  </div>
                </div>
              )}

              {/* Motion Selection (when creating without pre-selected motion) */}
              {proxyType === 'limited' && !selectedMotion && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Motion</label>
                  <select
                    value={selectedMotion || ''}
                    onChange={(e) => setSelectedMotion(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">-- Select Motion --</option>
                    {Object.keys(motionSettings).map((motion, i) => (
                      <option key={i} value={motion}>{motion}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Proxy Holder Selection - Text Inputs */}
              <div className="mb-4 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Proxy Holder Name *</label>
                  <input
                    type="text"
                    value={holderName}
                    onChange={(e) => setHolderName(e.target.value)}
                    placeholder="Enter full name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Proxy Holder Email *</label>
                  <input
                    type="email"
                    value={holderEmail}
                    onChange={(e) => setHolderEmail(e.target.value)}
                    placeholder="Enter email address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">System will connect this person if registered in HOA</p>
                </div>
              </div>

              {/* Answer Selection - ONLY for limited proxy with voting_quorum purpose */}
              {proxyType === 'limited' && purpose === 'voting_quorum' && selectedMotion && !motionCfg?.is_candidate_position && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Proxy Mode *</label>
                  <div className="space-y-3">
                    <label className="flex items-start space-x-2">
                      <input
                        type="radio"
                        value="specific"
                        checked={proxyMode === 'specific'}
                        onChange={(e) => setProxyMode(e.target.value)}
                        className="mt-1 w-4 h-4"
                      />
                      <div>
                        <span className="text-sm font-medium">Specific Answer</span>
                        <p className="text-xs text-gray-500">Proxy holder must vote exactly as you specify</p>
                      </div>
                    </label>
                    
                    <label className="flex items-start space-x-2">
                      <input
                        type="radio"
                        value="delegate"
                        checked={proxyMode === 'delegate'}
                        onChange={(e) => {
                          setProxyMode(e.target.value);
                          setSpecificAnswer(''); // Clear answer when delegating
                        }}
                        className="mt-1 w-4 h-4"
                      />
                      <div>
                        <span className="text-sm font-medium">Delegate Choice</span>
                        <p className="text-xs text-gray-500">Proxy holder can choose any answer</p>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Answer Dropdown - Only if specific mode selected AND NOT quorum_only */}
              {proxyType === 'limited' && proxyMode === 'specific' && purpose === 'voting_quorum' && selectedMotion && voteOptions.length > 0 && !motionCfg?.is_candidate_position && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Answer *
                  </label>
                  <select
                    value={specificAnswer}
                    onChange={(e) => setSpecificAnswer(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">-- Select Your Vote --</option>
                    {voteOptions.map((option, i) => (
                      <option key={i} value={option}>{option}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Proxy holder must vote this way (cannot change)</p>
                </div>
              )}

              {/* Quorum Only Message - No answer needed */}
              {purpose === 'quorum_only' && selectedMotion && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    ⚠️ <strong>Quorum Only:</strong> This proxy will count towards meeting quorum but cannot be used for voting on this motion. No answer selection needed.
                  </p>
                </div>
              )}

              {/* Debug Info */}
              <div className="mb-4 p-2 bg-gray-100 rounded text-xs text-gray-600">
                <div>Allow General: {allowGeneral ? 'Yes' : 'No'}</div>
                <div>Allow Limited: {allowLimited ? 'Yes' : 'No'}</div>
                <div>Purpose: {purpose}</div>
              </div>

              {/* Validation Messages */}
              {!holderName && (
                <div className="mb-2 p-2 bg-yellow-50 border border-yellow-300 rounded text-xs text-yellow-800">
                  ⚠️ Please enter proxy holder name
                </div>
              )}
              {!holderEmail && (
                <div className="mb-2 p-2 bg-yellow-50 border border-yellow-300 rounded text-xs text-yellow-800">
                  ⚠️ Please enter proxy holder email
                </div>
              )}
              {proxyType === 'limited' && !selectedMotion && (
                <div className="mb-2 p-2 bg-yellow-50 border border-yellow-300 rounded text-xs text-yellow-800">
                  ⚠️ Please select a motion for this limited proxy
                </div>
              )}
              {proxyMode === 'specific' && purpose === 'voting_quorum' && !specificAnswer && selectedMotion && (
                <div className="mb-2 p-2 bg-red-50 border border-red-300 rounded text-xs text-red-800">
                  🚨 Please select a specific answer for this proxy
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleCreateProxy}
                  disabled={loading || !holderEmail || !holderName || (proxyType === 'limited' && !selectedMotion) || (proxyMode === 'specific' && purpose === 'voting_quorum' && !specificAnswer)}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  title={loading || !holderEmail || !holderName || (proxyType === 'limited' && !selectedMotion) || (proxyMode === 'specific' && purpose === 'voting_quorum' && !specificAnswer) ? 'Please complete all required fields' : 'Create proxy assignment'}
                >
                  {loading ? 'Creating...' : 'Create Proxy'}
                </button>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setSelectedMotion(null);
                    setHolderEmail('');
                    setHolderName('');
                    setSpecificAnswer('');
                    setProxyMode('specific');
                  }}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Bulk Proxy Creation Modal */}
      {showBulkModal && bulkAnalysis && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h4 className="text-lg font-bold text-gray-900 mb-4">✨ Add Proxy for All Items</h4>
            <p className="text-sm text-gray-600 mb-4">This creates proxies for all candidate positions and special motions.</p>
            
            {/* Items List Preview - Show BOTH positions and motions */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm font-medium text-gray-700 mb-2">
                This will create proxies for {positionsOnly.length} position(s) and {motionsOnly.length} motion(s):
              </p>
              
              {/* Positions */}
              {positionsOnly.length > 0 && (
                <div className="mb-2">
                  <p className="text-xs font-semibold text-indigo-700 mb-1">📋 Candidate Positions:</p>
                  <ul className="text-xs text-gray-600 list-disc list-inside max-h-20 overflow-y-auto">
                    {positionsOnly.map((p, i) => {
                      const positionData = (currentElection?.positions_up_for_election || []).find(
                        pos => (pos.label || pos.role) === p
                      );
                      const purpose = positionData?.proxy_settings?.purpose || 'voting_and_quorum';
                      return (
                        <li key={i}>
                          {p}
                          <span className={`ml-1 text-xs ${purpose === 'quorum_only' ? 'text-amber-600' : 'text-green-600'}`}>
                            ({purpose === 'quorum_only' ? 'Quorum Only' : '🗳️ Voting'})
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              
              {/* Motions */}
              {motionsOnly.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-blue-700 mb-1">📜 Motions/Special Topics:</p>
                  <ul className="text-xs text-gray-600 list-disc list-inside max-h-20 overflow-y-auto">
                    {motionsOnly.map((m, i) => {
                      const cfg = motionSettings[m] || {};
                      const purpose = cfg.purpose || 'voting_and_quorum';
                      return (
                        <li key={i}>
                          {m} 
                          <span className={`ml-1 text-xs ${purpose === 'quorum_only' ? 'text-amber-600' : 'text-green-600'}`}>
                            ({purpose === 'quorum_only' ? 'Quorum Only' : '🗳️ Voting'})
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            {/* Status Banner - Show what options are available */}
            <div className={`mb-4 p-3 rounded-lg ${bulkAnalysis.allQuorumOnly ? 'bg-amber-50 border border-amber-200' : 'bg-blue-50 border border-blue-200'}`}>
              {bulkAnalysis.allQuorumOnly ? (
                <p className="text-xs text-amber-800">
                  <strong>📋 Quorum Only:</strong> All motions in this election are configured for quorum only. Proxy holders will count towards quorum but cannot vote.
                </p>
              ) : bulkAnalysis.allSameAnswers && bulkAnalysis.commonAnswers.length > 0 ? (
                <p className="text-xs text-blue-800">
                  <strong>✅ Voting Available:</strong> All motions have the same answer options ({bulkAnalysis.commonAnswers.join(', ')}). You can use Delegate or Specific Answer.
                </p>
              ) : (
                <p className="text-xs text-blue-800">
                  <strong>ℹ️ Voting Available:</strong> Motions have different answer options. You can use Delegate mode, or add proxies individually for specific answers.
                </p>
              )}
            </div>

            {/* Proxy Holder */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Proxy Holder Email *</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={bulkHolderEmail}
                  onChange={(e) => { setBulkHolderEmail(e.target.value); setBulkHolder(null); }}
                  placeholder="Enter email address"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  onClick={handleBulkHolderSearch}
                  disabled={searchingHolder || !bulkHolderEmail}
                  className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
                >
                  {searchingHolder ? '...' : 'Search'}
                </button>
              </div>
              {bulkHolder && (
                <p className="text-xs text-green-600 mt-1">✓ Found: {bulkHolderName}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Proxy Holder Name *</label>
              <input
                type="text"
                value={bulkHolderName}
                onChange={(e) => setBulkHolderName(e.target.value)}
                placeholder="Full name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Purpose Selection - Only show if voting is allowed on ANY motion */}
            {!bulkAnalysis.allQuorumOnly && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Purpose *</label>
                <select
                  value={bulkPurpose}
                  onChange={(e) => { setBulkPurpose(e.target.value); setBulkMode('delegate'); setBulkAnswer(''); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="quorum_and_vote">Voting & Quorum (full voting rights)</option>
                  <option value="quorum_only">Quorum Only (count for quorum, cannot vote)</option>
                </select>
              </div>
            )}

            {/* If all quorum only, just show info */}
            {bulkAnalysis.allQuorumOnly && (
              <div className="mb-4 p-3 bg-gray-100 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Purpose:</strong> Quorum Only
                </p>
                <p className="text-xs text-gray-500 mt-1">All motions are configured for quorum only. No answer selection needed.</p>
              </div>
            )}

            {/* Mode Selection - Only for voting purpose AND not all quorum only */}
            {!bulkAnalysis.allQuorumOnly && bulkPurpose === 'quorum_and_vote' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Proxy Mode *</label>
                <div className="space-y-2">
                  <label className="flex items-start space-x-2">
                    <input
                      type="radio"
                      value="delegate"
                      checked={bulkMode === 'delegate'}
                      onChange={(e) => { setBulkMode(e.target.value); setBulkAnswer(''); }}
                      className="mt-1 w-4 h-4"
                    />
                    <div>
                      <span className="text-sm font-medium">Delegate (Full Authority)</span>
                      <p className="text-xs text-gray-500">
                        Proxy holder decides how to vote on <strong>all positions ({positionsOnly.length}) AND motions ({motionsOnly.length})</strong>
                      </p>
                    </div>
                  </label>
                  
                  {/* Specific Answer - Only if ALL motions have same answer options */}
                  {bulkAnalysis.allSameAnswers && bulkAnalysis.commonAnswers.length > 0 ? (
                    <label className="flex items-start space-x-2">
                      <input
                        type="radio"
                        value="specific"
                        checked={bulkMode === 'specific'}
                        onChange={(e) => setBulkMode(e.target.value)}
                        className="mt-1 w-4 h-4"
                      />
                      <div>
                        <span className="text-sm font-medium">Specific Answer (Motions Only)</span>
                        <p className="text-xs text-gray-500">
                          Proxy holder must vote the same answer on <strong>motions only ({motionsOnly.length})</strong> - does NOT include nominee positions
                        </p>
                      </div>
                    </label>
                  ) : (
                    <div className="p-2 bg-gray-100 rounded text-xs text-gray-600">
                      <span className="font-medium">Specific Answer not available:</span> Motions have different answer options. 
                      Use Delegate mode for all, or add proxies individually to specify different answers per motion.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Specific Answer Selection - Only if voting, specific mode, and all same answers */}
            {!bulkAnalysis.allQuorumOnly && bulkPurpose === 'quorum_and_vote' && bulkMode === 'specific' && bulkAnalysis.allSameAnswers && bulkAnalysis.commonAnswers.length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Answer for ALL Motions *</label>
                <select
                  value={bulkAnswer}
                  onChange={(e) => setBulkAnswer(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select answer...</option>
                  {bulkAnalysis.commonAnswers.map((answer, i) => (
                    <option key={i} value={answer}>{answer}</option>
                  ))}
                </select>
                <p className="text-xs text-amber-600 mt-1">
                  ⚠️ This same answer will be applied to {motionsOnly.length} motions only (not nominee positions).
                </p>
              </div>
            )}

            {/* Warning */}
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-800">
                <strong>⚠️ Important:</strong> This will create {bulkMode === 'delegate' ? allItemsForDelegate.length : motionsOnly.length} separate proxy assignments 
                ({bulkMode === 'delegate' ? `${positionsOnly.length} positions + ${motionsOnly.length} motions` : `${motionsOnly.length} motions only`}). 
                Each proxy generates a legal document. Make sure this is what you intend.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleCreateBulkProxies}
                disabled={loading || !bulkHolder || !bulkHolderName || (!bulkAnalysis.allQuorumOnly && bulkMode === 'specific' && bulkPurpose === 'quorum_and_vote' && !bulkAnswer)}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : `Create ${bulkMode === 'delegate' ? allItemsForDelegate.length : motionsOnly.length} Proxies`}
              </button>
              <button
                onClick={() => setShowBulkModal(false)}
                disabled={loading}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Nominees Management Tab Component
function NomineesManagementTab({ election, saving, setSaving, hoaTimezone = 'America/Chicago', hoaSettings = {} }) {
  const [candidates, setCandidates] = useState([]);
  const [candidateGroups, setCandidateGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showAddCandidateModal, setShowAddCandidateModal] = useState(false);
  const [newCandidate, setNewCandidate] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/vote/election/${election.id}/candidates`);
      const data = response.data;
      
      setCandidates(data.candidates || []);
      setCandidateGroups(data.candidate_groups || []);
      
      console.log('Fetched candidates:', data);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      alert('Failed to load candidates. Please refresh and try again.');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (election?.id) {
      fetchCandidates();
    }
  }, [election?.id]);

  const handleAddCandidate = async () => {
    // Get the name from either the dynamic question_1 or fallback name field
    const candidateName = newCandidate.question_1 || newCandidate.name;
    
    if (!candidateName) {
      alert('Please enter the candidate name');
      return;
    }
    
    try {
      setSaving(true);
      
      // Map question responses to candidate fields
      const candidateData = {
        name: candidateName,
        property_identifier: newCandidate.question_4 || newCandidate.property_identifier || '',
        bio: newCandidate.question_5 || newCandidate.bio || '',
        experience: newCandidate.question_6 || newCandidate.experience || '',
        platform: newCandidate.question_7 || newCandidate.platform || '',
        priorities: newCandidate.question_8 || '',
        skills: newCandidate.question_9 || '',
        previous_service: newCandidate.question_10 || '',
        accomplishments: newCandidate.question_11 || '',
        time_commitment: newCandidate.question_12 || '',
        nomination_responses: newCandidate, // Store all responses
        nominated_by_type: 'Board',
        nominated_by_name: 'Board of Directors'
      };
      
      await api.post(`/api/vote/election/${election.id}/add-candidate`, candidateData);
      
      alert('✅ Candidate added successfully!');
      setShowAddCandidateModal(false);
      setNewCandidate({});
      await fetchCandidates();
    } catch (error) {
      console.error('Error adding candidate:', error);
      alert('Failed to add candidate: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleMergeDuplicates = async (primaryCandidateId, duplicateIds, mergedData) => {
    try {
      setSaving(true);
      await api.post(`/api/vote/candidates/${primaryCandidateId}/merge-duplicates`, {
        duplicate_candidate_ids: duplicateIds,
        ...mergedData
      });
      
      alert('✅ Duplicate nominations merged successfully!');
      await fetchCandidates();
      setShowMergeModal(false);
      setSelectedGroup(null);
    } catch (error) {
      console.error('Error merging duplicates:', error);
      alert('Failed to merge duplicates. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleApproveNomination = async (nominationId, candidateName) => {
    if (!confirm(`Approve the nomination for "${candidateName}"?\n\nThis will make the candidate visible to voters.`)) {
      return;
    }

    try {
      setSaving(true);
      await api.post(`/api/vote/nomination/${nominationId}/approve`);
      
      alert('✅ Nomination approved successfully!');
      await fetchCandidates();
    } catch (error) {
      console.error('Error approving nomination:', error);
      alert('Failed to approve nomination: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveCandidate = async (candidateId, candidateName) => {
    if (!confirm(`Are you sure you want to remove the nomination for "${candidateName}"?\n\nThis action will mark the nomination as removed but preserve it for audit purposes.`)) {
      return;
    }

    try {
      setSaving(true);
      await api.delete(`/api/vote/candidates/${candidateId}`);
      
      alert('✅ Candidate nomination removed successfully!');
      await fetchCandidates();
    } catch (error) {
      console.error('Error removing candidate:', error);
      alert('Failed to remove candidate. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Loading candidates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold">👥 Nominee Management</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAddCandidateModal(true)}
            className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            ➕ Add Candidate
          </button>
          <button
            onClick={() => printSubmittedNominations(election, candidates, hoaTimezone, hoaSettings)}
            className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
            disabled={candidates.length === 0}
          >
            🖨️ Print Nominations
          </button>
          <button
            onClick={fetchCandidates}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Add Candidate Modal - Uses nomination_questions from election config */}
      {showAddCandidateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h4 className="text-xl font-bold text-gray-900 mb-4">➕ Add Candidate Manually</h4>
            <p className="text-sm text-gray-600 mb-4">
              Use this form to add candidates who submitted nominations outside the system (e.g., paper forms, email).
            </p>
            
            <div className="space-y-4">
              {/* Render ALL nomination questions exactly as configured in the Nomination Form */}
              {(election.nomination_questions || []).map((question) => {
                  const fieldKey = `question_${question.id}`;
                  const isRequired = question.required;
                  
                  // Render based on the actual question type from configuration
                  const renderInput = () => {
                    switch (question.type) {
                      case 'textarea':
                        return (
                          <textarea
                            value={newCandidate[fieldKey] || ''}
                            onChange={(e) => setNewCandidate({...newCandidate, [fieldKey]: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                            rows={3}
                            placeholder={question.placeholder || ''}
                            required={isRequired}
                          />
                        );
                      case 'boolean':
                      case 'yes_no':
                        return (
                          <select
                            value={newCandidate[fieldKey] || ''}
                            onChange={(e) => setNewCandidate({...newCandidate, [fieldKey]: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                            required={isRequired}
                          >
                            <option value="">Select...</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </select>
                        );
                      case 'radio':
                        return (
                          <div className="space-y-2">
                            {(question.options || []).map((option, idx) => (
                              <label key={idx} className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={fieldKey}
                                  value={option}
                                  checked={newCandidate[fieldKey] === option}
                                  onChange={(e) => setNewCandidate({...newCandidate, [fieldKey]: e.target.value})}
                                  className="text-green-600 focus:ring-green-500"
                                />
                                <span className="text-gray-700">{option}</span>
                              </label>
                            ))}
                          </div>
                        );
                      case 'checkbox':
                        return (
                          <div className="space-y-2">
                            {(question.options || []).map((option, idx) => (
                              <label key={idx} className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  value={option}
                                  checked={(newCandidate[fieldKey] || '').split(',').includes(option)}
                                  onChange={(e) => {
                                    const currentValues = newCandidate[fieldKey] ? newCandidate[fieldKey].split(',').filter(v => v) : [];
                                    if (e.target.checked) {
                                      setNewCandidate({...newCandidate, [fieldKey]: [...currentValues, option].join(',')});
                                    } else {
                                      setNewCandidate({...newCandidate, [fieldKey]: currentValues.filter(v => v !== option).join(',')});
                                    }
                                  }}
                                  className="text-green-600 focus:ring-green-500"
                                />
                                <span className="text-gray-700">{option}</span>
                              </label>
                            ))}
                          </div>
                        );
                      case 'select':
                        return (
                          <select
                            value={newCandidate[fieldKey] || ''}
                            onChange={(e) => setNewCandidate({...newCandidate, [fieldKey]: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                            required={isRequired}
                          >
                            <option value="">Select...</option>
                            {(question.options || []).map((option, idx) => (
                              <option key={idx} value={option}>{option}</option>
                            ))}
                          </select>
                        );
                      case 'text':
                      default:
                        return (
                          <input
                            type="text"
                            value={newCandidate[fieldKey] || ''}
                            onChange={(e) => setNewCandidate({...newCandidate, [fieldKey]: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                            placeholder={question.placeholder || ''}
                            required={isRequired}
                          />
                        );
                    }
                  };
                  
                  return (
                    <div key={question.id}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {question.question} {isRequired && <span className="text-red-500">*</span>}
                      </label>
                      {renderInput()}
                    </div>
                  );
                })}
              
              {/* Fallback if no nomination_questions configured */}
              {(!election.nomination_questions || election.nomination_questions.length === 0) && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Candidate Name *</label>
                    <input
                      type="text"
                      value={newCandidate.name || ''}
                      onChange={(e) => setNewCandidate({...newCandidate, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Property Address/Unit</label>
                    <input
                      type="text"
                      value={newCandidate.property_identifier || ''}
                      onChange={(e) => setNewCandidate({...newCandidate, property_identifier: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="e.g., 123 Main St or Unit 4B"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Biography</label>
                    <textarea
                      value={newCandidate.bio || ''}
                      onChange={(e) => setNewCandidate({...newCandidate, bio: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      rows={3}
                      placeholder="Brief biography..."
                    />
                  </div>
                </>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={handleAddCandidate}
                disabled={saving || (!newCandidate.question_1 && !newCandidate.name)}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
              >
                {saving ? 'Adding...' : '✅ Add Candidate'}
              </button>
              <button
                onClick={() => {
                  setShowAddCandidateModal(false);
                  setNewCandidate({});
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-600">{candidates.length}</div>
          <div className="text-sm text-blue-800">Total Nominations</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">{candidateGroups.length}</div>
          <div className="text-sm text-green-800">Unique Candidates</div>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-orange-600">
            {candidateGroups.filter(g => g.is_duplicate_group).length}
          </div>
          <div className="text-sm text-orange-800">Duplicate Groups</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-600">
            {candidates.filter(c => c.status === 'approved').length}
          </div>
          <div className="text-sm text-purple-800">Approved</div>
        </div>
      </div>

      {/* Candidate Groups */}
      <div className="space-y-4">
        {candidateGroups.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-8 text-center">
            <p className="text-gray-600">No candidates nominated yet.</p>
            <p className="text-sm text-gray-500 mt-2">Nominations will appear here once submitted by community members.</p>
          </div>
        ) : (
          candidateGroups.map((group, index) => (
            <div key={index} className={`rounded-lg border-2 p-4 ${
              group.is_duplicate_group 
                ? 'border-orange-200 bg-orange-50' 
                : 'border-gray-200 bg-white'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <h4 className="font-semibold text-lg">{group.candidate_name}</h4>
                  {group.is_duplicate_group && (
                    <span className="px-2 py-1 bg-orange-200 text-orange-800 text-xs rounded-full">
                      ⚠️ {group.nominations.length} Duplicate Nominations
                    </span>
                  )}
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    group.consolidated_data.status === 'approved' ? 'bg-green-100 text-green-800' :
                    group.consolidated_data.status === 'pending_review' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {group.consolidated_data.status}
                  </span>
                </div>
                
                <div className="flex gap-2">
                  {(group.consolidated_data.status === 'pending_review' || group.consolidated_data.status === 'pending_approval') && (
                    <button
                      onClick={() => handleApproveNomination(group.nominations[0].nomination_id, group.candidate_name)}
                      className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                      disabled={saving}
                    >
                      ✅ Approve
                    </button>
                  )}
                  {group.is_duplicate_group && (
                    <button
                      onClick={() => {
                        setSelectedGroup(group);
                        setShowMergeModal(true);
                      }}
                      className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 transition-colors"
                    >
                      🔀 Manage Duplicates
                    </button>
                  )}
                  <button
                    onClick={() => handleRemoveCandidate(group.primary_candidate_id, group.candidate_name)}
                    className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                    disabled={saving}
                  >
                    🗑️ Remove
                  </button>
                </div>
              </div>
              
              {/* Candidate Details - Dynamically rendered from nomination_questions */}
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                {(election.nomination_questions || []).map((question) => {
                  // Get the answer from consolidated_data using question_X format or legacy field names
                  const fieldKey = `question_${question.id}`;
                  const legacyFieldMap = {
                    'property': 'property_identifier',
                    'bio': 'bio',
                    'biography': 'bio', 
                    'platform': 'platform',
                    'experience': 'experience',
                    'skills': 'skills',
                    'priorities': 'priorities',
                    'previous': 'previous_service',
                    'accomplishments': 'accomplishments',
                    'time': 'time_commitment',
                    'commitment': 'time_commitment'
                  };
                  
                  // Try to find the value - first check question_X, then legacy fields
                  let value = group.consolidated_data[fieldKey];
                  if (!value) {
                    const questionLower = (question.question || '').toLowerCase();
                    for (const [key, field] of Object.entries(legacyFieldMap)) {
                      if (questionLower.includes(key)) {
                        value = group.consolidated_data[field];
                        break;
                      }
                    }
                  }
                  // Also check nomination_responses
                  if (!value && group.consolidated_data.nomination_responses) {
                    value = group.consolidated_data.nomination_responses[fieldKey];
                  }
                  
                  // Skip if question is about name (shown in header) or nominated by (internal)
                  const questionLower = (question.question || '').toLowerCase();
                  if (questionLower.includes("nominee's name") || questionLower.includes("candidate name")) {
                    return null;
                  }
                  
                  // Skip if no value and not required
                  if (!value && !question.required) {
                    return null;
                  }
                  
                  // Determine if this should span 2 columns (textarea type)
                  const isWide = question.type === 'textarea';
                  
                  return (
                    <div key={question.id} className={isWide ? 'md:col-span-2' : ''}>
                      <strong>{question.question.replace(' *', '')}:</strong>
                      <p className="text-gray-600 mt-1">{value || 'Not provided'}</p>
                    </div>
                  );
                })}
              </div>

              {/* Nomination Details */}
              <div className="mt-4 border-t pt-3">
                <strong className="text-sm">Nominations ({group.nominations.length}):</strong>
                <div className="mt-2 space-y-2">
                  {group.nominations.map((nomination, nomIndex) => (
                    <div key={nomIndex} className="bg-gray-100 rounded p-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span>
                          Nominated by: <strong>{nomination.nominated_by_name || nomination.nominated_by_email}</strong>
                          {nomination.nominated_by_type && (
                            <span className="ml-2 text-gray-500">({nomination.nominated_by_type})</span>
                          )}
                        </span>
                        <span className="text-gray-500">
                          {formatLocal(nomination.created_at, hoaTimezone, 'MMM d, yyyy')}
                        </span>
                      </div>
                      {nomination.property_identifier && (
                        <div className="text-gray-600 mt-1">Property: {nomination.property_identifier}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Merge Duplicates Modal */}
      {showMergeModal && selectedGroup && (
        <DuplicateMergeModal
          group={selectedGroup}
          onMerge={handleMergeDuplicates}
          onClose={() => {
            setShowMergeModal(false);
            setSelectedGroup(null);
          }}
          saving={saving}
          hoaTimezone={hoaTimezone}
        />
      )}
    </div>
  );
}

// Duplicate Merge Modal Component - Enhanced with Individual Nomination Review
function DuplicateMergeModal({ group, onMerge, onClose, saving, hoaTimezone = 'America/Chicago' }) {
  const [mergeData, setMergeData] = useState({
    merged_bio: group.consolidated_data.bio || '',
    merged_platform: group.consolidated_data.platform || '',
    merged_experience: group.consolidated_data.experience || '',
    merged_priorities: group.consolidated_data.priorities || '',
    merged_skills: group.consolidated_data.skills || '',
    merged_previous_service: group.consolidated_data.previous_service || '',
    merged_accomplishments: group.consolidated_data.accomplishments || '',
    merged_time_commitment: group.consolidated_data.time_commitment || ''
  });
  const [selectedNomination, setSelectedNomination] = useState(null);
  const [showNominationDetail, setShowNominationDetail] = useState(false);

  const handleMerge = () => {
    const duplicateIds = group.nominations.slice(1).map(nom => nom.id); // All except the first (primary)
    onMerge(group.primary_candidate_id, duplicateIds, mergeData);
  };

  const viewNominationDetails = (nomination) => {
    setSelectedNomination(nomination);
    setShowNominationDetail(true);
  };

  const copyFromNomination = (nomination, field) => {
    const fieldValue = nomination[field] || '';
    setMergeData(prev => ({
      ...prev,
      [`merged_${field}`]: fieldValue
    }));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-6xl flex flex-col" style={{ maxHeight: '90vh' }}>
        {/* Fixed Header */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold">🔀 Review & Merge Duplicate Nominations</h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">✕</button>
          </div>
          <p className="text-gray-600 mt-2">
            Review {group.nominations.length} nominations for <strong>{group.candidate_name}</strong> and merge approved information
          </p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Left: Individual Nominations Review */}
            <div className="space-y-4">
              <h4 className="font-medium text-lg">📋 Individual Nominations (Click to Review)</h4>
              <div className="space-y-3">
                {group.nominations.map((nomination, index) => (
                  <div key={index} className={`rounded-lg border-2 p-4 cursor-pointer transition-colors ${
                    index === 0 ? 'border-blue-300 bg-blue-50' : 'border-gray-300 bg-gray-50'
                  } hover:border-indigo-400 hover:bg-indigo-50`}
                    onClick={() => viewNominationDetails(nomination)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium">
                          {index === 0 ? '👑 Primary Nomination' : `📝 Nomination #${index + 1}`}
                        </span>
                        <div className="text-sm text-gray-600 mt-1">
                          <div>Submitted by: <strong>{nomination.nominated_by_name || nomination.nominated_by_email}</strong></div>
                          <div>Date: {formatLocal(nomination.created_at, hoaTimezone, 'MMM d, yyyy')}</div>
                          <div>Property: {nomination.property_identifier || 'Not provided'}</div>
                        </div>
                      </div>
                      <div className="text-blue-600 font-medium">Click to Review →</div>
                    </div>
                    
                    {/* Full Form Preview - Show ALL fields */}
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="text-sm space-y-2">
                        <div><span className="text-green-700 font-medium">Property:</span> {nomination.property_identifier || 'Not provided'}</div>
                        <div><span className="text-green-700 font-medium">Bio:</span> {nomination.bio || 'Not provided'}</div>
                        <div><span className="text-green-700 font-medium">Experience:</span> {nomination.experience || 'Not provided'}</div>
                        {nomination.platform && <div><span className="text-green-700 font-medium">Platform & Goals:</span> {nomination.platform}</div>}
                        {nomination.skills && <div><span className="text-green-700 font-medium">Skills & Perspectives:</span> {nomination.skills}</div>}
                        {nomination.priorities && <div><span className="text-green-700 font-medium">Priorities:</span> {nomination.priorities}</div>}
                        {nomination.previous_service && <div><span className="text-green-700 font-medium">Previous Service:</span> {nomination.previous_service}</div>}
                        {nomination.accomplishments && <div><span className="text-green-700 font-medium">Accomplishments:</span> {nomination.accomplishments}</div>}
                        {nomination.time_commitment && <div><span className="text-green-700 font-medium">Time Commitment:</span> {nomination.time_commitment}</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Merged Information */}
            <div className="space-y-4">
              <h4 className="font-medium text-lg">📝 Consolidated Final Information</h4>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  Review each nomination on the left, then edit the consolidated information below. 
                  You can copy information from individual nominations using the copy buttons.
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Biography</label>
                  <textarea
                    value={mergeData.merged_bio}
                    onChange={(e) => setMergeData({...mergeData, merged_bio: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Platform & Goals</label>
                  <textarea
                    value={mergeData.merged_platform}
                    onChange={(e) => setMergeData({...mergeData, merged_platform: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Experience</label>
                  <textarea
                    value={mergeData.merged_experience}
                    onChange={(e) => setMergeData({...mergeData, merged_experience: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Skills & Perspectives</label>
                  <textarea
                    value={mergeData.merged_skills}
                    onChange={(e) => setMergeData({...mergeData, merged_skills: e.target.value})}
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priorities</label>
                  <textarea
                    value={mergeData.merged_priorities}
                    onChange={(e) => setMergeData({...mergeData, merged_priorities: e.target.value})}
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Previous Service</label>
                  <textarea
                    value={mergeData.merged_previous_service}
                    onChange={(e) => setMergeData({...mergeData, merged_previous_service: e.target.value})}
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Accomplishments</label>
                  <textarea
                    value={mergeData.merged_accomplishments}
                    onChange={(e) => setMergeData({...mergeData, merged_accomplishments: e.target.value})}
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Time Commitment</label>
                  <select
                    value={mergeData.merged_time_commitment || ''}
                    onChange={(e) => setMergeData({...mergeData, merged_time_commitment: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select...</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-between flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleMerge}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
          >
            {saving ? 'Merging...' : '🔀 Merge Approved Nominations'}
          </button>
        </div>
      </div>

      {/* Nomination Detail Modal */}
      {showNominationDetail && selectedNomination && (
        <NominationDetailModal
          nomination={selectedNomination}
          nominationQuestions={election.nomination_questions || []}
          onClose={() => setShowNominationDetail(false)}
          onCopyField={copyFromNomination}
          hoaTimezone={hoaTimezone}
        />
      )}
    </div>
  );
}

// Individual Nomination Detail Modal
function NominationDetailModal({ nomination, nominationQuestions = [], onClose, onCopyField, hoaTimezone = 'America/Chicago' }) {
  // Helper to get value from nomination using question_X format or legacy fields
  const getValueForQuestion = (question) => {
    const fieldKey = `question_${question.id}`;
    
    // First try question_X format
    let value = nomination[fieldKey];
    if (value) return value;
    
    // Try nomination_responses
    if (nomination.nomination_responses && nomination.nomination_responses[fieldKey]) {
      return nomination.nomination_responses[fieldKey];
    }
    
    // Try legacy field mapping based on question text
    const questionLower = (question.question || '').toLowerCase();
    const legacyFieldMap = {
      'property': 'property_identifier',
      'bio': 'bio',
      'biography': 'bio',
      'platform': 'platform',
      'experience': 'experience',
      'skills': 'skills',
      'priorities': 'priorities',
      'previous': 'previous_service',
      'accomplishments': 'accomplishments',
      'time': 'time_commitment',
      'commitment': 'time_commitment',
      'nominated by': 'nominated_by_type'
    };
    
    for (const [key, field] of Object.entries(legacyFieldMap)) {
      if (questionLower.includes(key)) {
        value = nomination[field];
        if (value) return value;
      }
    }
    
    return null;
  };
  
  // Get the field key for copy functionality
  const getFieldKeyForQuestion = (question) => {
    const questionLower = (question.question || '').toLowerCase();
    if (questionLower.includes('bio')) return 'bio';
    if (questionLower.includes('platform')) return 'platform';
    if (questionLower.includes('experience')) return 'experience';
    if (questionLower.includes('skills')) return 'skills';
    if (questionLower.includes('priorities')) return 'priorities';
    if (questionLower.includes('previous')) return 'previous_service';
    if (questionLower.includes('accomplishments')) return 'accomplishments';
    if (questionLower.includes('time') || questionLower.includes('commitment')) return 'time_commitment';
    return `question_${question.id}`;
  };

  return (
    <div className="fixed inset-0 z-60 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl flex flex-col" style={{ maxHeight: '85vh' }}>
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold">📝 Nomination Details</h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">✕</button>
          </div>
          <p className="text-gray-600 mt-2">
            Detailed review of nomination for <strong>{nomination.name}</strong>
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Nomination Metadata */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-3">📋 Nomination Information</h4>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div><strong>Submitted by:</strong> {nomination.nominated_by_name || nomination.nominated_by_email}</div>
                <div><strong>Submission Date:</strong> {formatLocal(nomination.created_at, hoaTimezone, 'MMM d, yyyy h:mm a z')}</div>
                <div><strong>Nomination Type:</strong> {nomination.nominated_by_type || 'Not specified'}</div>
                <div><strong>Property:</strong> {nomination.property_identifier || 'Not provided'}</div>
              </div>
            </div>

            {/* Dynamic Responses based on nomination_questions */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900">📝 Form Responses</h4>
              
              {nominationQuestions.length > 0 ? (
                nominationQuestions.map((question) => {
                  const value = getValueForQuestion(question);
                  const fieldKey = getFieldKeyForQuestion(question);
                  
                  // Skip name questions (shown in header)
                  const questionLower = (question.question || '').toLowerCase();
                  if (questionLower.includes("nominee's name") || questionLower.includes("candidate name")) {
                    return null;
                  }
                  
                  return (
                    <div key={question.id} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-medium">{question.question.replace(' *', '')}</h5>
                        {onCopyField && (
                          <button
                            onClick={() => onCopyField(nomination, fieldKey)}
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                          >
                            📋 Copy to Merged
                          </button>
                        )}
                      </div>
                      <p className="text-gray-700">{value || 'Not provided'}</p>
                    </div>
                  );
                })
              ) : (
                // Fallback to legacy fields if no nomination_questions configured
                <>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium">Biography</h5>
                      <button onClick={() => onCopyField(nomination, 'bio')} className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">📋 Copy</button>
                    </div>
                    <p className="text-gray-700">{nomination.bio || 'Not provided'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium">Platform</h5>
                      <button onClick={() => onCopyField(nomination, 'platform')} className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">📋 Copy</button>
                    </div>
                    <p className="text-gray-700">{nomination.platform || 'Not provided'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-medium">Experience</h5>
                      <button onClick={() => onCopyField(nomination, 'experience')} className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">📋 Copy</button>
                    </div>
                    <p className="text-gray-700">{nomination.experience || 'Not provided'}</p>
                  </div>
                </>
              )}

              {/* Raw Nomination Responses for debugging */}
              {nomination.nomination_responses && Object.keys(nomination.nomination_responses).length > 0 && (
                <div className="bg-yellow-50 rounded-lg p-4">
                  <h5 className="font-medium mb-2">📋 Raw Form Responses</h5>
                  <div className="text-sm space-y-2">
                    {Object.entries(nomination.nomination_responses).map(([key, value]) => (
                      <div key={key} className="flex">
                        <span className="font-medium w-32">{key}:</span>
                        <span className="text-gray-700">{value || 'Not provided'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close Review
          </button>
        </div>
      </div>
    </div>
  );
}

// Edit Election Modal - Comprehensive Election Management
// Poll Volunteer Management Component
function PollVolunteerManagement({ election, hoaTimezone = 'America/Chicago' }) {
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newVolunteer, setNewVolunteer] = useState({ name: '', email: '' });
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    loadVolunteers();
  }, [election.id]);

  const loadVolunteers = async () => {
    try {
      const response = await api.get(`/api/vote/election/${election.id}/poll-volunteers`);
      setVolunteers(response.data.volunteers || []);
    } catch (error) {
      console.error('Error loading volunteers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddVolunteer = async () => {
    if (!newVolunteer.name.trim() || !newVolunteer.email.trim()) {
      alert('Please enter both name and email');
      return;
    }

    setAdding(true);
    try {
      const response = await api.post(`/api/vote/election/${election.id}/poll-volunteers`, {
        name: newVolunteer.name,
        email: newVolunteer.email
      });

      const data = response.data;
      const accessUrl = `${window.location.origin}/poll-volunteer/${data.access_token}`;
      
      alert(`✅ Poll Volunteer Added!\n\nName: ${newVolunteer.name}\nEmail: ${newVolunteer.email}\n\nAccess URL:\n${accessUrl}\n\n📝 Submission Password:\n${data.temp_password}\nExpires: ${formatLocal(data.submission_expires_at || data.expires_at, hoaTimezone, 'MMM d, yyyy h:mm a z')}\n(Can enter ballot/proxy counts until then)\n\n👁️ View-Only Password (Audit):\n${data.audit_password}\n(Never expires - for audit purposes)\n\nShare these credentials securely with the volunteer.`);
      
      setNewVolunteer({ name: '', email: '' });
      setShowAddForm(false);
      loadVolunteers();
    } catch (error) {
      console.error('Error adding volunteer:', error);
      alert('Failed to add poll volunteer: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAdding(false);
    }
  };

  const handleRegeneratePassword = async (volunteer) => {
    if (!window.confirm(`Regenerate password for ${volunteer.name}?`)) return;

    try {
      const response = await api.post(`/api/vote/election/${election.id}/poll-volunteers/${volunteer.id}/regenerate-password`);
      const accessUrl = `${window.location.origin}/poll-volunteer/${volunteer.access_token}`;
      
      alert(`✅ Password Regenerated!\n\n` +
            `📋 Access URL:\n${accessUrl}\n\n` +
            `🔑 Poll Volunteer Password (Full Access):\n${response.data.new_password}\n\n` +
            `👁️ View-Only Password (Audit):\n${response.data.audit_password || 'N/A'}\n\n` +
            `📅 Expires: ${new Date(response.data.expires_at).toLocaleString()}\n\n` +
            `Share the full access password with ${volunteer.name} for ballot submissions.`);
    } catch (error) {
      alert('Failed to regenerate password: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRemoveVolunteer = async (volunteer) => {
    if (!window.confirm(`Remove ${volunteer.name} as poll volunteer?`)) return;

    try {
      await api.delete(`/api/vote/election/${election.id}/poll-volunteers/${volunteer.id}`);
      alert('Poll volunteer removed');
      loadVolunteers();
    } catch (error) {
      alert('Failed to remove volunteer: ' + (error.response?.data?.detail || error.message));
    }
  };

  const copyAccessUrl = (volunteer) => {
    const accessUrl = `${window.location.origin}/poll-volunteer/${volunteer.access_token}`;
    navigator.clipboard.writeText(accessUrl);
    alert('Access URL copied to clipboard!');
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium text-gray-900">👥 Poll Volunteers</h4>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700"
        >
          {showAddForm ? '❌ Cancel' : '➕ Add Volunteer'}
        </button>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Poll volunteers can view live voting statistics and see who has voted. Access expires 1 day after voting ends.
      </p>

      {showAddForm && (
        <div className="mb-4 p-4 bg-gray-50 border border-gray-300 rounded-lg">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Volunteer Name</label>
              <input
                type="text"
                value={newVolunteer.name}
                onChange={(e) => setNewVolunteer({...newVolunteer, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={newVolunteer.email}
                onChange={(e) => setNewVolunteer({...newVolunteer, email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="volunteer@example.com"
              />
            </div>
            <button
              onClick={handleAddVolunteer}
              disabled={adding}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
            >
              {adding ? 'Adding...' : '✓ Add Poll Volunteer'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-4">
          <div className="w-6 h-6 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      ) : volunteers.length > 0 ? (
        <div className="space-y-3">
          {volunteers.map((volunteer) => (
            <div key={volunteer.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h5 className="font-medium text-gray-900">{volunteer.name}</h5>
                  <p className="text-sm text-gray-600">{volunteer.email}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Expires: {formatLocal(volunteer.expires_at, hoaTimezone, 'MMM d, yyyy h:mm a z')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => copyAccessUrl(volunteer)}
                    className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                    title="Copy access URL"
                  >
                    🔗
                  </button>
                  <button
                    onClick={() => handleRegeneratePassword(volunteer)}
                    className="px-2 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700"
                    title="Regenerate password"
                  >
                    🔄
                  </button>
                  <button
                    onClick={() => handleRemoveVolunteer(volunteer)}
                    className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                    title="Remove volunteer"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-gray-500 text-sm">
          No poll volunteers added yet. Click "Add Volunteer" to get started.
        </div>
      )}
    </div>
  );
}

// Live Statistics Tab Component
function LiveStatisticsTab({ election, onRefresh }) {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => {
    loadResults();
    const interval = setInterval(loadResults, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, [election.id]);

  const loadResults = async () => {
    try {
      // Use the specific election ID, not just "current"
      const response = await api.get(`/api/vote/election/${election.id}/stats`);
      setResults(response.data);
    } catch (error) {
      console.error('Error loading results:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizeElection = async () => {
    if (!window.confirm('Are you ready to archive this election? This will:\n\n✅ Send final vote tallies to all members\n✅ Post results to the archive\n✅ Override any previous archive for this election\n\nThis action is final and official.')) {
      return;
    }

    setFinalizing(true);
    try {
      // First finalize manual votes (creates HTML archive)
      await api.post(`/api/vote/election/${election.id}/finalize-manual-votes`);
      // Then archive the election (changes status and sends notifications)
      await api.post(`/api/vote/election/${election.id}/archive`);
      alert('✅ Election archived successfully! Results have been posted and members notified.');
      loadResults(); // Reload to show archived status
      if (onRefresh) onRefresh(); // Refresh parent if available
    } catch (error) {
      alert('Failed to archive election: ' + (error.response?.data?.detail || error.message));
    } finally {
      setFinalizing(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-full overflow-x-auto">
        <h3 className="text-xl font-semibold">📈 Live Statistics</h3>
        <div className="bg-gray-100 rounded-lg p-8 text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="space-y-6 max-w-full overflow-x-auto">
        <h3 className="text-xl font-semibold">📈 Live Statistics</h3>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-800">Failed to load statistics. Please try refreshing.</p>
        </div>
      </div>
    );
  }

  // Check if stats are hidden (before meeting starts)
  if (results.stats_hidden) {
    return (
      <div className="space-y-6 max-w-full overflow-x-auto">
        <h3 className="text-xl font-semibold">📈 Live Statistics</h3>
        
        {/* Countdown to Meeting Start */}
        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-lg p-8 text-center">
          <div className="text-6xl mb-4">⏰</div>
          <h4 className="text-2xl font-bold text-gray-900 mb-2">Live Vote Tallies Not Yet Available</h4>
          <p className="text-gray-700 mb-4">
            To maintain election integrity, vote counts will be displayed when the meeting starts.
          </p>
          
          {results.time_until_meeting ? (
            <div className="bg-white rounded-lg p-6 mb-4 border-2 border-yellow-300">
              <div className="text-sm text-gray-600 mb-2">Meeting starts in:</div>
              <div className="text-4xl font-bold text-yellow-600">{results.time_until_meeting}</div>
            </div>
          ) : (
            <div className="bg-white rounded-lg p-6 mb-4 border-2 border-gray-300">
              <div className="text-lg text-gray-600">⏳ Waiting for meeting to start to show live stats</div>
            </div>
          )}
          
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <p className="text-sm text-blue-900">
              <strong>✅ What you CAN see:</strong> Voter tracking and eligibility lists are available in other tabs
            </p>
          </div>
        </div>
        
        {/* Show turnout but not vote breakdown */}
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <h4 className="font-semibold text-gray-900 mb-4">📊 Voter Turnout (Live)</h4>
          <div className="text-center">
            <div className="text-5xl font-bold text-blue-600">{results?.properties_voted || 0}</div>
            <div className="text-gray-600 mt-2">Properties Have Voted</div>
          </div>
        </div>
      </div>
    );
  }

  const isFinalized = election?.finalized_at;
  const isClosed = election?.status === 'closed';

  return (
    <div className="space-y-4 max-w-full">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold">📈 Live Statistics - Official Election Record</h3>
        {isClosed && !isFinalized && (
          <button
            onClick={handleFinalizeElection}
            disabled={finalizing}
            className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white font-semibold rounded-lg hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          >
            {finalizing ? 'Finalizing...' : '🔒 Finalize Election & Archive Results'}
          </button>
        )}
        {isFinalized && (
          <div className="px-6 py-3 bg-green-100 border-2 border-green-600 text-green-800 font-semibold rounded-lg">
            ✅ Election Finalized: {election.finalized_at}
          </div>
        )}
      </div>

      {/* Turnout Stats */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border-2 border-blue-300">
        <h4 className="font-semibold text-blue-900 mb-4 text-lg">📊 Voter Turnout - E-Voting System</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4 text-center border border-blue-200">
            <div className="text-4xl font-bold text-blue-900">{results?.properties_voted || 0}</div>
            <div className="text-sm text-blue-700 font-medium mt-1">Properties E-Voted</div>
            <div className="text-xs text-blue-600 mt-1">(Online ballots only)</div>
          </div>
          <div className="bg-white rounded-lg p-4 text-center border border-blue-200">
            <div className="text-4xl font-bold text-blue-900">{results?.total_votes_cast || 0}</div>
            <div className="text-sm text-blue-700 font-medium mt-1">Total E-Votes Cast</div>
            <div className="text-xs text-blue-600 mt-1">(Ballots submitted)</div>
          </div>
        </div>
      </div>

      {/* Candidate Results */}
      {results?.candidate_stats && results.candidate_stats.length > 0 && (
        <div className="bg-white rounded-lg border-2 border-gray-300 p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">🏆 Candidate Results - Official Count</h4>
          <div className="space-y-4">
            {results?.candidate_stats?.map((candidate, index) => (
              <div key={index} className="border-2 border-gray-300 rounded-lg p-6 bg-gradient-to-r from-purple-50 to-blue-50">
                <div className="mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="text-2xl font-bold text-gray-900">{candidate.name}</h5>
                      <p className="text-sm text-gray-600">{candidate.position || 'Board Position'}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-5xl font-bold text-green-600">{candidate.vote_count}</div>
                      <div className="text-sm text-gray-600">TOTAL VOTES</div>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white rounded-lg p-4 text-center border-2 border-blue-300">
                    <div className="text-3xl font-bold text-blue-600">{candidate.online_votes || 0}</div>
                    <div className="text-xs text-blue-700 font-medium mt-1">E-Votes</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center border-2 border-purple-300">
                    <div className="text-3xl font-bold text-purple-600">{candidate.manual_votes || 0}</div>
                    <div className="text-xs text-purple-700 font-medium mt-1">Manual Votes</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center border-2 border-orange-300">
                    <div className="text-3xl font-bold text-orange-600">{candidate.proxy_votes || 0}</div>
                    <div className="text-xs text-orange-700 font-medium mt-1">Proxy Votes</div>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-white rounded border-2 border-gray-200 text-center">
                  <p className="text-sm text-gray-700">
                    <strong>{candidate.name}</strong> received <strong className="text-blue-600">{candidate.online_votes} e-votes</strong> + <strong className="text-purple-600">{candidate.manual_votes} manual votes</strong> + <strong className="text-orange-600">{candidate.proxy_votes || 0} proxy votes</strong> = <strong className="text-green-600 text-lg">{candidate.vote_count} TOTAL</strong>
                  </p>
                </div>

                {/* Volunteer Manual Count Breakdown - AUDIT TRAIL */}
                {candidate.volunteer_manual_breakdown && candidate.volunteer_manual_breakdown.length > 0 && (
                  <div className="mt-4 p-4 bg-yellow-50 rounded border-2 border-yellow-300">
                    <h6 className="text-sm font-bold text-yellow-900 mb-3">📋 VOLUNTEER MANUAL COUNT AUDIT TRAIL</h6>
                    <div className="space-y-2">
                      {candidate.volunteer_manual_breakdown.map((volunteer, vIdx) => (
                        <div key={vIdx} className="bg-white rounded p-3 border border-yellow-200">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-gray-900">{volunteer.volunteer_name}</span>
                            <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full font-bold">
                              {volunteer.count} votes
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-yellow-800 mt-3 text-center font-medium">
                      ⚠️ This breakdown shows each volunteer's manual paper ballot count for audit verification
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Topic Results */}
      {results?.special_topic_stats && results.special_topic_stats.length > 0 && (
        <div className="bg-white rounded-lg border-2 border-gray-300 p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">📋 Motion Results - Official Count</h4>
          <div className="space-y-4">
            {results?.special_topic_stats?.map((topic, idx) => {
              // Get actual answer options - default to Yes/No if not specified
              const answerOptions = topic.answer_options || topic.answers || ['Yes', 'No'];
              const totalCounts = topic.total_counts || {};
              const onlineCounts = topic.online_counts || {};
              const manualCounts = topic.manual_counts || {};
              
              // Color map for different answer types
              const getAnswerStyle = (answer, index) => {
                const lowerAnswer = answer.toLowerCase();
                if (lowerAnswer === 'yes' || lowerAnswer === 'approve') return { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-600', label: 'text-green-700' };
                if (lowerAnswer === 'no' || lowerAnswer === 'oppose' || lowerAnswer === 'reject') return { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-600', label: 'text-red-700' };
                if (lowerAnswer === 'abstain') return { bg: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-600', label: 'text-gray-700' };
                // For custom answers, use different colors based on index
                const colors = [
                  { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-600', label: 'text-blue-700' },
                  { bg: 'bg-purple-50', border: 'border-purple-300', text: 'text-purple-600', label: 'text-purple-700' },
                  { bg: 'bg-yellow-50', border: 'border-yellow-300', text: 'text-yellow-600', label: 'text-yellow-700' },
                  { bg: 'bg-pink-50', border: 'border-pink-300', text: 'text-pink-600', label: 'text-pink-700' },
                  { bg: 'bg-indigo-50', border: 'border-indigo-300', text: 'text-indigo-600', label: 'text-indigo-700' },
                ];
                return colors[index % colors.length];
              };
              
              return (
                <div key={idx} className="border-2 border-gray-300 rounded-lg p-6 bg-gray-50">
                  <h5 className="text-xl font-bold text-gray-900 mb-4">{topic.title}</h5>
                  
                  {/* Dynamic Answer Breakdown */}
                  <div className={`grid gap-4 mb-4`} style={{ gridTemplateColumns: `repeat(${answerOptions.length}, minmax(0, 1fr))` }}>
                    {answerOptions.map((answer, ansIdx) => {
                      const style = getAnswerStyle(answer, ansIdx);
                      const count = totalCounts[answer] || 0;
                      const onlineCount = onlineCounts[answer] || 0;
                      const manualCount = manualCounts[answer] || 0;
                      
                      return (
                        <div key={ansIdx} className={`bg-white rounded-lg p-4 text-center border-2 ${style.border}`}>
                          <div className={`text-4xl font-bold ${style.text}`}>{count}</div>
                          <div className={`text-sm ${style.label} font-medium mt-1`}>{answer}</div>
                          <div className="text-xs text-gray-500 mt-2">E: {onlineCount} | M: {manualCount}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Summary */}
                  <div className="p-4 bg-white rounded border-2 border-blue-200 text-center">
                    <p className="text-sm text-gray-700">
                      <strong>{topic.title}:</strong> {' '}
                      {answerOptions.map((answer, ansIdx) => {
                        const style = getAnswerStyle(answer, ansIdx);
                        const count = totalCounts[answer] || 0;
                        return (
                          <span key={ansIdx}>
                            <span className={`${style.text} font-bold`}>{count} {answer}</span>
                            {ansIdx < answerOptions.length - 1 ? ' | ' : ' = '}
                          </span>
                        );
                      })}
                      <strong className="text-blue-600 text-lg">{topic.total_votes} TOTAL VOTES</strong>
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      E-Votes: {Object.values(onlineCounts).reduce((a, b) => a + b, 0)} | Manual: {Object.values(manualCounts).reduce((a, b) => a + b, 0)}
                    </p>
                  </div>

                  {/* Volunteer Manual Count Breakdown - AUDIT TRAIL */}
                  {topic.volunteer_manual_breakdown && topic.volunteer_manual_breakdown.length > 0 && (
                    <div className="mt-4 p-4 bg-yellow-50 rounded border-2 border-yellow-300">
                      <h6 className="text-sm font-bold text-yellow-900 mb-3">📋 VOLUNTEER MANUAL COUNT AUDIT TRAIL</h6>
                      <div className="space-y-2">
                        {topic.volunteer_manual_breakdown.map((volunteer, vIdx) => (
                          <div key={vIdx} className="bg-white rounded p-3 border border-yellow-200">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-gray-900">{volunteer.volunteer_name}</span>
                              <span className="text-sm text-gray-600">Total: {volunteer.total} votes</span>
                            </div>
                            <div className={`grid gap-2 mt-2 text-xs`} style={{ gridTemplateColumns: `repeat(${answerOptions.length}, minmax(0, 1fr))` }}>
                              {answerOptions.map((answer, ansIdx) => {
                                const style = getAnswerStyle(answer, ansIdx);
                                const count = volunteer.counts?.[answer] || volunteer[answer.toLowerCase()] || 0;
                                return (
                                  <div key={ansIdx} className={`text-center ${style.bg} rounded p-2 border ${style.border}`}>
                                    <div className={`font-bold ${style.text}`}>{count}</div>
                                    <div className={style.label}>{answer}</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-yellow-800 mt-3 text-center font-medium">
                        ⚠️ This breakdown shows each volunteer's manual paper ballot count for audit verification
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No Data Message */}
      {(!results?.candidate_stats || results.candidate_stats.length === 0) && 
       (!results?.special_topic_stats || results.special_topic_stats.length === 0) && (
        <div className="bg-gray-100 rounded-lg p-8 text-center">
          <p className="text-gray-600">No election data available.</p>
        </div>
      )}
    </div>
  );
}

function EditElectionModal({ election, onClose, onRefresh, boardMembers, hoaTimezone = 'America/Chicago', hoaSettings = {} }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [saving, setSaving] = useState(false);
  const [editingVotingSettings, setEditingVotingSettings] = useState(false);
  const [showMeetingExtensionModal, setShowMeetingExtensionModal] = useState(false);
  const [extensionType, setExtensionType] = useState('adjourn'); // 'adjourn' or 'postpone'
  const [newMeetingTime, setNewMeetingTime] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Election basic data
  const [electionData, setElectionData] = useState({
    name: election.name || '',
    description: election.description || '',
    nominations_open: election.nominations_open || new Date().toISOString().slice(0, 16),
    nominations_close: election.nominations_close || '',
    proxy_collection_start: election.proxy_collection_start || '',
    voting_open: election.voting_open || '',
    meeting_start_time: election.meeting_start_time || election.voting_close || '',
    positions_up_for_election: election.positions_up_for_election || [],
    votes_per_property: election.votes_per_property || 1,
    builder_vote_multiplier: election.builder_vote_multiplier || 1,
    builder_homeowner_id: election.builder_homeowner_id || '',
    default_proxy_role: election.default_proxy_role || 'board'
  });

  // Editable voting configuration
  const [editablePositions, setEditablePositions] = useState(election.positions_up_for_election || []);
  const [editableTopics, setEditableTopics] = useState(election.special_topics || []);

  // Nomination form questions
  const [nominationQuestions, setNominationQuestions] = useState([
    { id: 1, section: 'identification', question: "Nominee's Name", type: 'text', required: true, public: true },
    { id: 2, section: 'identification', question: 'Nominated By', type: 'radio', options: ['Self', 'Another Member'], required: true, public: false },
    { id: 3, section: 'identification', question: 'Nominator Name (if nominated by another)', type: 'text', required: false, public: false },
    { id: 4, section: 'identification', question: 'Property Address / Identifier', type: 'text', required: true, public: false },
    { id: 5, section: 'background', question: 'Brief Biography (2–3 sentences): Tell us a little about yourself, your background, and your connection to the community.', type: 'textarea', required: true, public: true },
    { id: 6, section: 'background', question: 'Relevant Experience: Any board, committee, volunteer, or professional experience that helps you serve the association.', type: 'textarea', required: true, public: true },
    { id: 7, section: 'vision', question: 'Why are you interested in serving on the Board? (what is your motivation)', type: 'textarea', required: true, public: true },
    { id: 8, section: 'vision', question: 'What are your top 2-3 priorities or areas you would like the Association to improve?', type: 'textarea', required: true, public: true },
    { id: 9, section: 'vision', question: 'What skills or perspectives will you bring to the Board?', type: 'textarea', required: true, public: true },
    { id: 10, section: 'community', question: 'Have you served on an HOA committee or Board before? If yes, which one(s)?', type: 'textarea', required: true, public: true },
    { id: 11, section: 'community', question: 'Any previous accomplishments or contributions in community service?', type: 'textarea', required: false, public: true },
    { id: 12, section: 'community', question: 'Are you willing to commit the time required for Board meetings, committee work, and community events?', type: 'radio', options: ['Yes', 'No'], required: true, public: true }
  ]);

  // Voting settings
  const [votingSettings, setVotingSettings] = useState({
    voting_type: 'online', // 'online', 'mail', 'both'
    allow_proxy_voting: true,
    show_live_results: true,
    require_verification: true
  });

  const addQuestion = () => {
    const newId = Math.max(...nominationQuestions.map(q => q.id)) + 1;
    setNominationQuestions([...nominationQuestions, {
      id: newId,
      section: 'custom',
      question: '',
      type: 'text',
      required: false,
      public: true
    }]);
  };

  const removeQuestion = (id) => {
    setNominationQuestions(nominationQuestions.filter(q => q.id !== id));
  };

  const updateQuestion = (id, field, value) => {
    setNominationQuestions(nominationQuestions.map(q => 
      q.id === id ? { ...q, [field]: value } : q
    ));
  };

  // Helper functions for managing voting settings
  const canEditVotingSettings = () => {
    const editableStatuses = ['draft', 'proxy_collection', 'nominating'];
    return editableStatuses.includes(election.status);
  };

  const addNewTopic = () => {
    const newTopic = {
      name: 'New Motion',
      answers: ['Yes', 'No', 'Abstain'],
      proxy_settings: {
        allow_general: true,
        allow_limited: true,
        board_receiver: true,
        purpose: 'voting_quorum'
      }
    };
    setEditableTopics([...editableTopics, newTopic]);
  };

  const removeTopic = (index) => {
    setEditableTopics(editableTopics.filter((_, i) => i !== index));
  };

  const updateTopic = (index, field, value) => {
    const updated = [...editableTopics];
    if (field.startsWith('proxy_settings.')) {
      const proxyField = field.split('.')[1];
      updated[index] = {
        ...updated[index],
        proxy_settings: {
          ...updated[index].proxy_settings,
          [proxyField]: value
        }
      };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setEditableTopics(updated);
  };

  const updatePositionProxySettings = (index, field, value) => {
    const updated = [...editablePositions];
    updated[index] = {
      ...updated[index],
      proxy_settings: {
        ...updated[index].proxy_settings,
        [field]: value
      }
    };
    setEditablePositions(updated);
  };

  const saveVotingSettings = async () => {
    if (!canEditVotingSettings()) {
      alert('Voting settings can only be edited in Draft, Proxy Collection, or Nominations phase');
      return;
    }

    console.log('🔍 DEBUG: Saving voting settings for election ID:', election.id);
    console.log('🔍 DEBUG: Editable Positions:', editablePositions);
    console.log('🔍 DEBUG: Editable Topics:', editableTopics);

    setSaving(true);
    try {
      const response = await api.put(`/api/vote/election/${election.id}`, {
        special_topics: editableTopics,
        positions_up_for_election: editablePositions
      });

      console.log('✅ Save response:', response.data);

      alert('Voting settings saved successfully!');
      setEditingVotingSettings(false);
      onRefresh();
    } catch (error) {
      console.error('❌ Error saving voting settings:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Request URL:', `/api/vote/election/${election.id}`);
      alert('Failed to save voting settings: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleMeetingExtension = async () => {
    if (!newMeetingTime) {
      alert('Please select a new meeting date and time');
      return;
    }

    setSaving(true);
    try {
      await api.post(`/api/vote/election/${election.id}/extend-meeting`, {
        extension_type: extensionType,
        new_meeting_time: newMeetingTime
      });

      const action = extensionType === 'adjourn' ? 'adjourned' : 'postponed';
      alert(`Meeting ${action} successfully! Limited proxies ${extensionType === 'adjourn' ? 'remain valid' : 'have been invalidated'}.`);
      
      setShowMeetingExtensionModal(false);
      setNewMeetingTime('');
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error extending meeting:', error);
      alert('Failed to extend meeting: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleStageProgression = async (action) => {
    console.log('=== handleStageProgression START ===');
    console.log('Action:', action);
    console.log('Election object:', election);
    console.log('Election ID:', election.id);
    console.log('Election status:', election.status);
    
    if (!election.id) {
      alert('Error: Election ID is missing. Cannot proceed.');
      console.error('Election ID is undefined!', election);
      return;
    }
    
    setSaving(true);
    try {
      let endpoint = '';
      let successMessage = '';
      
      switch (action) {
        case 'start_proxy_collection':
          endpoint = `/api/vote/election/${election.id}/start-proxy-collection`;
          successMessage = 'Proxy collection started! Election published to members.';
          break;
        case 'start_nominations':
          endpoint = `/api/vote/election/${election.id}/start-nominations`;
          successMessage = 'Nominations started successfully!';
          break;
        case 'end_nominations':
          endpoint = `/api/vote/election/${election.id}/end-nominations`;
          successMessage = 'Nominations ended successfully!';
          break;
        case 'start_voting':
          // VALIDATION: Check election readiness before opening voting
          const validationErrors = [];
          
          // Check if it's a candidate election with no nominees
          if (!election.not_member_election && (!candidates || candidates.length === 0)) {
            validationErrors.push('❌ No candidates/nominees found. You must have at least one approved candidate before opening voting.');
          }
          
          // Check if motions exist but have no voting options
          if (election.special_topics && election.special_topics.length > 0) {
            const motionsWithoutAnswers = election.special_topics.filter(t => !t.answers || t.answers.length === 0);
            if (motionsWithoutAnswers.length > 0) {
              validationErrors.push(`❌ ${motionsWithoutAnswers.length} motion(s) have no voting options configured. Each motion must have at least one answer option (e.g., Yes/No/Abstain) before voting can open.`);
              motionsWithoutAnswers.forEach(m => {
                validationErrors.push(`   • Motion: "${m.name}" - needs voting options`);
              });
            }
          }
          
          // If validation errors exist, show them and abort
          if (validationErrors.length > 0) {
            const errorMessage = '⚠️ ELECTION NOT READY FOR VOTING\n\n' + 
              'Please fix the following issues before opening voting:\n\n' +
              validationErrors.join('\n\n') +
              '\n\n💡 TIP: A motion without voting options will prevent ALL users from casting votes, even for candidates.';
            
            alert(errorMessage);
            setSaving(false);
            return;
          }
          
          endpoint = `/api/vote/election/${election.id}/start-voting`;
          successMessage = 'Voting started successfully!';
          break;
        case 'end_voting':
          endpoint = `/api/vote/election/${election.id}/end-voting`;
          successMessage = 'Voting ended successfully!';
          break;
        case 'publish':
          endpoint = `/api/vote/election/${election.id}/publish`;
          successMessage = 'Election published successfully!';
          break;
        case 'archive':
          if (!window.confirm('Are you ready to archive this election? This will:\n\n✅ Send final vote tallies to all members\n✅ Post results to the archive\n✅ Override any previous archive for this election\n\nThis action is final and official.')) {
            console.log('User cancelled archive');
            setSaving(false);
            return;
          }
          endpoint = `/api/vote/election/${election.id}/archive`;
          successMessage = 'Election archived successfully! Results have been posted and members notified.';
          break;
        case 'delete':
          if (!window.confirm('DELETE this election permanently? This cannot be undone!')) {
            console.log('User cancelled delete');
            setSaving(false);
            return;
          }
          console.log('Calling DELETE /api/vote/election/' + election.id);
          await api.delete(`/api/vote/election/${election.id}`);
          alert('Election deleted successfully');
          onRefresh();
          onClose();
          setSaving(false);
          console.log('=== handleStageProgression END (delete success) ===');
          return;
      }
      
      if (endpoint) {
        console.log('Calling POST', endpoint);
        const response = await api.post(endpoint);
        console.log('Response:', response);
        alert(successMessage);
        onRefresh();
        onClose();
      }
      console.log('=== handleStageProgression END (success) ===');
    } catch (error) {
      console.error('=== handleStageProgression ERROR ===');
      console.error('Error details:', error);
      console.error('Error response:', error.response);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to ${action.replace('_', ' ')}: ${errorMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const saveElectionSettings = async () => {
    setSaving(true);
    try {
      await api.put(`/api/vote/election/${election.id}`, {
        ...electionData,
        nomination_questions: nominationQuestions,
        voting_settings: votingSettings
      });
      alert('Election settings saved successfully!');
      onRefresh();
    } catch (error) {
      console.error('Error saving election settings:', error);
      alert('Failed to save election settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getActionButtons = () => {
    const status = election.status;
    const buttons = [];
    const isTopicsOnly = election.not_member_election; // Topics-only election (no officers)
    const hasProxies = election.positions_up_for_election?.some(p => 
      p.proxy_settings?.allow_general || p.proxy_settings?.allow_limited
    ) || election.special_topics?.some(t => 
      t.proxy_settings?.allow_general || t.proxy_settings?.allow_limited
    );

    if (status === 'draft' || status === 'published') {
      if (hasProxies) {
        // Election has proxies enabled - allow proxy collection phase
        buttons.push(
          <button
            key="start_proxy_collection"
            onClick={() => handleStageProgression('start_proxy_collection')}
            className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm sm:text-base bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            disabled={saving}
          >
            📋 Start Collecting Proxies
          </button>
        );
      }

      if (isTopicsOnly) {
        // Topics-only without proxies - skip straight to voting
        if (!hasProxies) {
          buttons.push(
            <button
              key="start_voting"
              onClick={() => handleStageProgression('start_voting')}
              className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm sm:text-base bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              disabled={saving}
            >
              🗳️ Start Voting (No Proxies)
            </button>
          );
        }
      } else {
        // Regular election with officer positions - start with nominations
        if (!hasProxies) {
          buttons.push(
            <button
              key="start_nominations"
              onClick={() => handleStageProgression('start_nominations')}
              className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              disabled={saving}
            >
              🎯 Start Nominations
            </button>
          );
        }
      }
    }

    // Proxy collection phase
    if (status === 'proxy_collection') {
      if (isTopicsOnly) {
        buttons.push(
          <button
            key="start_voting"
            onClick={() => handleStageProgression('start_voting')}
            className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm sm:text-base bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            disabled={saving}
          >
            🗳️ Start Voting
          </button>
        );
      } else {
        buttons.push(
          <button
            key="start_nominations"
            onClick={() => handleStageProgression('start_nominations')}
            className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            disabled={saving}
          >
            🎯 Start Nominations
          </button>
        );
      }
    }

    if (status === 'nominating') {
      buttons.push(
        <button
          key="end_nominations"
          onClick={() => handleStageProgression('end_nominations')}
          className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm sm:text-base bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
          disabled={saving}
        >
          ⏹️ End Nominations
        </button>
      );
    }

    if (status === 'queued') {
      buttons.push(
        <button
          key="start_voting"
          onClick={() => handleStageProgression('start_voting')}
          className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm sm:text-base bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          disabled={saving}
        >
          🗳️ Start Voting
        </button>
      );
    }

    if (status === 'voting') {
      buttons.push(
        <button
          key="end_voting"
          onClick={() => handleStageProgression('end_voting')}
          className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm sm:text-base bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          disabled={saving}
        >
          🔒 End Voting
        </button>
      );
    }

    if (status === 'closed') {
      buttons.push(
        <button
          key="archive"
          onClick={() => handleStageProgression('archive')}
          className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm sm:text-base bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          disabled={saving}
        >
          📁 Archive Election
        </button>
      );
    }

    return buttons;
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-6xl w-full h-[90vh] flex flex-col overflow-hidden">
        {/* Header - Fixed */}
        <div className="flex-shrink-0 p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Hamburger Menu - Mobile Only */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 hover:bg-blue-100 rounded-lg transition-colors"
                aria-label="Toggle menu"
              >
                <div className="space-y-1.5">
                  <div className={`w-6 h-0.5 bg-gray-700 transition-transform ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></div>
                  <div className={`w-6 h-0.5 bg-gray-700 transition-opacity ${mobileMenuOpen ? 'opacity-0' : ''}`}></div>
                  <div className={`w-6 h-0.5 bg-gray-700 transition-transform ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></div>
                </div>
              </button>
              
              <div>
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900">⚙️ Election Management</h2>
                <p className="text-xs sm:text-base text-gray-600 mt-1">{election.name}</p>
                <div className="flex items-center gap-2 sm:gap-3 mt-1 sm:mt-2">
                  <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getPhaseColor(election.status)}`}>
                    {getPhaseLabel(election.status)}
                  </span>
                  {election.created_at && (
                    <span className="hidden sm:inline text-sm text-gray-500">
                      Created: {formatLocal(election.created_at, hoaTimezone, 'MMM d, yyyy')}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-1 min-h-0 relative">
          {/* Mobile Menu Overlay */}
          {mobileMenuOpen && (
            <div 
              className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
              onClick={() => setMobileMenuOpen(false)}
            />
          )}
          
          {/* Sidebar - Desktop: always visible, Mobile: slide-in menu */}
          <div className={`
            flex-shrink-0 bg-gray-50 border-r border-gray-200 p-4 overflow-y-auto
            md:w-64 md:relative md:translate-x-0
            fixed inset-y-0 left-0 w-64 z-20 transition-transform duration-300 ease-in-out
            ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          `}>
            <nav className="space-y-2">
              {[
                { id: 'overview', label: '📊 Overview', desc: 'Election details & timeline', disabled: false },
                { id: 'nomination', label: '📝 Nomination Form', desc: 'Questions & requirements', disabled: election.not_member_election },
                { id: 'nominees', label: '👥 Nominees', desc: 'Approve/manage nominees', disabled: election.not_member_election },
                { id: 'voting_settings', label: '🗳️ Voting Settings', desc: 'Configure voting rules' },
                { id: 'eligible_voters', label: '✅ Eligible Voters', desc: 'Manage voter eligibility' },
                { id: 'statistics', label: '📈 Live Statistics', desc: 'Voting progress & results' },
                { id: 'management', label: '⚙️ Stage Management', desc: 'Progress election phases' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    if (!tab.disabled) {
                      setActiveTab(tab.id);
                      setMobileMenuOpen(false); // Close menu on mobile after selection
                    }
                  }}
                  disabled={tab.disabled}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    tab.disabled
                      ? 'opacity-40 cursor-not-allowed bg-gray-100'
                      : activeTab === tab.id
                      ? 'bg-blue-100 text-blue-900 border border-blue-200'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <div className="font-medium text-sm sm:text-base">
                    {tab.label}
                    {tab.disabled && <span className="ml-2 text-xs text-gray-500">(N/A)</span>}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{tab.desc}</div>
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 min-h-0">
            {/* Mobile: Active Tab Indicator */}
            <div className="md:hidden mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-xs text-blue-600 font-medium">Current Section</div>
              <div className="text-sm font-semibold text-blue-900 mt-1">
                {[
                  { id: 'overview', label: '📊 Overview' },
                  { id: 'nomination', label: '📝 Nomination Form' },
                  { id: 'nominees', label: '👥 Nominees' },
                  { id: 'voting_settings', label: '🗳️ Voting Settings' },
                  { id: 'eligible_voters', label: '✅ Eligible Voters' },
                  { id: 'statistics', label: '📈 Live Statistics' },
                  { id: 'management', label: '⚙️ Stage Management' }
                ].find(t => t.id === activeTab)?.label}
              </div>
              <div className="text-xs text-blue-700 mt-1">Tap ☰ to switch sections</div>
            </div>
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold">📊 Election Overview</h3>
                
                {/* Legal Notice Warning */}
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
                  <p className="text-sm text-amber-800 flex items-center gap-2">
                    <span className="text-amber-600">⚠️</span>
                    <strong>Important:</strong> Voting may not begin before notice is sent to all eligible voters.
                  </p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Election Name</label>
                    <input
                      type="text"
                      value={electionData.name}
                      onChange={(e) => setElectionData({...electionData, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={electionData.description}
                      onChange={(e) => setElectionData({...electionData, description: e.target.value})}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className={`grid md:grid-cols-2 gap-6 ${election.not_member_election ? 'opacity-40 pointer-events-none' : ''}`}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nominations Open
                      {election.not_member_election && <span className="ml-2 text-xs text-gray-500">(N/A for topics-only)</span>}
                    </label>
                    <input
                      type="datetime-local"
                      value={electionData.nominations_open}
                      onChange={(e) => setElectionData({...electionData, nominations_open: e.target.value})}
                      disabled={election.not_member_election}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nominations Close
                      {election.not_member_election && <span className="ml-2 text-xs text-gray-500">(N/A for topics-only)</span>}
                    </label>
                    <input
                      type="datetime-local"
                      value={electionData.nominations_close}
                      onChange={(e) => setElectionData({...electionData, nominations_close: e.target.value})}
                      disabled={election.not_member_election}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Proxy Collection Start</label>
                    <input
                      type="datetime-local"
                      value={electionData.proxy_collection_start}
                      onChange={(e) => setElectionData({...electionData, proxy_collection_start: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      When homeowners can start assigning proxies
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Voting Open</label>
                      <input
                        type="datetime-local"
                        value={electionData.voting_open}
                        onChange={(e) => setElectionData({...electionData, voting_open: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Start Time</label>
                      <input
                        type="datetime-local"
                        value={electionData.meeting_start_time}
                        onChange={(e) => setElectionData({...electionData, meeting_start_time: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-blue-600 mt-1">
                        ℹ️ Online voting will automatically close 10 minutes after meeting starts
                      </p>
                    </div>
                  </div>
                </div>

                {/* Builder Vote Multiplier */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-900 mb-3">🏗️ Builder Vote Settings</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Vote Multiplier</label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={electionData.builder_vote_multiplier}
                        onChange={(e) => setElectionData({...electionData, builder_vote_multiplier: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Builder Homeowner ID</label>
                      <input
                        type="text"
                        value={electionData.builder_homeowner_id}
                        onChange={(e) => setElectionData({...electionData, builder_homeowner_id: e.target.value})}
                        placeholder="Enter homeowner ID for builder vote"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <button
                  onClick={saveElectionSettings}
                  disabled={saving}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                >
                  {saving ? 'Saving...' : '💾 Save Election Settings'}
                </button>
              </div>
            )}

            {activeTab === 'nomination' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold">📝 Nomination Form Questions</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => printNominationForm(election, nominationQuestions, hoaTimezone, hoaSettings)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                    >
                      🖨️ Print Form for Mailing
                    </button>
                    <button
                      onClick={addQuestion}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      ➕ Add Question
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {nominationQuestions.map((question, index) => (
                    <div key={question.id} className="bg-gray-50 rounded-lg p-4 border">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-medium text-gray-500">
                              Section: {question.section.charAt(0).toUpperCase() + question.section.slice(1)}
                            </span>
                            {question.required && (
                              <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Required</span>
                            )}
                            {question.public && (
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Public</span>
                            )}
                          </div>
                          <textarea
                            value={question.question}
                            onChange={(e) => updateQuestion(question.id, 'question', e.target.value)}
                            placeholder="Enter question text..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows="2"
                          />
                        </div>
                        <button
                          onClick={() => removeQuestion(question.id)}
                          className="ml-2 p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                        >
                          🗑️
                        </button>
                      </div>
                      
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                          <select
                            value={question.type}
                            onChange={(e) => updateQuestion(question.id, 'type', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="text">Text</option>
                            <option value="textarea">Textarea</option>
                            <option value="radio">Radio Buttons</option>
                            <option value="checkbox">Checkbox</option>
                          </select>
                        </div>
                        
                        <div className="flex items-center space-x-4">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={question.required}
                              onChange={(e) => updateQuestion(question.id, 'required', e.target.checked)}
                              className="mr-1"
                            />
                            <span className="text-xs">Required</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={question.public}
                              onChange={(e) => updateQuestion(question.id, 'public', e.target.checked)}
                              className="mr-1"
                            />
                            <span className="text-xs">Public</span>
                          </label>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Section</label>
                          <select
                            value={question.section}
                            onChange={(e) => updateQuestion(question.id, 'section', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="identification">Identification</option>
                            <option value="background">Background</option>
                            <option value="vision">Vision & Motivation</option>
                            <option value="community">Community Involvement</option>
                            <option value="custom">Custom</option>
                          </select>
                        </div>
                      </div>

                      {(question.type === 'radio' || question.type === 'checkbox') && (
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-700 mb-1">Options (comma-separated)</label>
                          <input
                            type="text"
                            value={question.options ? question.options.join(', ') : ''}
                            onChange={(e) => updateQuestion(question.id, 'options', e.target.value.split(', '))}
                            placeholder="Option 1, Option 2, Option 3"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <button
                  onClick={saveElectionSettings}
                  disabled={saving}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                >
                  {saving ? 'Saving...' : '💾 Save Nomination Questions'}
                </button>
              </div>
            )}

            {activeTab === 'management' && (
              <div className="space-y-4 sm:space-y-6">
                <h3 className="text-lg sm:text-xl font-semibold">⚙️ Stage Management</h3>
                
                {/* Legal Notice Warning */}
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
                  <p className="text-sm text-amber-800 flex items-center gap-2">
                    <span className="text-amber-600">⚠️</span>
                    <strong>Important:</strong> Voting may not begin before notice is sent to all eligible voters.
                  </p>
                </div>
                
                {/* Election Guide for Board */}
                <details className="bg-blue-50 border border-blue-200 rounded-lg">
                  <summary className="p-3 cursor-pointer flex items-center gap-2 text-blue-900 font-medium text-sm">
                    <span>📘</span> Election Guide for Board Members
                    <span className="text-red-600 text-xs ml-auto">(Click here for more details)</span>
                  </summary>
                  <div className="px-4 pb-4 space-y-3 text-sm text-blue-900">
                    
                    {/* Key Election Dates - FIXED */}
                    <div className="border-t border-blue-200 pt-3 bg-yellow-50 -mx-4 px-4 py-3 border-b border-yellow-200">
                      <h5 className="font-semibold mb-2 text-yellow-900">📅 Key Election Dates</h5>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                        <div className="bg-white rounded p-2 border border-green-300">
                          <div className="text-gray-600">Meeting Start Time:</div>
                          <div className="font-semibold text-green-800">
                            {election.meeting_start_time 
                              ? formatLocal(election.meeting_start_time, hoaTimezone, 'MMM d, yyyy h:mm a z')
                              : 'Not scheduled'}
                          </div>
                        </div>
                        <div className="bg-white rounded p-2 border border-yellow-300">
                          <div className="text-gray-600">E-Voting Closes:</div>
                          <div className="font-semibold text-yellow-800">
                            {election.meeting_start_time 
                              ? '10 minutes after meeting start'
                              : 'Meeting time not set'}
                          </div>
                          <div className="text-gray-500 text-[10px]">Members must complete e-voting before this time</div>
                        </div>
                        <div className="bg-white rounded p-2 border border-orange-300">
                          <div className="text-gray-600">Volunteer Submission Ends:</div>
                          <div className="font-semibold text-orange-800">
                            1.5 hours after meeting start
                          </div>
                          <div className="text-gray-500 text-[10px]">Volunteers can enter ballots/proxies until then</div>
                        </div>
                        <div className="bg-white rounded p-2 border border-red-300">
                          <div className="text-gray-600">Auto-Finalize:</div>
                          <div className="font-semibold text-red-800">
                            1.5 hours after meeting start
                          </div>
                          <div className="text-gray-500 text-[10px]">Election auto-archives if not done manually</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="border-t border-blue-200 pt-3">
                      <h5 className="font-semibold mb-2">📋 Election Phases</h5>
                      <ol className="list-decimal list-inside space-y-1 text-xs">
                        <li><strong>Draft</strong> - Configure election settings, positions, and motions</li>
                        <li><strong>Nominations</strong> - Members can self-nominate or be nominated</li>
                        <li><strong>Proxy Collection</strong> - Members can assign voting proxies</li>
                        <li><strong>Voting</strong> - E-voting is open until 10 min after meeting start</li>
                        <li><strong>Closed</strong> - Meeting in progress, manual votes collected</li>
                        <li><strong>Finalize</strong> - Confirm results and archive</li>
                      </ol>
                    </div>
                    
                    <div>
                      <h5 className="font-semibold mb-2">📄 Sharing Voter Information</h5>
                      <p className="text-xs text-blue-800">
                        Extra voter information such as <strong>bylaw changes</strong> can be posted as documents and selected 
                        on the <strong>Public Page</strong> for non-HOA Manager Pro users. For those inside the platform, 
                        they can read the same document and see postings in the <strong>Announcements</strong> section.
                      </p>
                      <p className="text-xs text-blue-700 mt-1 italic">
                        Note: Announcements are NOT posted to the external public page - only selected documents appear there.
                      </p>
                    </div>
                    
                    <div>
                      <h5 className="font-semibold mb-2">👥 Nominee Profiles</h5>
                      <p className="text-xs text-blue-800">
                        After the nomination period ends, you can enable <strong>"Show Voting Profiles"</strong> on the 
                        Public Page Settings to display candidate profiles to external visitors. This helps community 
                        members learn about candidates before the election.
                      </p>
                    </div>
                    
                    <div>
                      <h5 className="font-semibold mb-2">🔐 Proxy Rules</h5>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>Proxies are valid for 90 days from the first meeting date</li>
                        <li><strong>Adjourn</strong>: All proxies remain valid</li>
                        <li><strong>Postpone</strong>: Limited proxies are invalidated; general proxies remain</li>
                        <li>If a proxy holder votes, the proxy cannot be revoked</li>
                      </ul>
                    </div>
                    
                    <div className="bg-yellow-50 border border-yellow-300 rounded p-3 -mx-4 px-4">
                      <h5 className="font-semibold mb-2 text-yellow-900">📊 Voter Turnout Note</h5>
                      <p className="text-xs text-yellow-800">
                        If voter turnout displays <strong>higher than 100%</strong>, this is likely because the 
                        <strong> "Number of Eligible Voters (Not in System)"</strong> count has not been entered. 
                        Go to the <strong>Eligible Voters</strong> tab and enter this number in the 
                        <strong> Manual Turnout Tracking</strong> section to ensure accurate turnout calculations.
                      </p>
                    </div>
                  </div>
                </details>
                
                {election.not_member_election && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
                    <div className="flex items-center gap-2">
                      <span className="text-yellow-600 font-semibold text-sm sm:text-base">ℹ️ Topics-Only Election</span>
                    </div>
                    <p className="text-xs sm:text-sm text-yellow-800 mt-1">
                      This is a special topics election without officer positions. Nominations phase will be automatically skipped.
                    </p>
                  </div>
                )}

                {/* Proxy Status Banner */}
                {(() => {
                  const hasProxies = election.positions_up_for_election?.some(p => 
                    p.proxy_settings?.allow_general || p.proxy_settings?.allow_limited
                  ) || election.special_topics?.some(t => 
                    t.proxy_settings?.allow_general || t.proxy_settings?.allow_limited
                  );
                  
                  return hasProxies ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                      <div className="flex items-center gap-2">
                        <span className="text-green-600 font-semibold text-sm sm:text-base">✅ Proxies Enabled</span>
                      </div>
                      <p className="text-xs sm:text-sm text-green-800 mt-1">
                        Proxy voting is configured for this election. Members can assign proxies during the collection phase.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-300 rounded-lg p-3 sm:p-4">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-600 font-semibold text-sm sm:text-base">❌ No Proxies</span>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-700 mt-1">
                        This vote will not be using proxies. Proxy collection phase will be skipped.
                      </p>
                    </div>
                  );
                })()}
                
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 sm:p-6 border border-blue-200">
                  <h4 className="font-medium text-blue-900 mb-3 sm:mb-4 text-sm sm:text-base">Current Status: {getPhaseLabel(election.status)}</h4>
                  
                  <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3">
                    {getActionButtons()}
                    
                    {/* Return to Draft Button */}
                    {election.status !== 'draft' && election.status !== 'published' && (
                      <button
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Return to Draft button clicked - election.id:', election.id);
                          if (window.confirm('Return this election to draft status? This will stop all current activities.')) {
                            try {
                              setSaving(true);
                              console.log('Calling /api/vote/election/' + election.id + '/set-draft');
                              await api.post(`/api/vote/election/${election.id}/set-draft`);
                              alert('Election returned to draft status');
                              onRefresh();
                              onClose();
                            } catch (error) {
                              console.error('Error returning to draft:', error);
                              alert('Failed to return to draft: ' + (error.response?.data?.detail || error.message));
                            } finally {
                              setSaving(false);
                            }
                          }
                        }}
                        disabled={saving}
                        type="button"
                        className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm sm:text-base bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors cursor-pointer"
                      >
                        ↩️ Return to Draft
                      </button>
                    )}

                    {/* Adjourn Meeting Button - Only show when voting is active */}
                    {election.status === 'voting' && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setExtensionType('adjourn');
                          setShowMeetingExtensionModal(true);
                        }}
                        disabled={saving}
                        type="button"
                        className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
                      >
                        ⏸️ Adjourn Meeting
                      </button>
                    )}

                    {/* Postpone Meeting Button - Only show when voting is active */}
                    {election.status === 'voting' && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setExtensionType('postpone');
                          setShowMeetingExtensionModal(true);
                        }}
                        disabled={saving}
                        type="button"
                        className="w-full sm:w-auto px-3 sm:px-4 py-2 text-sm sm:text-base bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors cursor-pointer"
                      >
                        📅 Postpone Meeting
                      </button>
                    )}
                  </div>

                  <div className="mt-4 sm:mt-6 text-sm text-blue-800">
                    <h5 className="font-medium mb-2 text-xs sm:text-sm">📋 Available Actions:</h5>
                    <ul className="space-y-1 text-xs">
                      {(() => {
                        const hasProxies = election.positions_up_for_election?.some(p => 
                          p.proxy_settings?.allow_general || p.proxy_settings?.allow_limited
                        ) || election.special_topics?.some(t => 
                          t.proxy_settings?.allow_general || t.proxy_settings?.allow_limited
                        );
                        
                        return (
                          <>
                            {hasProxies && <li>• <strong>Start Collecting Proxies:</strong> Publish election and allow proxy assignments</li>}
                            {!election.not_member_election && (
                              <>
                                <li>• <strong>Start Nominations:</strong> Open nomination period for candidates</li>
                                <li>• <strong>End Nominations:</strong> Close nominations and prepare for voting</li>
                              </>
                            )}
                            <li>• <strong>Start Voting:</strong> Begin the voting period (closes proxy submissions)</li>
                            <li>• <strong>End Voting:</strong> Close polls and finalize results</li>
                            <li>• <strong>Archive:</strong> Move completed election to archive</li>
                          </>
                        );
                      })()}
                    </ul>
                  </div>
                </div>

                {/* Quick Testing Controls */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
                  <h4 className="font-medium text-yellow-900 mb-2 sm:mb-3 text-sm sm:text-base">🧪 Testing Controls</h4>
                  <p className="text-xs sm:text-sm text-yellow-800 mb-2 sm:mb-3">
                    Use these controls to quickly progress through election stages for testing purposes.
                  </p>
                  <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('Delete button clicked - election.id:', election.id);
                        handleStageProgression('delete');
                      }}
                      className="w-full sm:w-auto px-3 py-2 bg-gray-600 text-white text-xs sm:text-sm rounded hover:bg-gray-700 transition-colors cursor-pointer"
                      disabled={saving}
                      type="button"
                    >
                      🗑️ Delete Election
                    </button>
                  </div>
                </div>

                {/* Poll Volunteer Management */}
                <PollVolunteerManagement election={election} hoaTimezone={hoaTimezone} />
              </div>
            )}

            {/* Nominees Tab - Duplicate Management */}
            {activeTab === 'nominees' && (
              <NomineesManagementTab 
                election={election}
                saving={saving}
                setSaving={setSaving}
                hoaTimezone={hoaTimezone}
                hoaSettings={hoaSettings}
              />
            )}

            {activeTab === 'voting_settings' && (
              <div className="h-full flex flex-col">
                {/* Header Section */}
                <div className="flex-shrink-0 pb-4 mb-4 border-b border-gray-200">
                  <h3 className="text-xl font-semibold">🗳️ Voting Settings</h3>
                  {!canEditVotingSettings() && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                      <p className="text-sm text-yellow-800">
                        ⚠️ Voting settings can only be edited during Draft, Proxy Collection, or Nominations phase.
                        Current status: <strong>{getPhaseLabel(election.status)}</strong>
                      </p>
                    </div>
                  )}
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto space-y-6 pr-2">
                  {/* Officer Positions */}
                  {!election.not_member_election && editablePositions.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <h4 className="text-lg font-medium text-gray-900 mb-4">👔 Officer Positions</h4>
                      <div className="space-y-4">
                        {editablePositions.map((position, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-gray-900">{position.label}</h5>
                              <span className="text-sm text-gray-600">{position.count} seat(s)</span>
                            </div>
                          
                          {/* Proxy Settings */}
                          <div className={`border rounded p-3 ${editingVotingSettings ? 'bg-white border-blue-300' : 'bg-blue-50 border-blue-200'}`}>
                            <h6 className="text-xs font-semibold text-blue-900 mb-3">🗳️ Proxy Settings:</h6>
                            <div className="grid grid-cols-2 gap-3">
                              <label className="flex items-center" title={position.proxy_settings?.purpose === 'quorum_only' ? 'Limited/General proxies only available for Voting & Quorum' : ''}>
                                <input
                                  type="checkbox"
                                  checked={position.proxy_settings?.allow_general || false}
                                  onChange={(e) => editingVotingSettings && updatePositionProxySettings(index, 'allow_general', e.target.checked)}
                                  disabled={!editingVotingSettings || position.proxy_settings?.purpose === 'quorum_only'}
                                  className="mr-2"
                                />
                                <span className={`text-xs ${position.proxy_settings?.purpose === 'quorum_only' ? 'text-gray-400' : ''}`}>Allow General Proxy</span>
                              </label>
                              <label className="flex items-center" title={position.proxy_settings?.purpose === 'quorum_only' ? 'Limited/General proxies only available for Voting & Quorum' : ''}>
                                <input
                                  type="checkbox"
                                  checked={position.proxy_settings?.allow_limited || false}
                                  onChange={(e) => editingVotingSettings && updatePositionProxySettings(index, 'allow_limited', e.target.checked)}
                                  disabled={!editingVotingSettings || position.proxy_settings?.purpose === 'quorum_only'}
                                  className="mr-2"
                                />
                                <span className={`text-xs ${position.proxy_settings?.purpose === 'quorum_only' ? 'text-gray-400' : ''}`}>Allow Limited Proxy</span>
                              </label>
                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={position.proxy_settings?.board_receiver || false}
                                  onChange={(e) => editingVotingSettings && updatePositionProxySettings(index, 'board_receiver', e.target.checked)}
                                  disabled={!editingVotingSettings}
                                  className="mr-2"
                                />
                                <span className="text-xs">Board Can Receive</span>
                              </label>
                              <label className="flex items-center">
                                <span className="text-xs mr-2">Purpose:</span>
                                <select
                                  value={position.proxy_settings?.purpose || 'voting_quorum'}
                                  onChange={(e) => {
                                    if (editingVotingSettings) {
                                      // If switching to quorum_only, uncheck limited/general
                                      if (e.target.value === 'quorum_only') {
                                        updatePositionProxySettings(index, 'allow_general', false);
                                        updatePositionProxySettings(index, 'allow_limited', false);
                                      }
                                      updatePositionProxySettings(index, 'purpose', e.target.value);
                                    }
                                  }}
                                  disabled={!editingVotingSettings}
                                  className="text-xs border rounded px-2 py-1"
                                >
                                  <option value="quorum_only">Quorum Only</option>
                                  <option value="voting_quorum">Voting & Quorum</option>
                                </select>
                              </label>
                            </div>
                            {position.proxy_settings?.purpose === 'quorum_only' && (
                              <p className="text-xs text-gray-500 mt-2 italic">
                                ⚠️ Quorum Only: Proxies can only be used to establish meeting quorum, not for voting.
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Special Topics/Motions */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-medium text-gray-900">📋 Special Topics/Motions</h4>
                    {editingVotingSettings && (
                      <button
                        onClick={addNewTopic}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                      >
                        ➕ Add Motion
                      </button>
                    )}
                  </div>
                  
                  {editableTopics.length > 0 ? (
                    <div className="space-y-4">
                      {editableTopics.map((topic, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center justify-between mb-3">
                            {editingVotingSettings ? (
                              <input
                                type="text"
                                value={topic.name}
                                onChange={(e) => updateTopic(index, 'name', e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-medium"
                                placeholder="Motion name"
                              />
                            ) : (
                              <h5 className="font-medium text-gray-900">{topic.name}</h5>
                            )}
                            {editingVotingSettings && (
                              <button
                                onClick={() => removeTopic(index)}
                                className="ml-2 px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                              >
                                🗑️ Remove
                              </button>
                            )}
                          </div>
                          
                          {/* Answers */}
                          <div className="mb-3">
                            <span className="text-xs font-medium text-gray-700">Vote Options:</span>
                            {editingVotingSettings ? (
                              <input
                                type="text"
                                value={topic.answers?.join(', ') || ''}
                                onChange={(e) => updateTopic(index, 'answers', e.target.value.split(',').map(a => a.trim()))}
                                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                placeholder="Yes, No, Abstain"
                              />
                            ) : (
                              <div className="flex flex-wrap gap-2 mt-1">
                                {topic.answers?.map((answer, i) => (
                                  <span key={i} className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                    {answer}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Proxy Settings */}
                          {topic.proxy_settings && (
                            <div className={`rounded p-3 ${editingVotingSettings ? 'bg-white border border-blue-300' : 'bg-blue-50 border border-blue-200'}`}>
                              <h6 className="text-xs font-semibold text-blue-900 mb-3">🗳️ Proxy Settings:</h6>
                              <div className="grid grid-cols-2 gap-3">
                                <label className="flex items-center" title={topic.proxy_settings.purpose === 'quorum_only' ? 'Limited/General proxies only available for Voting & Quorum' : ''}>
                                  <input
                                    type="checkbox"
                                    checked={topic.proxy_settings.allow_general || false}
                                    onChange={(e) => editingVotingSettings && updateTopic(index, 'proxy_settings.allow_general', e.target.checked)}
                                    disabled={!editingVotingSettings || topic.proxy_settings.purpose === 'quorum_only'}
                                    className="mr-2"
                                  />
                                  <span className={`text-xs ${topic.proxy_settings.purpose === 'quorum_only' ? 'text-gray-400' : ''}`}>Allow General Proxy</span>
                                </label>
                                <label className="flex items-center" title={topic.proxy_settings.purpose === 'quorum_only' ? 'Limited/General proxies only available for Voting & Quorum' : ''}>
                                  <input
                                    type="checkbox"
                                    checked={topic.proxy_settings.allow_limited || false}
                                    onChange={(e) => editingVotingSettings && updateTopic(index, 'proxy_settings.allow_limited', e.target.checked)}
                                    disabled={!editingVotingSettings || topic.proxy_settings.purpose === 'quorum_only'}
                                    className="mr-2"
                                  />
                                  <span className={`text-xs ${topic.proxy_settings.purpose === 'quorum_only' ? 'text-gray-400' : ''}`}>Allow Limited Proxy</span>
                                </label>
                                <label className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={topic.proxy_settings.board_receiver || false}
                                    onChange={(e) => editingVotingSettings && updateTopic(index, 'proxy_settings.board_receiver', e.target.checked)}
                                    disabled={!editingVotingSettings}
                                    className="mr-2"
                                  />
                                  <span className="text-xs">Board Can Receive</span>
                                </label>
                                <label className="flex items-center">
                                  <span className="text-xs mr-2">Purpose:</span>
                                  <select
                                    value={topic.proxy_settings.purpose || 'voting_quorum'}
                                    onChange={(e) => {
                                      if (editingVotingSettings) {
                                        // If switching to quorum_only, uncheck limited/general
                                        if (e.target.value === 'quorum_only') {
                                          updateTopic(index, 'proxy_settings.allow_general', false);
                                          updateTopic(index, 'proxy_settings.allow_limited', false);
                                        }
                                        updateTopic(index, 'proxy_settings.purpose', e.target.value);
                                      }
                                    }}
                                    disabled={!editingVotingSettings}
                                    className="text-xs border rounded px-2 py-1"
                                  >
                                    <option value="quorum_only">Quorum Only</option>
                                    <option value="voting_quorum">Voting & Quorum</option>
                                  </select>
                                </label>
                              </div>
                              {topic.proxy_settings.purpose === 'quorum_only' && (
                                <p className="text-xs text-gray-500 mt-2 italic">
                                  ⚠️ Quorum Only: Proxies can only be used to establish meeting quorum, not for voting.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      {editingVotingSettings ? (
                        <p>Click "Add Motion" to create a new voting topic</p>
                      ) : (
                        <p>No motions configured for this election.</p>
                      )}
                    </div>
                  )}
                </div>
                </div>
              </div>
            )}


            {activeTab === 'eligible_voters' && (
              <EligibleVotersTab electionId={election.id} />
            )}

            {activeTab === 'statistics' && (
              <LiveStatisticsTab election={election} onRefresh={onRefresh} />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Close
          </button>
          
          {/* Voting Settings Edit Mode Buttons */}
          {activeTab === 'voting_settings' && !editingVotingSettings && canEditVotingSettings() && (
            <button
              onClick={() => setEditingVotingSettings(true)}
              className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              ✏️ Edit Voting Settings
            </button>
          )}

          {activeTab === 'voting_settings' && editingVotingSettings && (
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setEditablePositions(election.positions_up_for_election || []);
                  setEditableTopics(election.special_topics || []);
                  setEditingVotingSettings(false);
                }}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                ❌ Cancel
              </button>
              <button
                onClick={saveVotingSettings}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {saving ? 'Saving...' : '💾 Save Voting Settings'}
              </button>
            </div>
          )}

          {/* Default Save Button for Other Tabs */}
          {activeTab !== 'voting_settings' && (
            <button
              onClick={saveElectionSettings}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
            >
              {saving ? 'Saving...' : '💾 Save All Changes'}
            </button>
          )}
        </div>
      </div>

      {/* Meeting Extension Modal */}
      {showMeetingExtensionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">
              {extensionType === 'adjourn' ? '⏸️ Adjourn Meeting' : '📅 Postpone Meeting'}
            </h3>
            
            <div className={`mb-4 p-4 rounded-lg ${extensionType === 'adjourn' ? 'bg-blue-50 border border-blue-200' : 'bg-yellow-50 border border-yellow-200'}`}>
              <p className="text-sm">
                {extensionType === 'adjourn' ? (
                  <>
                    <strong>Adjourn:</strong> Extends the meeting to a new date/time. 
                    <span className="text-green-700 font-medium"> All proxies (limited & general) remain valid.</span>
                  </>
                ) : (
                  <>
                    <strong>Postpone:</strong> Reschedules to a new meeting. 
                    <span className="text-red-700 font-medium"> Limited proxies become invalid. General proxies within 90-day window remain valid.</span>
                  </>
                )}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Meeting Date & Time
              </label>
              <input
                type="datetime-local"
                value={newMeetingTime}
                onChange={(e) => setNewMeetingTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowMeetingExtensionModal(false);
                  setNewMeetingTime('');
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleMeetingExtension}
                disabled={saving || !newMeetingTime}
                className={`px-4 py-2 text-white rounded-lg ${
                  extensionType === 'adjourn' 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-purple-600 hover:bg-purple-700'
                } disabled:bg-gray-400`}
              >
                {saving ? 'Processing...' : `Confirm ${extensionType === 'adjourn' ? 'Adjourn' : 'Postpone'}`}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}

// Helper functions
function getStatusColor(status) {
  const colors = {
    'draft': 'text-gray-600',
    'proxy_collection': 'text-green-600',
    'nominating': 'text-blue-600',
    'queued': 'text-yellow-600',
    'voting': 'text-green-600',
    'closed': 'text-purple-600',
    'archived': 'text-gray-600'
  };
  return colors[status] || 'text-gray-600';
}

function getStatusLabel(status) {
  const labels = {
    'draft': 'Draft',
    'proxy_collection': 'Collecting Proxies',
    'nominating': 'Nominations Open',
    'queued': 'Pending Vote',
    'voting': 'Voting Active',
    'closed': 'Completed',
    'archived': 'Archived',
    'completed': 'Completed',
    'finalized': 'Finalized'
  };
  return labels[status] || status || 'Unknown';
}

function getPhaseColor(phase) {
  const colors = {
    no_election: 'bg-gray-100 text-gray-800',
    draft: 'bg-gray-100 text-gray-800',
    proxy_collection: 'bg-green-100 text-green-800',
    nominating: 'bg-blue-100 text-blue-800',
    queued: 'bg-yellow-100 text-yellow-800',
    voting: 'bg-green-100 text-green-800',
    closed: 'bg-purple-100 text-purple-800'
  };
  return colors[phase] || 'bg-gray-100 text-gray-800';
}

function getPhaseLabel(phase) {
  const labels = {
    no_election: 'No Election',
    draft: 'Draft',
    proxy_collection: 'Collecting Proxies',
    nominating: 'Nominations Open',
    queued: 'Pending Vote',
    voting: 'Voting Active',
    closed: 'Completed',
    archived: 'Archived',
    completed: 'Completed',
    finalized: 'Finalized'
  };
  return labels[phase] || phase || 'Unknown';
}

// Board Terms Manager Component
function BoardTermsManager({ boardMembers, onRefresh, onClose }) {
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [termData, setTermData] = useState({
    term_start: '',
    term_end: ''
  });

  const startEditingTerm = (member) => {
    setEditingMemberId(member.user_id);
    setTermData({
      term_start: member.term_start ? new Date(member.term_start).toISOString().slice(0, 10) : '',
      term_end: member.term_end ? new Date(member.term_end).toISOString().slice(0, 10) : ''
    });
  };

  const saveTerm = async (userId) => {
    try {
      await api.post('/api/vote/board-terms', {
        user_id: userId,
        term_start: new Date(termData.term_start),
        term_end: new Date(termData.term_end)
      });
      
      alert('Term dates updated successfully!');
      setEditingMemberId(null);
      onRefresh();
    } catch (error) {
      console.error('Error updating term:', error);
      alert('Failed to update term dates. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-4xl bg-white rounded-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">Manage Board Member Terms</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">✕</button>
        </div>

        <div className="space-y-4">
          {boardMembers.map((member) => (
            <div key={member.user_id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-gray-900">{member.name}</h4>
                  <p className="text-sm text-indigo-600">{member.position_title}</p>
                  <p className="text-xs text-gray-500">{member.email}</p>
                </div>
                
                {editingMemberId === member.user_id ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => saveTerm(member.user_id)}
                      className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingMemberId(null)}
                      className="px-3 py-1 bg-gray-400 text-white text-sm rounded hover:bg-gray-500"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => startEditingTerm(member)}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    Edit Term
                  </button>
                )}
              </div>

              {editingMemberId === member.user_id ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Term Start</label>
                    <input
                      type="date"
                      value={termData.term_start}
                      onChange={(e) => setTermData(prev => ({ ...prev, term_start: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Term End</label>
                    <input
                      type="date"
                      value={termData.term_end}
                      onChange={(e) => setTermData(prev => ({ ...prev, term_end: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Term Start:</span>
                    <span className="ml-2 text-gray-900">
                      {member.term_start ? formatLocal(member.term_start, hoaTimezone, 'MMM d, yyyy') : 'Not set'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Term End:</span>
                    <span className="ml-2 text-gray-900">
                      {member.term_end ? formatLocal(member.term_end, hoaTimezone, 'MMM d, yyyy') : 'Not set'}
                    </span>
                    {member.is_expiring_soon && (
                      <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                        Expiring Soon
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Roll Call Modal
function RollCallModal({ boardMembers, attendance, onClose, onRefresh, meetingType = "BOD", teamMembers = [] }) {
  const [rollCallData, setRollCallData] = useState({});
  const [additionalHomeowners, setAdditionalHomeowners] = useState([]);
  const [newHomeowner, setNewHomeowner] = useState({ name: '', email: '', property: '' });
  const [saving, setSaving] = useState(false);
  const [hoaMembers, setHoaMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Load HOA members for dropdown
  useEffect(() => {
    const loadHoaMembers = async () => {
      try {
        const response = await api.get('/api/hoa/members');
        const members = response.data?.members || [];
        
        // AGGRESSIVELY filter out phantom users that shouldn't exist
        const validMembers = members.filter(member => {
          // BLOCK phantom users by name (case insensitive)
          const memberName = (member.name || '').toLowerCase();
          const firstName = (member.first_name || '').toLowerCase();
          const lastName = (member.last_name || '').toLowerCase();
          
          if (memberName.includes('john homeowner') || 
              memberName.includes('jane resident') ||
              (firstName === 'john' && lastName === 'homeowner') ||
              (firstName === 'jane' && lastName === 'resident')) {
            console.log('🚫 BLOCKING PHANTOM USER:', member.name);
            return false;
          }
          
          // BLOCK any user with test/example emails
          const email = (member.email || '').toLowerCase();
          if (email.includes('test.com') || 
              email.includes('example.com') || 
              email.includes('fake') ||
              email.includes('dummy')) {
            console.log('🚫 BLOCKING TEST USER:', member.email);
            return false;
          }
          
          // Only include verified members with proper data
          if (!member.name || !member.email || member.verification_status !== 'approved') {
            return false;
          }
          
          return true;
        });
        
        console.log('✅ Valid HOA members loaded:', validMembers.length);
        console.log('✅ Valid members:', validMembers.map(m => ({ name: m.name, email: m.email, role: m.role })));
        
        setHoaMembers(validMembers);
      } catch (error) {
        console.error('Error loading HOA members:', error);
        setHoaMembers([]);
      }
    };
    loadHoaMembers();
  }, []);

  // Initialize roll call data with current attendance - load existing attendance records
  useEffect(() => {
    const loadExistingAttendance = async () => {
      const initialData = {};
      const restoredHomeowners = [];
      
      // Add all board members
      boardMembers.forEach(member => {
        initialData[member.id] = {
          id: member.id,
          name: member.name,
          email: member.email || '',
          role: member.role,
          type: 'board_member',
          present: attendance[member.id] === true
        };
      });
      
      // CRITICAL: Load existing attendance records from database to preserve previously added homeowners/external
      try {
        const response = await api.get(`/api/vote/attendance/all-records?meeting_type=${meetingType}`);
        const existingAttendance = response.data?.attendance_records || [];
        
        console.log(`📋 Loading existing attendance for ${meetingType}:`, existingAttendance.length, 'records');
        
        // Add homeowners and external attendees that were previously added
        existingAttendance.forEach(record => {
          if (record.type === 'homeowner' || record.type === 'external' || record.type === 'team_member') {
            const recordId = record.id || record.user_id || `${record.type}_${Date.now()}_${Math.random()}`;
            
            const restoredPerson = {
              id: recordId,
              name: record.name,
              email: record.email || '',
              property: record.property_identifier || record.unit || record.lot || '',
              type: record.type,
              role: record.additional_name || record.role || (record.type === 'external' ? 'External Attendee' : ''),
              present: record.present === true || record.attendance_status === true
            };
            
            // Only add if not already in initialData (avoid duplicates)
            if (!initialData[recordId]) {
              initialData[recordId] = restoredPerson;
              restoredHomeowners.push(restoredPerson);
              console.log(`  ✅ Restored ${record.type}: ${record.name} (present: ${restoredPerson.present})`);
            }
          }
        });
      } catch (error) {
        console.error('Error loading existing attendance:', error);
      }
      
      console.log('📊 Final rollCallData being set:', Object.keys(initialData).length, 'total entries');
      console.log('   - Board members:', Object.values(initialData).filter(p => p.type === 'board_member').length);
      console.log('   - Homeowners:', Object.values(initialData).filter(p => p.type === 'homeowner').length);
      console.log('   - External:', Object.values(initialData).filter(p => p.type === 'external').length);
      console.log('📊 Restored homeowners array:', restoredHomeowners);
      
      setRollCallData(initialData);
      setAdditionalHomeowners(restoredHomeowners);
    };
    
    loadExistingAttendance();
  }, [boardMembers, attendance, meetingType]);

  const togglePresence = (personId) => {
    setRollCallData(prev => ({
      ...prev,
      [personId]: {
        ...prev[personId],
        present: !prev[personId]?.present
      }
    }));
  };

  const markAllBoardPresent = () => {
    setRollCallData(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(id => {
        if (updated[id].type === 'board_member') {
          updated[id].present = true;
        }
      });
      return updated;
    });
  };

  // Filter available homeowners for dropdown (exclude already added and phantom users)
  const availableHomeowners = hoaMembers.filter(member => {
    // Exclude board members (they're handled separately)
    if (['board_member', 'president', 'vice_president', 'treasurer', 'secretary'].includes(member.role)) {
      return false;
    }
    
    // Exclude phantom users explicitly
    if (member.name === 'John Homeowner' || member.name === 'Jane Resident') {
      console.log('Filtering out phantom user:', member.name);
      return false;
    }
    
    // Skip members without proper name data
    if (!member.name || member.name.trim() === '' || member.name === 'NO_NAME') {
      return false;
    }
    
    // Exclude if already a board member
    if (boardMembers.some(boardMember => boardMember.id === member.id)) {
      return false;
    }
    
    // Exclude if already added to roll call
    if (Object.values(rollCallData).some(person => person.email === member.email && person.type === 'homeowner')) {
      return false;
    }
    
    // Only include verified members
    if (!member.verification_status || member.verification_status !== 'approved') {
      return false;
    }
    
    // Include if search term matches name (case insensitive)
    if (searchTerm.trim() === '') {
      return true; // Show all when no search term
    }
    
    return member.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Filter available team members for dropdown (attorneys, accountants, staff, etc.)
  const availableTeamMembers = teamMembers.filter(member => {
    // Skip members without proper name data
    if (!member.name || member.name.trim() === '') {
      return false;
    }
    
    // Exclude if already added to roll call
    if (Object.values(rollCallData).some(person => person.email === member.email && person.type === 'team_member')) {
      return false;
    }
    
    // Only include active team members
    if (!member.is_active) {
      return false;
    }
    
    // Include if search term matches name (case insensitive)
    if (searchTerm.trim() === '') {
      return true; // Show all when no search term
    }
    
    return member.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Combine homeowners and team members for unified dropdown
  const allAvailableMembers = [
    ...availableHomeowners.map(member => ({ ...member, memberType: 'homeowner' })),
    ...availableTeamMembers.map(member => ({ ...member, memberType: 'team_member' }))
  ];

  // Debug logging for troubleshooting
  console.log('Roll Call Debug Info:');
  console.log('Total HOA Members loaded:', hoaMembers.length);
  console.log('Total Team Members loaded:', teamMembers.length);
  console.log('Available homeowners after filtering:', availableHomeowners.length);
  console.log('Available team members after filtering:', availableTeamMembers.length);
  console.log('Search term:', searchTerm);
  console.log('Sample available homeowners:', availableHomeowners.slice(0, 3).map(m => ({ 
    id: m.id, 
    name: m.name, 
    email: m.email,
    property_address: m.property_address,
    role: m.role,
    verification_status: m.verification_status
  })));

  const selectHomeownerFromDropdown = (member) => {
    let id, type;
    
    // Determine member type and generate appropriate ID
    if (member.memberType === 'team_member') {
      id = `team_member_${Date.now()}`;
      type = 'team_member';
    } else {
      id = `homeowner_${Date.now()}`;
      type = 'homeowner';
    }
    
    setRollCallData(prev => ({
      ...prev,
      [id]: {
        id,
        name: member.name,
        email: member.email,
        property: member.property_address || '',
        type: type,
        role: member.role || undefined, // For team members
        present: true
      }
    }));
    
    setSearchTerm('');
    setShowDropdown(false);
  };

  const addHomeowner = () => {
    if (!newHomeowner.name.trim()) return;
    
    const id = newHomeowner.isExternal ? `external_${Date.now()}` : `homeowner_${Date.now()}`;
    const type = newHomeowner.isExternal ? 'external' : 'homeowner';
    
    setRollCallData(prev => ({
      ...prev,
      [id]: {
        id,
        name: newHomeowner.name,
        email: newHomeowner.email || '',
        property: newHomeowner.property || '',
        type: type,
        externalRole: newHomeowner.isExternal ? newHomeowner.externalRole : undefined,
        present: true
      }
    }));
    
    setNewHomeowner({ name: '', email: '', property: '', isExternal: false, externalRole: '' });
  };

  const removeHomeowner = (id) => {
    setRollCallData(prev => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      // Submit attendance for all people
      const attendanceUpdates = Object.values(rollCallData).map(person => ({
        user_id: person.id,
        name: person.name,
        email: person.email,
        role: person.role || 'homeowner',
        property: person.property || '',
        present: person.present,
        type: person.type,
        externalRole: person.externalRole || undefined
      }));

      console.log('Submitting roll call data:', attendanceUpdates);

      await api.post('/api/vote/attendance/roll-call', {
        attendance_records: attendanceUpdates,
        meeting_type: meetingType  // FIXED: Per-tab roll call
      });

      alert(`Roll call completed! ${attendanceUpdates.filter(a => a.present).length} people marked present.`);
      
      // Close modal first
      onClose();
      
      // Force refresh with a longer delay to ensure backend processing is complete
      setTimeout(async () => {
        await onRefresh();
        console.log('Roll call attendance data refreshed');
        
        // Force a re-render to ensure UI updates
        window.dispatchEvent(new CustomEvent('attendanceUpdated'));
      }, 1500); // Increased delay to 1.5 seconds
      
    } catch (error) {
      console.error('Error submitting roll call:', error);
      alert('Failed to submit roll call. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const clearAllAttendanceData = async () => {
    if (!confirm('🗑️ COMPLETELY DELETE ALL attendance records?\n\nThis will fix the count showing 21 people by clearing all duplicate data.\nYou will need to redo roll call after this.\n\nContinue?')) {
      return;
    }
    
    try {
      const response = await api.delete('/api/vote/attendance/cleanup-all');
      
      if (response.data?.success) {
        alert(`✅ ATTENDANCE CLEANUP COMPLETE!\n\n🗑️ Deleted ${response.data.deleted_count} duplicate records\n\n💡 Roll call has been reset to 0. Please conduct a new roll call.`);
        
        // Reset local state
        setRollCallData({});
        
        // Close modal and refresh parent
        onClose();
        await onRefresh();
      }
    } catch (error) {
      console.error('Attendance cleanup error:', error);
      alert(`❌ Cleanup failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const presentCount = Object.values(rollCallData).filter(person => person.present).length;
  const boardPresentCount = Object.values(rollCallData).filter(person => person.type === 'board_member' && person.present).length;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-3xl bg-white rounded-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-xl text-gray-900">Meeting Roll Call</h3>
              <p className="text-sm text-gray-600 mt-1">Mark attendance for all meeting participants</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={clearAllAttendanceData}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                title="Fix count showing 21 people - delete all duplicate records"
              >
                🗑️ Fix Count
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="mt-4 grid grid-cols-3 gap-4 text-center">
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="text-xl font-bold text-blue-600">{boardPresentCount}</div>
              <div className="text-xs text-blue-700">Board Present</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3">
              <div className="text-xl font-bold text-green-600">{presentCount}</div>
              <div className="text-xs text-green-700">Total Present</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3">
              <div className="text-xl font-bold text-purple-600">{Object.keys(rollCallData).length}</div>
              <div className="text-xs text-purple-700">Total People</div>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Board Members Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-900">Board Members</h4>
              <button
                onClick={markAllBoardPresent}
                className="px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
              >
                Mark All Present
              </button>
            </div>
            
            <div className="space-y-2">
              {Object.values(rollCallData)
                .filter(person => person.type === 'board_member')
                .map(member => (
                  <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${member.present ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <div>
                        <div className="font-medium text-gray-900">{member.name}</div>
                        <div className="text-sm text-gray-600 capitalize">{member.role?.replace('_', ' ')}</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => togglePresence(member.id)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                          member.present
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {member.present ? '✅ Present' : '❌ Absent'}
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Additional Homeowners Section */}
          <div>
            <h4 className="font-semibold text-gray-900 mb-4">Additional Homeowners</h4>
            
            {/* Add Homeowner Form with Searchable Dropdown */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h5 className="font-medium text-blue-900 mb-3">Add Homeowner or Team Member to Meeting</h5>
              
              {/* Search existing homeowners and team members */}
              <div className="mb-4 relative">
                <input
                  type="text"
                  placeholder="Search for homeowners and team members..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowDropdown(e.target.value.length > 0);
                  }}
                  onFocus={() => setShowDropdown(searchTerm.length > 0)}
                  className="w-full px-3 py-2 border border-blue-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                
                {/* Dropdown */}
                {showDropdown && allAvailableMembers.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-blue-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {allAvailableMembers.slice(0, 15).map((member, index) => (
                      <button
                        key={`${member.memberType}-${member.id || index}`}
                        onClick={() => selectHomeownerFromDropdown(member)}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center justify-between"
                      >
                        <div>
                          <div className="font-medium text-gray-900 flex items-center gap-2">
                            {member.name}
                            {member.memberType === 'team_member' && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                                {member.role === 'attorney' && '⚖️ Attorney'}
                                {member.role === 'accountant' && '💼 Accountant'}
                                {member.role === 'staff' && '👔 Staff'}
                                {member.role === 'other' && '👤 Team'}
                                {!member.role && '👥 Team Member'}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600">
                            {member.memberType === 'team_member' 
                              ? member.email || 'No email' 
                              : (member.property_identifier || member.unit || member.lot || member.property_number || 'No identifier')
                            }
                          </div>
                        </div>
                        <div className="text-xs text-blue-600">Add to Roll Call</div>
                      </button>
                    ))}
                  </div>
                )}
                
                {showDropdown && searchTerm && allAvailableMembers.length === 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-blue-300 rounded-lg shadow-lg p-3 text-center text-gray-500">
                    No homeowners or team members found matching "{searchTerm}"
                  </div>
                )}
              </div>
              
              {/* Manual entry fallback */}
              <details className="cursor-pointer">
                <summary className="text-sm text-blue-700 hover:text-blue-800 mb-3">Or add manually (Homeowner or External)</summary>
                <div className="space-y-3">
                  {/* Toggle for External Attendee */}
                  <div className="flex items-center gap-2 bg-blue-50 p-2 rounded-lg">
                    <input
                      type="checkbox"
                      id="isExternalAttendee"
                      checked={newHomeowner.isExternal || false}
                      onChange={(e) => setNewHomeowner(prev => ({ 
                        ...prev, 
                        isExternal: e.target.checked,
                        property: e.target.checked ? '' : prev.property,
                        externalRole: e.target.checked ? (prev.externalRole || 'attorney') : ''
                      }))}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <label htmlFor="isExternalAttendee" className="text-sm font-medium text-gray-700">
                      External Attendee (Attorney, Accountant, Staff, etc.)
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input
                      type="text"
                      placeholder="Full name"
                      value={newHomeowner.name}
                      onChange={(e) => setNewHomeowner(prev => ({ ...prev, name: e.target.value }))}
                      className="px-3 py-2 border border-blue-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    
                    {newHomeowner.isExternal ? (
                      <select
                        value={newHomeowner.externalRole || 'attorney'}
                        onChange={(e) => setNewHomeowner(prev => ({ ...prev, externalRole: e.target.value }))}
                        className="px-3 py-2 border border-blue-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="attorney">⚖️ Attorney</option>
                        <option value="accountant">💼 Accountant</option>
                        <option value="staff">👔 Staff</option>
                        <option value="maintenance">🔧 Maintenance</option>
                        <option value="security">🛡️ Security</option>
                        <option value="other">👤 Other</option>
                      </select>
                    ) : (
                      <input
                        type="email"
                        placeholder="Email (optional)"
                        value={newHomeowner.email}
                        onChange={(e) => setNewHomeowner(prev => ({ ...prev, email: e.target.value }))}
                        className="px-3 py-2 border border-blue-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    )}
                    
                    <div className="flex gap-2">
                      {!newHomeowner.isExternal && (
                        <input
                          type="text"
                          placeholder="Property address"
                          value={newHomeowner.property}
                          onChange={(e) => setNewHomeowner(prev => ({ ...prev, property: e.target.value }))}
                          className="flex-1 px-3 py-2 border border-blue-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      )}
                      <button
                        onClick={addHomeowner}
                        disabled={!newHomeowner.name.trim()}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors whitespace-nowrap"
                      >
                        {newHomeowner.isExternal ? 'Add External' : 'Add Homeowner'}
                      </button>
                    </div>
                  </div>
                </div>
              </details>
            </div>

            {/* Homeowners List */}
            <div className="space-y-2">
              {Object.values(rollCallData)
                .filter(person => person.type === 'homeowner')
                .map(homeowner => (
                  <div key={homeowner.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${homeowner.present ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <div>
                        <div className="font-medium text-gray-900">{homeowner.name}</div>
                        <div className="text-sm text-gray-600">
                          {homeowner.property && `${homeowner.property} • `}Homeowner
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => togglePresence(homeowner.id)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                          homeowner.present
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {homeowner.present ? '✅ Present' : '❌ Absent'}
                      </button>
                      <button
                        onClick={() => removeHomeowner(homeowner.id)}
                        className="px-2 py-1 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove from roll call"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              
              {Object.values(rollCallData).filter(person => person.type === 'homeowner').length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  <div className="text-2xl mb-1">👥</div>
                  <p className="text-sm">No additional homeowners added</p>
                </div>
              )}
            </div>

            {/* External Attendees List */}
            {Object.values(rollCallData).filter(person => person.type === 'external').length > 0 && (
              <div className="mt-6 space-y-2">
                <h5 className="font-medium text-gray-700 mb-2">External Attendees</h5>
                {Object.values(rollCallData)
                  .filter(person => person.type === 'external')
                  .map(attendee => {
                    const roleIcons = {
                      attorney: '⚖️',
                      accountant: '💼',
                      staff: '👔',
                      maintenance: '🔧',
                      security: '🛡️',
                      other: '👤'
                    };
                    const roleLabels = {
                      attorney: 'Attorney',
                      accountant: 'Accountant',
                      staff: 'Staff',
                      maintenance: 'Maintenance',
                      security: 'Security',
                      other: 'Other'
                    };
                    return (
                      <div key={attendee.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${attendee.present ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                          <div>
                            <div className="font-medium text-gray-900">{attendee.name}</div>
                            <div className="text-sm text-gray-600">
                              {roleIcons[attendee.externalRole]} {roleLabels[attendee.externalRole] || 'External'}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => togglePresence(attendee.id)}
                            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                              attendee.present
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {attendee.present ? '✅ Present' : '❌ Absent'}
                          </button>
                          <button
                            onClick={() => removeHomeowner(attendee.id)}
                            className="px-2 py-1 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove from roll call"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-white border-t p-6">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 transition-colors"
            >
              {saving ? 'Saving...' : `Submit Roll Call (${presentCount} Present)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Other Discussions Section
function OtherDiscussionsSection({ boardMembers, onRefresh, refreshTrigger, meetingType = 'BOD', tabType = 'bod', hoaName = 'HOA', hoaTimezone = 'America/Chicago', discussionPropertyDisplay = 'address' }) {
  const [discussions, setDiscussions] = useState([]);
  const [showAddDiscussion, setShowAddDiscussion] = useState(false);
  const [hoaMembers, setHoaMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDiscussions = async () => {
    setLoading(true);
    try {
      // Filter discussions by meeting type to ensure tab-specific discussions
      const response = await api.get(`/api/vote/discussions?meeting_type=${meetingType}`);
      setDiscussions(response.data?.discussions || []);
      
      // Load HOA members for searchable dropdown
      const membersResponse = await api.get('/api/hoa/members');
      setHoaMembers(membersResponse.data?.members || []);
    } catch (error) {
      console.error('Error loading discussions:', error);
      setDiscussions([]);
      setHoaMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const exportDiscussions = () => {
    const now = new Date().toISOString();
    const currentDate = formatLocal(now, hoaTimezone, 'MMM d, yyyy');
    const currentTime = formatLocal(now, hoaTimezone, 'h:mm a z');
    
    let discussionReport = `OTHER DISCUSSIONS & MEMBER REQUESTS - ${meetingType} MEETING\n`;
    discussionReport += `Generated: ${currentDate} at ${currentTime}\n`;
    discussionReport += `HOA: ${hoaName}\n`;
    discussionReport += `Meeting Type: ${meetingType}\n\n`;
    
    if (discussions.length === 0) {
      discussionReport += `No discussions recorded for ${meetingType} meetings.\n`;
    } else {
      discussions.forEach((discussion, index) => {
        discussionReport += `DISCUSSION #${index + 1}\n`;
        discussionReport += `${'-'.repeat(50)}\n`;
        discussionReport += `Submitted by: ${discussion.member_name}\n`;
        if (discussion.property_address) {
          discussionReport += `Property: ${discussion.property_address}\n`;
        }
        if (discussion.ccr_rule || discussion.bylaw_rule) {
          const citations = [discussion.ccr_rule, discussion.bylaw_rule].filter(Boolean).join(' • ');
          discussionReport += `Rule cited: ${citations}\n`;
        }
        discussionReport += `Topic: ${discussion.topic}\n`;
        discussionReport += `Notes: ${discussion.notes || 'No notes recorded'}\n`;
        discussionReport += `Status: ${discussion.status?.toUpperCase() || 'PENDING'}\n`;
        discussionReport += `Meeting Type: ${discussion.meeting_type || meetingType}\n`;
        discussionReport += `Date: ${formatLocal(discussion.created_at, hoaTimezone, 'MMM d, yyyy')}\n\n`;
      });
    }
    
    // Download as text file
    const blob = new Blob([discussionReport], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `meeting-discussions-${meetingType.toLowerCase()}-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    loadDiscussions();
  }, [meetingType]); // Reload when meeting type changes

  // Respond to refresh trigger from clear session
  useEffect(() => {
    if (refreshTrigger > 0) {
      loadDiscussions();
    }
  }, [refreshTrigger]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading discussions...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-full">
            <div className="w-5 h-5 text-orange-600">💬</div>
          </div>
          <div>
            <h4 className="text-lg font-semibold text-gray-900">Other Discussions - {meetingType}</h4>
            <p className="text-sm text-gray-600">Member requests and additional topics for {meetingType} meetings</p>
          </div>
        </div>
        <div className="flex gap-2">
          {discussions.length > 0 && (
            <button
              onClick={exportDiscussions}
              className="px-3 py-1 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition-colors"
            >
              📄 Export Discussions
            </button>
          )}
          <button
            onClick={() => setShowAddDiscussion(true)}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Add Discussion
          </button>
        </div>
      </div>

      {discussions.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <div className="text-4xl mb-2">💬</div>
          <p>No discussions recorded. Add member requests or other topics.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {discussions.map((discussion) => (
            <div key={discussion.id} className="border border-gray-200 rounded-lg p-4 bg-orange-50">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h5 className="font-medium text-gray-900">{discussion.topic}</h5>
                  <div className="text-sm text-gray-600 mt-1">
                    <span className="font-medium">{discussion.member_name}</span>
                    {/* Show property based on HOA setting: address or lot/identifier */}
                    {(typeof discussionPropertyDisplay === 'string' && discussionPropertyDisplay !== 'address') ? (
                      (discussion.property_identifier || discussion.lot || discussion.unit) ? (
                        <span> • {discussion.property_identifier || discussion.lot || discussion.unit}</span>
                      ) : null
                    ) : (
                      discussion.property_address ? <span> • {discussion.property_address}</span> : null
                    )}
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  discussion.status === 'resolved' ? 'bg-green-100 text-green-800' :
                  discussion.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {discussion.status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                </span>
              </div>
              
              {/* Show CCR/Bylaw citations if present */}
              {(discussion.ccr_rule || discussion.bylaw_rule) && (
                <div className="bg-blue-50 rounded-lg p-3 mt-3 border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>Rule Citations:</strong>{' '}
                    {discussion.ccr_rule && discussion.bylaw_rule 
                      ? `${discussion.ccr_rule} • ${discussion.bylaw_rule}`
                      : discussion.ccr_rule || discussion.bylaw_rule
                    }
                  </p>
                </div>
              )}
              
              {discussion.notes && (
                <div className="bg-white rounded-lg p-3 mt-3">
                  <p className="text-sm text-gray-700">
                    <strong>Notes:</strong> {discussion.notes}
                  </p>
                </div>
              )}
              
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-orange-200">
                <span className="text-xs text-gray-500">
                  Added {formatLocal(discussion.created_at, hoaTimezone, 'MMM d, yyyy')}
                </span>
                <button
                  onClick={() => setShowAddDiscussion(discussion)}
                  className="text-xs text-orange-600 hover:text-orange-800"
                >
                  Edit Notes
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Discussion Modal */}
      {showAddDiscussion && (
        <AddDiscussionModal
          discussion={typeof showAddDiscussion === 'object' ? showAddDiscussion : null}
          hoaMembers={hoaMembers}
          boardMembers={boardMembers}
          onClose={() => setShowAddDiscussion(false)}
          onRefresh={() => {
            loadDiscussions();
            onRefresh();
          }}
          meetingType={meetingType}
        />
      )}
    </div>
  );
}

// Add Discussion Modal with Searchable Member Dropdown
function AddDiscussionModal({ discussion, hoaMembers, boardMembers, onClose, onRefresh, meetingType = 'BOD' }) {
  const { user } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    member_id: discussion?.member_id || '',
    member_name: discussion?.member_name || '',
    member_email: discussion?.member_email || '',
    property_address: discussion?.property_address || '',
    lot: discussion?.lot || discussion?.property_identifier || discussion?.unit || '',  // Add lot field
    topic: discussion?.topic || '',
    notes: discussion?.notes || '',
    status: discussion?.status || 'pending',
    meeting_type: discussion?.meeting_type || meetingType,
    ccr_rule: discussion?.ccr_rule || '',
    bylaw_rule: discussion?.bylaw_rule || ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // PHASE 3: Load voting settings for property display preference
  const [accessSettings, setAccessSettings] = useState({ discussion_property_display: 'property_identifier' });
  
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Load access settings for property display preference and committee permissions
        const accessResponse = await api.get(`/api/hoa/${user.hoa_id}/voting/settings`);
        if (accessResponse.data.success) {
          setAccessSettings(accessResponse.data.settings);
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    };
    
    if (user?.hoa_id) {
      loadSettings();
    }
  }, [user?.hoa_id]);
  
  // FIXED: Always allow board members access to both BOD and Committee tabs
  const canSearchMembers = useMemo(() => {
    const isBoard = ['board_member', 'president', 'vice_president', 'treasurer', 'secretary'].includes(user?.role?.toLowerCase());
    
    // Board members get access to both BOD and Committee tabs
    if (isBoard) {
      return true;
    }
    
    // For non-board members, check committee access only on Committee tab
    if (meetingType === 'COMMITTEE') {
      // Check if user is a committee member
      const isCommittee = user?.role === 'committee_member' || 
                          (user?.committees && user.committees.length > 0);
      
      if (isCommittee) {
        return true;  // Committee members can search
      }
      
      const committeeSettings = accessSettings?.live_voting_committee || {};
      const canViewCommittee = 
        committeeSettings.view_permissions === 'all_members' ||
        (committeeSettings.specific_users && committeeSettings.specific_users.includes(user?.id));
      
      return canViewCommittee;
    }
    
    // BOD tab: only board members (already handled above)
    return false;
  }, [user?.role, user?.id, user?.committees, meetingType, accessSettings]);

  // Combine board members and HOA members for search, with deduplication
  const allMembers = (() => {
    const memberMap = new Map();
    const emailMap = new Map(); // Track by email as well
    
    console.log('Discussion Modal Debug - Member Deduplication:');
    console.log('Board members:', boardMembers.length);
    console.log('HOA members:', hoaMembers.length);
    
    // Add board members first (they take priority)
    boardMembers.forEach(member => {
      if (member.id && member.name && member.name.trim() !== '' && member.name !== 'NO_NAME') {
        const memberData = { 
          ...member, 
          type: 'board_member',
          displayRole: member.role?.replace('_', ' ').toUpperCase() || 'BOARD MEMBER'
        };
        memberMap.set(member.id, memberData);
        if (member.email) {
          emailMap.set(member.email.toLowerCase(), member.id);
        }
        console.log('Added board member:', member.name, member.email, member.role);
      }
    });
    
    // Add homeowners, but skip if already exists (same person with board role)
    hoaMembers.forEach(member => {
      if (member.id && member.name && member.name.trim() !== '' && member.name !== 'NO_NAME') {
        
        // Check if this member already exists by ID
        if (memberMap.has(member.id)) {
          console.log('Skipping duplicate by ID:', member.name, member.email);
          return;
        }
        
        // Check if this member already exists by email
        if (member.email && emailMap.has(member.email.toLowerCase())) {
          console.log('Skipping duplicate by email:', member.name, member.email);
          return;
        }
        
        // Add this homeowner
        const memberData = { 
          ...member, 
          type: 'homeowner',
          displayRole: 'HOMEOWNER'
        };
        memberMap.set(member.id, memberData);
        if (member.email) {
          emailMap.set(member.email.toLowerCase(), member.id);
        }
        console.log('Added homeowner:', member.name, member.email);
      } else {
        console.log('Skipping member with no name:', member.id, member.name, member.email);
      }
    });
    
    const result = Array.from(memberMap.values());
    console.log('Final deduplicated members:', result.length);
    
    return result;
  })();

  // Filter members based on search term
  const filteredMembers = allMembers.filter(member => 
    member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.property_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (member.property_identifier || member.lot || member.unit || member.property_number || '')
      .toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectMember = (member) => {
    // PRIVACY FIX: Only populate address if setting allows, otherwise leave blank for privacy
    const shouldShowAddress = accessSettings.discussion_property_display === 'address';
    
    setFormData(prev => ({
      ...prev,
      member_id: member.id,
      member_name: member.name,
      member_email: member.email || '',
      property_address: shouldShowAddress ? (member.property_address || member.address || '') : '', // Privacy protection
      lot: member.property_identifier || member.unit || member.lot || member.property_number || '',
      property_identifier: member.property_identifier || member.unit || member.lot || member.property_number || ''
    }));
    setSearchTerm(member.name);
    setShowDropdown(false);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (discussion) {
        // Update existing discussion
        await api.put(`/api/vote/discussions/${discussion.id}`, formData);
        alert('Discussion updated successfully!');
      } else {
        // Create new discussion
        await api.post('/api/vote/discussions', formData);
        alert('Discussion added successfully!');
      }
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Error saving discussion:', error);
      alert('Failed to save discussion. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-xl text-gray-900">
              {discussion ? 'Edit Discussion' : 'Add Discussion'} - {meetingType} Meeting
            </h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">✕</button>
          </div>
          <p className="text-sm text-gray-600 mt-1">Record member requests and discussion topics for {meetingType} meetings</p>
        </div>

        <div className="p-6 space-y-4">
          {/* Searchable Member Selection */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Member
              {/* PHASE 3: Show access restriction notice */}
              {console.log('DEBUG: canSearchMembers =', canSearchMembers, 'meetingType =', meetingType, 'user role =', user?.role)}
              {!canSearchMembers && (
                <span className="ml-2 text-xs text-red-600 font-normal">
                  (Search disabled - {meetingType === 'BOD' ? 'Board members only' : 'Committee access required'})
                </span>
              )}
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                if (canSearchMembers) {
                  setSearchTerm(e.target.value);
                  setShowDropdown(true);
                }
              }}
              onFocus={() => canSearchMembers && setShowDropdown(true)}
              disabled={!canSearchMembers}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                !canSearchMembers ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
              placeholder={canSearchMembers 
                ? `Type member name, email, or ${accessSettings.discussion_property_display === 'address' ? 'address' : 'property identifier'}...`
                : "Search disabled for your access level"
              }
            />
            
            {showDropdown && canSearchMembers && filteredMembers.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredMembers.slice(0, 10).map((member) => (
                  <button
                    key={`${member.type}-${member.id}`}
                    onClick={() => selectMember(member)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="font-medium text-gray-900">{member.name}</div>
                    <div className="text-sm text-gray-600">
                      {member.email && `${member.email} • `}
                      {/* PRIVACY FIX: Only show address if setting allows, otherwise show lot for privacy */}
                      {accessSettings.discussion_property_display === 'address' 
                        ? (member.property_address || member.address || 'No address')
                        : (member.property_identifier || member.unit || member.lot || member.property_number || 'No identifier')
                      }
                      <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                        member.type === 'board_member' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {member.displayRole || (member.type === 'board_member' ? 'Board Member' : 'Homeowner')}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Auto-populated Member Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Member Name</label>
              <input
                type="text"
                value={formData.member_name}
                onChange={(e) => setFormData(prev => ({...prev, member_name: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Member name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.member_email}
                onChange={(e) => setFormData(prev => ({...prev, member_email: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Email address"
              />
            </div>
          </div>

          {/* Property Details - SIMPLE: Show address OR lot based on setting */}
          {accessSettings.discussion_property_display === 'address' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Property Address</label>
              <input
                type="text"
                value={formData.property_address}
                onChange={(e) => setFormData(prev => ({...prev, property_address: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Property address"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lot/Unit Number</label>
              <input
                type="text"
                value={formData.lot}
                onChange={(e) => setFormData(prev => ({...prev, lot: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Lot/unit number"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Discussion Topic</label>
            <input
              type="text"
              value={formData.topic}
              onChange={(e) => setFormData(prev => ({...prev, topic: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="e.g., Pool maintenance request, noise complaint, etc."
            />
          </div>

          {/* CCR/Bylaw Citation Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CCR Rule Citation</label>
              <input
                type="text"
                value={formData.ccr_rule}
                onChange={(e) => setFormData(prev => ({...prev, ccr_rule: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="e.g., CCR §4.2, CCR Article V.3"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bylaw Rule Citation</label>
              <input
                type="text"
                value={formData.bylaw_rule}
                onChange={(e) => setFormData(prev => ({...prev, bylaw_rule: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="e.g., Bylaw 7.1, Bylaw Article III.2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              rows="3"
              placeholder="Meeting discussion notes, board response, follow-up actions..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData(prev => ({...prev, status: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="deferred">Deferred</option>
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-white border-t p-6">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || !formData.member_name || !formData.topic}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 transition-colors"
            >
              {saving ? 'Saving...' : discussion ? 'Update Discussion' : 'Add Discussion'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Meeting Minutes Preview Modal
function MeetingMinutesPreviewModal({ 
  previewData, 
  meetingNotes, 
  setMeetingNotes, 
  actionItems, 
  setActionItems,
  proofOfNotice = {},
  meetingRules = [],
  manualPriorMinutes = '',
  setManualPriorMinutes = () => {},
  speakingTimeMinutes = 3,
  meetingType = 'BOD',
  hoaName = 'HOA',
  hoaTimezone = 'America/Chicago',
  supportingDocuments = [],
  user = null,
  hoaId = null,
  hoaData = null,
  hoaSettings = null,
  onClose, 
  onExport 
}) {
  // State for inline time editing
  const [editingStartTime, setEditingStartTime] = useState(false);
  const [editingEndTime, setEditingEndTime] = useState(false);
  const [tempStartTime, setTempStartTime] = useState('');
  const [tempEndTime, setTempEndTime] = useState('');
  const [localPreviewData, setLocalPreviewData] = useState(previewData);
  // State for section visibility (allow users to hide/delete sections)
  const [sectionVisibility, setSectionVisibility] = useState({
    meetingInfo: true,
    proofOfNotice: true,
    rules: true,
    attendance: true,
    priorBusiness: true,
    manualPriorMinutes: true,
    motions: true,
    discussions: true,
    notes: true,
    actionItems: true
  });

  // State for combined documents feature - DEFAULT TO TRUE
  const [useCombinedDocs, setUseCombinedDocs] = useState(true);
  const [combinedPacketId, setCombinedPacketId] = useState(null);
  const [combiningDocs, setCombiningDocs] = useState(false);
  const [combinedPacketUrl, setCombinedPacketUrl] = useState(null);

  // Auto-combine documents when modal opens (if default is ON and there are documents)
  React.useEffect(() => {
    const autoCombine = async () => {
      if (useCombinedDocs && supportingDocuments && supportingDocuments.length > 0 && !combinedPacketId && !combiningDocs) {
        setCombiningDocs(true);
        try {
          const docsToInclude = supportingDocuments
            .filter(doc => doc.includeInPacket !== false)
            .map(doc => ({
              id: doc.id,
              file_name: doc.file_name,
              file_path: doc.file_path || null,  // May be null for S3 documents
              document_id: doc.id,  // Always send document_id for DB lookup
              mime_type: doc.mime_type
            }));
          
          if (docsToInclude.length === 0) {
            setCombiningDocs(false);
            return;
          }
          
          const token = localStorage.getItem('hoa_token');
          const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin.replace(/:\d+$/, ':8001');
          
          const response = await fetch(`${backendUrl}/api/packets/build-meeting-packet`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              meeting_type: meetingType || 'BOD',
              minutes_content: null,
              attachments: docsToInclude,
              hoa_id: hoaId || user?.hoa_id,
              documents_only: true
            })
          });
          
          const data = await response.json();
          
          if (data.success) {
            setCombinedPacketId(data.packet_id);
            setCombinedPacketUrl(data.download_url);
          } else {
            console.error('Auto-combine failed:', data.error);
            setUseCombinedDocs(false);
          }
        } catch (error) {
          console.error('Error auto-combining documents:', error);
          setUseCombinedDocs(false);
        } finally {
          setCombiningDocs(false);
        }
      }
    };
    
    autoCombine();
  }, []); // Only run once on mount

  const toggleSection = (sectionName) => {
    setSectionVisibility(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const deleteSection = (sectionName) => {
    const confirmDelete = window.confirm(
      `Delete ${sectionName.replace(/([A-Z])/g, ' $1').toLowerCase()} section?\n\n` +
      'This will remove this section from the meeting minutes export.\n' +
      'You can restore it by refreshing the preview.\n\n' +
      'Continue?'
    );
    
    if (confirmDelete) {
      setSectionVisibility(prev => ({
        ...prev,
        [sectionName]: false
      }));
    }
  };

  // Function to send for approval (with signature requirement)
  const sendForApproval = async (format) => {
    const confirmApproval = window.confirm(
      `Send meeting minutes for electronic signature approval?\n\n` +
      `Format: ${format.toUpperCase()}\n\n` +
      'This will:\n' +
      '• Create an approval workflow\n' +
      '• Require electronic signatures from board members\n' +
      '• Lock the document until all signatures are collected\n' +
      '• Send notifications to required signers\n\n' +
      'Continue?'
    );
    
    if (confirmApproval) {
      try {
        // Create the minutes data with only visible sections
        const minutesData = {
          ...previewData,
          meeting_notes: meetingNotes, // Map to backend field name
          action_items: actionItems,   // Map to backend field name
          sectionVisibility: { ...sectionVisibility, attendance: true }, // ALWAYS show attendance/roll call
          // normalize times for backend approvals storage - use edited times from localPreviewData
          start_time: localPreviewData?.startTime || previewData?.startTime || previewData?.start_time || null,
          end_time: localPreviewData?.endTime || previewData?.endTime || previewData?.end_time || null,
          format,
          // Include additional fields from preview for complete document
          call_to_order: previewData?.callToOrder || previewData?.call_to_order,
          adjournment: previewData?.adjournment,
          rules_of_conduct: previewData?.rulesOfConduct || previewData?.rules_of_conduct || [],
          speaking_time_minutes: previewData?.speakingTimeMinutes || 3,
          roll_call_display_mode: previewData?.rollCallDisplayMode || previewData?.roll_call_display_mode || 'name_only', // Use from previewData or default
          proof_of_notice: previewData?.proofOfNotice || {},
          manual_prior_minutes: previewData?.manualPriorMinutes || '',
          meeting_location: previewData?.meetingLocation || previewData?.meeting_location || '', // FIX: Get from previewData
          supporting_documents: supportingDocuments || [], // ADD: Supporting documents
          // Combined packet info (if enabled)
          use_combined_packet: useCombinedDocs,
          combined_packet_id: combinedPacketId,
          // Include prior minutes approval data as-is from previewData
          prior_minutes_approval: previewData?.prior_minutes_approval || null,
          // Use attendance directly from preview - it already has correct present/absent status
          attendance: previewData?.attendance || [],
          // CRITICAL: Include formatted roll call data for PDF
          roll_call: {
            board_members: Array.isArray(previewData?.attendance) 
              ? previewData.attendance.filter(a => a.role && ['board_member', 'president', 'vice_president', 'treasurer', 'secretary'].includes(a.role.toLowerCase()))
              : [],
            homeowners: Array.isArray(previewData?.homeowners) ? previewData.homeowners : [],
            external_attendees: Array.isArray(previewData?.externalAttendees) ? previewData.externalAttendees : []
          }
        };
        
        // Call the canonical approvals API first
        let response;
        // Get current date in HOA timezone (not UTC)
        const hoaDate = formatLocal(new Date().toISOString(), hoaTimezone, 'yyyy-MM-dd');
        
        // Add HOA info for PDF header - use hoaData or hoaSettings for accurate info
        const hoaInfo = {
          hoa_name: hoaData?.name || hoaSettings?.hoa_name || hoaSettings?.name || user?.hoa_name || hoaName || '',
          hoa_address: hoaData?.address || hoaData?.mailing_address || hoaSettings?.mailing_address || user?.hoa_address || '',
          hoa_email: hoaData?.email || hoaSettings?.hoa_email || user?.hoa_email || '',
          hoa_phone: hoaData?.phone || hoaSettings?.phone || user?.hoa_phone || ''
        };
        
        // FIXED: Use homeowners directly from previewData (not from non-existent roll_call object)
        const allHomeowners = Array.isArray(previewData?.homeowners) ? previewData.homeowners : [];
        const allExternal = Array.isArray(previewData?.externalAttendees) ? previewData.externalAttendees : [];
        const allBoardMembers = Array.isArray(previewData?.boardMembers) ? previewData.boardMembers : [];
        
        console.log('[SEND FOR APPROVAL DEBUG] HOA Info:', hoaInfo);
        console.log('[SEND FOR APPROVAL DEBUG] previewData.homeowners:', allHomeowners.length, allHomeowners);
        console.log('[SEND FOR APPROVAL DEBUG] previewData.boardMembers:', allBoardMembers.length, allBoardMembers);
        console.log('[SEND FOR APPROVAL DEBUG] previewData.externalAttendees:', allExternal.length, allExternal);
        
        response = await api.post('/api/approvals/meeting-minutes', {
          ...minutesData,
          format: format,
          meeting_date: hoaDate,
          meeting_type: meetingType || 'BOD',
          hoa_info: hoaInfo,
          // Explicitly send homeowners, board members, and external at top level for PDF
          homeowners: allHomeowners,
          boardMembers: allBoardMembers,
          externalAttendees: allExternal
        });
        
        if (response.data.success) {
          alert(`Meeting minutes sent for approval successfully!\n\nApproval ID: ${response.data.approval_id}\nRequired signatures: ${response.data.required_signatures}\n\nYou will be notified when all signatures are collected.`);
          onClose();
        } else {
          throw new Error('Failed to send for approval');
        }
      } catch (error) {
        console.error('Error sending for approval:', error);
        const errorDetail = error.response?.data?.detail || error.message || 'Unknown error';
        alert(`Failed to send meeting minutes for approval.\n\nError: ${errorDetail}\n\nPlease check the console for more details.`);
      }
    }
  };

  const addActionItem = () => {
    setActionItems([...actionItems, '']);
  };

  const updateActionItem = (index, value) => {
    const updated = [...actionItems];
    updated[index] = value;
    setActionItems(updated);
  };

  const removeActionItem = (index) => {
    setActionItems(actionItems.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-4xl bg-white rounded-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-xl text-gray-900">Meeting Minutes Preview</h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">✕</button>
          </div>
          <p className="text-sm text-gray-600 mt-1">Review and customize meeting minutes before export</p>
        </div>

        <div className="p-6 space-y-6">
          {/* Meeting Header */}
          {sectionVisibility.meetingInfo && (
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-blue-900">📋 Meeting Information</h4>
                <button
                  onClick={() => deleteSection('meetingInfo')}
                  className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm transition-colors"
                  title="Delete this section"
                >
                  🗑️ Delete
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-blue-800">Date:</span>
                  <span className="ml-2 text-blue-700">{previewData?.meetingDate || formatLocal(new Date().toISOString(), hoaTimezone, 'MMM d, yyyy')}</span>
                </div>
                <div>
                  <span className="font-medium text-blue-800">HOA:</span>
                  <span className="ml-2 text-blue-700">{hoaName}</span>
                </div>
                <div>
                  <span className="font-medium text-blue-800">Start Time:</span>
                  {editingStartTime ? (
                    <div className="inline-flex items-center gap-1 ml-2">
                      <input
                        type="text"
                        placeholder="7:00"
                        value={tempStartTime}
                        onChange={(e) => setTempStartTime(e.target.value)}
                        className="px-2 py-1 border border-blue-300 rounded text-sm w-16"
                      />
                      <select
                        value={tempStartTime.includes('PM') ? 'PM' : 'AM'}
                        onChange={(e) => {
                          const time = tempStartTime.replace(/\s*(AM|PM)/i, '');
                          setTempStartTime(`${time} ${e.target.value}`);
                        }}
                        className="px-1 py-1 border border-blue-300 rounded text-sm"
                      >
                        <option>AM</option>
                        <option>PM</option>
                      </select>
                      <button
                        onClick={() => {
                          setLocalPreviewData(prev => ({...prev, startTime: tempStartTime}));
                          setEditingStartTime(false);
                        }}
                        className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                      >
                        ✓
                      </button>
                      <button
                        onClick={() => setEditingStartTime(false)}
                        className="px-2 py-1 bg-gray-400 text-white rounded text-xs hover:bg-gray-500"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <span className="ml-2 text-blue-700">
                      {localPreviewData?.startTime || 'Not recorded'}
                      <button
                        onClick={() => {
                          setTempStartTime(localPreviewData?.startTime || '');
                          setEditingStartTime(true);
                        }}
                        className="ml-2 text-blue-600 hover:text-blue-800 text-xs"
                        title="Edit start time"
                      >
                        ✏️
                      </button>
                    </span>
                  )}
                </div>
                <div>
                  <span className="font-medium text-blue-800">End Time:</span>
                  {editingEndTime ? (
                    <div className="inline-flex items-center gap-1 ml-2">
                      <input
                        type="text"
                        placeholder="8:00"
                        value={tempEndTime}
                        onChange={(e) => setTempEndTime(e.target.value)}
                        className="px-2 py-1 border border-blue-300 rounded text-sm w-16"
                      />
                      <select
                        value={tempEndTime.includes('PM') ? 'PM' : 'AM'}
                        onChange={(e) => {
                          const time = tempEndTime.replace(/\s*(AM|PM)/i, '');
                          setTempEndTime(`${time} ${e.target.value}`);
                        }}
                        className="px-1 py-1 border border-blue-300 rounded text-sm"
                      >
                        <option>AM</option>
                        <option>PM</option>
                      </select>
                      <button
                        onClick={() => {
                          setLocalPreviewData(prev => ({...prev, endTime: tempEndTime}));
                          setEditingEndTime(false);
                        }}
                        className="px-2 py-1 bg-green-500 text-white rounded text-xs hover:bg-green-600"
                      >
                        ✓
                      </button>
                      <button
                        onClick={() => setEditingEndTime(false)}
                        className="px-2 py-1 bg-gray-400 text-white rounded text-xs hover:bg-gray-500"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <span className="ml-2 text-blue-700">
                      {localPreviewData?.endTime || 'Not recorded'}
                      <button
                        onClick={() => {
                          setTempEndTime(localPreviewData?.endTime || '');
                          setEditingEndTime(true);
                        }}
                        className="ml-2 text-blue-600 hover:text-blue-800 text-xs"
                        title="Edit end time"
                      >
                        ✏️
                      </button>
                    </span>
                  )}
                </div>
                {(previewData?.meetingLocation || previewData?.meeting_location) && (
                  <div>
                    <span className="font-medium text-blue-800">Location:</span>
                    <span className="ml-2 text-blue-700">{previewData.meetingLocation || previewData.meeting_location}</span>
                  </div>
                )}
              </div>
            </div>
          )}



          {/* Proof of Notice Section */}
          {sectionVisibility.proofOfNotice && proofOfNotice && (proofOfNotice.postedOnEvents || proofOfNotice.mailPacket || proofOfNotice.other) && (
            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-yellow-900">📢 Proof of Notice</h4>
                <button
                  onClick={() => deleteSection('proofOfNotice')}
                  className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm transition-colors"
                  title="Delete this section"
                >
                  🗑️ Delete
                </button>
              </div>
              <div className="space-y-2 text-sm">
                {proofOfNotice.postedOnEvents && proofOfNotice.emailSent && (
                  <div className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    <span className="text-gray-900">Posted on events calendar and emailed to all homeowners</span>
                  </div>
                )}
                {proofOfNotice.mailPacket && (
                  <div className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    <span className="text-gray-900">
                      Sent via mail packet
                      {proofOfNotice.mailPacketDate && <span className="font-medium"> on {formatLocal(proofOfNotice.mailPacketDate, hoaTimezone, 'MMM d, yyyy')}</span>}
                    </span>
                  </div>
                )}
                {proofOfNotice.other && proofOfNotice.otherNotes && (
                  <div className="flex items-start gap-2">
                    <span className="text-green-600 mt-0.5">✓</span>
                    <div>
                      <span className="text-gray-900 font-medium">Other: </span>
                      <span className="text-gray-700">{proofOfNotice.otherNotes}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Rules Section */}
          {sectionVisibility.rules && previewData?.rulesOfConduct && previewData.rulesOfConduct.length > 0 && (
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-purple-900">📋 Meeting Rules</h4>
                <button
                  onClick={() => deleteSection('rules')}
                  className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm transition-colors"
                  title="Delete this section"
                >
                  🗑️ Delete
                </button>
              </div>
              <div className="space-y-2">
                {previewData.rulesOfConduct.map((rule, index) => (
                  <div key={index} className="flex items-start gap-2 text-sm">
                    <span className="text-purple-600 font-medium">{index + 1}.</span>
                    <span className="text-gray-900">{rule}</span>
                  </div>
                ))}
                {previewData.speakingTimeMinutes && (
                  <div className="flex items-start gap-2 text-sm mt-3 pt-3 border-t border-purple-200">
                    <span className="text-purple-600">⏱️</span>
                    <span className="text-gray-900">Speaking time limit: {previewData.speakingTimeMinutes} minutes per person</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Attendance Preview */}
          {sectionVisibility.attendance && (
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-green-900">👥 Attendance</h4>
                <button
                  onClick={() => deleteSection('attendance')}
                  className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm transition-colors"
                  title="Delete this section"
                >
                  🗑️ Delete
                </button>
              </div>
            
            {/* Board Members */}
            <div className="mb-4">
              <div className="text-sm font-medium text-green-800 mb-2">Board Members</div>
              <div className="grid grid-cols-1 gap-2">
                {previewData?.boardMembers?.map((member, index) => {
                  const memberId = member.id || member.user_id;
                  const attendanceStatus = previewData?.attendance?.[memberId];
                  const isPresent = attendanceStatus === true;
                  const isAbsent = attendanceStatus === false;
                  
                  return (
                    <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          isPresent ? 'bg-green-500' : isAbsent ? 'bg-red-500' : 'bg-gray-300'
                        }`}></div>
                        <span className="text-sm font-medium text-gray-900">{member.name}</span>
                        <span className="text-xs text-gray-500 px-2 py-1 bg-blue-100 rounded">
                          {member.role?.replace('_', ' ').toUpperCase() || 'BOARD'}
                        </span>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        isPresent ? 'bg-green-100 text-green-800' : 
                        isAbsent ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {isPresent ? 'Present' : isAbsent ? 'Absent' : 'Unknown'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Homeowners and Team Members - exclude external attendees */}
            {previewData?.homeowners && previewData.homeowners.filter(h => h.type !== 'external').length > 0 && (
              <div>
                <div className="text-sm font-medium text-green-800 mb-2">Homeowners & Team Members</div>
                <div className="grid grid-cols-1 gap-2">
                  {previewData.homeowners.filter(h => h.type !== 'external').map((homeowner, index) => {
                    const attendanceStatus = homeowner.present || homeowner.attendance_status;
                    const isPresent = attendanceStatus === true;
                    const isAbsent = attendanceStatus === false;
                    
                    // Check if this is a team member
                    const isTeamMember = homeowner.role && ['maintenance', 'security', 'staff', 'attorney', 'accountant'].includes(homeowner.role.toLowerCase());
                    const teamMemberRoleLabels = {
                      maintenance: 'Maintenance',
                      security: 'Security', 
                      staff: 'Staff',
                      attorney: 'Attorney',
                      accountant: 'Accountant'
                    };
                    
                    return (
                      <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            isPresent ? 'bg-green-500' : isAbsent ? 'bg-red-500' : 'bg-gray-300'
                          }`}></div>
                          <span className="text-sm font-medium text-gray-900">{homeowner.name}</span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            isTeamMember ? 'text-purple-700 bg-purple-100' : 'text-gray-500 bg-gray-100'
                          }`}>
                            {isTeamMember ? `TEAM MEMBER - ${teamMemberRoleLabels[homeowner.role.toLowerCase()] || homeowner.role.toUpperCase()}` : `HOMEOWNER${(() => {
                              // Default to showing property identifier (lot, unit, etc.)
                              const propertyDisplay = homeowner.property_identifier || homeowner.unit || homeowner.lot || homeowner.property_number || homeowner.property_address || homeowner.address || '';
                              return propertyDisplay ? ` ${propertyDisplay}` : '';
                            })()}`}
                          </span>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          isPresent ? 'bg-green-100 text-green-800' : 
                          isAbsent ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {isPresent ? 'Present' : isAbsent ? 'Absent' : 'Unknown'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* External Attendees - separate section */}
            {previewData?.homeowners && previewData.homeowners.filter(h => h.type === 'external').length > 0 && (
              <div>
                <div className="text-sm font-medium text-purple-800 mb-2">External Attendees</div>
                <div className="grid grid-cols-1 gap-2">
                  {previewData.homeowners.filter(h => h.type === 'external').map((attendee, index) => {
                    const attendanceStatus = attendee.present || attendee.attendance_status;
                    const isPresent = attendanceStatus === true;
                    const isAbsent = attendanceStatus === false;
                    
                    const roleIcons = {
                      attorney: '⚖️',
                      accountant: '💼',
                      staff: '👔',
                      maintenance: '🔧',
                      security: '🛡️',
                      other: '👤'
                    };
                    const roleLabels = {
                      attorney: 'Attorney',
                      accountant: 'Accountant',
                      staff: 'Staff',
                      maintenance: 'Maintenance',
                      security: 'Security',
                      other: 'Other'
                    };
                    const roleIcon = roleIcons[attendee.externalRole] || '👤';
                    const roleLabel = roleLabels[attendee.externalRole] || 'External';
                    
                    return (
                      <div key={index} className="flex items-center justify-between p-2 bg-purple-50 rounded border border-purple-200">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            isPresent ? 'bg-green-500' : isAbsent ? 'bg-red-500' : 'bg-gray-300'
                          }`}></div>
                          <span className="text-sm font-medium text-gray-900">{attendee.name}</span>
                          <span className="text-xs text-purple-700 px-2 py-1 bg-purple-100 rounded">
                            {roleIcon} {roleLabel.toUpperCase()}
                          </span>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          isPresent ? 'bg-green-100 text-green-800' : 
                          isAbsent ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {isPresent ? 'Present' : isAbsent ? 'Absent' : 'Unknown'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Attendance Summary */}
              <div className="mt-4 pt-3 border-t border-green-200">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-lg font-bold text-green-600">
                      {Object.values(previewData?.attendance || {}).filter(status => status === true).length}
                    </div>
                    <div className="text-xs text-green-700">Present</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-gray-600">
                      {(previewData?.boardMembers?.length || 0) + (previewData?.homeowners?.length || 0)}
                    </div>
                    <div className="text-xs text-gray-600">Total</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-blue-600">
                      {Object.values(previewData?.attendance || {}).filter(status => status === true).length >= 
                       Math.ceil((previewData?.boardMembers?.length || 0) / 2) ? 'YES' : 'NO'}
                    </div>
                    <div className="text-xs text-blue-700">Quorum</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Prior Business Section - Approval of Prior Meeting Minutes */}
          {previewData?.prior_minutes_approval && (
            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-yellow-900">📝 Prior Business - Approval of Prior Meeting Minutes</h4>
                <button
                  onClick={() => {
                    const confirmClear = window.confirm('Remove Prior Business section from minutes?');
                    if (confirmClear) {
                      previewData.prior_minutes_approval = null;
                      onClose(); // Close and reopen to refresh
                    }
                  }}
                  className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm transition-colors"
                  title="Delete this section"
                >
                  🗑️ Delete
                </button>
              </div>
              <div className="bg-white rounded p-3 space-y-2">
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-700 w-32">Meeting:</span>
                  <span className="text-sm text-gray-900">{previewData.prior_minutes_approval.title}</span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-700 w-32">Date:</span>
                  <span className="text-sm text-gray-900">
                    {previewData.prior_minutes_approval.date 
                      ? formatLocal(previewData.prior_minutes_approval.date, hoaTimezone, 'MMM d, yyyy') 
                      : 'Unknown'}
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-700 w-32">Motion:</span>
                  <span className="text-sm text-gray-900 italic">To approve the prior meeting minutes as presented.</span>
                </div>
                <div className="flex items-start">
                  <span className="text-sm font-medium text-gray-700 w-32">Status:</span>
                  <span className={`text-sm font-medium ${
                    previewData.prior_minutes_approval.status === 'approved' ? 'text-green-700' : 'text-yellow-700'
                  }`}>
                    {previewData.prior_minutes_approval.status === 'approved' ? 'Approved' : 'Pending approval during this meeting'}
                  </span>
                </div>
              </div>
            </div>
          )}


          {/* Manual Prior Minutes Notes */}
          {sectionVisibility.manualPriorMinutes && manualPriorMinutes && manualPriorMinutes.trim() !== '' && (
            <div className="bg-orange-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-orange-900">📄 Additional Prior Meeting Minutes Notes</h4>
                <button
                  onClick={() => deleteSection('manualPriorMinutes')}
                  className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm transition-colors"
                  title="Delete this section"
                >
                  🗑️ Delete
                </button>
              </div>
              <div className="bg-white rounded p-3">
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{manualPriorMinutes}</p>
              </div>
            </div>
          )}


          {/* Motions Preview */}
          {sectionVisibility.motions && previewData?.motions && previewData.motions.length > 0 && (
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-purple-900">🗳️ Motions & Voting Results</h4>
                <button
                  onClick={() => deleteSection('motions')}
                  className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm transition-colors"
                  title="Delete this section"
                >
                  🗑️ Delete
                </button>
              </div>
              <div className="space-y-3">
                {previewData.motions.map((motion, index) => (
                  <div key={index} className="bg-white rounded p-3">
                    <h5 className="font-medium text-gray-900 mb-2">Motion #{index + 1}: {motion.title}</h5>
                    {motion.description && (
                      <p className="text-sm text-gray-700 mb-2">{motion.description}</p>
                    )}
                    {motion.discussion && (
                      <p className="text-sm text-gray-600 mb-2"><strong>Discussion:</strong> {motion.discussion}</p>
                    )}
                    {motion.voting_rule === 'propose_second' && (motion.proposer_name || motion.seconder_name) && (
                      <div className="text-sm text-purple-700 mb-2">
                        <p><strong>Proposed by:</strong> {motion.proposer_name || 'Not set'}</p>
                        <p><strong>Seconded by:</strong> {motion.seconder_name || 'Not set'}</p>
                      </div>
                    )}
                    {(motion.votes || motion.total_counts) && (() => {
                      const answerOpts = motion.answer_options || motion.answers || ['Yes', 'No'];
                      const counts = motion.total_counts || motion.votes || {};
                      return (
                      <div className="grid gap-2 text-xs mb-2" style={{gridTemplateColumns: `repeat(${answerOpts.length}, 1fr)`}}>
                        {answerOpts.map((opt, idx) => (
                          <div key={opt} className="text-center">
                            <div className={`font-bold ${idx === 0 ? 'text-green-600' : idx === 1 ? 'text-red-600' : 'text-gray-600'}`}>{counts[opt] || 0}</div>
                            <div className="text-gray-600">{opt}</div>
                          </div>
                        ))}
                      </div>
                      );
                    })()}
                    {motion.individual_votes && (() => {
                      const answerOpts = motion.answer_options || motion.answers || ['Yes', 'No'];
                      const indVotes = motion.individual_votes || {};
                      return (
                      <div className="text-xs text-gray-700 bg-gray-50 p-2 rounded">
                        {answerOpts.map(opt => {
                          const voters = indVotes[opt] || [];
                          return voters.length > 0 ? (
                            <p key={opt} className="mb-1"><strong>{opt}:</strong> {voters.map(v => v.name).join(', ')}</p>
                          ) : null;
                        })}
                      </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Other Discussions Section */}
          {sectionVisibility.discussions && (
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-blue-900">💬 Other Discussions</h4>
                <button
                  onClick={() => deleteSection('discussions')}
                  className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm transition-colors"
                  title="Delete this section"
                >
                  🗑️ Delete
                </button>
              </div>
              <div className="space-y-3">
                {previewData.discussions.map((discussion, index) => (
                  <div key={index} className="bg-white rounded p-3 relative">
                    <button
                      onClick={() => {
                        // Remove this specific discussion from preview data
                        const updatedDiscussions = previewData.discussions.filter((_, i) => i !== index);
                        previewData.discussions = updatedDiscussions;
                        // Force re-render by updating state
                        setSectionVisibility(prev => ({ ...prev, discussions: false }));
                        setTimeout(() => setSectionVisibility(prev => ({ ...prev, discussions: true })), 100);
                      }}
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700 text-sm"
                      title="Remove this discussion from export"
                    >
                      ✕
                    </button>
                    <h5 className="font-medium text-gray-900 mb-2">
                      Discussion #{index + 1}: {discussion.topic || discussion.title}
                    </h5>
                    {discussion.member_name && (
                      <p className="text-sm text-gray-600 mb-1">
                        <span className="font-medium">Raised by:</span> {discussion.member_name}
                        {discussion.property_address && (
                          <span className="ml-2 text-gray-500">({discussion.property_address})</span>
                        )}
                      </p>
                    )}
                    {discussion.notes && (
                      <p className="text-sm text-gray-700 mb-2">{discussion.notes}</p>
                    )}
                    <div className="text-xs text-gray-500">
                      Status: <span className={`px-2 py-1 rounded-full ${
                        discussion.status === 'resolved' ? 'bg-green-100 text-green-800' :
                        discussion.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {discussion.status || 'pending'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Meeting Notes */}
          {sectionVisibility.notes && (
            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-yellow-900">📝 Meeting Notes</h4>
                <button
                  onClick={() => deleteSection('notes')}
                  className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm transition-colors"
                  title="Delete this section"
                >
                  🗑️ Delete
                </button>
              </div>
              <textarea
                value={meetingNotes}
                onChange={(e) => setMeetingNotes(e.target.value)}
                className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 bg-white"
                rows="6"
                placeholder="Add general meeting notes, discussions, and important points..."
              />
            </div>
          )}

          {/* Action Items */}
          {sectionVisibility.actionItems && (
            <div className="bg-orange-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-orange-900">✅ Action Items</h4>
                <div className="flex gap-2">
                  <button
                    onClick={addActionItem}
                    className="px-3 py-1 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    + Add Item
                  </button>
                  <button
                    onClick={() => deleteSection('actionItems')}
                    className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm transition-colors"
                    title="Delete this section"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                {actionItems.map((item, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => updateActionItem(index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                      placeholder={`Action item ${index + 1}...`}
                    />
                    <button
                      onClick={() => removeActionItem(index)}
                      className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                {actionItems.length === 0 && (
                  <p className="text-sm text-orange-700 italic">No action items added yet.</p>
                )}
              </div>
            </div>
          )}
          
          {/* Supporting Documents */}
          {supportingDocuments && supportingDocuments.length > 0 && (
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-blue-900">📎 Supporting Documents</h4>
                
                {/* Toggle for Auto-Combine */}
                <label className="flex items-center gap-2 cursor-pointer bg-white px-3 py-1.5 rounded-lg border border-blue-300 hover:bg-blue-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={useCombinedDocs}
                    onChange={async (e) => {
                      const willCombine = e.target.checked;
                      setUseCombinedDocs(willCombine);
                      
                      // Auto-combine when toggle is enabled
                      if (willCombine && !combinedPacketId) {
                        setCombiningDocs(true);
                        try {
                          const docsToInclude = supportingDocuments
                            .filter(doc => doc.includeInPacket !== false)
                            .map(doc => ({
                              id: doc.id,
                              file_name: doc.file_name,
                              file_path: doc.file_path || null,  // May be null for S3 documents
                              document_id: doc.id,  // Always send document_id for DB lookup
                              mime_type: doc.mime_type
                            }));
                          
                          const token = localStorage.getItem('hoa_token');
                          const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin.replace(/:\d+$/, ':8001');
                          
                          const response = await fetch(`${backendUrl}/api/packets/build-meeting-packet`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                              meeting_type: meetingType || 'BOD',
                              minutes_content: null,
                              attachments: docsToInclude,
                              hoa_id: hoaId || user?.hoa_id,
                              documents_only: true
                            })
                          });
                          
                          const data = await response.json();
                          
                          if (data.success) {
                            setCombinedPacketId(data.packet_id);
                            setCombinedPacketUrl(data.download_url);
                            alert(`✅ Documents combined successfully!\n\n${data.stats.total_documents} documents • ${data.stats.total_pages} pages\n\nThis combined PDF will be used in minutes, approvals, and final documents.`);
                          } else {
                            throw new Error(data.error || 'Failed to combine documents');
                          }
                        } catch (error) {
                          console.error('Error combining documents:', error);
                          alert(`Failed to combine documents: ${error.message}`);
                          setUseCombinedDocs(false);
                        } finally {
                          setCombiningDocs(false);
                        }
                      }
                    }}
                    disabled={combiningDocs}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-xs font-medium text-blue-900">
                    {combiningDocs ? 'Combining...' : 'Auto-combine into single PDF'}
                  </span>
                </label>
              </div>
              
              {/* Show combined packet info when enabled */}
              {useCombinedDocs && combinedPacketId && (
                <div className="mb-3 p-3 bg-green-50 border border-green-300 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-green-700 font-semibold text-sm">✅ Combined Packet Ready</span>
                  </div>
                  <p className="text-xs text-green-700 mb-2">
                    All supporting documents have been combined into a single PDF. This combined packet will be embedded in minutes, approvals, and final documents.
                  </p>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL || window.location.origin.replace(/:\d+$/, ':8001')}${combinedPacketUrl}?token=${localStorage.getItem('hoa_token')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    📄 Preview Combined Packet
                  </a>
                </div>
              )}
              
              {/* Show individual documents or combined message */}
              {!useCombinedDocs ? (
                <>
                  <div className="space-y-2">
                    {supportingDocuments.map((doc, index) => (
                      <div key={doc.id} className="flex items-center p-2 bg-white rounded border border-blue-200">
                        <span className="text-2xl mr-3">📄</span>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{doc.file_name}</p>
                          <p className="text-xs text-gray-500">{doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : "Unknown"}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-blue-700 mt-2 italic">
                    {supportingDocuments.length} individual document{supportingDocuments.length !== 1 ? 's' : ''} will be listed separately
                  </p>
                </>
              ) : (
                <div className="p-3 bg-white rounded border border-blue-200">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">📦</span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Combined Supporting Documents Packet</p>
                      <p className="text-xs text-gray-500">{supportingDocuments.length} documents merged into single PDF</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Export Actions */}
        <div className="sticky bottom-0 bg-white border-t p-4 sm:p-6">
          <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-3">
            <button
              onClick={onClose}
              className="col-span-2 sm:flex-1 px-3 sm:px-4 py-2.5 sm:py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm sm:text-base font-medium"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                console.log('Text Export button clicked');
                onExport('text', sectionVisibility, {
                  useCombinedDocs,
                  combinedPacketId,
                  combinedPacketUrl,
                  // Pass edited times
                  updatedStartTime: localPreviewData?.startTime,
                  updatedEndTime: localPreviewData?.endTime
                });
              }}
              className="sm:flex-1 px-3 sm:px-4 py-2.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base font-medium flex items-center justify-center gap-1"
            >
              <span className="text-base sm:text-lg">📄</span>
              <span className="hidden sm:inline">Export as Text</span>
              <span className="sm:hidden">Text</span>
            </button>
            <button
              onClick={() => {
                console.log('PDF Export button clicked');
                console.log('Modal State - useCombinedDocs:', useCombinedDocs);
                console.log('Modal State - combinedPacketId:', combinedPacketId);
                console.log('Modal State - combinedPacketUrl:', combinedPacketUrl);
                onExport('pdf', sectionVisibility, {
                  useCombinedDocs,
                  combinedPacketId,
                  combinedPacketUrl,
                  // Pass edited times
                  updatedStartTime: localPreviewData?.startTime,
                  updatedEndTime: localPreviewData?.endTime
                });
              }}
              className="sm:flex-1 px-3 sm:px-4 py-2.5 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base font-medium flex items-center justify-center gap-1"
            >
              <span className="text-base sm:text-lg">📋</span>
              <span className="hidden sm:inline">Export as PDF</span>
              <span className="sm:hidden">PDF</span>
            </button>
            <button
              onClick={() => sendForApproval('pdf')}
              className="col-span-2 sm:flex-1 px-3 sm:px-4 py-2.5 sm:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm sm:text-base font-medium flex items-center justify-center gap-1"
            >
              <span className="text-base sm:text-lg">✍️</span>
              <span className="hidden sm:inline">Send for Approval</span>
              <span className="sm:hidden">Approve</span>
            </button>
          </div>
          <div className="mt-3 text-center">
            <p className="text-xs text-gray-500">
              💡 <strong>Tip:</strong> Export creates draft documents for review. Send for Approval requires electronic signatures.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

}

// ============================================================================
// ELIGIBLE VOTERS TAB - Manage voter eligibility with auto-suspension rules
// ============================================================================
function EligibleVotersTab({ electionId }) {
  const [voters, setVoters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [electionData, setElectionData] = useState(null);
  const [suspendModal, setSuspendModal] = useState({ show: false, voter: null, suspend: true });
  const [suspendReason, setSuspendReason] = useState('');

  useEffect(() => {
    loadEligibleVoters();
  }, [electionId]);

  const loadEligibleVoters = async () => {
    setLoading(true);
    try {
      const [votersResponse, electionResponse] = await Promise.all([
        api.get(`/api/vote/election/${electionId}/eligible-voters`),
        api.get(`/api/vote/election/${electionId}`)
      ]);
      
      const electionInfo = electionResponse.data.election || electionResponse.data;
      setElectionData(electionInfo);
      
      setVoters(votersResponse.data.voters);
      setStats({
        total: votersResponse.data.total_voters,
        eligible: votersResponse.data.eligible_count,
        suspended: votersResponse.data.suspended_count,
        voted: votersResponse.data.voted_count || 0,
        volunteerBallots: electionInfo.volunteer_ballots_collected || 0,
        volunteerProxies: electionInfo.volunteer_proxies_collected || 0,
        manualEligible: electionInfo.manual_eligible_count || 0
      });
    } catch (error) {
      console.error('Error loading eligible voters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendVoter = async (voter, suspend) => {
    setSuspendModal({ show: true, voter, suspend });
    setSuspendReason('');
  };

  const confirmSuspension = async () => {
    if (!suspendModal.voter || (suspendModal.suspend && !suspendReason.trim())) {
      alert('Please provide a reason for suspension');
      return;
    }

    try {
      await api.post(`/api/vote/election/${electionId}/suspend-voter`, {
        user_id: suspendModal.voter.user_id,
        reason: suspendReason,
        suspend: suspendModal.suspend
      });
      
      setSuspendModal({ show: false, voter: null, suspend: true });
      setSuspendReason('');
      loadEligibleVoters();
    } catch (error) {
      console.error('Error suspending voter:', error);
      alert('Failed to update voter status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold mb-2">✅ Eligible Voters Management</h3>
        <p className="text-sm text-gray-600">
          Manage voter eligibility based on automatic rules (violations, unpaid dues) and manual suspensions.
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-3xl font-bold text-blue-900">{stats?.total}</div>
          <div className="text-sm text-blue-700">Total Voters</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-3xl font-bold text-green-900">{stats?.eligible}</div>
          <div className="text-sm text-green-700">Eligible</div>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="text-3xl font-bold text-purple-900">{stats?.voted || 0}</div>
          <div className="text-sm text-purple-700">Voted</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-3xl font-bold text-red-900">{stats?.suspended}</div>
          <div className="text-sm text-red-700">Suspended</div>
        </div>
      </div>

      {/* Manual Turnout Tracking - for non-system voters */}
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-300 rounded-lg p-6">
        <h4 className="font-semibold text-purple-900 mb-3 text-lg">📋 Manual Turnout Tracking</h4>
        <p className="text-sm text-gray-700 mb-4">
          Enter the number of eligible voters <strong>NOT in the online system</strong> to calculate accurate turnout percentage.
        </p>
        
        <div className="bg-white rounded-lg p-4 border border-purple-200 mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Number of Eligible Voters (Not in System)
          </label>
          <input
            type="number"
            min="0"
            placeholder="e.g., 25"
            value={stats?.manualEligible || 0}
            onChange={(e) => {
              const newValue = parseInt(e.target.value) || 0;
              setStats(prev => ({ ...prev, manualEligible: newValue }));
            }}
            onBlur={async (e) => {
              try {
                const value = parseInt(e.target.value) || 0;
                await api.post(`/api/vote/election/${electionId}/update-manual-eligible`, {
                  manual_eligible_count: value
                });
                alert('✅ Manual eligible count updated');
                loadEligibleVoters(); // Refresh
              } catch (error) {
                console.error('Error updating manual eligible count:', error);
                alert('Failed to update count');
              }
            }}
          />
          <p className="text-xs text-gray-600 mt-2">
            This count will be added to system voters for total turnout calculation
          </p>
        </div>
        
        {/* Volunteer Collected Counts */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-400 rounded-lg p-4">
          <h5 className="font-semibold text-green-900 mb-3">📊 Ballots Collected by Volunteers</h5>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-lg p-3 border border-green-300 text-center">
              <div className="text-3xl font-bold text-green-900">{stats?.volunteerBallots || 0}</div>
              <div className="text-xs text-gray-700 mt-1">Physical Ballots</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-green-300 text-center">
              <div className="text-3xl font-bold text-green-900">{stats?.volunteerProxies || 0}</div>
              <div className="text-xs text-gray-700 mt-1">Quorum Proxies</div>
            </div>
          </div>
          <p className="text-xs text-gray-600 mt-2">
            ✅ These are aggregated from all poll volunteers and included in turnout calculation
          </p>
        </div>
      </div>

      {/* Auto-Suspension Rules Info */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="font-semibold text-yellow-900 mb-2">🔒 Automatic Suspension Rules:</h4>
        <ul className="text-sm text-yellow-800 space-y-1">
          <li>• Voters with <strong>violations over 90 days old</strong> are automatically suspended</li>
          <li>• Voters with <strong>unpaid dues</strong> are automatically suspended (e.g., 90 day past due bill)</li>
          <li>• Board can manually suspend voters for other reasons</li>
        </ul>
      </div>

      {/* Cleanup Invalid Voters for THIS Election */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h4 className="font-semibold text-red-900 mb-2">🧹 Cleanup Invalid Voters</h4>
        <p className="text-sm text-red-800 mb-3">
          Remove voters from <strong>this election only</strong> who don't have a valid property in Property Management.
          This will NOT affect other elections or delete user accounts.
        </p>
        <button
          onClick={async () => {
            if (!electionData?.hoa_id) {
              alert('❌ Election data not loaded. Please refresh and try again.');
              return;
            }
            
            if (!window.confirm(`🧹 This will remove from THIS election only:\n\n• Eligible voters without valid Property ID\n• Vote records for non-existent users\n• Ballots from deleted users\n\nThis will NOT affect:\n• Other elections\n• User accounts\n• Property Management data\n\nContinue?`)) return;
            
            try {
              const response = await api.post('/api/vote/admin/cleanup-orphaned-votes', {
                hoa_id: electionData.hoa_id,
                election_id: electionId
              });
              
              if (response.data?.success) {
                const details = response.data.details || {};
                alert(`✅ Cleanup Complete for this election!\n\n• Property votes removed: ${details.property_votes_removed || 0}\n• Ballots removed: ${details.ballots_removed || 0}\n• Proxies removed: ${details.proxies_removed || 0}\n• Votes removed: ${details.votes_removed || 0}\n• Invalid users found: ${details.invalid_users_found || 0}`);
                loadEligibleVoters(); // Refresh the list
              }
            } catch (error) {
              console.error('Cleanup error:', error);
              alert(`❌ Cleanup failed: ${error.response?.data?.detail || error.message}`);
            }
          }}
          disabled={!electionData?.hoa_id}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm disabled:bg-gray-400"
        >
          🗑️ Remove Invalid Voters from This Election
        </button>
      </div>

      {/* Voters Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Property ID</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Address</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Owner</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">Status</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">Voted</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Proxies</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Suspension Reasons</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {voters.map((voter, idx) => (
                <tr key={idx} className={`border-b border-gray-100 ${
                  !voter.is_eligible ? 'bg-red-50' : 'hover:bg-gray-50'
                }`}>
                  <td className="py-3 px-4 text-sm font-semibold text-blue-900">{voter.property_identifier || 'N/A'}</td>
                  <td className="py-3 px-4 text-sm">{voter.property_address}</td>
                  <td className="py-3 px-4 text-sm">
                    <div>{voter.name}</div>
                    <div className="text-xs text-gray-500">{voter.email}</div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    {voter.is_eligible ? (
                      <span className="inline-flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                        ✅ Eligible
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                        🚫 Suspended
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {voter.has_voted ? (
                      <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                        ✅ Voted
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                        ⏳ Not Voted
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm">
                    {(voter.proxies_given?.length > 0 || voter.proxies_received?.length > 0) ? (
                      <div className="space-y-2 text-xs">
                        {voter.proxies_given?.length > 0 && (
                          <div>
                            <div className="font-semibold text-purple-700">Gave {voter.proxies_given.length} proxy(ies):</div>
                            {voter.proxies_given.map((p, i) => (
                              <div key={i} className="ml-2 text-gray-600">
                                → {p.holder_name} ({p.proxy_type === 'quorum_only' ? 'Quorum Only' : 'Full Proxy'})
                              </div>
                            ))}
                          </div>
                        )}
                        {voter.proxies_received?.length > 0 && (
                          <div>
                            <div className="font-semibold text-blue-700">Received {voter.proxies_received.length} proxy(ies):</div>
                            {voter.proxies_received.map((p, i) => (
                              <div key={i} className="ml-2 text-gray-600">
                                ← {p.giver_name} ({p.proxy_type === 'quorum_only' ? 'Quorum Only' : 'Full Proxy'})
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">No proxies</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-sm">
                    {voter.suspension_reasons.length > 0 ? (
                      <div className="space-y-1">
                        {voter.suspension_reasons.map((reason, i) => (
                          <div key={i} className="text-xs">
                            <span className={`inline-block px-2 py-1 rounded ${
                              reason.includes('Manual') 
                                ? 'bg-orange-100 text-orange-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {reason}
                            </span>
                          </div>
                        ))}
                        {voter.old_violations_count > 0 && (
                          <div className="mt-2 text-xs text-gray-600">
                            <details className="cursor-pointer">
                              <summary className="font-medium">View {voter.old_violations_count} violation(s)</summary>
                              <div className="mt-1 space-y-1 pl-2">
                                {voter.old_violations.map((v, i) => (
                                  <div key={i}>• {v.description} ({v.days_old} days old)</div>
                                ))}
                              </div>
                            </details>
                          </div>
                        )}
                        {voter.unpaid_dues_amount > 0 && (
                          <div className="text-xs text-gray-600">
                            {voter.unpaid_dues_count} unpaid item(s): ${voter.unpaid_dues_amount.toFixed(2)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">None</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {voter.manually_suspended ? (
                      <button
                        onClick={() => handleSuspendVoter(voter, false)}
                        className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                      >
                        Remove Suspension
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSuspendVoter(voter, true)}
                        className="px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                      >
                        Manually Suspend
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Suspend/Unsuspend Modal */}
      {suspendModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">
              {suspendModal.suspend ? '🚫 Manually Suspend Voter' : '✅ Remove Suspension'}
            </h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>{suspendModal.voter?.name}</strong> ({suspendModal.voter?.property_address})
              </p>
              
              {suspendModal.suspend ? (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Suspension *
                  </label>
                  <textarea
                    value={suspendReason}
                    onChange={(e) => setSuspendReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                    placeholder=""
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This reason will be visible to board members and recorded in the system.
                  </p>
                </>
              ) : (
                <p className="text-sm text-gray-700">
                  This will remove the manual suspension. Auto-suspensions (violations, unpaid dues) will still apply.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setSuspendModal({ show: false, voter: null, suspend: true })}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmSuspension}
                className={`px-4 py-2 text-white rounded-lg ${
                  suspendModal.suspend ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {suspendModal.suspend ? 'Suspend Voter' : 'Remove Suspension'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rule Sets Management Modal - Removed from EligibleVotersTab as it doesn't have access to showRuleSetsModal */}
      {false && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" 
          style={{ zIndex: 9999 }}
          onClick={() => {
            console.log('Rules modal overlay clicked, closing');
            setShowRuleSetsModal(false);
          }}
        >
          <div 
            className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto" 
            onClick={e => {
              e.stopPropagation();
              console.log('Rules modal content clicked, staying open');
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">📋 Meeting Rules Manager</h3>
              <button onClick={() => setShowRuleSetsModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
            </div>

            <div className="space-y-6">
              {/* Current Active Rule Set */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-900 mb-2">Current Rule Set</h4>
                <p className="text-purple-700 font-medium">{activeRuleSetName}</p>
              </div>

              {/* Meeting Rules Display */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Active Meeting Rules:</h4>
                {localMeetingRules && localMeetingRules.length > 0 ? (
                  <ul className="space-y-2">
                    {localMeetingRules.map((rule, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-purple-600 font-bold">{idx + 1}.</span>
                        <span className="flex-1">{rule}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 italic">No meeting rules configured.</p>
                )}
              </div>

              {/* Save Current Rules as New Set */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-3">Save Current Rules as Template</h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newRuleSetName}
                    onChange={(e) => setNewRuleSetName(e.target.value)}
                    placeholder="Enter template name (e.g., 'Board Meetings', 'Committee Rules')"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={async () => {
                      if (!newRuleSetName.trim()) {
                        alert('Please enter a name for this rule set');
                        return;
                      }
                      
                      const newRuleSet = {
                        id: Date.now().toString(),
                        name: newRuleSetName,
                        rules: localMeetingRules,
                        speakingTime: localSpeakingTimeMinutes,
                        createdAt: new Date().toISOString()
                      };
                      
                      const updatedSets = [...savedRuleSets, newRuleSet];
                      setSavedRuleSets(updatedSets);
                      localStorage.setItem('savedRuleSets', JSON.stringify(updatedSets));
                      
                      alert(`✅ Rule set "${newRuleSetName}" saved successfully!`);
                      setNewRuleSetName('');
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium whitespace-nowrap"
                  >
                    💾 Save Template
                  </button>
                </div>
                <p className="text-xs text-blue-700 mt-2">
                  Save the current rules as a reusable template for future meetings
                </p>
              </div>

              {/* Saved Rule Sets */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Saved Templates ({savedRuleSets.length})</h4>
                {savedRuleSets.length > 0 ? (
                  <div className="space-y-2">
                    {savedRuleSets.map((ruleSet) => (
                      <div key={ruleSet.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{ruleSet.name}</p>
                          <p className="text-xs text-gray-500">{ruleSet.rules?.length || 0} rules • Speaking time: {ruleSet.speakingTime}min</p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              if (window.confirm(`Load "${ruleSet.name}" rules for this meeting?`)) {
                                setLocalMeetingRules(ruleSet.rules);
                                setLocalSpeakingTimeMinutes(ruleSet.speakingTime);
                                setActiveRuleSetName(ruleSet.name);
                                localStorage.setItem(getTabStateKey('meetingRules'), JSON.stringify(ruleSet.rules));
                                localStorage.setItem(getTabStateKey('speakingTimeMinutes'), ruleSet.speakingTime.toString());
                                alert(`✅ Loaded "${ruleSet.name}" rules`);
                              }
                            }}
                            className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                          >
                            Load
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Delete "${ruleSet.name}" template?`)) {
                                const updatedSets = savedRuleSets.filter(rs => rs.id !== ruleSet.id);
                                setSavedRuleSets(updatedSets);
                                localStorage.setItem('savedRuleSets', JSON.stringify(updatedSets));
                                alert(`🗑️ Deleted "${ruleSet.name}"`);
                              }
                            }}
                            className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic text-center py-4">
                    No saved templates yet. Save your current rules to reuse them later.
                  </p>
                )}
              </div>

              {/* Instructions */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 mb-2">💡 How to Use</h4>
                <ul className="text-sm text-yellow-800 space-y-1">
                  <li>• <strong>Save Template:</strong> Save current rules to reuse in future meetings</li>
                  <li>• <strong>Load Template:</strong> Apply saved rules to the current meeting</li>
                  <li>• <strong>Edit Rules:</strong> Close this modal and edit rules in the main meeting view</li>
                  <li>• <strong>Templates are saved locally</strong> in your browser for quick access</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowRuleSetsModal(false)}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Save/Load Meeting State Modal - Removed from EligibleVotersTab as it doesn't have access to showSaveLoadModal */}
      {false && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
          style={{ zIndex: 9999 }}
          onClick={() => {
            console.log('Modal overlay clicked, closing');
            setShowSaveLoadModal(false);
          }}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" 
            onClick={e => {
              e.stopPropagation();
              console.log('Modal content clicked, staying open');
            }}
          >
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">💾 Meeting State Manager</h2>
                <button
                  onClick={() => setShowSaveLoadModal(false)}
                  className="text-white hover:bg-white/20 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
                >
                  ✕
                </button>
              </div>
              <p className="text-purple-100 text-sm mt-2">Save and restore meeting states • Auto-expires after 30 days</p>
            </div>

            <div className="p-6 space-y-6">
              {/* Save Current State Section */}
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-5">
                <h3 className="text-lg font-semibold text-purple-900 mb-3 flex items-center gap-2">
                  <span className="text-2xl">💾</span>
                  Save Current State
                </h3>
                <p className="text-sm text-purple-700 mb-4">Save all motions, attendance, discussions, notes, and meeting data</p>
                
                <input
                  type="text"
                  value={saveStateName}
                  onChange={(e) => setSaveStateName(e.target.value)}
                  placeholder="e.g., Board Meeting Nov 7 - Draft"
                  className="w-full px-4 py-3 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-3"
                />
                
                <button
                  onClick={async () => {
                    if (!saveStateName.trim()) {
                      alert('Please enter a name for this state');
                      return;
                    }
                    
                    setSavingState(true);
                    try {
                      // Fetch current discussions before saving
                      let currentDiscussions = [];
                      try {
                        const discussionsResp = await api.get(`/api/vote/discussions?meeting_type=${meetingTypeFilter}`);
                        currentDiscussions = discussionsResp.data?.discussions || [];
                      } catch (err) {
                        console.error('Error fetching discussions for save:', err);
                      }
                      
                      // Gather all meeting data
                      const meetingData = {
                        meeting_type: meetingTypeFilter,
                        committee_id: selectedCommittee?.id,
                        attendance: attendance,
                        meeting_start_time: meetingStartTime,
                        meeting_end_time: meetingEndTime,
                        motions: activeMotions,
                        discussions: currentDiscussions,
                        meeting_notes: liveMeetingNotes,
                        action_items: liveActionItems,
                        meeting_location: meetingLocation,
                        meeting_rules: localMeetingRules,
                        speaking_time_minutes: localSpeakingTimeMinutes,
                        proof_of_notice: proofOfNotice,
                        manual_prior_minutes: manualPriorMinutes,
                        preview_data: livePreviewData,
                        supporting_documents: supportingDocuments
                      };
                      
                      // Save state
                      const response = await api.post('/api/meeting-states/save', {
                        name: saveStateName.trim(),
                        data: meetingData
                      });
                      
                      alert('✅ Meeting state saved successfully!\nExpires: ' + formatLocal(response.data.expires_at, hoaTimezone, 'MMM d, yyyy'));
                      setSaveStateName('');
                      
                      // Reload saved states list
                      loadSavedStates();
                    } catch (error) {
                      console.error('Error saving state:', error);
                      alert('❌ Failed to save meeting state. Please try again.');
                    } finally {
                      setSavingState(false);
                    }
                  }}
                  disabled={savingState || !saveStateName.trim()}
                  className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium transition-colors"
                >
                  {savingState ? 'Saving...' : '💾 Save State'}
                </button>
              </div>

              {/* Load Saved States Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
                  <span className="text-2xl">📂</span>
                  Load Saved State
                </h3>
                
                <button
                  onClick={loadSavedStates}
                  className="mb-4 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm"
                >
                  🔄 Refresh List
                </button>
                
                {savedStates.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-4xl mb-2">📭</p>
                    <p>No saved states yet</p>
                    <p className="text-sm">Save your current meeting to resume later</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {savedStates.map((state) => (
                      <div key={state.id} className="bg-white border border-blue-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{state.name}</h4>
                            <div className="text-xs text-gray-500 mt-1 space-y-1">
                              <div>📅 Saved: {formatLocal(state.saved_at, hoaSettings?.timezone || 'US/Central', 'MMM d, yyyy h:mm a')}</div>
                              <div>📊 {state.motion_count} motion(s), {state.discussion_count} discussion(s)</div>
                              <div>⏰ Expires in {state.days_until_expiry} day(s)</div>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2 ml-3">
                            <button
                              onClick={async () => {
                                if (window.confirm(`Load "${state.name}"?\n\nThis will replace your current meeting data.`)) {
                                  setLoadingState(true);
                                  try {
                                    const response = await api.get(`/api/meeting-states/${state.id}`);
                                    const data = response.data.state.data;
                                    
                                    // Restore all state
                                    setAttendance(data.attendance || []);
                                    setActiveMotions(data.motions || []);
                                    setLiveMeetingNotes(data.meeting_notes || '');
                                    setLiveActionItems(data.action_items || []);
                                    setMeetingLocation(data.meeting_location || '');
                                    setLocalMeetingRules(data.meeting_rules || []);
                                    setLocalSpeakingTimeMinutes(data.speaking_time_minutes || 3);
                                    setProofOfNotice(data.proof_of_notice || {uploaded: false});
                                    setManualPriorMinutes(data.manual_prior_minutes || '');
                                    setLivePreviewData(data.preview_data || null);
                                    setSupportingDocuments(data.supporting_documents || []);
                                    
                                    // Restore meeting times if they exist
                                    if (data.meeting_start_time) {
                                      setMeetingStartTime(new Date(data.meeting_start_time));
                                    }
                                    if (data.meeting_end_time) {
                                      setMeetingEndTime(new Date(data.meeting_end_time));
                                    }
                                    
                                    alert(`✅ State "${state.name}" loaded successfully!`);
                                    setShowSaveLoadModal(false);
                                  } catch (error) {
                                    console.error('Error loading state:', error);
                                    alert('❌ Failed to load meeting state. It may have expired.');
                                  } finally {
                                    setLoadingState(false);
                                  }
                                }
                              }}
                              disabled={loadingState}
                              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                            >
                              📂 Load
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm(`Delete "${state.name}"?\n\nThis action cannot be undone.`)) {
                                  try {
                                    await api.delete(`/api/meeting-states/${state.id}`);
                                    alert('✅ State deleted');
                                    loadSavedStates();
                                  } catch (error) {
                                    console.error('Error deleting state:', error);
                                    alert('❌ Failed to delete state');
                                  }
                                }
                              }}
                              className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Info Section */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <h4 className="font-semibold text-yellow-900 mb-2">ℹ️ How It Works</h4>
                <ul className="text-sm text-yellow-800 space-y-1">
                  <li>• <strong>Save</strong>: Store your current meeting progress</li>
                  <li>• <strong>Load</strong>: Resume a saved meeting anytime</li>
                  <li>• <strong>Expires</strong>: Saved states automatically delete after 30 days</li>
                  <li>• <strong>Not Saved</strong>: File attachments (can be re-added)</li>
                </ul>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t p-4 rounded-b-2xl">
              <button
                onClick={() => setShowSaveLoadModal(false)}
                className="w-full px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );

}